import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import PageTransition from './components/PageTransition';
import { HeroFadeIn } from './components/HeroFadeIn';
import { RevealOnScroll } from './components/RevealOnScroll';
import { TiltCard } from './components/TiltCard';
import { Plus, X, FileText, CheckCircle, Circle, Home, Award, Send, ChevronRight, Bell, Building, Mail, Filter, Edit, Search, RefreshCw, ArrowDownUp, RotateCcw, Trash2, AlertTriangle, Cpu, LogOut, GripVertical, BarChart3, CheckSquare, Square, ChevronDown, Mic, MicOff, Image, Paperclip, MoreVertical, Play, Pause, Volume2, Hash, Copy, Download, FileSpreadsheet, FileText as FileTextIcon, Users, DollarSign, Package, ChevronDown as ChevronDownIcon, Calendar } from 'lucide-react';
import ProformaManager from './components/ProformaManager';
import BaseDialog from './components/BaseDialog';
import SalesAnalytics from './components/SalesAnalytics';
import CostSheet from './components/costSheet/CostSheet';
import { prefetchQuotes, fetchQuotesForOrder } from './utils/quotesService';
import { suppressMetaMaskErrors } from './utils/web3Utils';
import Header from './components/common/Header';
import LoginScreen from './components/common/LoginScreen';
import { sortOrders, filterOrdersByStatus, filterOrdersByDate, filterOrdersBySearch, getStatusColors, getStatusDisplayNames } from './utils/orderUtils';

// --- Firebase Imports ---
import { app, db, storage, auth } from './firebase';
import { geminiRateLimiter, rateLimitedApiCall, retryWithBackoff } from './utils/rateLimiter';

/**
 * RN NUMBER SYSTEM FOR CATEGORIES:
 * 
 * This system handles RN numbers for orders in categories with the following rules:
 * 
 * 1. ORDERS CREATED WITHIN CATEGORIES:
 *    - Get the category's RN number (e.g., if category has RN00001, all orders created within it get RN00001)
 *    - These orders maintain their RN number even if moved between categories
 * 
 * 2. EXISTING ORDERS ADDED TO CATEGORIES:
 *    - Keep their original RN number (preserved from when they were standalone orders)
 *    - Can be removed from categories and will get a new RN number when they become standalone again
 * 
 * 3. RN NUMBER ASSIGNMENT:
 *    - Regular orders: Get sequential RN numbers (RN00001, RN00002, etc.)
 *    - Category orders: Get the category's RN number
 *    - Orders added to categories later: Keep their original RN number
 * 
 * 4. FUNCTIONS AVAILABLE:
 *    - correctOrderRNNumbers(): Fixes all RN numbers based on date order
 *    - validateOrderRNNumbers(): Checks RN number consistency
 *    - Add existing orders to categories (preserves RN numbers)
 *    - Remove orders from categories (restores RN numbers if needed)
 * 
 * 5. UI FEATURES:
 *    - "Add to Category" button on standalone orders
 *    - "Remove from Category" button on orders added to categories later
 *    - Modal for selecting which category to add orders to
 *    - Automatic RN number management
 */

import {
    collection,
    addDoc,
    onSnapshot,
    query,
    doc,
    updateDoc,
    writeBatch,
    serverTimestamp,
    where,
    getDocs,
    setDoc,
    deleteDoc,
    getDoc,
    runTransaction,
    orderBy,
    arrayUnion, arrayRemove, deleteField
} from 'firebase/firestore';

import {
    getAuth,
    onAuthStateChanged,
    signInWithPopup,
    OAuthProvider,
    signOut
} from 'firebase/auth';

import {
    ref as storageRef,
    uploadBytes,
    getDownloadURL,
    deleteObject
} from 'firebase/storage';

// --- Configuration ---
// Validate required environment variables
const GEMINI_API_KEY = process.env.REACT_APP_GEMINI_API_KEY;
const requiredEnvVars = {
    REACT_APP_FIREBASE_API_KEY: process.env.REACT_APP_FIREBASE_API_KEY,
    REACT_APP_FIREBASE_AUTH_DOMAIN: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    REACT_APP_FIREBASE_PROJECT_ID: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    REACT_APP_FIREBASE_STORAGE_BUCKET: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    REACT_APP_FIREBASE_MESSAGING_SENDER_ID: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    REACT_APP_FIREBASE_APP_ID: process.env.REACT_APP_FIREBASE_APP_ID,
    REACT_APP_GEMINI_API_KEY: process.env.REACT_APP_GEMINI_API_KEY
};

// Check for missing environment variables
const missingEnvVars = Object.entries(requiredEnvVars)
    .filter(([key, value]) => !value)
    .map(([key]) => key);

if (missingEnvVars.length > 0) {
    console.error('Missing required environment variables:', missingEnvVars);
    throw new Error(`Missing required environment variables: ${missingEnvVars.join(', ')}`);
}


// --- Initialize Firebase ---
export { app, db, storage, auth };

// --- Custom Hooks ---

// Test Firebase connection
const testFirebaseConnection = async () => {
    try {
        const testDoc = await addDoc(collection(db, 'test'), {
            timestamp: serverTimestamp(),
            test: true
        });

        // Clean up test document
        await deleteDoc(testDoc);

        return true;
    } catch (error) {
        console.error('Firebase connection test failed:', error);
        return false;
    }
};

const useClickOutside = (ref, callback) => {
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (ref.current && !ref.current.contains(event.target)) {
                callback();
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [ref, callback]);
};

const useEscKey = (callback) => {
    useEffect(() => {
        const handleEsc = (e) => { if (e.key === 'Escape') callback(); };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [callback]);
};



// --- Helper Components ---

const Modal = ({ children, onClose }) => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        {children}
    </div>
);

const Button = ({ children, onClick, disabled = false, className = "", type = "button", ...props }) => (
    <button
        type={type}
        onClick={onClick}
        disabled={disabled}
        className={`bg-black text-white px-4 py-2 rounded-md hover:bg-gray-800 disabled:bg-gray-400 ${className}`}
        {...props}
    >
        {children}
    </button>
);

const LoadingScreen = ({ message }) => (
    <div className="w-full h-full flex flex-col items-center justify-center p-8">
        <div className="loader ease-linear rounded-full border-4 border-t-4 border-gray-200 h-12 w-12 mb-4"></div>
        <h3 className="text-lg font-semibold">{message}</h3>
        <style>{`.loader { border-top-color: #000; animation: spinner 1.5s linear infinite; } @keyframes spinner { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
    </div>
);


const ErrorModal = ({ title, message, onClose }) => {
    useEscKey(onClose);
    return (
        <Modal>
            <BaseDialog
                title={title}
                actions={[
                    <Button key="ok" onClick={onClose}>OK</Button>
                ]}
            >
                <div className="flex items-start">
                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                        <AlertTriangle className="h-6 w-6 text-red-600" aria-hidden="true" />
                    </div>
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                        <div className="mt-2">
                            <p className="text-sm text-gray-500 whitespace-pre-wrap">{message}</p>
                        </div>
                    </div>
                </div>
            </BaseDialog>
        </Modal>
    );
};


const CountdownTimer = ({ targetDate }) => {
    const calculateTimeLeft = () => {
        if (!targetDate) return {};

        // Ensure targetDate is a valid Date object
        let target;
        if (targetDate.toDate) {
            target = targetDate.toDate();
        } else if (targetDate instanceof Date) {
            target = targetDate;
        } else {
            target = new Date(targetDate);
        }

        const difference = target.getTime() - new Date().getTime();
        let timeLeft = {};

        if (difference > 0) {
            timeLeft = {
                days: Math.floor(difference / (1000 * 60 * 60 * 24)),
                hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
                minutes: Math.floor((difference / (1000 * 60)) % 60),
            };
        }

        return timeLeft;
    };

    const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());

    useEffect(() => {
        // Update every second for more accurate countdown
        const timer = setInterval(() => {
            const newTimeLeft = calculateTimeLeft();
            setTimeLeft(newTimeLeft);
        }, 1000);

        return () => clearInterval(timer);
    }, [targetDate]);

    const { days, hours, minutes } = timeLeft;

    if (!targetDate) {
        return <div className="text-sm text-gray-500 mt-1 font-semibold">No delivery date</div>
    }

    if (days === undefined || days < 0) {
        return <div className="text-sm text-red-700 mt-1 font-semibold">Delivery due!</div>;
    }

    let outputString = '';
    if (days > 0) {
        outputString += `${days}d `;
    }
    if (hours > 0) {
        outputString += `${hours}h `;
    }
    if (minutes > 0 && days === 0) {
        outputString += `${minutes}m `;
    }

    if (outputString === '' && (+new Date(targetDate) - +new Date()) > 0) {
        return <div className="text-sm text-purple-700 mt-1 font-semibold">Less than 1h left</div>;
    }

    return (
        <div className="text-sm text-purple-700 mt-1 font-semibold">
            {outputString ? <>{outputString.trim()} left</> : <span>Delivery due!</span>}
        </div>
    );
};


const InputField = ({ label, name, value, onChange, icon, type = "text", required = false, autoComplete = "on", placeholder, disabled = false, onWheel }) => (
    <div>
        <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
        <div className="relative">
            {icon && <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">{icon}</span>}
            <input
                type={type}
                id={name}
                name={name}
                value={value}
                onChange={onChange}
                required={required}
                placeholder={placeholder || label}
                onWheel={onWheel || ((e) => { if (type === 'number') e.target.blur() })}
                className={`w-full ${icon ? 'pl-10' : 'pl-3'} pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''} no-arrows`}
                autoComplete={autoComplete}
                disabled={disabled}
            />
        </div>
    </div>
);


const ConfirmDeleteModal = ({ onCancel, onConfirm }) => {
    useEscKey(onCancel);
    return (
        <Modal>
            <BaseDialog
                title="Delete Order"
                actions={[
                    <Button key="delete" onClick={onConfirm} className="bg-red-600 hover:bg-red-700">Delete</Button>,
                    <Button key="cancel" onClick={onCancel} className="bg-gray-200 text-black hover:bg-gray-300">Cancel</Button>
                ]}
            >
                <p className="text-sm text-gray-500">Are you sure you want to delete this order? This action cannot be undone.</p>
            </BaseDialog>
        </Modal>
    );
};

const statusColors = {
    'Awaiting Requirements': 'bg-amber-100 text-amber-800 border border-amber-200',
    'Negotiation': 'bg-orange-100 text-orange-800 border border-orange-200',
    'Information': 'bg-sky-100 text-sky-800 border border-sky-200',
    'Proforma': 'bg-emerald-100 text-emerald-800 border border-emerald-200',
    'Advance': 'bg-violet-100 text-violet-800 border border-violet-200',
    'In Progress': 'bg-indigo-100 text-indigo-800 border border-indigo-200',
    'Completed': 'bg-green-100 text-green-800 border border-green-200',
    'Category': 'bg-gray-100 text-gray-800 border border-gray-200'
};

const EmailNotificationModal = ({ notification, onClose }) => {
    useEscKey(onClose);
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md m-4">
                <div className="flex items-start">
                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                        <Mail className="h-6 w-6 text-blue-600" aria-hidden="true" />
                    </div>
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                        <h3 className="text-lg leading-6 font-medium text-gray-900">Email Notification</h3>
                        <div className="mt-2">
                            <p className="text-sm text-gray-500 whitespace-pre-wrap">{notification.message}</p>
                            <p className="text-xs text-gray-400 mt-2">{notification.timestamp.toLocaleString()}</p>
                        </div>
                    </div>
                </div>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                    <Button onClick={onClose} className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm">
                        OK
                    </Button>
                </div>
            </div>
        </div>
    );
};


// --- Main App Component ---
const App = () => {
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [authError, setAuthError] = useState(null);
    const [msAccessToken, setMsAccessToken] = useState(() => localStorage.getItem('msAccessToken') || null); // Restore from localStorage
    const [allVendors, setAllVendors] = useState(null); // <-- Initialize as null to indicate loading state
    const [emailNotifications, setEmailNotifications] = useState([]); // <-- Store email notifications for pop-ups

    // Suppress MetaMask connection errors
    useEffect(() => {
        suppressMetaMaskErrors();
    }, []);

    const isAutoMaster = user?.role === 'auto-master';

    // Persist msAccessToken to localStorage whenever it changes
    useEffect(() => {
        if (msAccessToken) {
            localStorage.setItem('msAccessToken', msAccessToken);
        } else {
            localStorage.removeItem('msAccessToken');
        }
    }, [msAccessToken]);

    useEffect(() => {
        // Only fetch vendors when user is authenticated
        if (!user) {
            return;
        }

        // Fetch all vendors for use in email sync and elsewhere
        const vendorsQuery = query(collection(db, 'vendors'));
        const unsubscribeVendors = onSnapshot(vendorsQuery, (snapshot) => {
            const vendorsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setAllVendors(vendorsData);
        }, (error) => {
            setAllVendors([]); // Set empty array on error
        });
        return () => {
            unsubscribeVendors();
        };
    }, [user]); // Add user as dependency

    // Add a useEffect for authentication state that runs for all users
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            if (firebaseUser && !firebaseUser.isAnonymous) {
                const userDocRef = doc(db, "users", firebaseUser.uid);
                const userDocSnap = await getDoc(userDocRef);
                let userProfile;
                if (!userDocSnap.exists()) {
                    userProfile = {
                        uid: firebaseUser.uid,
                        displayName: firebaseUser.displayName || 'New User',
                        email: firebaseUser.email,
                        role: "employee"
                    };
                    await setDoc(userDocRef, userProfile);
                } else {
                    userProfile = userDocSnap.data();
                }
                setUser(userProfile);
            } else {
                setUser(null);
                setMsAccessToken(null); // Clear token on sign out
            }
            setIsLoading(false);
        });
        return () => unsubscribe();
    }, []);

    // Keep the email sync logic in a separate useEffect for auto-master only
    useEffect(() => {
        const fetchEmailUpdates = async () => {
            if (!isAutoMaster || !user) return;
            if (!msAccessToken) {
                setAuthError({
                    title: "Microsoft Sign-in Required",
                    message: "Your Microsoft access token is missing or expired. Please sign in again to enable email sync."
                });
                return;
            }
            if (!allVendors || allVendors.length === 0) {
                // Wait until vendors are loaded, do not log a warning
                return;
            }

            try {
                // 1. Get last sync time from Firestore (user doc)
                const userDocRef = doc(db, 'users', user.uid);
                const userDocSnap = await getDoc(userDocRef);
                let lastSync = null;
                if (userDocSnap.exists()) {
                    lastSync = userDocSnap.data().lastEmailSync || null;
                }

                // 2. Fetch supplier emails from vendors collection
                // Use allVendors from state
                const supplierEmails = allVendors
                    .map(doc => (doc.email || '').toLowerCase())
                    .filter(email => !!email);

                // 3. Use msAccessToken for Microsoft Graph
                const accessToken = msAccessToken;
                if (!accessToken) {
                    return;
                }

                // 4. Fetch new emails from suppliers
                const newEmails = await fetchSupplierEmails(accessToken, supplierEmails, lastSync);
                let lastSyncStr = 'never';
                if (lastSync) {
                    try {
                        if (lastSync.toDate) {
                            lastSyncStr = lastSync.toDate().toISOString();
                        } else {
                            lastSyncStr = new Date(lastSync).toISOString();
                        }
                    } catch (e) {
                        lastSyncStr = 'invalid date';
                    }
                }
                console.log(`Email sync: Found ${newEmails.length} new emails since last sync (${lastSyncStr})`);
                console.log(`Email sync: Supplier emails being checked:`, supplierEmails);
                console.log(`Email sync: New emails:`, newEmails.map(email => ({
                    id: email.id,
                    subject: email.subject,
                    from: email.from?.emailAddress?.address,
                    receivedDateTime: email.receivedDateTime
                })));

                // 5. For each email:
                let latestEmailDate = lastSync ? new Date(lastSync) : null;
                console.log(`Email sync: Starting to process ${newEmails.length} emails...`);
                for (const email of newEmails) {
                    console.log(`Email sync: Starting to process email: "${email.subject}" from ${email.from?.emailAddress?.address}`);

                    // Check if this email has already been processed using a dedicated collection
                    console.log(`Email sync: Checking if email "${email.subject}" (ID: ${email.id}) has been processed...`);
                    const processedEmailsQuery = query(collection(db, 'processedEmails'), where('emailId', '==', email.id));
                    const processedEmailsSnapshot = await getDocs(processedEmailsQuery);

                    console.log(`Email sync: Processed emails check result: ${processedEmailsSnapshot.empty ? 'Not processed' : 'Already processed'}`);

                    if (!processedEmailsSnapshot.empty) {
                        console.log(`Email sync: Skipping already processed email: ${email.subject}`);
                        continue;
                    }

                    console.log(`Email sync: Processing email "${email.subject}" with AI...`);
                    const extracted = await processEmailWithGemini(email.body && email.body.content ? email.body.content : '', email.subject || '');
                    console.log(`Email sync: AI extraction result:`, extracted);

                    if (!extracted) {
                        // AI extraction failed, add to email notifications for pop-up
                        console.log(`Email sync: AI extraction failed for email "${email.subject}"`);
                        setEmailNotifications(prev => [...prev, {
                            id: Date.now() + Math.random(), // Generate unique ID
                            message: `Failed to process supplier email: ${email.subject || 'No subject'}`,
                            timestamp: new Date()
                        }]);
                        continue;
                    }

                    // Check if we have a part number for order matching
                    console.log(`Email sync: Checking for part number in extracted data:`, extracted.partNumber);

                    if (!extracted.partNumber) {
                        // No part number found, but still create a notification for manual review
                        console.log(`Email sync: No part number found in email "${email.subject}"`);
                        setEmailNotifications(prev => [...prev, {
                            id: Date.now() + Math.random(), // Generate unique ID
                            message: `Supplier email received (no part number): ${email.subject || 'No subject'} - Manual review required`,
                            timestamp: new Date()
                        }]);

                        // Try to find any order that might match based on supplier or other criteria
                        // For now, we'll skip creating quotes without part numbers to avoid confusion
                        // The user can manually create quotes from the email notification
                        continue;
                    }
                    const partNumber = (extracted.partNumber || '').trim().toUpperCase();
                    const extractedQty = extracted.quantity ? parseInt(extracted.quantity) : null;
                    // Fetch all orders and find those whose description or partNumber field contains the part number
                    const matchingOrdersQuery = query(collection(db, 'orders'));
                    const ordersSnap = await getDocs(matchingOrdersQuery);
                    let matchingOrders = ordersSnap.docs.filter(doc => {
                        const data = doc.data();
                        const description = (data.description || '').toUpperCase();
                        const orderPartNumber = (data.partNumber || '').toUpperCase();
                        const orderTitle = (data.orderTitle || '').toUpperCase();

                        // Check for exact match first
                        if (description.includes(partNumber) || orderPartNumber.includes(partNumber) || orderTitle.includes(partNumber)) {
                            return true;
                        }

                        // Check for partial matches (e.g., "4P-5152" might match "4P5152" or "4P 5152")
                        const cleanPartNumber = partNumber.replace(/[^A-Z0-9]/g, '');
                        const cleanDescription = description.replace(/[^A-Z0-9]/g, '');
                        const cleanOrderPartNumber = orderPartNumber.replace(/[^A-Z0-9]/g, '');
                        const cleanOrderTitle = orderTitle.replace(/[^A-Z0-9]/g, '');

                        return (
                            cleanDescription.includes(cleanPartNumber) ||
                            cleanOrderPartNumber.includes(cleanPartNumber) ||
                            cleanOrderTitle.includes(cleanPartNumber)
                        );
                    });

                    console.log(`Email sync: Looking for part number "${partNumber}" in ${ordersSnap.docs.length} orders, found ${matchingOrders.length} matches`);
                    if (matchingOrders.length === 0) {
                        console.log(`Email sync: No matches found. Available orders:`, ordersSnap.docs.map(doc => ({
                            id: doc.id,
                            title: doc.data().orderTitle,
                            description: doc.data().description,
                            partNumber: doc.data().partNumber
                        })));
                    }
                    // If more than one order matches, filter by quantity if available
                    if (matchingOrders.length > 1 && extractedQty !== null) {
                        matchingOrders = matchingOrders.filter(doc => {
                            const data = doc.data();
                            const orderQty = data.quantity ? parseInt(data.quantity) : null;
                            return orderQty === extractedQty;
                        });
                    }
                    if (matchingOrders.length === 0) {
                        // No order found, but create a quote for manual review
                        console.log(`Email sync: No matching order found for part number "${partNumber}". Creating quote for manual review.`);

                        // Create a quote that needs manual review and order assignment
                        const supplierName = extracted.supplier || (email.from && email.from.emailAddress ? email.from.emailAddress.address : 'Unknown Supplier');

                        // Find any order to attach this quote to (we'll use the first available order as a placeholder)
                        const firstOrder = ordersSnap.docs[0];
                        if (firstOrder) {
                            const orderId = firstOrder.id;
                            const quotesCol = collection(db, `orders/${orderId}/quotes`);
                            await addDoc(quotesCol, {
                                supplier: supplierName,
                                status: QUOTE_STATUSES.AWAITING_INFORMATION,
                                price: extracted.price || null,
                                currency: extracted.currency || 'INR',
                                quantity: extracted.quantity || null,
                                timeline: extracted.timeline || null,
                                timelineUnit: extracted.timelineUnit || 'days',
                                notes: `Auto-extracted from email. Part number "${partNumber}" not found in any order - manual review required. Original subject: ${email.subject}`,
                                needsManualReview: true,
                                needsOrderAssignment: true,
                                sourceEmailId: email.id,
                                fetchedAutomatically: true,
                                createdAt: serverTimestamp(),
                                submittedBy: 'Auto-fetch'
                            });

                            // Mark this email as processed
                            await addDoc(collection(db, 'processedEmails'), {
                                emailId: email.id,
                                processedAt: serverTimestamp(),
                                orderId: orderId,
                                subject: email.subject,
                                from: email.from?.emailAddress?.address || 'unknown',
                                needsManualReview: true
                            });

                            setEmailNotifications(prev => [...prev, {
                                id: Date.now() + Math.random(),
                                message: `Quote created for unknown part number "${partNumber}" - manual review required`,
                                timestamp: new Date()
                            }]);
                        } else {
                            // No orders exist at all, just create notification
                            setEmailNotifications(prev => [...prev, {
                                id: Date.now() + Math.random(),
                                message: `No matching order found for supplier email: ${email.subject || 'No subject'}`,
                                timestamp: new Date()
                            }]);
                        }
                        continue;
                    }
                    // 4. Auto-fill supplier name using sender's email
                    let supplierName = '';
                    let supplierCity = '';
                    if (email.from && email.from.emailAddress && email.from.emailAddress.address) {
                        const senderEmail = email.from.emailAddress.address.toLowerCase();
                        // Try to match to a vendor
                        const vendor = allVendors && allVendors.find(v => v.email && v.email.toLowerCase() === senderEmail);
                        supplierName = vendor ? vendor.name : (extracted.supplier || senderEmail);
                        supplierCity = vendor ? vendor.city : '';
                    } else {
                        supplierName = extracted.supplier || '';
                        supplierCity = '';
                    }

                    // 5. Get the final quantity first (needed for price calculation)
                    const orderDoc = matchingOrders[0];
                    const orderId = orderDoc.id;
                    const orderData = orderDoc.data();
                    // Use extracted quantity if present, otherwise use order's quantity
                    const finalQuantity = extracted.quantity ? parseInt(extracted.quantity) : (orderData.quantity ? parseInt(orderData.quantity) : null);

                    // 6. Handle price based on priceType and ensure it's in INR
                    let price = extracted.price ? parseFloat(extracted.price) : null;
                    let currency = (extracted.currency || 'INR').toUpperCase();

                    // Handle per-unit vs total pricing
                    if (price && extracted.priceType === 'perQty' && finalQuantity) {
                        // If it's per-unit pricing, multiply by quantity to get total price
                        price = price * finalQuantity;
                    }

                    // Convert to INR using live rate if needed
                    if (price && currency !== 'INR') {
                        if (currency === 'USD') {
                            const usdToInr = await fetchUsdToInrRate();
                            price = price * usdToInr;
                            currency = 'INR';
                        } else {
                            // Optionally add more currency support here
                            // For now, treat as INR if unknown
                            currency = 'INR';
                        }
                    }

                    let deliveryTimeline = extracted.timeline ? parseInt(extracted.timeline) : null;
                    let deliveryTimelineUnit = (extracted.timelineUnit || 'days').toLowerCase();
                    if (deliveryTimelineUnit.includes('week') && deliveryTimeline) {
                        deliveryTimeline = deliveryTimeline * 7;
                        deliveryTimelineUnit = 'days';
                    }
                    // Add the quote

                    // Determine payment terms based on advance payment
                    let paymentTerms = 'Before Delivery';
                    if (extracted.advancePayment) {
                        const advancePercent = parseInt(extracted.advancePayment);
                        if (advancePercent === 100) {
                            paymentTerms = 'Full Advance';
                        } else if (advancePercent > 0) {
                            paymentTerms = 'Before Delivery'; // Default to Before Delivery for partial advance
                        }
                    }

                    console.log(`Email sync: Creating quote for order ${orderId} with supplier ${supplierName}`);
                    const quotesCol = collection(db, `orders/${orderId}/quotes`);
                    const quoteRef = await addDoc(quotesCol, {
                        price,
                        currency: 'INR',
                        quantity: finalQuantity,
                        supplier: supplierName,
                        supplierCity: supplierCity, // Add supplier city
                        notes: extracted.notes || '',
                        deliveryTimeline,
                        deliveryTimelineUnit,
                        advancePayment: extracted.advancePayment ? parseInt(extracted.advancePayment) : null,
                        paymentTerms: paymentTerms,
                        priceType: extracted.priceType || 'total', // Store the price type for reference
                        status: QUOTE_STATUSES.UNDER_NEGOTIATION,
                        submittedBy: { uid: user.uid, displayName: user.displayName },
                        createdAt: serverTimestamp(),
                        isBestQuote: false,
                        fetchedAutomatically: true,
                        needsConfirmation: true,
                        sourceEmailId: email.id, // Store the email ID to prevent duplicate processing
                    });
                    console.log(`Email sync: Quote created successfully with ID: ${quoteRef.id}`);

                    // Mark this email as processed
                    await addDoc(collection(db, 'processedEmails'), {
                        emailId: email.id,
                        processedAt: serverTimestamp(),
                        orderId: orderId,
                        quoteId: quoteRef.id,
                        subject: email.subject,
                        from: email.from?.emailAddress?.address || 'unknown'
                    });

                    // Add notification for the new auto-fetched quote to email notifications
                    setEmailNotifications(prev => [...prev, {
                        id: Date.now() + Math.random(), // Generate unique ID
                        message: `Quote fetched automatically from supplier email for order '${extracted.orderTitle}'. Please review and confirm.`,
                        timestamp: new Date()
                    }]);
                    // Track the latest receivedDateTime
                    if (email.receivedDateTime) {
                        const emailDate = new Date(email.receivedDateTime);
                        if (!latestEmailDate || emailDate > latestEmailDate) {
                            latestEmailDate = emailDate;
                        }
                    }
                }

                // 6. Update last sync time in Firestore to latest receivedDateTime
                if (latestEmailDate && !isNaN(latestEmailDate.getTime())) {
                    await setDoc(userDocRef, { lastEmailSync: latestEmailDate }, { merge: true });
                } else if (newEmails.length === 0) {
                    // If no new emails found, only update timestamp if we had a previous sync
                    // This prevents setting a timestamp when there's never been a sync
                    if (lastSync) {
                        console.log(`Email sync: No new emails found, updating timestamp to prevent re-fetching`);
                        await setDoc(userDocRef, { lastEmailSync: new Date() }, { merge: true });
                    } else {
                        console.log(`Email sync: No emails found and no previous sync, keeping timestamp as null`);
                    }
                }
            } catch (err) {
                console.error('Email sync error:', err);
            }
        };

        fetchEmailUpdates();
    }, [isAutoMaster, user, msAccessToken, allVendors]);

    const handleSignIn = async () => {
        setIsLoading(true);
        const provider = new OAuthProvider('microsoft.com');
        provider.addScope('mail.send');
        provider.addScope('user.read');
        provider.addScope('Mail.Read'); // <-- Add this line for email read access
        provider.setCustomParameters({
            prompt: 'consent'
        });
        try {
            const result = await signInWithPopup(auth, provider);
            const credential = OAuthProvider.credentialFromResult(result);
            const accessToken = credential && credential.accessToken;
            setMsAccessToken(accessToken); // <-- Store Microsoft Graph token
            localStorage.setItem('msAccessToken', accessToken); // Persist token
        } catch (error) {
            setAuthError({
                title: "Sign-in Failed",
                message: `Could not sign in with Microsoft. Please try again. Error: ${error.message}`
            });
            setIsLoading(false);
        }
    };

    const handleSignOut = async () => {
        try {
            await signOut(auth);
            setUser(null);
            setMsAccessToken(null); // Clear token on sign out
            localStorage.removeItem('msAccessToken'); // Remove token from storage
        } catch (error) {
        }
    };



    if (isLoading) {
        return <LoadingScreen message="Loading..." />;
    }

    return (
        <>
            <style>{`
                .no-arrows::-webkit-outer-spin-button,
                .no-arrows::-webkit-inner-spin-button {
                    -webkit-appearance: none;
                    margin: 0;
                }
                .no-arrows {
                    -moz-appearance: textfield;
                }
                @keyframes swishIn {
                    from { opacity: 0; transform: translateX(20px); }
                    to { opacity: 1; transform: translateX(0); }
                }
                .order-list-container {
                    animation: swishIn 0.4s ease-out forwards;
                }
                @keyframes disintegrate {
                    0% { transform: scale(1); opacity: 1; }
                    100% { transform: scale(0.8); opacity: 0; }
                }
                .order-card-deleting {
                    animation: disintegrate 0.5s forwards;
                }
                .interactive-button {
                    transition: transform 0.1s ease-in-out;
                }
                .interactive-button:active {
                    transform: scale(0.95);
                }
                @keyframes slideOutLeft {
                    from { opacity: 1; transform: translateX(0); }
                    to { opacity: 0; transform: translateX(-100%); }
                }
                @keyframes slideInRight {
                    from { opacity: 0; transform: translateX(100%); }
                    to { opacity: 1; transform: translateX(0); }
                }
                .status-transition-out {
                    animation: slideOutLeft 0.3s ease-in-out forwards;
                }
                .status-transition-in {
                    animation: slideInRight 0.3s ease-in-out forwards;
                }
                /* Sliding tab content animation */
                .slide-left {
                    transform: translateX(-100%);
                    opacity: 0;
                    transition: transform 0.3s, opacity 0.3s;
                }
                .slide-right {
                    transform: translateX(100%);
                    opacity: 0;
                    transition: transform 0.3s, opacity 0.3s;
                }
                .translate-x-0 {
                    transform: translateX(0);
                    opacity: 1;
                    transition: transform 0.3s, opacity 0.3s;
                }
                @keyframes dragEnter {
                    0% { transform: translateY(0); }
                    50% { transform: translateY(-5px); }
                    100% { transform: translateY(0); }
                }
                .drag-enter {
                    animation: dragEnter 0.3s ease-out;
                }
                .quote-card-dragging {
                    transform: translateY(-4px);
                    box-shadow: 0 12px 24px -6px rgba(0, 0, 0, 0.16);
                    z-index: 10;
                    border-color: #000;
                }
                .quote-card-drop-zone {
                    transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.2s cubic-bezier(0.4, 0, 0.2, 1);
                }
                .quote-card-drop-zone:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 20px -4px rgba(0, 0, 0, 0.12);
                }
                .quote-card-drag-over {
                    transform: translateY(-3px);
                    box-shadow: 0 8px 24px -6px rgba(0, 0, 0, 0.14);
                    border-color: #000;
                }
                .quote-card-drop-zone:active {
                    cursor: grabbing;
                }
                .quote-card-drop-zone:focus {
                    outline: none;
                }
            `}</style>
            <div className="bg-white text-black min-h-screen font-sans">
                {authError && <ErrorModal title={authError.title} message={authError.message} onClose={() => setAuthError(null)} />}
                {user ? (
                    <MainApp user={user} onSignOut={handleSignOut} onSignIn={handleSignIn} msAccessToken={msAccessToken} allVendors={allVendors} />
                ) : (
                    <LoginScreen onSignIn={handleSignIn} />
                )}
                {/* Email Notification Pop-ups */}
                {emailNotifications.map((notification, index) => (
                    <EmailNotificationModal
                        key={notification.id}
                        notification={notification}
                        onClose={() => {
                            setEmailNotifications(prev => prev.filter(n => n.id !== notification.id));
                        }}
                    />
                ))}
            </div>
        </>
    );
};

// LoginScreen now imported from ./components/common/LoginScreen


// --- Main Application ---
const MainApp = ({ user, onSignOut, onSignIn, msAccessToken, allVendors }) => {
    const [editingCategoryId, setEditingCategoryId] = useState(null);
    const [editingCategoryTitle, setEditingCategoryTitle] = useState("");
    const [showCategoryEmailModal, setShowCategoryEmailModal] = useState(null);
    const [currentView, setCurrentView] = useState('list');
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [selectedVendor, setSelectedVendor] = useState(null);
    const [statusFilter, setStatusFilter] = useState('All Orders');
    const [dateFilter, setDateFilter] = useState('All');
    const [searchQuery, setSearchQuery] = useState('');
    const [isLoadingQuotes, setIsLoadingQuotes] = useState(false);

    const [sortBy, setSortBy] = useState('serial');
    const [sortOrder, setSortOrder] = useState('desc');
    const [allUsers, setAllUsers] = useState([]);
    const [draftQuotes, setDraftQuotes] = useState({});
    const [newOrderDraft, setNewOrderDraft] = useState({});
    const [orderCounts, setOrderCounts] = useState({
        awaiting: 0,
        awaitingSubStatuses: {
            negotiation: 0,
            information: 0,
            proforma: 0,
            advance: 0
        },
        inProgress: 0,
        completed: 0,
        all: 0
    });

    // In MainApp, add state for vendor search and sort
    const [vendorSearch, setVendorSearch] = useState('');
    const [vendorSortBy, setVendorSortBy] = useState('alphabetical');

    // In MainApp, add state for vendor scroll position
    const [vendorScroll, setVendorScroll] = useState(0);

    const isMaster = user.role === 'master' || user.role === 'auto-master';

    const [categoryModalOpen, setCategoryModalOpen] = useState(false);
    const [categoryTitle, setCategoryTitle] = useState("");
    const [categoryLoading, setCategoryLoading] = useState(false);
    const [categories, setCategories] = useState([]);
    const [expandedCategoryId, setExpandedCategoryId] = useState(null);
    const [categoryOrders, setCategoryOrders] = useState({}); // { [categoryId]: [orders] }
    const [addOrderCategoryId, setAddOrderCategoryId] = useState(null); // categoryId for which add order modal is open
    // In MainApp, add state for delete modal
    const [categoryToDelete, setCategoryToDelete] = useState(null);
    const [deletingCategory, setDeletingCategory] = useState(false);
    const [showAddOrderModalFor, setShowAddOrderModalFor] = useState(null);
    const [orderSearch, setOrderSearch] = useState('');
    const [allOrders, setAllOrders] = useState([]);

    // In MainApp, update navigateTo to store createOrderCategory
    const [createOrderCategory, setCreateOrderCategory] = useState(null);
    const navigateTo = (view, data = null) => {
        setCurrentView(view);
        if (view === 'details') {
            setSelectedOrder(data);
            setSelectedVendor(null);
            setCreateOrderCategory(null);
        } else if (view === 'vendorDetails') {
            setSelectedVendor(data);
            setSelectedOrder(null);
            setCreateOrderCategory(null);
        } else if (view === 'create') {
            // Only set createOrderCategory if data.category is provided, otherwise preserve existing value
            if (data?.category) {
                setCreateOrderCategory(data.category);
            }
            setSelectedOrder(null);
            setSelectedVendor(null);
        } else {
            setSelectedOrder(null);
            setSelectedVendor(null);
            setCreateOrderCategory(null);
        }
    };

    useEffect(() => {
        const q = query(collection(db, 'orders'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const orders = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setAllOrders(orders);

            // Prefetch quotes for analytics in the background
            setTimeout(() => {
                prefetchQuotes(orders);
            }, 1000); // Delay to avoid blocking initial load
        });
        return unsubscribe;
    }, []);

    useEffect(() => {
        const usersQuery = query(collection(db, 'users'));
        const unsubscribeUsers = onSnapshot(usersQuery, (snapshot) => {
            setAllUsers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });

        const ordersQuery = query(collection(db, 'orders'));
        const unsubscribeOrders = onSnapshot(ordersQuery, (snapshot) => {
            const counts = {
                awaitingRequirements: 0,
                negotiation: 0,
                information: 0,
                proforma: 0,
                advance: 0,
                inProgress: 0,
                completed: 0,
                all: 0
            };
            snapshot.forEach(doc => {
                const order = doc.data();
                counts.all++;
                if (order.status === 'Awaiting Requirements') counts.awaitingRequirements++;
                else if (order.status === 'Negotiation') counts.negotiation++;
                else if (order.status === 'Information') counts.information++;
                else if (order.status === 'Proforma') counts.proforma++;
                else if (order.status === 'Advance') counts.advance++;
                else if (order.status === 'In Progress') counts.inProgress++;
                else if (order.status === 'Completed') counts.completed++;
            });
            setOrderCounts(counts);
        });

        return () => {
            unsubscribeUsers();
            unsubscribeOrders();
        };
    }, []);

    const onOrderCreated = (newOrder) => {
        setNewOrderDraft({});
        navigateTo('details', newOrder);
    }
    const onOrderDeleted = () => navigateTo('list');
    const onQuoteSelected = () => {
        if (selectedOrder) {
            handleDraftSubmit(selectedOrder.id);
        }
        navigateTo('list');
    }

    const handleDeleteFromList = async (orderId) => {
        try {
            await deleteDoc(doc(db, 'orders', orderId));
            // Decrease RN counter when order is deleted
            await decreaseRNCounter();
        } catch (error) {
            console.error('Error deleting order:', error);
        }
    }

    const handleMarkOrderAsCompleted = async (orderId) => {
        const orderRef = doc(db, 'orders', orderId);
        try {
            await updateDoc(orderRef, { status: 'Completed' });
            navigateTo('list');
        } catch (error) {
        }
    };

    const handleRevertStatus = async (order) => {
        if (!order || !order.id) return;
        const orderRef = doc(db, 'orders', order.id);
        const batch = writeBatch(db);

        batch.update(orderRef, {
            status: 'Awaiting Requirements',
            deliveryDate: null,
            deliveryTimeline: null,
            price: null,
            advancePayment: null,
            paymentTerms: null,
            finalPrice: null,
        });

        if (order.status === 'In Progress' || order.status === 'Completed') {
            const quotesQuery = query(collection(db, `orders/${order.id}/quotes`), where('isBestQuote', '==', true));
            const quotesSnapshot = await getDocs(quotesQuery);
            quotesSnapshot.forEach(quoteDoc => {
                const quoteRef = doc(db, `orders/${order.id}/quotes`, quoteDoc.id);
                batch.update(quoteRef, { isBestQuote: false });
            });
        }

        await batch.commit();
        const updatedOrderSnap = await getDoc(orderRef);
        if (updatedOrderSnap.exists()) {
            setSelectedOrder({ id: updatedOrderSnap.id, ...updatedOrderSnap.data() });
        }
    };

    const handleDraftChange = (orderId, newDraft) => {
        setDraftQuotes(prev => ({ ...prev, [orderId]: newDraft }));
    };

    const handleDraftSubmit = (orderId) => {
        setDraftQuotes(prev => {
            const newDrafts = { ...prev };
            delete newDrafts[orderId];
            return newDrafts;
        });
    }

    // Add this handler in MainApp
    const handleStatusFilterChange = (newStatus) => {
        // If switching away from "In Progress" and current sort is "daysLeft", revert to "serial"
        if (statusFilter === 'In Progress' && newStatus !== 'In Progress' && sortBy === 'daysLeft') {
            setSortBy('serial');
        }
        setStatusFilter(newStatus);
    };

    // Fetch categories
    useEffect(() => {
        const q = query(collection(db, 'orderCategories'), orderBy('createdAt', 'desc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const categoriesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setCategories(categoriesData);
        });
        return unsubscribe;
    }, []);

    // Helper function to decrease RN counter when order is deleted
    const decreaseRNCounter = async () => {
        try {
            const counterRef = doc(db, 'counters', 'orders');
            await runTransaction(db, async (transaction) => {
                const counterDoc = await transaction.get(counterRef);
                if (counterDoc.exists()) {
                    const currentSerial = counterDoc.data().lastSerial || 0;
                    if (currentSerial > 0) {
                        transaction.set(counterRef, { lastSerial: currentSerial - 1 }, { merge: true });
                    }
                }
            });
        } catch (error) {
            console.error('Error decreasing RN counter:', error);
        }
    };

    // Category RN generator
    const createCategory = async (title) => {
        setCategoryLoading(true);
        try {
            // Generate serial number for category using the same counter as orders
            let serialNumber = '';
            const counterRef = doc(db, 'counters', 'orders');
            await runTransaction(db, async (transaction) => {
                const counterDoc = await transaction.get(counterRef);
                let lastSerial = 0;
                if (counterDoc.exists()) {
                    lastSerial = counterDoc.data().lastSerial || 0;
                }
                const nextSerial = lastSerial + 1;
                serialNumber = `RN${String(nextSerial).padStart(5, '0')}`;
                transaction.set(counterRef, { lastSerial: nextSerial }, { merge: true });
            });
            const newCategory = {
                title: title.trim(),
                createdAt: serverTimestamp(),
                createdBy: { uid: user.uid, displayName: user.displayName },
                orderIds: [],
                serialNumber, // Store the RN number for the category
            };
            await addDoc(collection(db, 'orderCategories'), newCategory);
            setCategoryModalOpen(false);
            setCategoryTitle("");
        } catch (err) {
            console.error('Error creating category:', err);
            alert('Failed to create category. Please try again.');
        } finally {
            setCategoryLoading(false);
        }
    };

    // Fetch orders for expanded category
    useEffect(() => {
        if (!expandedCategoryId) return;
        const q = query(collection(db, `orderCategories/${expandedCategoryId}/orders`), orderBy('createdAt', 'desc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setCategoryOrders(prev => ({ ...prev, [expandedCategoryId]: snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) }));
        });
        return unsubscribe;
    }, [expandedCategoryId]);

    const renderContent = () => {
        switch (currentView) {
            case 'proforma':
                return <ProformaManager />;
            case 'analytics':
                return <SalesAnalytics allOrders={allOrders} allVendors={allVendors} currentUser={user} />;
            case 'costSheet':
                return <CostSheet onBack={() => navigateTo('list')} currentUser={user} />;
            case 'create':
                return <CreateOrder
                    onBack={() => navigateTo('list')}
                    currentUser={user}
                    onOrderCreated={onOrderCreated}
                    draft={newOrderDraft}
                    onDraftChange={setNewOrderDraft}
                    category={createOrderCategory}
                    onAddCategory={() => setCategoryModalOpen(true)}
                    categoryModalOpen={categoryModalOpen}
                    onCloseCategoryModal={() => setCategoryModalOpen(false)}
                    createCategory={createCategory}
                    categoryLoading={categoryLoading}
                />;
            case 'details':
                // Check if this is a category order and route accordingly
                if (selectedOrder && (selectedOrder.isCategoryOrder || selectedOrder.categoryId)) {
                    // Find the category for this order
                    const category = categories.find(cat => cat.id === selectedOrder.categoryId);
                    if (category) {
                        return <CategoryOrderDetails
                            order={selectedOrder}
                            categoryId={selectedOrder.categoryId}
                            setCurrentView={navigateTo}
                            allVendors={allVendors}
                            currentUser={user}
                            isMaster={isMaster}
                            categories={categories}
                            decreaseRNCounter={decreaseRNCounter}
                            allUsers={allUsers}
                            setSelectedOrder={setSelectedOrder}
                        />;
                    }
                }

                // Regular order details
                return <OrderDetails
                    order={selectedOrder}
                    setSelectedOrder={setSelectedOrder}
                    currentUser={user}
                    isMaster={isMaster}
                    allUsers={allUsers}
                    allVendors={allVendors}
                    onBack={() => navigateTo('list')}
                    onOrderDeleted={onOrderDeleted}
                    onQuoteSelected={onQuoteSelected}
                    onMarkAsCompleted={handleMarkOrderAsCompleted}
                    onRevertStatus={handleRevertStatus}
                    draftQuote={draftQuotes[selectedOrder.id] || {}}
                    onDraftChange={(newDraft) => handleDraftChange(selectedOrder.id, newDraft)}
                    onDraftSubmit={() => handleDraftSubmit(selectedOrder.id)}
                    categories={categories}
                    decreaseRNCounter={decreaseRNCounter}
                />;
            case 'vendors':
                return <VendorsPage
                    vendors={allVendors}
                    onSelectVendor={(vendor) => {
                        setVendorScroll(window.scrollY);
                        navigateTo('vendorDetails', vendor);
                    }}
                    onBack={() => navigateTo('list')}
                    search={vendorSearch}
                    setSearch={setVendorSearch}
                    sortBy={vendorSortBy}
                    setSortBy={setVendorSortBy}
                    scrollPosition={vendorScroll}
                />;
            case 'vendorDetails':
                return <VendorDetailsPage vendor={selectedVendor} onBack={() => navigateTo('vendors')} onSelectOrder={(order) => navigateTo('details', order)} allOrders={allOrders} />;
            case 'list':
            default:
                return (
                    <div>
                        <div className="flex justify-between items-center mb-6">
                            <FilterBar
                                activeFilter={statusFilter}
                                onFilterChange={handleStatusFilterChange}
                                orderCounts={orderCounts}
                            />
                            <div className="flex items-center gap-2">
                                <SearchInput value={searchQuery} onChange={setSearchQuery} isLoadingQuotes={isLoadingQuotes} />
                                <SortControl
                                    sortBy={sortBy}
                                    setSortBy={setSortBy}
                                    sortOrder={sortOrder}
                                    setSortOrder={setSortOrder}
                                    currentStatus={statusFilter}
                                    dateFilter={dateFilter}
                                    onDateFilterChange={setDateFilter}
                                />
                            </div>
                        </div>
                        <OrderListPage
                            statusFilter={statusFilter}
                            onSelectOrder={order => navigateTo('details', order)}
                            isMaster={isMaster}
                            onDeleteOrder={handleDeleteFromList}
                            onDeleteCategory={handleDeleteCategory}
                            onNavigateToCreateOrderWithCategory={handleNavigateToCreateOrderWithCategory}
                            onSaveCategoryTitle={handleSaveCategoryTitle}
                            setCategories={setCategories}
                            handleRemoveOrderFromCategory={handleRemoveOrderFromCategory}
                            setSelectedOrderForCategory={setSelectedOrderForCategory}
                            setShowAddOrderToCategoryModal={setShowAddOrderToCategoryModal}
                            setSelectedCategoryForAddingOrders={setSelectedCategoryForAddingOrders}
                            setShowAddOrdersToCategoryModal={setShowAddOrdersToCategoryModal}
                            editingCategoryId={editingCategoryId}
                            setEditingCategoryId={setEditingCategoryId}
                            editingCategoryTitle={editingCategoryTitle}
                            setEditingCategoryTitle={setEditingCategoryTitle}
                            dateFilter={dateFilter}
                            searchQuery={searchQuery}
                            sortBy={sortBy}
                            sortOrder={sortOrder}
                            categories={categories}
                            allOrders={allOrders}
                            allVendors={allVendors}
                            onOpenCategoryEmailModal={(category, orders) => {
                                setShowCategoryEmailModal(category);
                                setCategoryEmailOrders(orders);
                            }}
                            expandedCategoryId={expandedCategoryId}
                            setExpandedCategoryId={setExpandedCategoryId}
                            onQuotesLoadingChange={setIsLoadingQuotes}
                        />
                        {categoryModalOpen && (
                            <AddCategoryModal
                                onClose={() => setCategoryModalOpen(false)}
                                onCreate={createCategory}
                                loading={categoryLoading}
                            />
                        )}
                    </div>
                );
        }
    };

    // 2. Ensure handleAddExistingOrderToCategory is defined inside MainApp
    const handleAddExistingOrderToCategory = async (categoryId, orderId) => {
        try {
            setAddingOrderToCategory(true);

            // Get the order data to check if it's already in a category
            const orderRef = doc(db, 'orders', orderId);
            const orderDoc = await getDoc(orderRef);

            if (!orderDoc.exists()) {
                throw new Error('Order not found');
            }

            const orderData = orderDoc.data();

            // If the order is already in a category, remove it first
            if (orderData.categoryId) {
                const oldCategoryRef = doc(db, 'orderCategories', orderData.categoryId);
                await updateDoc(oldCategoryRef, {
                    orderIds: arrayRemove(orderId)
                });
            }

            // Add the order to the new category
            const categoryRef = doc(db, 'orderCategories', categoryId);
            await updateDoc(categoryRef, {
                orderIds: arrayUnion(orderId)
            });

            // Update the order to mark it as a category order
            // BUT preserve the original RN number
            await updateDoc(orderRef, {
                categoryId: categoryId,
                isCategoryOrder: true
                // Note: We don't update serialNumber here to preserve the original RN
            });

            // Update local state
            setAllOrders(prev => prev.map(order =>
                order.id === orderId
                    ? { ...order, categoryId, isCategoryOrder: true }
                    : order
            ));

            // Close the modal
            setShowAddOrderToCategoryModal(false);
            setSelectedOrderForCategory(null);

            console.log(`Order ${orderId} successfully added to category ${categoryId} with preserved RN: ${orderData.serialNumber}`);

        } catch (err) {
            console.error('Error adding order to category:', err);
            alert('Failed to add order to category. Please try again.');
        } finally {
            setAddingOrderToCategory(false);
        }
    };

    // Function to add multiple orders to a category
    const handleAddMultipleOrdersToCategory = async (categoryId, orderIds) => {
        try {
            setAddingOrderToCategory(true);

            // Process each order
            for (const orderId of orderIds) {
                // Get the order data to check if it's already in a category
                const orderRef = doc(db, 'orders', orderId);
                const orderDoc = await getDoc(orderRef);

                if (!orderDoc.exists()) {
                    console.warn(`Order ${orderId} not found, skipping...`);
                    continue;
                }

                const orderData = orderDoc.data();

                // If the order is already in a category, remove it first
                if (orderData.categoryId) {
                    const oldCategoryRef = doc(db, 'orderCategories', orderData.categoryId);
                    await updateDoc(oldCategoryRef, {
                        orderIds: arrayRemove(orderId)
                    });
                }

                // Add the order to the new category
                const categoryRef = doc(db, 'orderCategories', categoryId);
                await updateDoc(categoryRef, {
                    orderIds: arrayUnion(orderId)
                });

                // Update the order to mark it as a category order
                // BUT preserve the original RN number
                await updateDoc(orderRef, {
                    categoryId: categoryId,
                    isCategoryOrder: true
                    // Note: We don't update serialNumber here to preserve the original RN
                });
            }

            // Update local state
            setAllOrders(prev => prev.map(order =>
                orderIds.includes(order.id)
                    ? { ...order, categoryId, isCategoryOrder: true }
                    : order
            ));

            // Close the modal
            setShowAddOrdersToCategoryModal(false);
            setSelectedCategoryForAddingOrders(null);

            console.log(`Successfully added ${orderIds.length} orders to category ${categoryId}`);

        } catch (err) {
            console.error('Error adding multiple orders to category:', err);
            alert('Failed to add orders to category. Please try again.');
        } finally {
            setAddingOrderToCategory(false);
        }
    };

    // Function to navigate to create order page with category pre-selected
    const handleNavigateToCreateOrderWithCategory = (category) => {
        setCreateOrderCategory(category);
        navigateTo('create');
    };

    // Function to save category title changes
    const handleSaveCategoryTitle = async (categoryId) => {
        if (!editingCategoryTitle.trim()) {
            alert('Category title cannot be empty');
            return;
        }

        try {
            const categoryRef = doc(db, 'orderCategories', categoryId);
            await updateDoc(categoryRef, {
                title: editingCategoryTitle.trim()
            });

            // Update local state
            setCategories(prev => prev.map(cat =>
                cat.id === categoryId
                    ? { ...cat, title: editingCategoryTitle.trim() }
                    : cat
            ));

            setEditingCategoryId(null);
            setEditingCategoryTitle("");
        } catch (error) {
            console.error('Error updating category title:', error);
            alert('Failed to update category title. Please try again.');
        }
    };

    // Delete category handler
    const handleDeleteCategory = async (categoryId) => {
        setDeletingCategory(true);
        try {
            // Get the category to find all order IDs
            const categoryRef = doc(db, 'orderCategories', categoryId);
            const categoryDoc = await getDoc(categoryRef);

            if (categoryDoc.exists()) {
                const categoryData = categoryDoc.data();
                const orderIds = categoryData.orderIds || [];

                // Delete all orders in the category
                const batch = writeBatch(db);

                // Delete each order
                for (const orderId of orderIds) {
                    const orderRef = doc(db, 'orders', orderId);
                    batch.delete(orderRef);
                }

                // Delete the category itself
                batch.delete(categoryRef);

                // Execute the batch
                await batch.commit();

                // Decrease RN counter for each deleted order
                for (let i = 0; i < orderIds.length; i++) {
                    await decreaseRNCounter();
                }
            }

            setCategoryToDelete(null);
        } catch (err) {
            console.error('Error deleting category:', err);
            alert('Failed to delete category. Please try again.');
        } finally {
            setDeletingCategory(false);
        }
    };

    // In MainApp, add handler to remove order from category
    const handleRemoveOrderFromCategory = async (categoryId, orderId) => {
        try {
            // Get the order data to check if it needs an RN number
            const orderRef = doc(db, 'orders', orderId);
            const orderDoc = await getDoc(orderRef);

            if (!orderDoc.exists()) {
                throw new Error('Order not found');
            }

            const orderData = orderDoc.data();

            // Remove from category
            const categoryRef = doc(db, 'orderCategories', categoryId);
            await updateDoc(categoryRef, {
                orderIds: arrayRemove(orderId)
            });

            // Update the order to remove category association
            const updateData = {
                categoryId: deleteField(),
                isCategoryOrder: false
            };

            // If this order was added to category later (doesn't have category's RN),
            // generate a new RN number for it
            if (!orderData.serialNumber || orderData.serialNumber !== categories.find(cat => cat.id === categoryId)?.serialNumber) {
                const counterRef = doc(db, 'counters', 'orders');
                await runTransaction(db, async (transaction) => {
                    const counterDoc = await transaction.get(counterRef);
                    let lastSerial = 0;
                    if (counterDoc.exists()) {
                        lastSerial = counterDoc.data().lastSerial || 0;
                    }
                    const nextSerial = lastSerial + 1;
                    const serialNumber = `RN${String(nextSerial).padStart(5, '0')}`;
                    updateData.serialNumber = serialNumber;
                    transaction.set(counterRef, { lastSerial: nextSerial }, { merge: true });
                });
            }

            await updateDoc(orderRef, updateData);

            // Update local state
            setAllOrders(prev => prev.map(order =>
                order.id === orderId
                    ? { ...order, categoryId: undefined, isCategoryOrder: false, ...updateData }
                    : order
            ));

            console.log(`Order ${orderId} successfully removed from category ${categoryId}`);

        } catch (err) {
            console.error('Error removing order from category:', err);
            alert('Failed to remove order from category. Please try again.');
        }
    };

    // In MainApp, add handler to move order to another category
    const handleMoveOrderToCategory = async (fromCategoryId, toCategoryId, orderId) => {
        try {
            const fromRef = doc(db, 'orderCategories', fromCategoryId);
            const toRef = doc(db, 'orderCategories', toCategoryId);
            await updateDoc(fromRef, { orderIds: arrayRemove(orderId) });
            await updateDoc(toRef, { orderIds: arrayUnion(orderId) });
        } catch (err) {
        }
    };

    // In MainApp, add state for selectedCategoryOrderId
    const [selectedCategoryOrderId, setSelectedCategoryOrderId] = useState(null);
    // In MainApp, add state for showing the add order modal for a category
    const [showAddOrderModalForCategory, setShowAddOrderModalForCategory] = useState(null);

    // Add state for categoryEmailOrders
    const [categoryEmailOrders, setCategoryEmailOrders] = useState([]);
    const [showAddOrderToCategoryModal, setShowAddOrderToCategoryModal] = useState(false);
    const [selectedOrderForCategory, setSelectedOrderForCategory] = useState(null);
    const [addingOrderToCategory, setAddingOrderToCategory] = useState(false);
    const [showAddOrdersToCategoryModal, setShowAddOrdersToCategoryModal] = useState(false);
    const [selectedCategoryForAddingOrders, setSelectedCategoryForAddingOrders] = useState(null);

    // Delivery notification system
    useEffect(() => {
        if (!user) return;

        const checkDeliveryNotifications = async () => {
            try {
                const now = new Date();
                const threeDaysFromNow = new Date(now.getTime() + (3 * 24 * 60 * 60 * 1000));
                const oneDayFromNow = new Date(now.getTime() + (24 * 60 * 60 * 1000));
                const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

                // Get all orders with delivery dates
                const ordersQuery = query(collection(db, 'orders'), where('status', '==', 'In Progress'));
                const ordersSnapshot = await getDocs(ordersQuery);

                for (const orderDoc of ordersSnapshot.docs) {
                    const order = orderDoc.data();
                    if (!order.deliveryDate) continue;

                    const deliveryDate = order.deliveryDate.toDate ? order.deliveryDate.toDate() : new Date(order.deliveryDate);
                    const deliveryDateOnly = new Date(deliveryDate.getFullYear(), deliveryDate.getMonth(), deliveryDate.getDate());

                    // Check if we've already sent notifications for this order
                    const notificationsQuery = query(
                        collection(db, 'notifications'),
                        where('orderId', '==', orderDoc.id),
                        where('type', 'in', ['delivery_3days', 'delivery_1day', 'delivery_today'])
                    );
                    const existingNotifications = await getDocs(notificationsQuery);
                    const sentNotificationTypes = new Set(existingNotifications.docs.map(n => n.data().type));

                    // 3 days notification
                    if (deliveryDateOnly.getTime() === threeDaysFromNow.getTime() && !sentNotificationTypes.has('delivery_3days')) {
                        await addDoc(collection(db, 'notifications'), {
                            recipientUid: user.uid,
                            orderId: orderDoc.id,
                            orderTitle: order.orderTitle,
                            orderStatus: order.status,
                            message: `Order "${order.orderTitle}" is due for delivery in 3 days (${deliveryDate.toLocaleDateString()})`,
                            type: 'delivery_3days',
                            createdAt: serverTimestamp(),
                            isRead: false,
                        });
                    }

                    // 1 day notification
                    if (deliveryDateOnly.getTime() === oneDayFromNow.getTime() && !sentNotificationTypes.has('delivery_1day')) {
                        await addDoc(collection(db, 'notifications'), {
                            recipientUid: user.uid,
                            orderId: orderDoc.id,
                            orderTitle: order.orderTitle,
                            orderStatus: order.status,
                            message: `Order "${order.orderTitle}" is due for delivery tomorrow (${deliveryDate.toLocaleDateString()})`,
                            type: 'delivery_1day',
                            createdAt: serverTimestamp(),
                            isRead: false,
                        });
                    }

                    // Delivery day notification
                    if (deliveryDateOnly.getTime() === today.getTime() && !sentNotificationTypes.has('delivery_today')) {
                        await addDoc(collection(db, 'notifications'), {
                            recipientUid: user.uid,
                            orderId: orderDoc.id,
                            orderTitle: order.orderTitle,
                            orderStatus: order.status,
                            message: `Order "${order.orderTitle}" is due for delivery today!`,
                            type: 'delivery_today',
                            createdAt: serverTimestamp(),
                            isRead: false,
                        });
                    }
                }
            } catch (error) {
                console.error('Error checking delivery notifications:', error);
            }
        };

        // Check notifications every hour
        const interval = setInterval(checkDeliveryNotifications, 60 * 60 * 1000);

        // Also check immediately on mount
        checkDeliveryNotifications();

        // Request notification permission
        if ('Notification' in window && Notification.permission === 'default') {
            Notification.requestPermission();
        }

        return () => clearInterval(interval);
    }, [user]);

    // Browser notification system
    useEffect(() => {
        if (!user || !('Notification' in window) || Notification.permission !== 'granted') return;

        const showBrowserNotification = (title, options) => {
            if (Notification.permission === 'granted') {
                new Notification(title, options);
            }
        };

        // Listen for new notifications and show browser notifications
        const notificationsQuery = query(collection(db, 'notifications'), where('recipientUid', '==', user.uid));
        const unsubscribe = onSnapshot(notificationsQuery, (snapshot) => {
            snapshot.docChanges().forEach((change) => {
                if (change.type === 'added' && !change.doc.data().isRead) {
                    const notification = change.doc.data();
                    if (notification.type && notification.type.startsWith('delivery_')) {
                        showBrowserNotification('Delivery Reminder', {
                            body: notification.message,
                            icon: '/favicon.ico',
                            tag: `delivery_${notification.orderId}`,
                            requireInteraction: true
                        });
                    }
                }
            });
        });

        return unsubscribe;
    }, [user]);

    // Make functions available globally for console access
    window.correctOrderRNNumbers = () => correctOrderRNNumbers(categories);
    window.validateOrderRNNumbers = () => validateOrderRNNumbers(categories);

    return (
        <div className="flex flex-col min-h-screen">
            <Header
                onNavigate={navigateTo}
                user={user}
                currentView={currentView}
                onSignOut={onSignOut}
                onSignIn={onSignIn}
                msAccessToken={msAccessToken}
                allOrders={allOrders}
            />
            <PageTransition keyProp={currentView}>
                <main className="flex-grow p-4 sm:p-6 lg:p-8">
                    {renderContent()}
                </main>
            </PageTransition>
            {showCategoryEmailModal && (
                <Modal onClose={() => setShowCategoryEmailModal(null)}>
                    <CategoryEmailModal
                        category={showCategoryEmailModal}
                        orders={categoryEmailOrders}
                        onClose={() => setShowCategoryEmailModal(null)}
                        allVendors={allVendors}
                    />
                </Modal>
            )}

            {/* Modal for adding existing orders to categories */}
            {showAddOrderToCategoryModal && selectedOrderForCategory && (
                <AddExistingOrderToCategoryModal
                    onClose={() => {
                        setShowAddOrderToCategoryModal(false);
                        setSelectedOrderForCategory(null);
                    }}
                    onAdd={(categoryId) => handleAddExistingOrderToCategory(categoryId, selectedOrderForCategory.id)}
                    categories={categories}
                    order={selectedOrderForCategory}
                    loading={addingOrderToCategory}
                />
            )}

            {/* Modal for adding multiple orders to a specific category */}
            {showAddOrdersToCategoryModal && selectedCategoryForAddingOrders && (
                <AddOrdersToCategoryModal
                    onClose={() => {
                        setShowAddOrdersToCategoryModal(false);
                        setSelectedCategoryForAddingOrders(null);
                    }}
                    onAdd={(orderIds) => handleAddMultipleOrdersToCategory(selectedCategoryForAddingOrders.id, orderIds)}
                    category={selectedCategoryForAddingOrders}
                    allOrders={allOrders}
                    loading={addingOrderToCategory}
                />
            )}
        </div>
    );
};

const SearchInput = ({ value, onChange, isLoadingQuotes }) => (
    <div className="relative">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
            type="text"
            placeholder="Search orders, categories, or vendor names..."
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="pl-10 pr-10 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
        />
        {value && (
            <button onClick={() => onChange('')} className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-black">
                <X size={16} />
            </button>
        )}
        {isLoadingQuotes && (
            <div className="absolute right-10 top-1/2 -translate-y-1/2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black"></div>
            </div>
        )}
    </div>
);




const FilterBar = ({ activeFilter, onFilterChange, orderCounts }) => {
    const dropdownButtonRef = useRef(null);
    const [showDropdown, setShowDropdown] = useState(false);
    const dropdownRef = useRef(null);

    const currentStatusLabel = activeFilter === 'All Orders'
        ? 'All Orders'
        : statusDisplayNames[activeFilter] || activeFilter;

    const currentStatusCount = activeFilter === 'All Orders'
        ? orderCounts.all
        : orderCounts[activeFilter === 'Awaiting Requirements' ? 'awaitingRequirements' :
            activeFilter === 'Negotiation' ? 'negotiation' :
                activeFilter === 'Information' ? 'information' :
                    activeFilter === 'Proforma' ? 'proforma' :
                        activeFilter === 'Advance' ? 'advance' :
                            activeFilter === 'In Progress' ? 'inProgress' :
                                activeFilter === 'Completed' ? 'completed' : 'all'] || 0;

    const statusOptions = [
        { label: 'All Orders', key: 'All Orders', count: orderCounts.all },
        { label: statusDisplayNames['Awaiting Requirements'], key: 'Awaiting Requirements', count: orderCounts.awaitingRequirements },
        { label: statusDisplayNames['Negotiation'], key: 'Negotiation', count: orderCounts.negotiation },
        { label: statusDisplayNames['Information'], key: 'Information', count: orderCounts.information },
        { label: statusDisplayNames['Proforma'], key: 'Proforma', count: orderCounts.proforma },
        { label: statusDisplayNames['Advance'], key: 'Advance', count: orderCounts.advance },
        { label: statusDisplayNames['In Progress'], key: 'In Progress', count: orderCounts.inProgress },
        { label: statusDisplayNames['Completed'], key: 'Completed', count: orderCounts.completed }
    ];

    // Minimal button style
    const buttonClass = (filterKey) =>
        `px-4 py-2 text-sm font-medium bg-transparent border-none outline-none transition-colors interactive-button ` +
        (activeFilter === filterKey
            ? 'text-black font-semibold'
            : 'text-gray-500 hover:text-black');

    useClickOutside(dropdownRef, () => setShowDropdown(false));

    return (
        <div className="relative flex items-center space-x-2 px-0 py-0 bg-transparent border-none shadow-none" style={{ minHeight: 40 }}>
            {/* Status Dropdown (All Orders, Awaiting, etc.) */}
            <div className="relative" ref={dropdownRef}>
                <button
                    ref={dropdownButtonRef}
                    onClick={() => setShowDropdown(!showDropdown)}
                    className={buttonClass(activeFilter)}
                    style={{ opacity: 1, zIndex: 'auto', background: 'transparent', border: 'none' }}
                >
                    {currentStatusLabel} ({currentStatusCount})
                </button>
                {showDropdown && (
                    <div className="absolute left-0 mt-2 w-56 bg-white border rounded-md shadow-lg z-20">
                        {statusOptions.map(option => (
                            <button
                                key={option.key}
                                onClick={() => {
                                    onFilterChange(option.key);
                                    setShowDropdown(false);
                                }}
                                className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${option.key === activeFilter ? 'bg-gray-50 font-semibold' : ''}`}
                            >
                                {option.label} ({option.count})
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

// Date filter options - moved outside component to avoid recreation on every render
const DATE_FILTER_OPTIONS = ['All', 'Last 15 Days', 'Last 30 Days', 'Last Year'];

const SortControl = ({ sortBy, setSortBy, sortOrder, setSortOrder, currentStatus, dateFilter, onDateFilterChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [dateFilterOpen, setDateFilterOpen] = useState(false);
    const [customStartDate, setCustomStartDate] = useState('');
    const [customEndDate, setCustomEndDate] = useState('');
    const dropdownRef = useRef(null);
    const dateDropdownRef = useRef(null);

    // Only show "Days Left" option for "In Progress" status (Awaiting Delivery)
    const sortOptions = useMemo(() => ({
        serial: 'Request Number (RN)',
        date: 'Date Submitted',
        alphabetical: 'Alphabetical',
        quantity: 'Quantity',
        recentlyUpdated: 'Recently Updated',
        ...(currentStatus === 'In Progress' && { daysLeft: 'Days Left' })
    }), [currentStatus]);

    const handleSortChange = (newSortBy) => {
        setSortBy(newSortBy);
        setIsOpen(false);
    };

    const handleDateFilterClick = (filter) => {
        onDateFilterChange(filter);
        setDateFilterOpen(false);
    };

    const handleCustomDateApply = () => {
        if (customStartDate && customEndDate) {
            onDateFilterChange({
                custom: true,
                start: new Date(customStartDate),
                end: new Date(customEndDate)
            });
            setDateFilterOpen(false);
        }
    };

    useClickOutside(dropdownRef, () => setIsOpen(false));
    useClickOutside(dateDropdownRef, () => setDateFilterOpen(false));

    return (
        <div className="flex items-center gap-1 bg-white border border-gray-300 rounded-md p-1">
            {/* Sort by dropdown */}
            <div className="relative" ref={dropdownRef}>
                <Tooltip text="Sort orders by field">
                    <button
                        onClick={() => setIsOpen(!isOpen)}
                        className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
                    >
                        <span>Sort by: {sortOptions[sortBy]}</span>
                        <ChevronDown size={14} />
                    </button>
                </Tooltip>
                {isOpen && (
                    <div className="absolute left-0 mt-2 w-56 bg-white border rounded-md shadow-lg z-20">
                        {Object.entries(sortOptions).map(([key, value]) => (
                            <button
                                key={key}
                                onClick={() => handleSortChange(key)}
                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            >
                                {value}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {/* Divider */}
            <div className="w-px h-6 bg-gray-300"></div>

            {/* Sort order toggle */}
            <Tooltip text={`Sort ${sortOrder === 'asc' ? 'Ascending' : 'Descending'}`}>
                <button
                    onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                    className="p-2 text-gray-600 hover:bg-gray-50 rounded-md transition-colors"
                >
                    <ArrowDownUp size={16} />
                </button>
            </Tooltip>

            {/* Divider */}
            <div className="w-px h-6 bg-gray-300"></div>

            {/* Date filter */}
            <div className="relative" ref={dateDropdownRef}>
                <Tooltip text="Filter by Date">
                    <button
                        onClick={() => setDateFilterOpen(!dateFilterOpen)}
                        className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
                    >
                        <Filter size={16} />
                        <span className="text-xs">
                            {dateFilter === 'All' ? 'All Time' :
                                dateFilter === 'Last 15 Days' ? '15D' :
                                    dateFilter === 'Last 30 Days' ? '30D' :
                                        dateFilter === 'Last Year' ? '1Y' :
                                            dateFilter.custom ? 'Custom' : 'All Time'}
                        </span>
                        <ChevronDown size={14} />
                    </button>
                </Tooltip>
                {dateFilterOpen && (
                    <div className="absolute right-0 mt-2 w-56 bg-white border rounded-md shadow-lg z-20">
                        {DATE_FILTER_OPTIONS.map(filter => (
                            <button
                                key={filter}
                                onClick={() => handleDateFilterClick(filter)}
                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            >
                                {filter}
                            </button>
                        ))}
                        <div className="border-t my-1"></div>
                        <div className="p-2 space-y-2">
                            <p className="text-xs font-semibold text-gray-500 px-2">Custom Range</p>
                            <InputField label="Start" type="date" value={customStartDate} onChange={e => setCustomStartDate(e.target.value)} />
                            <InputField label="End" type="date" value={customEndDate} onChange={e => setCustomEndDate(e.target.value)} />
                            <button
                                onClick={handleCustomDateApply}
                                className="w-full bg-black text-white text-sm py-1.5 rounded-md hover:bg-gray-800"
                            >
                                Apply
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// Header now imported from ./components/common/Header


// NotificationPanel is now included in Header component
const NotificationPanel = ({ notifications, allOrders, onSelectOrder, onClose, onMarkAsRead, onDelete, onDeleteAll }) => {
    const [selectedNotifications, setSelectedNotifications] = useState(new Set());
    const [isSelectionMode, setIsSelectionMode] = useState(false);

    const getNotificationIcon = (type, isRead) => {
        switch (type) {
            case 'delivery_3days':
                return <div className="mt-1 h-2 w-2 rounded-full bg-orange-500"></div>;
            case 'delivery_1day':
                return <div className="mt-1 h-2 w-2 rounded-full bg-yellow-500"></div>;
            case 'delivery_today':
                return <div className="mt-1 h-2 w-2 rounded-full bg-red-500"></div>;
            default:
                return <div className={`mt-1 h-2 w-2 rounded-full ${isRead ? 'bg-transparent' : 'bg-blue-500'}`}></div>;
        }
    };

    const getNotificationStyle = (type) => {
        switch (type) {
            case 'delivery_3days':
                return 'border-l-4 border-l-orange-500 bg-orange-50';
            case 'delivery_1day':
                return 'border-l-4 border-l-yellow-500 bg-yellow-50';
            case 'delivery_today':
                return 'border-l-4 border-l-red-500 bg-red-50';
            default:
                return '';
        }
    };

    const handleNotificationClick = (notification) => {
        if (isSelectionMode) {
            // Toggle selection
            const newSelected = new Set(selectedNotifications);
            if (newSelected.has(notification.id)) {
                newSelected.delete(notification.id);
            } else {
                newSelected.add(notification.id);
            }
            setSelectedNotifications(newSelected);
        } else if (notification.orderId) {
            // Find the full order from allOrders to ensure all data is available
            const fullOrder = allOrders.find(order => order.id === notification.orderId);
            if (fullOrder) {
                onSelectOrder(fullOrder);
            } else {
                // Fallback to minimal object if order not found
                onSelectOrder({ id: notification.orderId, status: notification.orderStatus });
            }
            onClose();
        }
    };

    const handleMarkAsRead = (notificationId, e) => {
        e.stopPropagation();
        onMarkAsRead(notificationId);
    };

    const handleDeleteSelected = () => {
        if (selectedNotifications.size > 0) {
            onDelete(Array.from(selectedNotifications));
            setSelectedNotifications(new Set());
            setIsSelectionMode(false);
        }
    };

    const handleDeleteAll = () => {
        if (window.confirm('Are you sure you want to delete all notifications?')) {
            onDeleteAll();
            setSelectedNotifications(new Set());
            setIsSelectionMode(false);
        }
    };

    const toggleSelectionMode = () => {
        setIsSelectionMode(!isSelectionMode);
        setSelectedNotifications(new Set());
    };

    const selectedCount = selectedNotifications.size;

    return (
        <div className="absolute top-full right-0 mt-2 w-80 bg-white border rounded-lg shadow-lg z-20">
            <div className="p-3 border-b font-semibold flex justify-between items-center">
                <span>Notifications</span>
                <div className="flex items-center gap-2">
                    {notifications.length > 0 && (
                        <>
                            <button
                                onClick={toggleSelectionMode}
                                className={`p-1 rounded text-xs ${isSelectionMode ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                title={isSelectionMode ? 'Exit Selection Mode' : 'Select Notifications'}
                            >
                                {isSelectionMode ? 'Exit' : 'Select'}
                            </button>
                            {isSelectionMode && (
                                <>
                                    <button
                                        onClick={handleDeleteSelected}
                                        disabled={selectedCount === 0}
                                        className="p-1 rounded text-xs bg-red-100 text-red-700 hover:bg-red-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                        title={`Delete ${selectedCount} selected`}
                                    >
                                        Delete ({selectedCount})
                                    </button>
                                    <button
                                        onClick={handleDeleteAll}
                                        className="p-1 rounded text-xs bg-red-100 text-red-700 hover:bg-red-200"
                                        title="Delete all notifications"
                                    >
                                        Delete All
                                    </button>
                                </>
                            )}
                        </>
                    )}
                </div>
            </div>
            <div className="max-h-96 overflow-y-auto">
                {notifications.length > 0 ? notifications.map(n => (
                    <div
                        key={n.id}
                        onClick={() => handleNotificationClick(n)}
                        className={`p-3 border-b hover:bg-gray-50 flex items-start gap-3 ${!n.orderId ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                            } ${getNotificationStyle(n.type)} ${isSelectionMode && selectedNotifications.has(n.id) ? 'bg-blue-50 border-blue-200' : ''
                            }`}
                        title={!n.orderId ? "No order to view for this notification" : ""}
                    >
                        {isSelectionMode && (
                            <input
                                type="checkbox"
                                checked={selectedNotifications.has(n.id)}
                                onChange={() => { }} // Handled by onClick
                                className="mt-1"
                                onClick={(e) => e.stopPropagation()}
                            />
                        )}
                        {getNotificationIcon(n.type, n.isRead)}
                        <div className="flex-1">
                            <div className="flex justify-between items-start">
                                <div className="flex-1">
                                    <p className="text-sm">{n.message}</p>
                                    <p className="text-xs text-gray-500 truncate">{n.orderTitle}</p>
                                    <p className="text-xs text-gray-400 mt-1">{n.createdAt?.toDate().toLocaleString('en-GB')}</p>
                                </div>
                                <div className="flex items-center gap-1 ml-2">
                                    {!n.isRead && (
                                        <button
                                            onClick={(e) => handleMarkAsRead(n.id, e)}
                                            className="p-1 rounded text-xs bg-green-100 text-green-700 hover:bg-green-200"
                                            title="Mark as read"
                                        >
                                            ✓
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )) : <p className="p-4 text-sm text-gray-500">No notifications.</p>}
            </div>
        </div>
    );
};




const OrderListPage = ({
    statusFilter,
    onSelectOrder,
    isMaster,
    onDeleteOrder,
    onDeleteCategory,
    onNavigateToCreateOrderWithCategory,
    onSaveCategoryTitle,
    setCategories,
    editingCategoryId,
    setEditingCategoryId,
    editingCategoryTitle,
    setEditingCategoryTitle,
    dateFilter,
    searchQuery,
    sortBy,
    sortOrder,
    categories,
    allOrders,
    allVendors,
    onOpenCategoryEmailModal,
    expandedCategoryId,
    setExpandedCategoryId,
    handleRemoveOrderFromCategory,
    setSelectedOrderForCategory,
    setShowAddOrderToCategoryModal,
    setSelectedCategoryForAddingOrders,
    setShowAddOrdersToCategoryModal,
    onQuotesLoadingChange
}) => {
    const [orderQuotes, setOrderQuotes] = useState({});
    const [isLoadingQuotes, setIsLoadingQuotes] = useState(false);

    // Fetch quotes for all orders when search query changes
    useEffect(() => {
        if (searchQuery && searchQuery.trim()) {
            // Check if we need to fetch quotes - only if search doesn't match order fields directly
            const needsQuotesSearch = !allOrders.some(order => {
                const lowercasedQuery = searchQuery.toLowerCase();
                return (
                    (order.orderTitle && order.orderTitle.toLowerCase().includes(lowercasedQuery)) ||
                    (order.description && order.description.toLowerCase().includes(lowercasedQuery)) ||
                    (order.serialNumber && order.serialNumber.toLowerCase().includes(lowercasedQuery))
                );
            });

            if (needsQuotesSearch) {
                const fetchQuotesForSearch = async () => {
                    setIsLoadingQuotes(true);
                    onQuotesLoadingChange?.(true);
                    try {
                        const quotesMap = {};
                        // Fetch quotes for all orders to enable vendor-based search
                        for (const order of allOrders) {
                            try {
                                const quotes = await fetchQuotesForOrder(order.id);
                                quotesMap[order.id] = quotes;
                            } catch (error) {
                                console.error(`Error fetching quotes for order ${order.id}:`, error);
                                quotesMap[order.id] = [];
                            }
                        }
                        setOrderQuotes(quotesMap);
                    } catch (error) {
                        console.error('Error fetching quotes for search:', error);
                    } finally {
                        setIsLoadingQuotes(false);
                        onQuotesLoadingChange?.(false);
                    }
                };

                fetchQuotesForSearch();
            } else {
                setOrderQuotes({});
                onQuotesLoadingChange?.(false);
            }
        } else {
            setOrderQuotes({});
            onQuotesLoadingChange?.(false);
        }
    }, [searchQuery, allOrders, onQuotesLoadingChange]);

    // Helper function to check if an order has quotes with matching vendor names
    const hasMatchingVendorQuotes = (order, searchTerm) => {
        if (!searchTerm || !orderQuotes[order.id]) return false;

        const lowercasedSearch = searchTerm.toLowerCase();
        return orderQuotes[order.id].some(quote => {
            // Check supplier name
            if (quote.supplier && quote.supplier.toLowerCase().includes(lowercasedSearch)) {
                return true;
            }
            // Also check supplier city if available
            if (quote.supplierCity && quote.supplierCity.toLowerCase().includes(lowercasedSearch)) {
                return true;
            }
            return false;
        });
    };
    function sortOrders(orders, sortBy, sortOrder) {
        return [...orders].sort((a, b) => {
            let valA, valB;
            switch (sortBy) {
                case 'alphabetical':
                    valA = a.orderTitle?.toLowerCase() || '';
                    valB = b.orderTitle?.toLowerCase() || '';
                    if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
                    if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
                    return 0;
                case 'date':
                    valA = a.createdAt?.toDate ? a.createdAt.toDate() : a.createdAt;
                    valB = b.createdAt?.toDate ? b.createdAt.toDate() : b.createdAt;
                    if (valA === valB) {
                        const serialA = parseInt((a.serialNumber || '').replace(/\D/g, '')) || 0;
                        const serialB = parseInt((b.serialNumber || '').replace(/\D/g, '')) || 0;
                        return sortOrder === 'asc' ? serialA - serialB : serialB - serialA;
                    }
                    return sortOrder === 'asc' ? valA - valB : valB - valA;
                case 'serial':
                    const serialA = parseInt((a.serialNumber || '').replace(/\D/g, '')) || 0;
                    const serialB = parseInt((b.serialNumber || '').replace(/\D/g, '')) || 0;
                    return sortOrder === 'asc' ? serialA - serialB : serialB - serialA;
                case 'quantity':
                    valA = a.quantity || 0;
                    valB = b.quantity || 0;
                    return sortOrder === 'asc' ? valA - valB : valB - valA;
                case 'daysLeft':
                    const calculateDaysLeft = (order) => {
                        if (!order.deliveryDate) return Infinity;
                        const deliveryDate = order.deliveryDate.toDate ? order.deliveryDate.toDate() : order.deliveryDate;
                        const now = new Date();
                        const diffTime = deliveryDate - now;
                        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                        return diffDays;
                    };
                    valA = calculateDaysLeft(a);
                    valB = calculateDaysLeft(b);
                    return sortOrder === 'asc' ? valA - valB : valB - valA;
                default:
                    return 0;
            }
        });
    }
    const [isLoading, setIsLoading] = useState(false);
    const [deletingOrderId, setDeletingOrderId] = useState(null);

    // Build a set of all order IDs that are in categories (as a backup)
    const ordersInCategories = new Set();
    categories.forEach(cat => (cat.orderIds || []).forEach(id => ordersInCategories.add(id)));

    // Filter uncategorized orders - prioritize checking order properties over category arrays
    let uncategorizedOrders = allOrders.filter(order => {
        // First check if the order itself has category properties
        if (order.isCategoryOrder || order.categoryId) {
            return false; // This order belongs to a category
        }

        // Then check if the order ID is in any category's orderIds array
        if (ordersInCategories.has(order.id)) {
            return false; // This order is listed in a category
        }

        return true; // This order is not in any category
    });

    // Status filter
    if (statusFilter && statusFilter !== 'All Orders') {
        uncategorizedOrders = uncategorizedOrders.filter(order => order.status === statusFilter);
    }

    // Date filter
    if (dateFilter && dateFilter !== 'All') {
        if (typeof dateFilter === 'string') {
            const now = new Date();
            let startDate = new Date();
            if (dateFilter === 'Last 15 Days') {
                startDate.setDate(now.getDate() - 15);
            } else if (dateFilter === 'Last 30 Days') {
                startDate.setDate(now.getDate() - 30);
            } else if (dateFilter === 'Last Year') {
                startDate.setFullYear(now.getFullYear() - 1);
            }
            uncategorizedOrders = uncategorizedOrders.filter(order => {
                const orderDate = order.createdAt?.toDate ? order.createdAt.toDate() : order.createdAt;
                return orderDate && orderDate >= startDate;
            });
        } else if (dateFilter.custom) {
            uncategorizedOrders = uncategorizedOrders.filter(order => {
                const orderDate = order.createdAt?.toDate ? order.createdAt.toDate() : order.createdAt;
                return orderDate && orderDate >= dateFilter.start && orderDate <= dateFilter.end;
            });
        }
    }

    // Search filter for orders
    if (searchQuery) {
        const lowercasedQuery = searchQuery.toLowerCase();
        uncategorizedOrders = uncategorizedOrders.filter(order => {
            // Check order fields first
            const orderFieldsMatch =
                (order.orderTitle && order.orderTitle.toLowerCase().includes(lowercasedQuery)) ||
                (order.description && order.description.toLowerCase().includes(lowercasedQuery)) ||
                (order.serialNumber && order.serialNumber.toLowerCase().includes(lowercasedQuery));

            if (orderFieldsMatch) return true;

            // Check if any quotes for this order have a matching vendor name
            return hasMatchingVendorQuotes(order, searchQuery);
        });
    }

    // Filter categories based on search query and status filter
    let filteredCategories = categories;

    // Apply search filter
    if (searchQuery) {
        const lowercasedQuery = searchQuery.toLowerCase();
        filteredCategories = filteredCategories.filter(category =>
            (category.title && category.title.toLowerCase().includes(lowercasedQuery)) ||
            (category.serialNumber && category.serialNumber.toLowerCase().includes(lowercasedQuery))
        );
    }

    // Apply status filter to categories - only show categories that contain orders matching the status
    if (statusFilter && statusFilter !== 'All Orders') {
        filteredCategories = filteredCategories.filter(category => {
            // Check if this category has any orders matching the current status filter
            const categoryOrders = (category.orderIds || [])
                .map(orderId => allOrders.find(o => o.id === orderId))
                .filter(Boolean);

            return categoryOrders.some(order => order.status === statusFilter);
        });
    }

    // Sort uncategorized orders
    uncategorizedOrders = sortOrders(uncategorizedOrders, sortBy, sortOrder);

    // Build a unified list of all items (orders and categories):
    const mergedList = [
        ...filteredCategories.map(cat => ({
            type: 'category',
            ...cat,
            orderTitle: cat.title,
            serialNumber: cat.serialNumber,
            createdAt: cat.createdAt,
            quantity: null,
            status: null,
        })),
        ...uncategorizedOrders.map(order => ({ type: 'order', ...order })),
    ];
    // Sort the merged list using sortOrders
    const sortedMergedList = sortOrders(mergedList, sortBy, sortOrder);

    return (
        <div className="order-list-container">
            <div className="space-y-4">
                {sortedMergedList.length === 0 && (
                    <div className="text-center text-gray-500 mt-8 py-8 border-2 border-dashed rounded-lg">
                        {statusFilter && statusFilter !== 'All Orders' ? (
                            <div>
                                <p className="mb-2">No orders or categories found with status: <span className="font-semibold">{statusFilter}</span></p>
                                <p className="text-sm">Try changing the status filter or search query.</p>
                            </div>
                        ) : searchQuery ? (
                            <div>
                                <p className="mb-2">No orders or categories found for: <span className="font-semibold">"{searchQuery}"</span></p>
                                <p className="text-sm">Search supports order titles, descriptions, RN numbers, and vendor names from quotes.</p>
                            </div>
                        ) : (
                            <p>No orders or categories found.</p>
                        )}
                    </div>
                )}
                {sortedMergedList.map(item => {
                    if (item.type === 'category') {
                        const isExpanded = expandedCategoryId === item.id;
                        let categoryOrders = (item.orderIds || [])
                            .map(orderId => allOrders.find(o => o.id === orderId))
                            .filter(Boolean);

                        // Apply search filter to category orders if search query exists
                        if (searchQuery) {
                            const lowercasedQuery = searchQuery.toLowerCase();
                            categoryOrders = categoryOrders.filter(order => {
                                // Check order fields first
                                const orderFieldsMatch =
                                    (order.orderTitle && order.orderTitle.toLowerCase().includes(lowercasedQuery)) ||
                                    (order.description && order.description.toLowerCase().includes(lowercasedQuery)) ||
                                    (order.serialNumber && order.serialNumber.toLowerCase().includes(lowercasedQuery));

                                if (orderFieldsMatch) return true;

                                // Check if any quotes for this order have a matching vendor name
                                return hasMatchingVendorQuotes(order, searchQuery);
                            });
                        }

                        // Apply status filter to category orders
                        if (statusFilter && statusFilter !== 'All Orders') {
                            categoryOrders = categoryOrders.filter(order => order.status === statusFilter);
                        }

                        categoryOrders = sortOrders(categoryOrders, sortBy, sortOrder);

                        return (
                            <div
                                key={item.id}
                                className="border border-gray-200 rounded-lg group hover:shadow-md hover:border-black transition-all duration-200 cursor-pointer"
                            >
                                <div
                                    className="p-4 flex items-center justify-between"
                                    onClick={() => setExpandedCategoryId(isExpanded ? null : item.id)}
                                >
                                    <div className="flex-shrink-0 mr-4 flex items-center">
                                        <span className="font-bold text-lg text-black">
                                            {item.serialNumber || <span className="text-gray-300">RN-----</span>}
                                        </span>
                                        <span className="mx-4 h-8 border-l border-gray-300"></span>
                                    </div>
                                    <div className="flex-grow pr-4">
                                        {editingCategoryId === item.id ? (
                                            <div className="space-y-2">
                                                <input
                                                    type="text"
                                                    value={editingCategoryTitle}
                                                    onChange={(e) => setEditingCategoryTitle(e.target.value)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') {
                                                            e.stopPropagation();
                                                            onSaveCategoryTitle(item.id);
                                                        } else if (e.key === 'Escape') {
                                                            e.stopPropagation();
                                                            setEditingCategoryId(null);
                                                            setEditingCategoryTitle("");
                                                        }
                                                    }}
                                                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black font-bold text-lg"
                                                    placeholder="Enter category title"
                                                    autoFocus
                                                />
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onSaveCategoryTitle(item.id);
                                                        }}
                                                        className="px-3 py-1 bg-black text-white text-sm rounded-md hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                        disabled={!editingCategoryTitle.trim()}
                                                    >
                                                        Save
                                                    </button>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setEditingCategoryId(null);
                                                            setEditingCategoryTitle("");
                                                        }}
                                                        className="px-3 py-1 bg-gray-200 text-gray-700 text-sm rounded-md hover:bg-gray-300 transition-colors"
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="flex items-center gap-2">
                                                <p className="font-bold text-lg flex items-center">{item.title}</p>
                                                {isMaster && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setEditingCategoryId(item.id);
                                                            setEditingCategoryTitle(item.title);
                                                        }}
                                                        className="p-1 rounded-full text-gray-400 hover:bg-gray-200 hover:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity"
                                                        title="Edit Category Title"
                                                    >
                                                        <Edit size={14} />
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                        <p className="text-sm text-gray-500 truncate max-w-md">
                                            Category • {categoryOrders.length} order{categoryOrders.length !== 1 ? 's' : ''}
                                            {statusFilter && statusFilter !== 'All Orders' && (
                                                <span className="ml-2 text-blue-600">
                                                    ({categoryOrders.length} matching {statusFilter})
                                                </span>
                                            )}
                                        </p>
                                    </div>
                                    <div className="text-right flex-shrink-0 w-48">
                                        <span className={`px-3 py-1 text-xs font-semibold rounded-full ${statusColors['Category'] || 'bg-gray-100 text-gray-800'}`}>{statusDisplayNames['Category'] || 'Category'}</span>
                                    </div>
                                    <div className="flex items-center pl-4 gap-2">
                                        {isMaster && (
                                            <button
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    if (window.confirm(`Are you sure you want to delete the category "${item.title}"? This will also delete all ${categoryOrders.length} orders inside it.`)) {
                                                        onDeleteCategory(item.id);
                                                    }
                                                }}
                                                className="p-2 rounded-full text-gray-400 hover:bg-red-100 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                                                title="Delete Category"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        )}
                                        <ChevronRight
                                            size={20}
                                            className={`text-gray-400 group-hover:text-black transition-colors transform ${isExpanded ? 'rotate-90' : ''}`}
                                        />
                                    </div>
                                </div>
                                {isExpanded && (
                                    <div className="bg-gray-50 border-t border-gray-200 px-4 pb-4">
                                        <div className="flex justify-between items-center pt-4 pb-2">
                                            <h3 className="text-lg font-bold">Orders in Category</h3>
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={e => {
                                                        e.stopPropagation();
                                                        onNavigateToCreateOrderWithCategory(item);
                                                    }}
                                                    className={`flex items-center gap-2 px-3 py-2 text-sm rounded-md transition-colors ${categoryOrders.length === 0
                                                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                                                        : 'bg-black text-white hover:bg-gray-800'
                                                        }`}
                                                    title="Add new order to this category"
                                                >
                                                    <Plus size={16} />
                                                    {categoryOrders.length === 0 ? 'Add First Order' : 'Add Order'}
                                                </button>
                                                <button
                                                    onClick={e => {
                                                        e.stopPropagation();
                                                        setSelectedCategoryForAddingOrders(item);
                                                        setShowAddOrdersToCategoryModal(true);
                                                    }}
                                                    className="px-3 py-2 text-sm rounded-md bg-black text-white hover:bg-gray-800 transition-colors"
                                                    title="Add existing orders to this category"
                                                >
                                                    Add Existing Orders
                                                </button>
                                                <button
                                                    onClick={e => {
                                                        e.stopPropagation();
                                                        onOpenCategoryEmailModal(item, categoryOrders);
                                                    }}
                                                    className="p-2 rounded-full hover:bg-gray-200"
                                                    title="Email all suppliers for this category"
                                                >
                                                    <Mail size={18} />
                                                </button>
                                            </div>
                                        </div>
                                        <div className="space-y-4">
                                            {categoryOrders.length === 0 && (
                                                <div className="text-gray-500 border border-dashed rounded-lg p-4 text-center">
                                                    <p className="mb-2">No orders in this category.</p>
                                                    <p className="text-sm text-gray-400">Click "Add Order" above to create your first order.</p>
                                                </div>
                                            )}
                                            {categoryOrders.map(order => (
                                                <div
                                                    key={order.id}
                                                    className="border border-gray-200 rounded-lg group hover:shadow-md hover:border-black transition-all duration-200 cursor-pointer"
                                                    onClick={() => onSelectOrder(order)}
                                                >
                                                    <div className="p-4 flex items-center justify-between">
                                                        <div className="flex-shrink-0 mr-4 flex items-center">
                                                            <span className="font-bold text-lg text-black">
                                                                {item.serialNumber || <span className="text-gray-300">RN-----</span>}
                                                            </span>
                                                            <span className="mx-4 h-8 border-l border-gray-300"></span>
                                                        </div>
                                                        <div className="flex-grow pr-4">
                                                            <TruncatedTitle
                                                                title={order.orderTitle}
                                                                quantity={order.quantity}
                                                            />
                                                            <div className="flex items-center max-w-md">
                                                                <p className="text-xs text-gray-500 truncate flex-grow">
                                                                    {order.description ? order.description.split(' ').slice(0, 3).join(' ') + (order.description.split(' ').length > 3 ? '...' : '') : ''}
                                                                    {order.description && (
                                                                        <Tooltip text="Copy description">
                                                                            <button
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    navigator.clipboard.writeText(order.description || '');
                                                                                }}
                                                                                className="inline hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600"
                                                                            >
                                                                                <Copy size={10} />
                                                                            </button>
                                                                        </Tooltip>
                                                                    )}
                                                                </p>
                                                            </div>
                                                            {searchQuery && hasMatchingVendorQuotes(order, searchQuery) && (
                                                                <p className="text-xs text-blue-600 mt-1">
                                                                    ✓ Found via vendor quotes
                                                                </p>
                                                            )}
                                                        </div>
                                                        <div className="text-right flex-shrink-0 w-48">
                                                            <span className={`px-3 py-1 text-xs font-semibold rounded-full ${statusColors[order.status] || 'bg-gray-100 text-gray-800'}`}>{statusDisplayNames[order.status] || order.status}</span>
                                                            {order.status === 'In Progress' && order.deliveryDate ? (
                                                                <div>
                                                                    <CountdownTimer targetDate={order.deliveryDate.toDate()} />
                                                                    {(() => {
                                                                        const now = new Date();
                                                                        const deliveryDate = order.deliveryDate.toDate();
                                                                        const daysLeft = Math.ceil((deliveryDate - now) / (1000 * 60 * 60 * 24));
                                                                        if (daysLeft <= 3 && daysLeft > 0) {
                                                                            return <div className="text-xs text-orange-600 font-medium mt-1">Due soon</div>;
                                                                        } else if (daysLeft <= 0) {
                                                                            return <div className="text-xs text-red-600 font-medium mt-1">Overdue!</div>;
                                                                        }
                                                                        return null;
                                                                    })()}
                                                                    {/* Delivery Remarks Display */}
                                                                    {order.deliveryRemarks && (
                                                                        <div className="mt-2 p-2 bg-indigo-50 border border-indigo-200 rounded text-xs">
                                                                            <div className="flex items-start gap-1">
                                                                                <span className="text-indigo-600 font-medium">Remarks:</span>
                                                                                <span className="text-indigo-700 flex-1">
                                                                                    {order.deliveryRemarks.length > 50
                                                                                        ? `${order.deliveryRemarks.substring(0, 50)}...`
                                                                                        : order.deliveryRemarks
                                                                                    }
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ) : (
                                                                <p className="text-sm text-gray-500 mt-1">{order.createdAt ? new Date(order.createdAt.toDate()).toLocaleDateString('en-GB') : 'Just now'}</p>
                                                            )}
                                                        </div>
                                                        <div className="flex items-center pl-4 gap-2">
                                                            {/* Remove from Category Button - only show for orders that were added to category later */}
                                                            {order.serialNumber !== item.serialNumber && (
                                                                <button
                                                                    onClick={e => {
                                                                        e.stopPropagation();
                                                                        if (window.confirm('Remove this order from the category? It will become a standalone order.')) {
                                                                            handleRemoveOrderFromCategory(item.id, order.id);
                                                                        }
                                                                    }}
                                                                    className="p-2 rounded-full text-gray-400 hover:bg-orange-100 hover:text-orange-600 opacity-0 group-hover:opacity-100 transition-opacity"
                                                                    title="Remove from Category"
                                                                >
                                                                    <X size={16} />
                                                                </button>
                                                            )}

                                                            {isMaster && (
                                                                <button
                                                                    onClick={e => {
                                                                        e.stopPropagation();
                                                                        if (window.confirm('Are you sure you want to delete this order?')) {
                                                                            setDeletingOrderId(order.id);
                                                                            onDeleteOrder(order.id);
                                                                        }
                                                                    }}
                                                                    className="p-2 rounded-full text-gray-400 hover:bg-red-100 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                                                                    title="Delete Order"
                                                                >
                                                                    <Trash2 size={16} />
                                                                </button>
                                                            )}
                                                            <ChevronRight size={20} className="text-gray-400 group-hover:text-black transition-colors" />
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    } else if (item.type === 'order') {
                        return (
                            <div
                                key={item.id}
                                className={`border border-gray-200 rounded-lg group hover:shadow-md hover:border-black transition-all duration-200 cursor-pointer ${deletingOrderId === item.id ? 'order-card-deleting' : ''}`}
                                onClick={() => onSelectOrder(item)}
                            >
                                <div className="p-4 flex items-center justify-between">
                                    <div className="flex-shrink-0 mr-4 flex items-center">
                                        <span className="font-bold text-lg text-black">
                                            {item.serialNumber || <span className="text-gray-300">RN-----</span>}
                                        </span>
                                        <span className="mx-4 h-8 border-l border-gray-300"></span>
                                    </div>
                                    <div className="flex-grow pr-4">
                                        <TruncatedTitle
                                            title={item.orderTitle}
                                            quantity={item.quantity}
                                        />
                                        <div className="flex items-center max-w-md">
                                            <p className="text-xs text-gray-500 truncate flex-grow">
                                                {item.description ? item.description.split(' ').slice(0, 3).join(' ') + (item.description.split(' ').length > 3 ? '...' : '') : ''}
                                                {item.description && (
                                                    <Tooltip text="Copy description">
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                navigator.clipboard.writeText(item.description || '');
                                                            }}
                                                            className="inline hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600"
                                                        >
                                                            <Copy size={10} />
                                                        </button>
                                                    </Tooltip>
                                                )}
                                            </p>
                                        </div>
                                        {searchQuery && hasMatchingVendorQuotes(item, searchQuery) && (
                                            <p className="text-xs text-blue-600 mt-1">
                                                ✓ Found via vendor quotes
                                            </p>
                                        )}
                                    </div>
                                    <div className="text-right flex-shrink-0 w-48">
                                        <span className={`px-3 py-1 text-xs font-semibold rounded-full ${statusColors[item.status] || 'bg-gray-100 text-gray-800'}`}>{statusDisplayNames[item.status] || item.status}</span>
                                        {item.status === 'In Progress' && item.deliveryDate ? (
                                            <div>
                                                <CountdownTimer targetDate={item.deliveryDate.toDate()} />
                                                {(() => {
                                                    const now = new Date();
                                                    const deliveryDate = item.deliveryDate.toDate();
                                                    const daysLeft = Math.ceil((deliveryDate - now) / (1000 * 60 * 60 * 24));
                                                    if (daysLeft <= 3 && daysLeft > 0) {
                                                        return <div className="text-xs text-orange-600 font-medium mt-1">⚠️ Due soon</div>;
                                                    } else if (daysLeft <= 0) {
                                                        return <div className="text-xs text-red-600 font-medium mt-1">🚨 Overdue!</div>;
                                                    }
                                                    return null;
                                                })()}
                                            </div>
                                        ) : (
                                            <p className="text-sm text-gray-500 mt-1">{item.createdAt ? new Date(item.createdAt.toDate()).toLocaleDateString('en-GB') : 'Just now'}</p>
                                        )}
                                    </div>
                                    <div className="flex items-center pl-4 gap-2">
                                        {isMaster && (
                                            <button
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    if (window.confirm('Are you sure you want to delete this order?')) {
                                                        setDeletingOrderId(item.id);
                                                        onDeleteOrder(item.id);
                                                    }
                                                }}
                                                className="p-2 rounded-full text-gray-400 hover:bg-red-100 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                                                title="Delete Order"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        )}
                                        <ChevronRight size={20} className="text-gray-400 group-hover:text-black transition-colors" />
                                    </div>
                                </div>
                            </div>
                        );
                    }
                    return null;
                })}
            </div>
        </div>
    );
};


const CreateOrder = ({ onBack, currentUser, onOrderCreated, draft, onDraftChange, category, onAddCategory, categoryModalOpen, onCloseCategoryModal, createCategory, categoryLoading }) => {
    const [step, setStep] = useState(draft.step || 'input');
    const [orderTitle, setOrderTitle] = useState(draft.orderTitle || '');
    const [description, setDescription] = useState(draft.description || '');
    const [quantity, setQuantity] = useState(draft.quantity || '');
    const [editableSummary, setEditableSummary] = useState(draft.aiSummary || '');
    const [isProcessing, setIsProcessing] = useState(false);
    const [categoryState] = useState(category || null);
    const [geminiError, setGeminiError] = useState('');
    const [aiAutoFilled, setAiAutoFilled] = useState({ orderTitle: false, quantity: false });
    const [quantityExtracting, setQuantityExtracting] = useState(false);

    useEffect(() => {
        onDraftChange({ step, orderTitle, description, quantity, aiSummary: editableSummary });
    }, [step, orderTitle, description, quantity, editableSummary, onDraftChange]);


    const cleanDescription = (desc) => {
        return desc
            .split('\n')
            .map(line => line.trim())
            .filter(line => line.length > 0)
            .join('\n')
            .trim();
    };

    // Clean description after AI generation by removing quantity-related text
    const cleanDescriptionAfterGeneration = (desc, quantity) => {
        if (!quantity) return desc;

        // More comprehensive quantity patterns to remove
        const quantityPatterns = [
            // qty patterns
            /\bqty\s*:?\s*\d+\b/gi,
            /\bquantity\s*:?\s*\d+\b/gi,

            // number + units patterns
            /\b\d+\s*(?:units?|pcs?|pieces?|nos?|items?|sets?|pieces?)\b/gi,

            // number + x patterns
            /\b\d+x\b/gi,

            // need/require/want patterns
            /\bneed\s+\d+\b/gi,
            /\brequire\s+\d+\b/gi,
            /\bwant\s+\d+\b/gi,
            /\blooking\s+for\s+\d+\b/gi,

            // combined patterns
            /\bneed\s+\d+\s*(?:units?|pcs?|pieces?|nos?|items?|sets?)\b/gi,
            /\brequire\s+\d+\s*(?:units?|pcs?|pieces?|nos?|items?|sets?)\b/gi,
            /\bwant\s+\d+\s*(?:units?|pcs?|pieces?|nos?|items?|sets?)\b/gi,

            // standalone numbers that might be quantities
            /\b\d+\b(?=\s+(?:units?|pcs?|pieces?|nos?|items?|sets?))/gi
        ];

        let cleanedDesc = desc;

        // Remove quantity patterns
        quantityPatterns.forEach((pattern) => {
            cleanedDesc = cleanedDesc.replace(pattern, '').trim();
        });

        // Clean up extra spaces, commas, and empty lines
        cleanedDesc = cleanedDesc
            .replace(/\s*,\s*,/g, ',') // Remove double commas
            .replace(/,\s*,/g, ',') // Remove double commas
            .replace(/^\s*,\s*/g, '') // Remove leading comma
            .replace(/\s*,\s*$/g, '') // Remove trailing comma
            .split('\n')
            .map(line => line.trim())
            .filter(line => line.length > 0)
            .join('\n')
            .trim();

        return cleanedDesc;
    };

    // Lightweight quantity extraction function
    const extractQuantityFromText = (text) => {
        if (!text || text.trim().length < 3) return null;

        // Common quantity patterns
        const patterns = [
            /qty\s*:?\s*(\d+)/i,
            /quantity\s*:?\s*(\d+)/i,
            /(\d+)\s*(?:units?|pcs?|pieces?|nos?|items?|sets?|pieces?)/i,
            /(\d+)x\b/i,
            /need\s+(\d+)/i,
            /require\s+(\d+)/i,
            /want\s+(\d+)/i,
            /looking\s+for\s+(\d+)/i,
            /require\s+(\d+)\s*(?:units?|pcs?|pieces?|nos?|items?|sets?)/i,
            /need\s+(\d+)\s*(?:units?|pcs?|pieces?|nos?|items?|sets?)/i
        ];

        for (const pattern of patterns) {
            const match = text.match(pattern);
            if (match) {
                const qty = parseInt(match[1], 10);
                if (qty > 0 && qty < 10000) { // Reasonable quantity range
                    return qty;
                }
            }
        }

        return null;
    };

    // Debounced quantity extraction - always re-extract on description changes
    useEffect(() => {
        if (!description.trim()) {
            // If description is cleared and quantity was auto-filled, clear it
            if (aiAutoFilled.quantity) {
                setQuantity('');
                setAiAutoFilled(prev => ({ ...prev, quantity: false }));
            }
            return;
        }

        // Don't extract quantity if we're in the processing step (AI generation)
        if (step === 'processing') {
            return;
        }

        const timeoutId = setTimeout(async () => {
            const extractedQty = extractQuantityFromText(description);
            if (extractedQty) {
                setQuantityExtracting(true);
                // Small delay to show the extracting state
                setTimeout(() => {
                    setQuantity(extractedQty.toString());
                    setAiAutoFilled(prev => ({ ...prev, quantity: true }));
                    setQuantityExtracting(false);
                }, 500);
            }
        }, 1000); // 1 second debounce

        return () => clearTimeout(timeoutId);
    }, [description, step]);

    const handleGenerateSummary = async (e) => {
        e.preventDefault();
        if (!description.trim()) return;
        setIsProcessing(true);
        setStep('processing');
        setGeminiError(''); // Clear any previous errors

        // Clean the description before processing
        const cleanedDescription = cleanDescription(description);
        setDescription(cleanedDescription);

        try {
            const result = await processWithGemini(cleanedDescription);

            // Get the current quantity (either from user input or AI extraction)
            const currentQuantity = quantity || result.quantity;

            // Create enhanced summary that includes quantity if available
            let enhancedSummary = result.summary;
            if (currentQuantity) {
                // Add quantity as the first bullet point in the summary
                enhancedSummary = `* Quantity: ${currentQuantity} units\n${result.summary}`;
            }

            setEditableSummary(enhancedSummary);

            // Track which fields were auto-filled by AI
            const newAiAutoFilled = { orderTitle: false, quantity: false };

            // Auto-fill order title if AI detected one and user hasn't entered one
            if (result.orderTitle && !orderTitle.trim()) {
                setOrderTitle(result.orderTitle);
                newAiAutoFilled.orderTitle = true;
            }

            // Auto-fill quantity if AI detected one and user hasn't entered one
            if (result.quantity && !quantity) {
                setQuantity(result.quantity);
                newAiAutoFilled.quantity = true;
            }

            setAiAutoFilled(newAiAutoFilled);

            // Clean the description by removing quantity-related text
            const cleanedDescriptionAfterAI = cleanDescriptionAfterGeneration(cleanedDescription, currentQuantity);
            setDescription(cleanedDescriptionAfterAI);

            // Check if quantity is missing and provide user feedback
            if (!result.quantity && !quantity) {
                setGeminiError('⚠️ AI could not detect quantity from your description. Please enter the quantity manually below.');
            } else if (result.quantity && !quantity) {
                setGeminiError('✅ AI detected quantity: ' + result.quantity + ' units');
            }

            setStep('review');
        } catch (error) {
            console.error('Error generating summary:', error);
            setGeminiError('Failed to generate summary. Please try again.');
        } finally {
            setIsProcessing(false);
        }
    };


    const handleConfirmAndCreate = async () => {
        setIsProcessing(true);
        try {
            // Validate required fields
            if (!description.trim()) {
                throw new Error('Description is required');
            }
            if (!currentUser || !currentUser.uid) {
                throw new Error('User authentication required');
            }

            const geminiResult = await processWithGemini(description);

            // Use the order title as provided by user or AI, or generate from AI if not provided
            const finalOrderTitle = orderTitle.trim() || geminiResult.orderTitle || 'Untitled Requirement';

            let newOrderData = {
                orderTitle: finalOrderTitle,
                orderTitleNormalized: finalOrderTitle.toLowerCase(),
                description: cleanDescription(description),
                aiSummary: typeof editableSummary === 'string' ? editableSummary.trim() : '',
                quantity: quantity ? parseInt(quantity, 10) : null,
                creator: { uid: currentUser.uid, displayName: currentUser.displayName },
                status: 'Awaiting Requirements',
                createdAt: serverTimestamp(),
            };

            // If this is a category order, assign the category's RN number
            if (categoryState && categoryState.id) {
                newOrderData = {
                    ...newOrderData,
                    categoryId: categoryState.id,
                    isCategoryOrder: true,
                    serialNumber: categoryState.serialNumber, // Use category's RN number
                };
            } else {
                // Generate serial number using Firestore transaction for regular orders
                let serialNumber = '';
                const counterRef = doc(db, 'counters', 'orders');
                await runTransaction(db, async (transaction) => {
                    const counterDoc = await transaction.get(counterRef);
                    let lastSerial = 0;
                    if (counterDoc.exists()) {
                        lastSerial = counterDoc.data().lastSerial || 0;
                    }
                    const nextSerial = lastSerial + 1;
                    serialNumber = `RN${String(nextSerial).padStart(5, '0')}`;
                    transaction.set(counterRef, { lastSerial: nextSerial }, { merge: true });
                });
                newOrderData.serialNumber = serialNumber;
            }

            // If category is present, use a batch to ensure atomic operations
            let docRef;
            if (categoryState && categoryState.id) {
                console.log('Creating category order for category:', categoryState.id, categoryState.title);
                const batch = writeBatch(db);
                const orderRef = doc(collection(db, 'orders'));

                // Add the order to the batch
                batch.set(orderRef, newOrderData);

                // Update the category to include this order
                const categoryRef = doc(db, 'orderCategories', categoryState.id);
                batch.update(categoryRef, {
                    orderIds: arrayUnion(orderRef.id)
                });

                // Commit the batch
                await batch.commit();

                // Use the generated ID from the batch
                docRef = orderRef;
            } else {
                // Regular order creation
                docRef = await addDoc(collection(db, 'orders'), newOrderData);
            }

            onOrderCreated({ id: docRef.id, ...newOrderData });
        } catch (error) {
            console.error('Error creating order:', error);
            console.error('Error details:', {
                code: error.code,
                message: error.message,
                stack: error.stack
            });
            alert('Failed to create order. Please try again.');
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <div>
            {(!categoryState) && (
                <button onClick={onBack} className="text-sm font-medium mb-4">&larr; Back to List</button>
            )}
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold">Add New Requirement{categoryState ? ` to ${categoryState.title}` : ''}</h1>
                <button
                    onClick={onAddCategory}
                    className="flex items-center gap-2 bg-black text-white px-4 py-2 rounded-md hover:bg-gray-800 transition-colors"
                    title="Add Category"
                >
                    <Plus size={18} />
                    <span>Add Category</span>
                </button>
            </div>
            <div className="max-w-2xl mx-auto border p-8 rounded-lg">

                <form
                    onSubmit={step === 'input' ? handleGenerateSummary : (e) => { e.preventDefault(); if (step === 'review') handleConfirmAndCreate(); }}
                    className="space-y-6"
                >
                    {step === 'input' && (
                        <>
                            <div>
                                <InputField
                                    label="Order Title (Optional)"
                                    name="orderTitle"
                                    value={orderTitle}
                                    onChange={(e) => setOrderTitle(e.target.value)}
                                    icon={<FileText size={18} />}
                                    placeholder="Leave empty to auto-generate from description"
                                />
                            </div>
                            <div>
                                <div className="relative">
                                    <InputField
                                        label="Quantity (Required)"
                                        name="quantity"
                                        type="number"
                                        value={quantity}
                                        onChange={(e) => {
                                            setQuantity(e.target.value);
                                            // Clear auto-fill indicator when user manually changes
                                            if (aiAutoFilled.quantity) {
                                                setAiAutoFilled(prev => ({ ...prev, quantity: false }));
                                            }
                                        }}
                                        icon={<Circle size={18} />}
                                        required
                                    />
                                    {quantityExtracting && (
                                        <div className="absolute right-3 top-8 flex items-center">
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                                        </div>
                                    )}
                                    {aiAutoFilled.quantity && !quantityExtracting && (
                                        <div className="absolute right-3 top-8 flex items-center">
                                            <div className="flex items-center text-green-600 text-xs">
                                                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                </svg>
                                                <span className="text-xs">AI-filled</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Received Requirement (Required)</label>
                                <textarea
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    rows="8"
                                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                                    placeholder="Enter the full requirement description here (required)."
                                    required
                                />
                            </div>
                        </>
                    )}

                    {step === 'processing' && <LoadingScreen message="Generating summary..." />}

                    {geminiError && (
                        <div className={`border rounded-md p-4 mb-4 ${geminiError.startsWith('✅')
                            ? 'bg-green-50 border-green-200'
                            : geminiError.startsWith('⚠️')
                                ? 'bg-amber-50 border-amber-200'
                                : 'bg-red-50 border-red-200'
                            }`}>
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    {geminiError.startsWith('✅') ? (
                                        <CheckCircle className="h-5 w-5 text-green-400" />
                                    ) : geminiError.startsWith('⚠️') ? (
                                        <AlertTriangle className="h-5 w-5 text-amber-400" />
                                    ) : (
                                        <AlertTriangle className="h-5 w-5 text-red-400" />
                                    )}
                                </div>
                                <div className="ml-3">
                                    <p className={`text-sm ${geminiError.startsWith('✅')
                                        ? 'text-green-800'
                                        : geminiError.startsWith('⚠️')
                                            ? 'text-amber-800'
                                            : 'text-red-800'
                                        }`}>
                                        {geminiError}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {step === 'review' && (
                        <div
                            className="space-y-4"
                            tabIndex={-1}
                            onKeyDown={e => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    handleConfirmAndCreate();
                                }
                            }}
                        >
                            <div>
                                <InputField
                                    label="Order Title (Optional)"
                                    name="orderTitle"
                                    value={orderTitle}
                                    onChange={(e) => setOrderTitle(e.target.value)}
                                    icon={<FileText size={18} />}
                                    placeholder="Leave empty to auto-generate from description"
                                />
                            </div>
                            <div>
                                <InputField
                                    label="Quantity (Required)"
                                    name="quantity"
                                    type="number"
                                    value={quantity}
                                    onChange={(e) => setQuantity(e.target.value)}
                                    icon={<Circle size={18} />}
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Cleaned Description</label>
                                <textarea
                                    value={cleanDescription(description)}
                                    readOnly
                                    rows="4"
                                    className="w-full p-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700"
                                    placeholder="Your description with unnecessary spaces and lines removed."
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">AI Generated Summary</label>
                                <textarea
                                    value={editableSummary}
                                    onChange={(e) => setEditableSummary(e.target.value)}
                                    rows="10"
                                    className="w-full p-2 border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-blue-50"
                                    placeholder="AI-generated summary of your requirement. You can edit this text as needed."
                                />
                            </div>
                            <div className="flex gap-4 pt-4">
                                <button type="button" onClick={() => setStep('input')} className="w-full bg-white border border-gray-300 py-2 rounded-md hover:bg-gray-100">Re-enter Requirement</button>
                                <button type="submit" disabled={isProcessing} className="w-full bg-black text-white py-2 rounded-md hover:bg-gray-800 disabled:bg-gray-400">
                                    {isProcessing ? 'Saving...' : 'Confirm & Create'}
                                </button>
                            </div>
                        </div>
                    )}

                    {step === 'input' && (
                        <div className="flex gap-4 pt-4">
                            <button
                                type="button"
                                onClick={onBack}
                                className="w-1/2 bg-white border border-gray-300 px-6 py-3 rounded-lg text-base font-semibold hover:bg-gray-100 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={isProcessing}
                                className="w-1/2 bg-black text-white px-6 py-3 rounded-lg text-base font-semibold hover:bg-gray-800 disabled:bg-gray-400 transition-colors"
                            >
                                Generate
                            </button>
                        </div>
                    )}
                </form>
            </div>

            {/* Add Category Modal */}
            {categoryModalOpen && (
                <AddCategoryModal
                    onClose={onCloseCategoryModal}
                    onCreate={createCategory}
                    loading={categoryLoading}
                />
            )}
        </div>
    );
};


const QuoteStatusDropdown = ({ currentStatus, onStatusChange, disabled = false }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    const statusOptions = [
        { label: quoteStatusDisplayNames[QUOTE_STATUSES.AWAITING_REQUIREMENTS], value: QUOTE_STATUSES.AWAITING_REQUIREMENTS },
        { label: quoteStatusDisplayNames[QUOTE_STATUSES.UNDER_NEGOTIATION], value: QUOTE_STATUSES.UNDER_NEGOTIATION },
        { label: quoteStatusDisplayNames[QUOTE_STATUSES.AWAITING_INFORMATION], value: QUOTE_STATUSES.AWAITING_INFORMATION },
        { label: quoteStatusDisplayNames[QUOTE_STATUSES.PROFORMA_RECEIVED], value: QUOTE_STATUSES.PROFORMA_RECEIVED },
        { label: quoteStatusDisplayNames[QUOTE_STATUSES.ADVANCE_PAID], value: QUOTE_STATUSES.ADVANCE_PAID },
        { label: quoteStatusDisplayNames[QUOTE_STATUSES.IN_PROGRESS], value: QUOTE_STATUSES.IN_PROGRESS },
        { label: quoteStatusDisplayNames[QUOTE_STATUSES.COMPLETED], value: QUOTE_STATUSES.COMPLETED }
    ];

    const handleStatusChange = (newStatus) => {
        onStatusChange(newStatus);
        setIsOpen(false);
    };

    useClickOutside(dropdownRef, () => setIsOpen(false));

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={(e) => {
                    e.stopPropagation();
                    if (!disabled) setIsOpen(!isOpen);
                }}
                disabled={disabled}
                className={`px-3 py-1 text-xs font-semibold rounded-full border transition-colors ${disabled ? 'bg-gray-100 text-gray-400 cursor-not-allowed' :
                    currentStatus ? quoteStatusColors[currentStatus] || 'bg-gray-100 text-gray-800' :
                        'bg-gray-100 text-gray-800 hover:bg-gray-200'
                    }`}
            >
                {currentStatus ? quoteStatusDisplayNames[currentStatus] : 'Set Status'}
            </button>
            {isOpen && !disabled && (
                <div className="absolute z-20 mt-1 w-36 bg-white border rounded-md shadow-lg max-h-48 overflow-y-auto">
                    {statusOptions.map(status => (
                        <button
                            key={status.value}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleStatusChange(status.value);
                            }}
                            className={`w-full text-left px-2 py-1.5 text-xs rounded-md mx-1 my-0.5 transition-colors ${currentStatus === status.value
                                ? `${quoteStatusColors[status.value] || 'bg-gray-100 text-gray-800'} font-semibold`
                                : 'hover:bg-gray-100'
                                }`}
                        >
                            {status.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

const StatusChangeDropdown = ({ currentStatus, onStatusChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    const statusOptions = [
        { label: statusDisplayNames['Awaiting Requirements'], value: 'Awaiting Requirements' },
        { label: statusDisplayNames['Negotiation'], value: 'Negotiation' },
        { label: statusDisplayNames['Information'], value: 'Information' },
        { label: statusDisplayNames['Proforma'], value: 'Proforma' },
        { label: statusDisplayNames['Advance'], value: 'Advance' },
        { label: statusDisplayNames['In Progress'], value: 'In Progress' },
        { label: statusDisplayNames['Completed'], value: 'Completed' }
    ];

    const handleStatusChange = (newStatus) => {
        onStatusChange(newStatus);
        setIsOpen(false);
    };



    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen]);

    return (
        <div className="relative" ref={dropdownRef}>
            <Tooltip text="Change Status">
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    className={`px-3 py-1.5 text-sm font-medium rounded-md border transition-colors interactive-button ${statusColors[currentStatus] || 'bg-gray-100 text-gray-800'}`}
                >
                    {statusDisplayNames[currentStatus] || currentStatus}
                </button>
            </Tooltip>
            {isOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white border rounded-md shadow-lg z-20">
                    {statusOptions.map(status => (
                        <button
                            key={status.value}
                            onClick={() => handleStatusChange(status.value)}
                            className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${status.value === currentStatus ? 'bg-gray-50 font-semibold' : ''}`}
                        >
                            {statusDisplayNames[status.value] || status.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

const OrderDetails = ({ order, setSelectedOrder, currentUser, isMaster, allUsers, onBack, onOrderDeleted, onQuoteSelected, allVendors, onMarkAsCompleted, onRevertStatus, draftQuote, onDraftChange, onDraftSubmit, categories = [], decreaseRNCounter, handleRemoveOrderFromCategory }) => {

    const { quotes, loading: quotesLoading, error: quotesError } = useQuotesWithLoading(order?.id);
    const [expandedQuoteId, setExpandedQuoteId] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [activeTab, setActiveTab] = useState('received');
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [isEditingDesc, setIsEditingDesc] = useState(false);
    const [isEditingSummary, setIsEditingSummary] = useState(false);
    const [description, setDescription] = useState(order.description);
    const [aiSummary, setAiSummary] = useState(order.aiSummary);
    const [quoteToEdit, setQuoteToEdit] = useState(null);
    const [isEditingFinalPrice, setIsEditingFinalPrice] = useState(false);
    const [finalPrice, setFinalPrice] = useState(order.finalPrice || '');
    const [draggedIndex, setDraggedIndex] = useState(null);
    const [showDescriptionDropdown, setShowDescriptionDropdown] = useState(false);
    const descriptionDropdownRef = useRef(null);
    const [deliveryRemarks, setDeliveryRemarks] = useState(order.deliveryRemarks || '');
    const [isEditingDeliveryRemarks, setIsEditingDeliveryRemarks] = useState(false);

    const handleDragStart = (index) => {
        setDraggedIndex(index);
    };

    // Click outside handler for description dropdown
    useClickOutside(descriptionDropdownRef, () => {
        setShowDescriptionDropdown(false);
    });

    // Extract key points from description for dropdown
    const getDescriptionKeyPoints = () => {
        if (!description) return [];
        const lines = description.split('\n').filter(line => line.trim());
        return lines.slice(0, 5); // Show first 5 lines as key points
    };

    const handleDragOver = (index) => {
        if (draggedIndex === null || draggedIndex === index) return;

        // Reorder quotes array
        const reorderedQuotes = [...quotes];
        const [draggedQuote] = reorderedQuotes.splice(draggedIndex, 1);
        reorderedQuotes.splice(index, 0, draggedQuote);

        // Update the dragged index to the new position
        setDraggedIndex(index);

        // Persist the new order to Firestore immediately
        const batch = writeBatch(db);
        reorderedQuotes.forEach((quote, idx) => {
            const quoteRef = doc(db, `orders/${order.id}/quotes`, quote.id);
            batch.update(quoteRef, { orderIndex: idx });
        });
        batch.commit().catch(error => console.error('Failed to update quote order:', error));
    };

    const handleDrop = async () => {
        setDraggedIndex(null);
        // Remove dragging class from all quote cards
        document.querySelectorAll('.quote-card-dragging').forEach(el => {
            el.classList.remove('quote-card-dragging');
        });

        // Update order status based on the top quote's status
        if (quotes.length > 0) {
            try {
                const topQuote = quotes[0];
                if (topQuote && topQuote.status && quoteStatusToOrderStatus[topQuote.status]) {
                    const orderRef = doc(db, 'orders', order.id);
                    const newOrderStatus = quoteStatusToOrderStatus[topQuote.status];
                    await updateDoc(orderRef, { status: newOrderStatus });
                }
            } catch (error) {
                console.error('Failed to persist quote order:', error);
            }
        }
    };

    // Restore missing variables and functions
    const tabClass = (tabName) => `py-2 px-4 text-sm font-medium cursor-pointer ${activeTab === tabName ? 'border-b-2 border-black text-black' : 'text-gray-500 hover:text-black'}`;

    // Improved quote separation logic with better error handling
    const finalizedQuote = useMemo(() => {
        const finalized = quotes.find(q => q.id === order.finalizedQuoteId);
        return finalized;
    }, [quotes, order.finalizedQuoteId]);

    const otherQuotes = useMemo(() => {
        const others = quotes.filter(q => q.id !== order.finalizedQuoteId);
        return others;
    }, [quotes, order.finalizedQuoteId]);

    // Check if any quote has "Advance Paid" status
    const hasAdvancePaidQuote = useMemo(() => {
        return quotes.some(quote => quote.status === QUOTE_STATUSES.ADVANCE_PAID);
    }, [quotes]);
    const handleUpdateOrder = async (updates) => {
        const orderRef = doc(db, 'orders', order.id);
        try {
            // If description is being updated, regenerate AI summary
            if (updates.description && updates.description !== order.description) {
                try {
                    const result = await processWithGemini(updates.description);
                    updates.aiSummary = result.summary;
                } catch (error) {
                    console.error('Failed to regenerate AI summary:', error);
                    // If AI summary generation fails, keep the old one
                    delete updates.aiSummary;
                }
            }

            await updateDoc(orderRef, updates);
            const updatedOrderSnap = await getDoc(orderRef);
            if (updatedOrderSnap.exists()) {
                setSelectedOrder({ id: updatedOrderSnap.id, ...updatedOrderSnap.data() });
            }
            setShowEditModal(false);
            setIsEditingFinalPrice(false);
        } catch (error) {
            console.error('Error updating order:', error);
        }
    };

    const handleSaveDeliveryRemarks = async () => {
        try {
            await handleUpdateOrder({ deliveryRemarks: deliveryRemarks.trim() });
            setIsEditingDeliveryRemarks(false);
        } catch (error) {
            console.error('Error saving delivery remarks:', error);
        }
    };
    const handleSelectBestQuote = async (quoteId) => {
        const orderRef = doc(db, 'orders', order.id);
        const batch = writeBatch(db);
        const winningQuote = quotes.find(q => q.id === quoteId);
        if (!winningQuote) return;
        let deliveryDate = null;
        if (winningQuote.deliveryTimeline && winningQuote.deliveryTimeline > 0) {
            deliveryDate = new Date();
            deliveryDate.setDate(deliveryDate.getDate() + winningQuote.deliveryTimeline);
        }
        quotes.forEach(q => {
            const quoteRef = doc(db, `orders/${order.id}/quotes`, q.id);
            batch.update(quoteRef, {
                isBestQuote: (q.id === quoteId),
                finalizedAt: q.id === quoteId ? serverTimestamp() : null
            });
        });
        batch.update(orderRef, {
            status: 'In Progress',
            deliveryDate: deliveryDate,
            deliveryTimeline: winningQuote.deliveryTimeline,
            quantity: winningQuote.quantity,
            supplierCity: winningQuote.supplierCity,
            price: winningQuote.price,
            advancePayment: winningQuote.advancePayment,
            paymentTerms: winningQuote.paymentTerms,
            finalPrice: winningQuote.price, // Set initial final price from winning quote
        });
        await batch.commit();
        if (winningQuote) {
            const notifRef = doc(collection(db, 'notifications'));
            try {
                await setDoc(notifRef, {
                    recipientUid: winningQuote.submittedBy.uid,
                    orderId: order.id,
                    orderTitle: order.orderTitle,
                    orderStatus: 'In Progress',
                    message: `Your quote for '${order.orderTitle}' was selected!`,
                    createdAt: serverTimestamp(),
                    isRead: false,
                });
            } catch (error) {
            }
        }
        onQuoteSelected();
    };
    const handleDeleteOrder = async () => {
        try {
            await deleteDoc(doc(db, 'orders', order.id));
            // Decrease RN counter when order is deleted
            await decreaseRNCounter();
            onOrderDeleted();
        } catch (error) {
            console.error('Error deleting order:', error);
        }
    };
    const handleUpdateQuote = async (quoteId, updates) => {
        const quoteRef = doc(db, `orders/${order.id}/quotes`, quoteId);
        try {
            // Get current quote to check if status needs to be updated
            const quoteDoc = await getDoc(quoteRef);
            const currentQuote = quoteDoc.data();

            // Determine new status based on updated fields
            const updatedQuoteData = { ...currentQuote, ...updates };
            const newStatus = determineQuoteStatus(updatedQuoteData);

            // Update quote with new status if it changed
            const updateData = {
                ...updates,
                status: newStatus,
                lastUpdated: serverTimestamp(),
                orderIndex: 0 // Move edited quote to top
            };

            await updateDoc(quoteRef, updateData);

            // Update orderIndex for all other quotes to shift them down
            const allQuotesQuery = query(collection(db, `orders/${order.id}/quotes`));
            const snapshot = await getDocs(allQuotesQuery);
            const batch = writeBatch(db);

            snapshot.docs.forEach(doc => {
                if (doc.id !== quoteId) {
                    const currentOrderIndex = doc.data().orderIndex || 999;
                    if (currentOrderIndex < 999) {
                        batch.update(doc.ref, { orderIndex: currentOrderIndex + 1 });
                    }
                }
            });

            await batch.commit();

            // Update order status if quote status changed
            if (currentQuote.status !== newStatus) {
                const newOrderStatus = quoteStatusToOrderStatus[newStatus];
                if (newOrderStatus) {
                    const orderRef = doc(db, 'orders', order.id);
                    await updateDoc(orderRef, { status: newOrderStatus });
                }
            }

            setQuoteToEdit(null);
        } catch (error) {
            console.error('Error updating quote:', error);
        }
    };

    // Handle quote status change and sync with order status
    const handleQuoteStatusChange = async (quoteId, newStatus) => {
        const quoteRef = doc(db, `orders/${order.id}/quotes`, quoteId);
        try {
            // Get the current quote to check its previous status
            const quoteDoc = await getDoc(quoteRef);
            const currentQuote = quoteDoc.data();

            // Update the quote status
            await updateDoc(quoteRef, {
                status: newStatus,
                lastUpdated: serverTimestamp()
            });

            // Update order status based on quote status change
            const orderRef = doc(db, 'orders', order.id);
            const newOrderStatus = quoteStatusToOrderStatus[newStatus];
            if (newOrderStatus && newOrderStatus !== order.status) {
                await updateDoc(orderRef, { status: newOrderStatus });
                // Update local state to reflect the change immediately
                setSelectedOrder(prev => ({ ...prev, status: newOrderStatus }));
            }
        } catch (error) {
            console.error('Failed to update quote status:', error);
        }
    };
    const handleDescriptionSave = async () => {
        const orderRef = doc(db, 'orders', order.id);
        try {
            // Regenerate AI summary when description changes
            let updates = { description };
            try {
                const result = await processWithGemini(description);
                updates.aiSummary = result.summary;
            } catch (error) {
                console.error('Failed to regenerate AI summary:', error);
                // If AI summary generation fails, keep the old one
            }

            await updateDoc(orderRef, updates);
            setIsEditingDesc(false);

            // Update local state to reflect the new AI summary
            if (updates.aiSummary) {
                setSelectedOrder(prev => ({ ...prev, aiSummary: updates.aiSummary }));
            }
        } catch (error) {
            console.error('Error updating description:', error);
        }
    };
    const handleSummarySave = async () => {
        const orderRef = doc(db, 'orders', order.id);
        try {
            await updateDoc(orderRef, { aiSummary });
            setIsEditingSummary(false);

            // Update local state to reflect the new AI summary
            setSelectedOrder(prev => ({ ...prev, aiSummary }));
        } catch (error) {
            console.error('Error saving AI summary:', error);
        }
    };

    // In OrderDetails, add a handler for creating a new quote
    const handleCreateQuote = async (updates) => {
        const quotesCol = collection(db, `orders/${order.id}/quotes`);
        try {
            // Validate required fields
            if (!order || !order.id) {
                throw new Error('Order ID is required');
            }
            if (!currentUser || !currentUser.uid) {
                throw new Error('User authentication required');
            }
            if (!updates || Object.keys(updates).length === 0) {
                throw new Error('Quote data is required');
            }

            // Determine status based on filled fields
            const quoteStatus = determineQuoteStatus(updates);

            const docRef = await addDoc(quotesCol, {
                ...updates,
                status: quoteStatus, // Set status based on filled fields
                submittedBy: { uid: currentUser.uid, displayName: currentUser.displayName },
                createdAt: serverTimestamp(),
                lastUpdated: serverTimestamp(), // Add timestamp for sorting
                isBestQuote: false,
            });

            // Update order status based on the new quote status
            const newOrderStatus = quoteStatusToOrderStatus[quoteStatus];
            if (newOrderStatus && order.status === 'Awaiting Requirements') {
                const orderRef = doc(db, 'orders', order.id);
                await updateDoc(orderRef, { status: newOrderStatus });
            }

            setQuoteToEdit(null);
        } catch (error) {
            console.error('Error creating quote:', error);
            console.error('Error details:', {
                code: error.code,
                message: error.message,
                stack: error.stack
            });
            alert('Failed to create quote. Please try again.');
        }
    };

    // In OrderDetails, add this handler before the return statement:
    const handleDeleteQuoteOrder = async (quoteId) => {
        if (!window.confirm('Are you sure you want to delete this quotation? This action cannot be undone.')) return;
        const quoteRef = doc(db, `orders/${order.id}/quotes`, quoteId);
        await deleteDoc(quoteRef);
    };

    return (
        <div>
            <button onClick={onBack} className="text-sm font-medium mb-4">&larr; Back to List</button>
            <div className="flex flex-col lg:flex-row gap-8">
                <div className="lg:w-2/3 space-y-8">
                    <div>
                        <div className="flex justify-between items-start mb-4 gap-4">
                            <div className="flex-grow min-w-0">
                                <h2 className="text-xl font-bold break-words flex items-center">
                                    {order.orderTitle}
                                    <span className="font-normal text-gray-600 ml-2">(Qty: {order.quantity !== null && order.quantity !== undefined && order.quantity !== '' ? order.quantity : 'N/A'})</span>
                                    {/* Delivery Remarks in Title - Only for In Progress orders */}
                                    {order.status === 'In Progress' && order.deliveryRemarks && (
                                        <span className="ml-3 px-2 py-1 text-xs font-medium bg-indigo-100 text-indigo-800 border border-indigo-200 rounded-full">
                                            📦 {order.deliveryRemarks.length > 30 ? `${order.deliveryRemarks.substring(0, 30)}...` : order.deliveryRemarks}
                                        </span>
                                    )}
                                </h2>
                            </div>
                            <div className="flex-shrink-0 flex items-center gap-2">
                                {/* Removed Proforma Invoice Upload Button at order-level */}
                                {isMaster && (
                                    <StatusChangeDropdown
                                        currentStatus={order.status}
                                        onStatusChange={(newStatus) => handleUpdateOrder({ status: newStatus })}
                                    />
                                )}
                                <Tooltip text="Edit Order">
                                    <button onClick={() => setShowEditModal(true)} className="p-2 rounded-full hover:bg-gray-200">
                                        <Edit size={16} />
                                    </button>
                                </Tooltip>
                                <Tooltip text="Email Supplier">
                                    <button onClick={() => setShowEmailModal(true)} className="p-2 rounded-full hover:bg-gray-200">
                                        <Mail size={16} />
                                    </button>
                                </Tooltip>
                            </div>
                        </div>


                        <div className="border-b border-gray-200 mb-4">
                            <nav className="-mb-px flex space-x-4">
                                <button className={tabClass('received')} onClick={() => setActiveTab('received')}>
                                    Received Requirement
                                </button>
                                <button className={tabClass('generated')} onClick={() => setActiveTab('generated')}>
                                    Requirement Summary
                                </button>
                                <button className={tabClass('emails')} onClick={() => setActiveTab('emails')}>
                                    Mails Sent
                                </button>
                            </nav>
                        </div>

                        {/* Sliding tab content animation (fade+slide, block layout) */}
                        <div className="relative min-h-[120px]" style={{ overflow: 'visible' }}>
                            <div className={`transition-all duration-300 ${activeTab === 'received' ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8 pointer-events-none h-0 overflow-hidden'}`}
                                aria-hidden={activeTab !== 'received'}>
                                <div className="p-4 bg-gray-50 border rounded-lg">
                                    {isEditingDesc ? (
                                        <div className="space-y-2">
                                            <textarea value={description} onChange={e => setDescription(e.target.value)} rows={8} className="w-full p-2 border-gray-300 rounded-md" />
                                            <div className="flex gap-2">
                                                <button onClick={handleDescriptionSave} className="bg-black text-white px-3 py-1 rounded-md text-sm">Save</button>
                                                <button onClick={() => setIsEditingDesc(false)} className="bg-gray-200 px-3 py-1 rounded-md text-sm">Cancel</button>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="flex justify-end mb-2">
                                                <Tooltip text="Edit Received Requirement">
                                                    <button onClick={() => setIsEditingDesc(true)} className="p-2 rounded-full hover:bg-gray-200">
                                                        <Edit size={16} />
                                                    </button>
                                                </Tooltip>
                                            </div>
                                            <TruncatedText text={description} className="text-gray-700" maxLines={4} />
                                        </>
                                    )}
                                </div>
                            </div>
                            <div className={`transition-all duration-300 ${activeTab === 'generated' ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8 pointer-events-none h-0 overflow-hidden'}`}
                                aria-hidden={activeTab !== 'generated'}>
                                <div className="p-4 bg-gray-50 border rounded-lg">
                                    {isEditingSummary ? (
                                        <div className="space-y-2">
                                            <textarea value={aiSummary} onChange={e => setAiSummary(e.target.value)} rows={8} className="w-full p-2 border border-gray-300 rounded-md" />
                                            <div className="flex gap-2">
                                                <button onClick={handleSummarySave} className="bg-black text-white px-3 py-1 rounded-md text-sm">Save</button>
                                                <button onClick={() => setIsEditingSummary(false)} className="bg-gray-200 px-3 py-1 rounded-md text-sm">Cancel</button>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="flex justify-end mb-2">
                                                <Tooltip text="Edit Summarized Requirement">
                                                    <button onClick={() => setIsEditingSummary(true)} className="p-2 rounded-full hover:bg-gray-200">
                                                        <Edit size={16} />
                                                    </button>
                                                </Tooltip>
                                            </div>
                                            {order.aiSummary ? <AISummary data={aiSummary} /> : <div className="p-4 text-center text-gray-500 border-2 border-dashed rounded-lg flex items-center justify-center gap-2"><Cpu size={16} /><p>Awaiting requirement generation...</p></div>}
                                        </>
                                    )}
                                </div>
                            </div>
                            <div className={`transition-all duration-300 ${activeTab === 'emails' ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8 pointer-events-none h-0 overflow-hidden'}`}
                                aria-hidden={activeTab !== 'emails'}>
                                <SentEmailsTimeline orderId={order.id} />
                            </div>
                        </div>
                    </div>
                    <div>
                        {finalizedQuote && (order.status === 'In Progress' || order.status === 'Completed') && (
                            <div className="mb-8">
                                <h2 className="font-semibold mb-4">Finalized Requirement</h2>
                                {isEditingFinalPrice ? (
                                    <div className="p-4 border border-blue-500 rounded-lg bg-blue-50">
                                        <InputField label="Final Price (INR)" name="finalPrice" type="number" value={finalPrice} onChange={(e) => setFinalPrice(e.target.value)} />
                                        <div className="flex gap-2 mt-2">
                                            <button onClick={() => handleUpdateOrder({ finalPrice: parseFloat(finalPrice) })} className="bg-black text-white px-3 py-1 rounded-md text-sm">Save</button>
                                            <button onClick={() => { setIsEditingFinalPrice(false); setFinalPrice(order.finalPrice || finalizedQuote.price); }} className="bg-gray-200 px-3 py-1 rounded-md text-sm">Cancel</button>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div className="flex justify-between items-center mb-2">
                                            <p className="text-lg font-bold">Final Price: ₹{order.finalPrice ? formatINR(order.finalPrice) : (finalizedQuote?.price.toFixed(2) || 'N/A')}</p>
                                            {isMaster && (
                                                <button onClick={() => setIsEditingFinalPrice(true)} className="p-2 rounded-full hover:bg-gray-200" title="Edit Final Price">
                                                    <Edit size={16} />
                                                </button>
                                            )}
                                        </div>
                                        <QuoteCard
                                            key={finalizedQuote.id}
                                            quote={finalizedQuote}
                                            orderId={order.id}
                                            currentUser={currentUser}
                                            isMaster={isMaster}
                                            onSelectBest={() => { }}
                                            onEdit={() => setQuoteToEdit(finalizedQuote)}
                                            disabled={order.status === 'Completed'}
                                            expanded={expandedQuoteId === finalizedQuote.id}
                                            onToggle={() => setExpandedQuoteId(expandedQuoteId === finalizedQuote.id ? null : finalizedQuote.id)}
                                            onDelete={isMaster ? () => handleDeleteQuoteOrder(finalizedQuote.id) : undefined}
                                            onQuoteStatusChange={handleQuoteStatusChange}
                                            categoryId={undefined}
                                            orderStatus={order.status}
                                            order={order}
                                            allUsers={allUsers}
                                            setSelectedOrder={setSelectedOrder}
                                        />

                                        {/* Revert Finalization Button */}
                                        {isMaster && order.status === 'In Progress' && (
                                            <div className="mt-3 flex justify-center">
                                                <button
                                                    onClick={async () => {
                                                        if (!window.confirm('Are you sure you want to revert this quote finalization? This will move the quote back to RFQ and set the order status to the previous status.')) {
                                                            return;
                                                        }

                                                        try {
                                                            // Remove finalizedAt timestamp from the quote
                                                            const quoteRef = doc(db, `orders/${order.id}/quotes`, finalizedQuote.id);
                                                            await updateDoc(quoteRef, {
                                                                finalizedAt: deleteField()
                                                            });

                                                            // Revert order status to previous status (based on quote status)
                                                            const previousStatus = finalizedQuote.status === QUOTE_STATUSES.PROFORMA_RECEIVED ? 'Proforma' :
                                                                finalizedQuote.status === QUOTE_STATUSES.UNDER_NEGOTIATION ? 'Negotiation' :
                                                                    'Awaiting Requirements';

                                                            const orderRef = doc(db, 'orders', order.id);
                                                            await updateDoc(orderRef, {
                                                                status: previousStatus,
                                                                finalizedQuoteId: deleteField(),
                                                                deliveryDate: deleteField(),
                                                                deliveryTimeline: deleteField(),
                                                                quantity: deleteField(),
                                                                supplierCity: deleteField(),
                                                                price: deleteField(),
                                                                advancePayment: deleteField(),
                                                                paymentTerms: deleteField(),
                                                                finalPrice: deleteField()
                                                            });

                                                            // Clear quotes cache to ensure fresh data
                                                            const { clearQuotesCache } = await import('./utils/quotesService');
                                                            clearQuotesCache();

                                                            // Update local state immediately for instant UI update
                                                            if (setSelectedOrder) {
                                                                setSelectedOrder(prev => ({
                                                                    ...prev,
                                                                    status: previousStatus,
                                                                    finalizedQuoteId: undefined,
                                                                    deliveryDate: undefined,
                                                                    deliveryTimeline: undefined,
                                                                    quantity: undefined,
                                                                    supplierCity: undefined,
                                                                    price: undefined,
                                                                    advancePayment: undefined,
                                                                    paymentTerms: undefined,
                                                                    finalPrice: undefined
                                                                }));
                                                            }

                                                            // Force a small delay to ensure state updates are processed
                                                            setTimeout(() => {
                                                                alert(`Quote finalization reverted! Order status set back to ${previousStatus}.`);
                                                            }, 100);
                                                        } catch (error) {
                                                            console.error('Error reverting quote finalization:', error);
                                                            alert('Failed to revert quote finalization. Please try again.');
                                                        }
                                                    }}
                                                    className="bg-orange-600 text-white px-4 py-2 rounded-md hover:bg-orange-700 transition-colors text-sm font-medium"
                                                    title="Revert quote finalization and move back to RFQ"
                                                >
                                                    Revert Finalization
                                                </button>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        )}

                        {/* Info note when quotes are finalized */}
                        {finalizedQuote && order.status === 'In Progress' && (
                            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                <p className="text-sm text-blue-800">
                                    Other quotes are currently disabled since a quote has been finalized.
                                    You can revert the finalization to make them interactive again.
                                </p>
                            </div>
                        )}

                        <h2 className="font-semibold mb-4">Request for Quotation ({otherQuotes.length})</h2>
                        <div className="space-y-4">
                            {otherQuotes.map((quote, index) => (
                                <QuoteCard
                                    key={quote.id}
                                    quote={quote}
                                    orderId={order.id}
                                    currentUser={currentUser}
                                    isMaster={isMaster}
                                    onSelectBest={() => { }}
                                    onEdit={() => setQuoteToEdit(quote)}
                                    disabled={order.status === 'Completed' || (finalizedQuote && order.status === 'In Progress')}
                                    expanded={expandedQuoteId === quote.id}
                                    onToggle={() => setExpandedQuoteId(expandedQuoteId === quote.id ? null : quote.id)}
                                    onDelete={isMaster ? () => handleDeleteQuoteOrder(quote.id) : undefined}
                                    draggable={!finalizedQuote || order.status !== 'In Progress'}
                                    isDragging={draggedIndex === index}
                                    onDragStart={() => handleDragStart(index)}
                                    onDragOver={() => handleDragOver(index)}
                                    onDrop={handleDrop}
                                    onQuoteStatusChange={handleQuoteStatusChange}
                                    categoryId={undefined}
                                    orderStatus={order.status}
                                    order={order}
                                    allUsers={allUsers}
                                    setSelectedOrder={setSelectedOrder}
                                />
                            ))}
                            {/* Show the empty + card at the end, only if order is not completed and no quote is finalized */}
                            {(order.status !== 'Completed') && (!finalizedQuote || order.status !== 'In Progress') && (
                                <QuoteCard
                                    key="empty-new-quote"
                                    quote={{}}
                                    orderId={order.id}
                                    currentUser={currentUser}
                                    isMaster={isMaster}
                                    onSelectBest={() => { }}
                                    onEdit={() => setQuoteToEdit({ isNew: true })}
                                    disabled={false}
                                    isEmptyCard={true}
                                    categoryId={undefined}
                                    orderStatus={order.status}
                                    order={order}
                                    allUsers={allUsers}
                                    setSelectedOrder={setSelectedOrder}
                                />
                            )}
                            {quotes.length === 0 && !finalizedQuote && <p className="text-gray-500">No requirements submitted yet.</p>}
                        </div>
                    </div>

                </div>
                <div className="lg:w-1/3">
                    <div className="lg:sticky top-24 space-y-4 max-h-[calc(100vh-7rem)] overflow-y-auto pr-2">
                        {/* Replace QuoteForm with DiscussionBox */}
                        <DiscussionBox orderId={order.id} order={order} currentUser={currentUser} allUsers={allUsers} />
                        {/* Delivery Remarks Section - Only for In Progress orders */}
                        {order.status === 'In Progress' && (
                            <div className="mt-8 p-6 border border-gray-200 bg-gray-50 rounded-lg">
                                <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                                    <span className="px-3 py-1 text-xs font-semibold rounded-full bg-indigo-100 text-indigo-800 border border-indigo-200">
                                        Awaiting Delivery
                                    </span>
                                    Delivery Remarks
                                </h3>
                                {isEditingDeliveryRemarks ? (
                                    <div className="space-y-3">
                                        <textarea
                                            value={deliveryRemarks}
                                            onChange={(e) => setDeliveryRemarks(e.target.value)}
                                            placeholder="Add delivery remarks (e.g., 'Delivery in transit', 'Expected delivery tomorrow')..."
                                            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
                                            rows={3}
                                        />
                                        <div className="flex gap-2">
                                            <button
                                                onClick={handleSaveDeliveryRemarks}
                                                className="px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 transition-colors"
                                            >
                                                Save Remarks
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setIsEditingDeliveryRemarks(false);
                                                    setDeliveryRemarks(order.deliveryRemarks || '');
                                                }}
                                                className="px-4 py-2 bg-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-300 transition-colors"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {deliveryRemarks ? (
                                            <div className="p-3 bg-white border border-gray-200 rounded-lg">
                                                <p className="text-gray-700 text-sm">{deliveryRemarks}</p>
                                            </div>
                                        ) : (
                                            <p className="text-gray-500 text-sm italic">No delivery remarks added yet</p>
                                        )}
                                        <button
                                            onClick={() => setIsEditingDeliveryRemarks(true)}
                                            className="w-full flex items-center justify-center gap-2 text-sm bg-indigo-600 text-white px-3 py-2 rounded-md hover:bg-indigo-700 transition-colors"
                                        >
                                            <Edit size={16} /> {deliveryRemarks ? 'Edit Remarks' : 'Add Remarks'}
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}

                        {isMaster && (
                            <div className="mt-8 p-6 border border-gray-200 bg-gray-50 rounded-lg">
                                <h3 className="font-semibold text-lg mb-4">Master Actions</h3>
                                <div className="space-y-4">
                                    {order.status === 'In Progress' && (
                                        <button
                                            onClick={() => onMarkAsCompleted(order.id)}
                                            className="w-full flex items-center justify-center gap-2 text-sm bg-green-600 text-white px-3 py-2 rounded-md hover:bg-green-700 transition-colors"
                                        >
                                            <CheckCircle size={16} /> Mark as Completed
                                        </button>
                                    )}
                                    <button
                                        onClick={() => setShowDeleteConfirm(true)}
                                        className="w-full flex items-center justify-center gap-2 text-sm bg-red-600 text-white px-3 py-2 rounded-md hover:bg-red-700 transition-colors"
                                    >
                                        <Trash2 size={16} /> Delete This Order
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {showDeleteConfirm && <ConfirmDeleteModal onCancel={() => setShowDeleteConfirm(false)} onConfirm={handleDeleteOrder} />}
            {showEmailModal && <EmailModal order={order} onClose={() => setShowEmailModal(false)} onEmailSent={() => { }} allVendors={allVendors} />}
            {showEditModal && <EditOrderModal order={order} onClose={() => setShowEditModal(false)} onSave={handleUpdateOrder} />}
            {quoteToEdit && (
                quoteToEdit.isNew ? (
                    <EditQuoteModal orderId={order.id} quote={{}} onClose={() => setQuoteToEdit(null)} onCreate={handleCreateQuote} allVendors={allVendors} isNew={true} />
                ) : (
                    <EditQuoteModal orderId={order.id} quote={quoteToEdit} onClose={() => setQuoteToEdit(null)} onSave={handleUpdateQuote} allVendors={allVendors} />
                )
            )}
        </div>
    );
};

const EditOrderModal = ({ order, onClose, onSave }) => {
    const [title, setTitle] = useState(order.orderTitle);
    const [description, setDescription] = useState(order.description || '');
    const [quantity, setQuantity] = useState(order.quantity || '');
    const [orderDate, setOrderDate] = useState(() => {
        if (order.createdAt?.toDate) {
            return order.createdAt.toDate().toISOString().split('T')[0];
        }
        return '';
    });
    const [remainingDays, setRemainingDays] = useState(() => {
        if (order.deliveryDate?.toDate) {
            const now = new Date();
            now.setHours(0, 0, 0, 0);
            const delivery = order.deliveryDate.toDate();
            delivery.setHours(0, 0, 0, 0);
            const diffTime = delivery - now;
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            return diffDays > 0 ? diffDays : 0;
        }
        return '';
    });

    const handleSave = useCallback(() => {
        const updates = {
            orderTitle: title,
            description: description.trim(),
            quantity: quantity ? parseInt(quantity, 10) : null,
        };
        if (orderDate) {
            updates.createdAt = new Date(orderDate);
        }
        if (order.status === 'In Progress' && remainingDays !== '' && !isNaN(parseInt(remainingDays))) {
            const newDeliveryDate = new Date();
            newDeliveryDate.setDate(newDeliveryDate.getDate() + parseInt(remainingDays, 10));
            updates.deliveryDate = newDeliveryDate;
        }
        onSave(updates);
    }, [title, description, quantity, orderDate, order.status, remainingDays, onSave]);

    React.useEffect(() => {
        const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [onClose]);

    const requiredFilled = title.trim() && description.trim() && quantity;
    React.useEffect(() => {
        const handleEscEnter = (e) => {
            if (e.key === 'Escape') onClose();
            if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
                if (requiredFilled) handleSave();
            }
        };
        window.addEventListener('keydown', handleEscEnter);
        return () => window.removeEventListener('keydown', handleEscEnter);
    }, [title, quantity, onClose, requiredFilled, handleSave]);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-lg m-4 mx-auto">
                <h2 className="text-xl font-bold mb-4">Edit Order</h2>
                <div className="space-y-4">
                    <InputField label="Order Title" name="title" value={title} onChange={(e) => setTitle(e.target.value)} />
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={4}
                            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                            placeholder="Enter the order description"
                        />
                    </div>
                    <InputField label="Quantity" name="quantity" type="number" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
                    <InputField label="Order Date" name="orderDate" type="date" value={orderDate} onChange={(e) => setOrderDate(e.target.value)} />
                    {order.status === 'In Progress' && (
                        <InputField label="Remaining Delivery Days" name="remainingDays" type="number" value={remainingDays} onChange={(e) => setRemainingDays(e.target.value)} />
                    )}
                </div>
                <div className="mt-6 flex justify-end gap-4">
                    <button onClick={onClose} className="bg-gray-200 px-4 py-2 rounded-md hover:bg-gray-300">Cancel</button>
                    <button onClick={handleSave} className="bg-black text-white px-4 py-2 rounded-md hover:bg-gray-800">Save Changes</button>
                </div>
            </div>
        </div>
    );
};

const EditQuoteModal = ({ orderId, quote, onClose, onSave, onCreate, allVendors, isNew }) => {
    // Find vendor data if vendorId exists
    const vendor = quote.vendorId && allVendors ? allVendors.find(v => v.id === quote.vendorId) : null;

    const [price, setPrice] = useState(quote.price || '');
    const [quantity, setQuantity] = useState(quote.quantity || '');
    const [supplier, setSupplier] = useState(quote.supplier || vendor?.name || '');
    const [supplierCity, setSupplierCity] = useState(quote.supplierCity || vendor?.city || '');
    const [deliveryTimeline, setDeliveryTimeline] = useState(quote.deliveryTimeline || '');
    const [notes, setNotes] = useState(quote.notes || '');
    const [priceInputType, setPriceInputType] = useState('total');
    const [pricePerQty, setPricePerQty] = useState('');
    const [advancePayment, setAdvancePayment] = useState(quote.advancePayment || '');
    const [paymentTerms, setPaymentTerms] = useState(quote.paymentTerms || 'Before Delivery');
    const [suggestions, setSuggestions] = useState([]);
    const [email, setEmail] = useState(quote.email || vendor?.email || ''); // Optionally allow email

    // Suggestion logic for supplier name
    const handleSupplierChange = (e) => {
        const value = e.target.value;
        setSupplier(value);
        if (value && Array.isArray(allVendors)) {
            const filtered = allVendors.filter(v => v && v.name && typeof v.name === 'string' && v.name.toLowerCase().includes(value.toLowerCase()));
            setSuggestions(filtered);
        } else {
            setSuggestions([]);
        }
    };
    const handleSuggestionClick = (vendor) => {
        setSupplier(vendor.name);
        if (vendor.city) {
            setSupplierCity(vendor.city);
        }
        if (vendor.email) {
            setEmail(vendor.email);
        }
        setSuggestions([]);
    };

    useEffect(() => {
        if (priceInputType === 'perQty') {
            const qtyNum = parseFloat(quantity);
            const perQtyPriceNum = parseFloat(pricePerQty);
            if (!isNaN(qtyNum) && !isNaN(perQtyPriceNum) && qtyNum > 0) {
                setPrice((qtyNum * perQtyPriceNum).toFixed(2));
            } else {
                setPrice('');
            }
        }
    }, [quantity, pricePerQty, priceInputType]);

    // --- MODIFIED handleSave to add vendor if not exists ---
    const handleSave = useCallback(async () => {
        let finalPrice = parseFloat(price);
        let selectedVendor = allVendors && allVendors.find(v => v && v.name && v.name.toLowerCase() === supplier.toLowerCase().trim());
        let vendorId = selectedVendor ? selectedVendor.id : undefined;
        // Add vendor if not exists
        if (!selectedVendor) {
            const vendorDoc = await addDoc(collection(db, 'vendors'), { name: supplier.trim(), city: supplierCity.trim(), email: email.trim() });
            vendorId = vendorDoc.id;
        }
        const updates = {
            price: finalPrice,
            quantity: parseInt(quantity),
            supplier, // keep for legacy
            supplierCity,
            deliveryTimeline: deliveryTimeline ? parseInt(deliveryTimeline) : null,
            notes,
            advancePayment: advancePayment ? parseInt(advancePayment) : null,
            paymentTerms,
            vendorId,
        };
        if (isNew && onCreate) {
            onCreate(updates);
        } else if (onSave) {
            onSave(quote.id, updates);
        }
    }, [price, quantity, supplier, supplierCity, deliveryTimeline, notes, advancePayment, paymentTerms, allVendors, email, isNew, onCreate, onSave, quote.id]);

    React.useEffect(() => {
        const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [onClose]);

    const requiredFilled = supplier.trim() && price && quantity && supplierCity && advancePayment;
    React.useEffect(() => {
        const handleEscEnter = (e) => {
            if (e.key === 'Escape') onClose();
            if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
                if (requiredFilled) handleSave();
            }
        };
        window.addEventListener('keydown', handleEscEnter);
        return () => window.removeEventListener('keydown', handleEscEnter);
    }, [supplier, price, quantity, supplierCity, advancePayment, onClose, requiredFilled, handleSave]);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl m-4 mx-auto overflow-y-auto max-h-[90vh] relative">
                {/* Header Section */}
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 rounded-t-xl border-b border-gray-200">
                    <div className="flex items-center justify-between">
                        <h2 className="text-2xl font-bold text-gray-900">{isNew ? 'Create Quotation' : 'Edit Quotation'}</h2>
                        <button
                            onClick={onClose}
                            className="p-2 rounded-full bg-white hover:bg-gray-100 text-gray-600 transition-colors shadow-sm"
                            title="Cancel"
                        >
                            <X size={20} />
                        </button>
                    </div>
                    <p className="text-gray-600 mt-1">Fill in the quotation details below</p>
                </div>

                {/* Form Content */}
                <div className="p-6">
                    <form className="space-y-6">
                        {/* Supplier Information Section */}
                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                <Users size={20} />
                                Supplier Information
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="relative">
                                    <InputField
                                        label="Supplier Name"
                                        name="supplier"
                                        value={supplier}
                                        onChange={handleSupplierChange}
                                        required
                                    />
                                    {suggestions.length > 0 && (
                                        <ul className="absolute z-10 w-full bg-white border border-gray-300 rounded-lg mt-1 max-h-40 overflow-y-auto shadow-lg">
                                            {suggestions.map(v => (
                                                <li
                                                    key={v.id}
                                                    onClick={() => handleSuggestionClick(v)}
                                                    className="p-3 cursor-pointer hover:bg-gray-100 border-b border-gray-100 last:border-b-0 transition-colors"
                                                >
                                                    <div className="font-medium text-gray-900">{v.name}</div>
                                                    <div className="text-sm text-gray-600">{v.city}</div>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                                <InputField
                                    label="Supplier Email"
                                    name="email"
                                    type="email"
                                    value={email}
                                    onChange={e => setEmail(e.target.value)}
                                />
                                <InputField
                                    label="Supplier City"
                                    name="supplierCity"
                                    type="text"
                                    value={supplierCity}
                                    onChange={e => setSupplierCity(e.target.value)}
                                    required
                                />
                            </div>
                        </div>

                        {/* Pricing Section */}
                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                <DollarSign size={20} />
                                Pricing & Payment
                            </h3>
                            <div className="space-y-4">
                                {/* Price Type Selection */}
                                <div className="flex items-center gap-6 p-3 bg-white rounded-lg border border-gray-200">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="radio"
                                            name="priceTypeEdit"
                                            value="total"
                                            checked={priceInputType === 'total'}
                                            onChange={() => setPriceInputType('total')}
                                            className="w-4 h-4 text-gray-600 focus:ring-gray-500"
                                        />
                                        <span className="font-medium text-gray-700">Total Price</span>
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="radio"
                                            name="priceTypeEdit"
                                            value="perQty"
                                            checked={priceInputType === 'perQty'}
                                            onChange={() => setPriceInputType('perQty')}
                                            className="w-4 h-4 text-gray-600 focus:ring-gray-500"
                                        />
                                        <span className="font-medium text-gray-700">Price per Quantity</span>
                                    </label>
                                </div>

                                {/* Price Input */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        {priceInputType === 'perQty' ? (
                                            <InputField
                                                label="Price per Quantity"
                                                type="number"
                                                value={pricePerQty}
                                                onChange={(e) => setPricePerQty(e.target.value)}
                                                icon='₹'
                                                placeholder="0.00"
                                            />
                                        ) : (
                                            <InputField
                                                label="Total Price"
                                                name="price"
                                                type="number"
                                                value={price}
                                                onChange={(e) => setPrice(e.target.value)}
                                                icon='₹'
                                                required
                                                placeholder="0.00"
                                            />
                                        )}
                                    </div>
                                    <InputField
                                        label="Quantity"
                                        name="quantity"
                                        type="number"
                                        value={quantity}
                                        onChange={(e) => setQuantity(e.target.value)}
                                        required
                                        placeholder="0"
                                    />
                                </div>

                                {/* Calculated Total Display */}
                                {priceInputType === 'perQty' && price && (
                                    <div className="bg-white p-3 rounded-lg border border-gray-200">
                                        <p className="text-sm font-medium text-gray-700">
                                            Calculated Total: <span className="text-lg font-bold">₹{formatINR(price)}</span>
                                        </p>
                                    </div>
                                )}

                                {/* Payment Terms */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <InputField
                                        label="Advance Payment (%)"
                                        name="advancePayment"
                                        type="number"
                                        value={advancePayment}
                                        onChange={(e) => setAdvancePayment(e.target.value)}
                                        icon={'%'}
                                        required
                                        disabled={paymentTerms === 'Full Advance'}
                                        placeholder="0"
                                    />
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Payment Terms</label>
                                        <select
                                            value={paymentTerms}
                                            onChange={(e) => setPaymentTerms(e.target.value)}
                                            className="w-full h-10 px-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500 transition-colors"
                                        >
                                            <option>Before Delivery</option>
                                            <option>After Delivery</option>
                                            <option>Full Advance</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Delivery & Notes Section */}
                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                <Package size={20} />
                                Delivery & Additional Information
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <InputField
                                    label="Delivery Timeline (days)"
                                    name="timeline"
                                    type="number"
                                    value={deliveryTimeline}
                                    onChange={(e) => setDeliveryTimeline(e.target.value)}
                                    placeholder="0"
                                />
                            </div>
                            <div className="mt-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Notes & Additional Details</label>
                                <textarea
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    rows="4"
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500 transition-colors resize-none"
                                    placeholder="Enter any additional notes, specifications, or requirements..."
                                />
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex justify-end gap-4 pt-4 border-t border-gray-200">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleSave}
                                className="px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors font-medium shadow-lg"
                            >
                                {isNew ? 'Create Quotation' : 'Save Changes'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    )
};


const EmailModal = ({ order, onClose, onEmailSent, allVendors }) => {
    const [selectedVendors, setSelectedVendors] = useState([]); // Array of vendor objects
    const [vendorSearch, setVendorSearch] = useState('');
    const [vendorSuggestions, setVendorSuggestions] = useState([]);
    const [showVendorSuggestions, setShowVendorSuggestions] = useState(false);
    const [subject, setSubject] = useState(`Requirement for: ${order.orderTitle}`);
    const [body, setBody] = useState('');
    const [cc, setCc] = useState('');
    const [bcc, setBcc] = useState('');
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState('');
    const [successCount, setSuccessCount] = useState(0);
    const [failCount, setFailCount] = useState(0);
    const vendorSearchRef = useRef(null);

    useEffect(() => {
        const qtyLine = order.quantity ? `\nQty: ${order.quantity}` : '';
        const mainText = order.aiSummary ? order.aiSummary : (order.description ? order.description : '');
        const emailBody = `Dear Sir/Madam,\n\nPlease provide a quote for the following requirement:\n\n${mainText}${qtyLine}\n\nRegards,`;
        setBody(emailBody);
    }, [order]);

    // Vendor search and multi-select
    useEffect(() => {
        if (vendorSearch && Array.isArray(allVendors)) {
            const filtered = allVendors.filter(v =>
                v && v.name && typeof v.name === 'string' &&
                v.name.toLowerCase().includes(vendorSearch.toLowerCase())
            );
            setVendorSuggestions(filtered);
            setShowVendorSuggestions(filtered.length > 0);
        } else {
            setVendorSuggestions([]);
            setShowVendorSuggestions(false);
        }
    }, [vendorSearch, allVendors]);

    // Add/remove vendor from selection
    const handleVendorToggle = (vendor) => {
        setSelectedVendors(prev => {
            if (prev.some(v => v.id === vendor.id)) {
                return prev.filter(v => v.id !== vendor.id);
            } else {
                return [...prev, vendor];
            }
        });
        setShowVendorSuggestions(false);
    };

    // Remove vendor from selected list
    const handleRemoveVendor = (vendorId) => {
        setSelectedVendors(prev => prev.filter(v => v.id !== vendorId));
    };

    // Send email to each selected vendor individually
    const handleSendEmail = useCallback(async () => {
        setSending(true);
        setSent(false);
        setError('');
        setSuccessCount(0);
        setFailCount(0);
        const provider = new OAuthProvider('microsoft.com');
        provider.addScope('mail.send');
        try {
            const result = await signInWithPopup(auth, provider);
            const credential = OAuthProvider.credentialFromResult(result);
            const token = credential.accessToken;
            let successes = 0;
            let fails = 0;
            for (const vendor of selectedVendors) {
                const toEmail = vendor.email;
                if (!toEmail) continue;
                // Parse CC/BCC (comma-separated)
                const ccList = cc.split(',').map(e => e.trim()).filter(Boolean);
                const bccList = bcc.split(',').map(e => e.trim()).filter(Boolean);
                const message = {
                    message: {
                        subject: subject,
                        body: {
                            contentType: "Text",
                            content: body,
                        },
                        toRecipients: [
                            {
                                emailAddress: {
                                    address: toEmail,
                                },
                            },
                        ],
                        ccRecipients: ccList.map(addr => ({ emailAddress: { address: addr } })),
                        bccRecipients: bccList.map(addr => ({ emailAddress: { address: addr } })),
                    },
                    saveToSentItems: "true",
                };
                const graphResponse = await fetch("https://graph.microsoft.com/v1.0/me/sendMail", {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(message),
                });
                if (graphResponse.ok) {
                    // Save email details to Firestore
                    try {
                        const emailData = {
                            orderId: order.id,
                            recipient: toEmail,
                            cc,
                            bcc,
                            subject,
                            body,
                            sentBy: {
                                uid: auth.currentUser?.uid,
                                displayName: auth.currentUser?.displayName || 'Unknown User',
                                email: auth.currentUser?.email
                            },
                            sentAt: serverTimestamp(),
                            status: 'sent'
                        };
                        await addDoc(collection(db, 'sentEmails'), emailData);
                        if (onEmailSent) onEmailSent(emailData);
                        // --- Add placeholder quote for this vendor ---
                        const quotesCol = collection(db, `orders/${order.id}/quotes`);
                        await addDoc(quotesCol, {
                            supplier: vendor.name,
                            status: QUOTE_STATUSES.AWAITING_INFORMATION,
                            awaitingResponse: true,
                            createdAt: serverTimestamp(),
                            submittedBy: { uid: auth.currentUser?.uid, displayName: auth.currentUser?.displayName || 'Unknown User' },
                            // All other fields intentionally left empty
                        });
                    } catch (firestoreError) {
                        // Don't fail the email send if Firestore save fails
                    }
                    successes++;
                } else {
                    fails++;
                }
            }
            setSuccessCount(successes);
            setFailCount(fails);
            setSent(true);
            setSending(false);
        } catch (error) {
            setError(error.message || 'Failed to send email.');
            setSending(false);
        }
    }, [subject, body, cc, bcc, selectedVendors, order.id, onEmailSent]);

    // Keyboard shortcuts (unchanged)
    React.useEffect(() => {
        const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [onClose]);
    const requiredFilled = selectedVendors.length > 0 && subject.trim() && body.trim();
    React.useEffect(() => {
        const handleEscEnter = (e) => {
            if (e.key === 'Escape') onClose();
            if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
                if (requiredFilled && !sending && !sent) handleSendEmail();
            }
        };
        window.addEventListener('keydown', handleEscEnter);
        return () => window.removeEventListener('keydown', handleEscEnter);
    }, [selectedVendors, subject, body, sending, sent, onClose, requiredFilled, handleSendEmail]);

    // Close vendor suggestions when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (vendorSearchRef.current && !vendorSearchRef.current.contains(event.target)) {
                setShowVendorSuggestions(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl m-4 max-h-[90vh] overflow-y-auto">
                <h2 className="text-xl font-bold mb-4">Send Email</h2>
                <div className="space-y-4">
                    {/* Vendor Multi-Select */}
                    <div className="vendor-search-container" ref={vendorSearchRef}>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Select Vendors</label>
                        <input
                            type="text"
                            value={vendorSearch}
                            onChange={e => setVendorSearch(e.target.value)}
                            placeholder="Search vendor name..."
                            className="w-full p-2 border border-gray-300 rounded-md"
                        />
                        {showVendorSuggestions && (
                            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                                {vendorSuggestions.map((vendor) => (
                                    <div key={vendor.id} className="flex items-center px-3 py-2 hover:bg-gray-100 border-b border-gray-100 last:border-b-0">
                                        <input
                                            type="checkbox"
                                            checked={selectedVendors.some(v => v.id === vendor.id)}
                                            onChange={() => handleVendorToggle(vendor)}
                                            className="mr-2"
                                        />
                                        <div className="flex-1 cursor-pointer" onClick={() => handleVendorToggle(vendor)}>
                                            <span className="font-medium">{vendor.name}</span>
                                            {vendor.email && <span className="text-xs text-gray-600 ml-2">{vendor.email}</span>}
                                            {vendor.city && <span className="text-xs text-gray-500 ml-2">{vendor.city}</span>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                        {/* Show selected vendors as tags */}
                        <div className="flex flex-wrap gap-2 mt-2">
                            {selectedVendors.map(vendor => (
                                <span key={vendor.id} className="bg-gray-200 px-2 py-1 rounded-full text-xs flex items-center">
                                    {vendor.name}
                                    <button type="button" className="ml-1 text-gray-500 hover:text-red-600" onClick={() => handleRemoveVendor(vendor.id)}>&times;</button>
                                </span>
                            ))}
                        </div>
                    </div>
                    {/* To (disabled, shows selected emails) */}
                    <InputField
                        label="To"
                        name="recipient"
                        value={selectedVendors.map(v => v.email).filter(Boolean).join(', ')}
                        onChange={() => { }}
                        placeholder="Select vendors above"
                        disabled
                    />
                    {/* CC and BCC fields */}
                    <InputField
                        label="CC"
                        name="cc"
                        value={cc}
                        onChange={e => setCc(e.target.value)}
                        placeholder="Comma-separated emails"
                    />
                    <InputField
                        label="BCC"
                        name="bcc"
                        value={bcc}
                        onChange={e => setBcc(e.target.value)}
                        placeholder="Comma-separated emails"
                    />
                    <InputField label="Subject" name="subject" value={subject} onChange={e => setSubject(e.target.value)} />
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Body</label>
                        <textarea value={body} onChange={e => setBody(e.target.value)} rows="10" className="w-full p-2 border border-gray-300 rounded-md"></textarea>
                    </div>
                </div>
                <div className="mt-6 flex justify-end gap-4">
                    <button onClick={onClose} className="bg-gray-200 px-4 py-2 rounded-md hover:bg-gray-300">Cancel</button>
                    <button onClick={handleSendEmail} className="bg-black text-white px-4 py-2 rounded-md hover:bg-gray-800" disabled={sending || sent || !requiredFilled}>Send</button>
                </div>
                {/* Status message */}
                <div className="mt-4 min-h-[24px]">
                    {sending && <span className="text-blue-600 font-medium">Sending email...</span>}
                    {sent && !sending && (
                        <span className="text-green-600 font-medium">
                            {successCount > 0 && `Sent to ${successCount} vendor${successCount > 1 ? 's' : ''}. `}
                            {failCount > 0 && `${failCount} failed.`}
                        </span>
                    )}
                    {error && <span className="text-red-600 font-medium">{error}</span>}
                </div>
            </div>
        </div>
    );
};


const TruncatedSummary = ({ data, maxLines = 4 }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [showCopiedMessage, setShowCopiedMessage] = useState(false);
    const summaryLines = data.split('\n').filter(line => line.trim() !== '' && line.trim() !== '*');
    const shouldShowButton = summaryLines.length > maxLines;
    const displayedLines = isExpanded ? summaryLines : summaryLines.slice(0, maxLines);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(data);
            setShowCopiedMessage(true);
            setTimeout(() => setShowCopiedMessage(false), 2000);
        } catch (err) {
            // Fallback for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = data;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            setShowCopiedMessage(true);
            setTimeout(() => setShowCopiedMessage(false), 2000);
        }
    };

    return (
        <div className="p-4 bg-white border rounded-lg">
            <div className="flex items-start justify-between mb-3">
                <div className="flex-grow">
                    <ul className="space-y-2 text-sm text-gray-800 list-disc list-inside">
                        {displayedLines.map((line, index) => {
                            const cleanLine = line.trim().startsWith('*') ? line.trim().substring(1).trim() : line;
                            return <li key={index}>{cleanLine}</li>
                        })}
                    </ul>
                </div>
                <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                    <Tooltip text="Copy summary">
                        <button
                            onClick={handleCopy}
                            className="p-1.5 rounded-md hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-700"
                        >
                            {showCopiedMessage ? (
                                <CheckCircle size={14} className="text-green-600" />
                            ) : (
                                <Copy size={14} />
                            )}
                        </button>
                    </Tooltip>
                </div>
            </div>
            {shouldShowButton && (
                <button
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="text-black hover:text-gray-700 text-sm font-medium mt-2 transition-colors"
                >
                    {isExpanded ? 'View Less' : 'View More'}
                </button>
            )}
        </div>
    );
};

const AISummary = ({ data }) => {
    return <TruncatedSummary data={data} maxLines={4} />;
};

// Helper function to format Firestore timestamps
const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Unknown time';

    try {
        // Handle Firestore Timestamp objects
        if (timestamp.toDate && typeof timestamp.toDate === 'function') {
            return timestamp.toDate().toLocaleString();
        }

        // Handle regular Date objects or timestamp numbers
        if (timestamp instanceof Date) {
            return timestamp.toLocaleString();
        }

        // Handle timestamp as number (milliseconds)
        if (typeof timestamp === 'number') {
            return new Date(timestamp).toLocaleString();
        }

        // Handle timestamp as string
        if (typeof timestamp === 'string') {
            return new Date(timestamp).toLocaleString();
        }

        return 'Unknown time';
    } catch (error) {
    }
};

const SentEmailsTimeline = ({ orderId }) => {
    const [sentEmails, setSentEmails] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deletingEmailId, setDeletingEmailId] = useState(null);

    // Handle email deletion
    const handleDeleteEmail = async (emailId) => {
        if (!window.confirm('Are you sure you want to delete this email from the sent emails list?')) {
            return;
        }

        setDeletingEmailId(emailId);
        try {
            await deleteDoc(doc(db, 'sentEmails', emailId));
        } catch (error) {
        } finally {
            setDeletingEmailId(null);
        }
    };

    useEffect(() => {
        if (!orderId) return;

        const emailsQuery = query(
            collection(db, 'sentEmails'),
            where('orderId', '==', orderId),
            orderBy('sentAt', 'desc')
        );

        const unsubscribe = onSnapshot(emailsQuery, (snapshot) => {
            const emails = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setSentEmails(emails);
            setLoading(false);
        });

        return unsubscribe;
    }, [orderId]);

    if (loading) {
        return (
            <div className="p-4 bg-gray-50 border rounded-lg">
                <div className="flex items-center justify-center gap-2 text-gray-500">
                    <RefreshCw size={16} className="animate-spin" />
                    <span>Loading emails...</span>
                </div>
            </div>
        );
    }

    if (sentEmails.length === 0) {
        return (
            <div className="p-4 bg-gray-50 border rounded-lg">
                <div className="text-center text-gray-500">
                    <Mail size={24} className="mx-auto mb-2 text-gray-400" />
                    <p>No emails sent yet</p>
                </div>
            </div>
        );
    }

    return (
        <div className="p-4 bg-gray-50 border rounded-lg">
            <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                <Mail size={20} />
                Mails Sent ({sentEmails.length})
            </h3>
            <div className="space-y-4">
                {sentEmails.map((email) => (
                    <div key={email.id} className="bg-white p-4 rounded-lg border border-gray-200">
                        <div className="flex justify-between items-start mb-2">
                            <div className="flex-1">
                                <h4 className="font-medium text-gray-900">{email.subject}</h4>
                                <p className="text-sm text-gray-600">To: {email.recipient}</p>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="text-right text-xs text-gray-500">
                                    {formatTimestamp(email.sentAt)}
                                </div>
                                <button
                                    onClick={() => handleDeleteEmail(email.id)}
                                    disabled={deletingEmailId === email.id}
                                    className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                                    title="Delete email"
                                >
                                    {deletingEmailId === email.id ? (
                                        <RefreshCw size={14} className="animate-spin" />
                                    ) : (
                                        <Trash2 size={14} />
                                    )}
                                </button>
                            </div>
                        </div>
                        <div className="text-sm text-gray-700 mb-2">
                            <span className="font-medium">Sent by:</span> {email.sentBy?.displayName || 'Unknown User'}
                        </div>
                        <details className="text-sm">
                            <summary className="cursor-pointer text-blue-600 hover:text-blue-800 font-medium">
                                View email content
                            </summary>
                            <div className="mt-2 p-3 bg-gray-50 rounded border whitespace-pre-wrap text-gray-800">
                                {email.body}
                            </div>
                        </details>
                    </div>
                ))}
            </div>
        </div>
    );
};





const QuoteCard = ({ quote, orderId, currentUser, isMaster, onSelectBest, disabled, onEdit, isEmptyCard,
    expanded, onToggle, isDragging, draggable, onDragStart, onDragOver, onDrop, onDelete, allVendors, onQuoteStatusChange, categoryId, orderStatus, order, allUsers, setSelectedOrder }) => {
    // Move all hooks to the top
    const [uploadingPI, setUploadingPI] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const [uploadSuccess, setUploadSuccess] = useState(false);
    const [deletingPI, setDeletingPI] = useState(false);
    const [showDiscussion, setShowDiscussion] = useState(false);
    const isEmpty = isEmptyCard || !quote || Object.keys(quote).length === 0;
    const priceInr = useMemo(() => {
        return quote.price ? quote.price.toFixed(2) : '0.00';
    }, [quote.price]);
    const pricePerQtyInr = useMemo(() => {
        if (quote && quote.price && quote.quantity > 0) {
            return (quote.price / quote.quantity).toFixed(2);
        }
        return 'N/A';
    }, [quote]);
    const paymentTermsText = quote.paymentTerms === 'Full Advance' ? 'Full Advance' : (quote.paymentTerms ? `Bal. ${quote.paymentTerms}` : 'Before Delivery');

    // Helper function to get status color based on status (for card background only)
    const getStatusColor = () => {
        if (!quote.status) return 'bg-gray-100';
        // Extract only background color from the full color scheme
        const fullColor = quoteStatusColors[quote.status] || 'bg-gray-100';
        return fullColor.split(' ')[0]; // Get only the background color (first part)
    };

    // Determine if this quote should be disabled
    const shouldBeDisabled = disabled;

    // --- PI upload state and handlers ---
    const handleProformaUpload = async (e) => {
        const file = e.target.files[0];
        if (!file || file.type !== "application/pdf") {
            setUploadError("Please select a PDF file.");
            return;
        }
        setUploadingPI(true);
        setUploadError(null);
        setUploadSuccess(false);
        try {
            // Use the original PDF filename instead of hardcoded 'proforma.pdf'
            const originalFilename = file.name;
            const piRef = storageRef(storage, categoryId
                ? `orderCategories/${categoryId}/orders/${orderId}/quotes/${quote.id}/${originalFilename}`
                : `orders/${orderId}/quotes/${quote.id}/${originalFilename}`
            );
            await uploadBytes(piRef, file);
            const url = await getDownloadURL(piRef);

            // Update quote with PI URL and set status to Proforma Received
            const quoteRef = categoryId
                ? doc(db, `orderCategories/${categoryId}/orders/${orderId}/quotes`, quote.id)
                : doc(db, `orders/${orderId}/quotes`, quote.id);

            await updateDoc(quoteRef, {
                proformaInvoiceUrl: url,
                proformaInvoiceFilename: originalFilename,
                status: QUOTE_STATUSES.PROFORMA_RECEIVED
            });

            // Update order status to Proforma
            const orderRef = categoryId
                ? doc(db, `orderCategories/${categoryId}/orders`, orderId)
                : doc(db, 'orders', orderId);

            await updateDoc(orderRef, { status: 'Proforma' });

            // Clear quotes cache to ensure fresh data
            const { clearQuotesCache } = await import('./utils/quotesService');
            clearQuotesCache();

            setUploadSuccess(true);
            setTimeout(() => setUploadSuccess(false), 3000);
        } catch (err) {
            setUploadError("Failed to upload PI: " + err.message);
        } finally {
            setUploadingPI(false);
        }
    };

    const handleDeletePI = async () => {
        if (!window.confirm('Are you sure you want to delete the Proforma Invoice? This action cannot be undone.')) return;
        setDeletingPI(true);
        try {
            // Use the stored filename or fallback to 'proforma.pdf' for backward compatibility
            const filename = quote.proformaInvoiceFilename || 'proforma.pdf';
            const piRef = storageRef(storage, categoryId
                ? `orderCategories/${categoryId}/orders/${orderId}/quotes/${quote.id}/${filename}`
                : `orders/${orderId}/quotes/${quote.id}/${filename}`
            );
            await deleteObject(piRef);

            const quoteRef = categoryId
                ? doc(db, `orderCategories/${categoryId}/orders/${orderId}/quotes`, quote.id)
                : doc(db, `orders/${orderId}/quotes`, quote.id);

            await updateDoc(quoteRef, {
                proformaInvoiceUrl: null,
                proformaInvoiceFilename: null
            });

            // Clear quotes cache to ensure fresh data
            const { clearQuotesCache } = await import('./utils/quotesService');
            clearQuotesCache();
        } catch (err) {
            setUploadError("Failed to delete PI: " + err.message);
        } finally {
            setDeletingPI(false);
        }
    };

    // Confirm handler for auto-fetched quotes
    const handleConfirmQuote = async () => {
        const quoteRef = categoryId
            ? doc(db, `orderCategories/${categoryId}/orders/${orderId}/quotes`, quote.id)
            : doc(db, `orders/${orderId}/quotes`, quote.id);

        await updateDoc(quoteRef, {
            needsConfirmation: false,
            fetchedAutomatically: false,
        });
    };

    // Delete handler for masters/automaster
    const handleDeleteQuote = async () => {
        if (!window.confirm('Are you sure you want to delete this quotation? This action cannot be undone.')) return;
        const quoteRef = categoryId
            ? doc(db, `orderCategories/${categoryId}/orders/${orderId}/quotes`, quote.id)
            : doc(db, `orders/${orderId}/quotes`, quote.id);

        await deleteDoc(quoteRef);
    };

    if (isEmpty) {
        return (
            <div
                className={`relative border rounded-md bg-white flex flex-col p-3 transition-colors duration-200 group ${shouldBeDisabled
                    ? 'opacity-50 cursor-not-allowed'
                    : 'cursor-pointer hover:bg-gray-50'
                    }`}
                onClick={shouldBeDisabled ? undefined : onEdit}
                title={shouldBeDisabled ? "Cannot add quotation while another quote has 'Advance Paid' status" : "Add Quotation"}
            >
                {/* Subtle + icon that appears on hover */}
                {!shouldBeDisabled && (
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                        <div className="bg-gray-200 bg-opacity-90 text-gray-600 rounded-full p-3">
                            <Plus size={24} />
                        </div>
                    </div>
                )}
                <div className="grid grid-cols-5 gap-2 items-start text-center">
                    {/* Supplier (leftmost) */}
                    <div className="col-span-1 flex flex-col items-center justify-center">
                        <p className="font-bold text-lg text-gray-300">+</p>
                        <p className="text-xs text-gray-400 mt-1">Supplier</p>
                    </div>
                    {/* Advance % */}
                    <div className="col-span-1 flex flex-col items-center justify-center border-l border-gray-200">
                        <p className="font-bold text-lg text-gray-300">+</p>
                        <p className="text-xs text-gray-400 mt-1">Bal. Before Delivery</p>
                    </div>
                    {/* Timeline */}
                    <div className="col-span-1 flex flex-col items-center justify-center border-l border-gray-200">
                        <p className="font-bold text-lg text-gray-300">+</p>
                        <p className="text-xs text-gray-400 mt-1">days</p>
                    </div>
                    {/* Qty (second from right) */}
                    <div className="col-span-1 flex flex-col items-center justify-center border-l border-gray-200">
                        <p className="font-bold text-lg text-gray-300">+</p>
                        <p className="text-xs text-gray-400 mt-1">Qty</p>
                    </div>
                    {/* Price (rightmost) */}
                    <div className="col-span-1 flex flex-col items-center justify-center border-l border-gray-200">
                        <p className="font-bold text-lg text-gray-300">+</p>
                        <p className="text-xs text-gray-400 mt-1">per Qty</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            className={`relative border border-black rounded-md transition-all duration-200 mb-2 select-none quote-card-drop-zone ${isDragging ? 'quote-card-dragging cursor-grabbing' : ''} ${!isEmpty ? getStatusColor() : 'bg-white'} ${shouldBeDisabled
                ? 'opacity-50 cursor-not-allowed'
                : 'hover:shadow cursor-grab'
                }`}
            onClick={shouldBeDisabled ? undefined : onToggle}
            style={{ padding: '0.75rem 1rem' }}
            draggable={draggable && !shouldBeDisabled}
            title={shouldBeDisabled ? "This quote is disabled" : undefined}
            onDragStart={onDragStart && !shouldBeDisabled ? (e => {
                e.stopPropagation();
                e.dataTransfer.effectAllowed = 'move';
                e.dataTransfer.setData('text/plain', '');
                e.currentTarget.classList.add('quote-card-dragging');
                onDragStart();
            }) : undefined}
            onDragOver={onDragOver && !shouldBeDisabled ? (e => {
                e.preventDefault();
                e.currentTarget.classList.add('quote-card-drag-over');
                onDragOver();
            }) : undefined}
            onDragLeave={onDragOver && !shouldBeDisabled ? (e => {
                e.currentTarget.classList.remove('quote-card-drag-over');
            }) : undefined}
            onDrop={onDrop && !shouldBeDisabled ? (e => {
                e.preventDefault();
                e.currentTarget.classList.remove('quote-card-drag-over');
                onDrop();
            }) : undefined}
        >


            {/* Main grid */}
            <div className="grid grid-cols-5 gap-2 items-start text-center">
                {/* Supplier, Advance, Timeline, Qty, Price columns as before */}
                <div className="col-span-1 flex flex-col items-center justify-center">
                    <p className="font-medium text-gray-800">{getVendorNameById(quote.vendorId, allVendors, quote.supplier)}</p>
                    <p className="text-sm text-gray-600">{quote.supplierCity}</p>
                </div>
                <div className="col-span-1 flex flex-col items-center justify-center border-l border-gray-200">
                    <p className="font-semibold text-lg text-gray-800">{quote.advancePayment}%</p>
                    <p className="text-xs text-gray-600">{paymentTermsText}</p>
                </div>
                <div className="col-span-1 flex flex-col items-center justify-center border-l border-gray-200">
                    <p className="font-semibold text-lg text-gray-800">{quote.deliveryTimeline || 'N/A'}</p>
                    <p className="text-xs text-gray-600 mt-1">days</p>
                </div>
                <div className="col-span-1 flex flex-col items-center justify-center border-l border-gray-200">
                    <p className="font-bold text-lg text-gray-800">{quote.quantity}</p>
                    <p className="text-xs text-gray-600 mt-1">Qty</p>
                </div>
                <div className="col-span-1 flex flex-col items-center justify-center border-l border-gray-200">
                    <p className="font-bold text-lg text-gray-800">₹{formatINR(priceInr)}</p>
                    <p className="text-xs text-gray-600 mt-1">₹{formatINR(pricePerQtyInr)} per Qty</p>
                </div>
            </div>

            {/* Finalization indicator - Show when quote is finalized */}
            {quote.finalizedAt && (
                <div className="mt-2 flex justify-center">
                    <span className="inline-block px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800 border border-green-200">
                        Finalized on {quote.finalizedAt?.toDate ? quote.finalizedAt.toDate().toLocaleDateString() : 'Unknown date'}
                    </span>
                </div>
            )}

            {quote.notes && <p className="mt-2 text-xs text-gray-700 bg-gray-100 p-2 rounded">{quote.notes}</p>}
            {/* Expanded section */}
            {expanded && (
                <div className="mt-2 border-t border-gray-200 pt-3">
                    <div className="flex items-center justify-between mb-2">
                        {/* Edit/Delete buttons on the left */}
                        {isMaster && !shouldBeDisabled && (
                            <div className="flex gap-2">
                                <button onClick={e => { e.stopPropagation(); onEdit(); }} className="p-1 rounded-full hover:bg-gray-200 text-gray-700"><Edit size={14} /></button>
                                {onDelete && (
                                    <button onClick={e => { e.stopPropagation(); onDelete(); }} className="p-1 rounded-full hover:bg-red-100 text-red-600" title="Delete Quotation">
                                        <Trash2 size={14} />
                                    </button>
                                )}
                            </div>
                        )}
                        {/* Finalized Quote and PI controls */}
                        <div className="flex gap-2">
                            {/* Finalized Quote Button - Only show when order is NOT already in "In Progress" status */}
                            {isMaster && !shouldBeDisabled && quote.deliveryTimeline && orderStatus !== 'In Progress' && (
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={async (e) => {
                                            e.stopPropagation();
                                            try {
                                                // First, finalize this quote by adding finalizedAt timestamp
                                                const quoteRef = categoryId
                                                    ? doc(db, `orderCategories/${categoryId}/orders/${orderId}/quotes`, quote.id)
                                                    : doc(db, `orders/${orderId}/quotes`, quote.id);

                                                await updateDoc(quoteRef, {
                                                    finalizedAt: serverTimestamp()
                                                });

                                                // Then update order status to "In Progress" (Awaiting Delivery) and store the finalized quote ID
                                                const orderRef = categoryId
                                                    ? doc(db, `orderCategories/${categoryId}/orders`, orderId)
                                                    : doc(db, 'orders', orderId);

                                                await updateDoc(orderRef, {
                                                    status: 'In Progress',
                                                    deliveryDate: new Date(Date.now() + (quote.deliveryTimeline * 24 * 60 * 60 * 1000)),
                                                    finalizedQuoteId: quote.id,
                                                    deliveryTimeline: quote.deliveryTimeline,
                                                    quantity: quote.quantity,
                                                    supplierCity: quote.supplierCity,
                                                    price: quote.price,
                                                    advancePayment: quote.advancePayment,
                                                    paymentTerms: quote.paymentTerms,
                                                    finalPrice: quote.price
                                                });

                                                // Clear quotes cache to ensure fresh data
                                                const { clearQuotesCache } = await import('./utils/quotesService');
                                                clearQuotesCache();

                                                // Update local state immediately for instant UI update
                                                if (setSelectedOrder) {
                                                    setSelectedOrder(prev => ({
                                                        ...prev,
                                                        status: 'In Progress',
                                                        deliveryDate: new Date(Date.now() + (quote.deliveryTimeline * 24 * 60 * 60 * 1000)),
                                                        finalizedQuoteId: quote.id,
                                                        deliveryTimeline: quote.deliveryTimeline,
                                                        quantity: quote.quantity,
                                                        supplierCity: quote.supplierCity,
                                                        price: quote.price,
                                                        advancePayment: quote.advancePayment,
                                                        paymentTerms: quote.paymentTerms,
                                                        finalPrice: quote.price
                                                    }));
                                                }

                                                // Force a small delay to ensure state updates are processed
                                                setTimeout(() => {
                                                    // Show success message
                                                    alert(`Quote finalized! Order set to Awaiting Delivery. Expected delivery in ${quote.deliveryTimeline} days.`);
                                                }, 100);
                                            } catch (error) {
                                                console.error('Error finalizing quote:', error);
                                                alert('Failed to finalize quote. Please try again.');
                                            }
                                        }}
                                        className="bg-green-600 text-white px-3 py-2 rounded-md hover:bg-green-700 transition-colors text-sm font-medium"
                                        title={`Finalize this quote and set order to Awaiting Delivery (${quote.deliveryTimeline} days)`}
                                    >
                                        Finalized Quote
                                    </button>
                                </div>
                            )}

                            {/* PI controls */}
                            {isMaster && !shouldBeDisabled && (
                                <div className="flex items-center gap-2">
                                    {quote.proformaInvoiceUrl ? (
                                        <Tooltip text="View Proforma Invoice">
                                            <a href={quote.proformaInvoiceUrl} target="_blank" rel="noopener noreferrer" className="p-2 rounded-full bg-green-100 text-green-700 hover:bg-green-200 transition-colors">📄</a>
                                        </Tooltip>
                                    ) : (
                                        <Tooltip text="Upload Proforma Invoice">
                                            <label className="p-2 rounded-full bg-orange-100 text-orange-700 hover:bg-orange-200 transition-colors cursor-pointer">
                                                📤
                                                <input type="file" accept="application/pdf" onChange={handleProformaUpload} disabled={uploadingPI} className="hidden" />
                                            </label>
                                        </Tooltip>
                                    )}
                                    {quote.proformaInvoiceUrl && (
                                        <Tooltip text="Delete Proforma Invoice">
                                            <button onClick={e => { e.stopPropagation(); handleDeletePI(); }} disabled={deletingPI} className="p-2 rounded-full bg-red-100 text-red-700 hover:bg-red-200 transition-colors disabled:opacity-50 ml-1">🗑️</button>
                                        </Tooltip>
                                    )}
                                    {uploadingPI && <span className="text-xs text-gray-500 ml-2">Uploading...</span>}
                                    {uploadSuccess && <span className="text-xs text-green-600 ml-2">✓ Uploaded!</span>}
                                    {uploadError && <span className="text-xs text-red-600 ml-2">{uploadError}</span>}
                                </div>
                            )}
                        </div>
                    </div>
                    {/* Confirm UI for auto-fetched quotes */}
                    {isMaster && quote.needsConfirmation && quote.fetchedAutomatically && (
                        <div className="flex flex-col items-start gap-2 mb-2">
                            <span className="inline-block px-3 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800 mb-1">Fetched automatically - Pending Confirmation</span>
                            <button onClick={e => { e.stopPropagation(); handleConfirmQuote(); }} className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700">Confirm Quote</button>
                        </div>
                    )}
                    {/* Quote Status */}
                    {isMaster && onQuoteStatusChange && (
                        <div className="flex items-center gap-2 mb-2" onClick={(e) => e.stopPropagation()}>
                            <span className="text-sm font-medium text-gray-800">Quote Status:</span>
                            <QuoteStatusDropdown
                                currentStatus={quote.status}
                                onStatusChange={(newStatus) => onQuoteStatusChange(quote.id, newStatus)}
                                disabled={shouldBeDisabled}
                            />
                        </div>
                    )}

                    {/* Finalization Date - Show when quote is finalized */}
                    {quote.finalizedAt && (
                        <div className="flex items-center gap-2 mb-2" onClick={(e) => e.stopPropagation()}>
                            <span className="inline-block px-3 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                Finalized on {quote.finalizedAt?.toDate ? quote.finalizedAt.toDate().toLocaleDateString() : 'Unknown date'}
                            </span>
                        </div>
                    )}


                    {/* Discussion section */}
                    <CommentSection quoteId={quote.id} orderId={orderId} currentUser={currentUser} order={order} allUsers={allUsers} />
                </div>
            )}
        </div>
    );
}


const CommentSection = ({ orderId, quoteId, currentUser, order, allUsers }) => {
    const [comments, setComments] = useState([]);
    const [newComment, setNewComment] = useState("");


    useEffect(() => {
        const commentsQuery = query(collection(db, `orders/${orderId}/quotes/${quoteId}/comments`));
        const unsubscribe = onSnapshot(commentsQuery, (snapshot) => {
            const fetchedComments = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            fetchedComments.sort((a, b) => (a.createdAt?.toDate() || 0) - (b.createdAt?.toDate() || 0));
            setComments(fetchedComments);
        });
        return unsubscribe;
    }, [orderId, quoteId]);


    const handleAddComment = async (e) => {
        e.preventDefault();
        if (!newComment.trim()) return;
        const commentsCol = collection(db, `orders/${orderId}/quotes/${quoteId}/comments`);
        await addDoc(commentsCol, {
            text: newComment.trim(),
            author: { uid: currentUser.uid, displayName: currentUser.displayName, role: currentUser.role },
            createdAt: serverTimestamp(),
        });

        // Send notification to other users about the quote comment
        if (order && allUsers) {
            await sendDiscussionNotification(orderId, order, currentUser, allUsers, 'text');
        }

        setNewComment('');
    };


    return (
        <div className="bg-gray-50 p-4 border-t border-gray-200" onClick={(e) => e.stopPropagation()}>
            <h4 className="text-sm font-semibold mb-2 text-gray-800">Discussion</h4>
            <div className="space-y-3 mb-4 max-h-40 overflow-y-auto">
                {comments.map(comment => (
                    <div key={comment.id} className="text-sm">
                        <span className={`font-semibold ${comment.author.role === 'master' ? 'text-gray-900' : 'text-gray-700'}`}>{comment.author.displayName}:</span> <span className="text-gray-700">{comment.text}</span>
                        <span className="text-xs text-gray-500 ml-2">{comment.createdAt?.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                ))}
                {comments.length === 0 && <p className="text-xs text-gray-500">No comments yet.</p>}
            </div>
            <form onSubmit={handleAddComment} className="flex gap-2">
                <input type="text" value={newComment} onChange={(e) => setNewComment(e.target.value)} placeholder="Add a comment..." className="flex-grow w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-black bg-white text-gray-800 placeholder-gray-500" />
                <button type="submit" className="bg-black text-white px-4 py-1.5 rounded-md hover:bg-gray-800"><Send size={16} /></button>
            </form>
        </div>
    );
};


const VendorsPage = ({ vendors, onSelectVendor, onBack, search, setSearch, sortBy, setSortBy, scrollPosition }) => {
    const [vendorToEdit, setVendorToEdit] = useState(null);
    const [completedCounts, setCompletedCounts] = useState({});
    const [showAddVendor, setShowAddVendor] = useState(false); // NEW: state for add vendor modal
    const [showSearchSection, setShowSearchSection] = useState(false);
    const [selectedTags, setSelectedTags] = useState([]);
    const [tempSearch, setTempSearch] = useState('');
    const [searchSuggestions, setSearchSuggestions] = useState([
        "CONTROL VALVE",
        "GASKET",
        "GOVERNOR VALVE ASSEMBLY",
        "pipes",
        "SEAL",
        "valve"
    ]);
    const [newSuggestion, setNewSuggestion] = useState('');
    const [showAddSuggestion, setShowAddSuggestion] = useState(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [suggestionToDelete, setSuggestionToDelete] = useState('');

    // Helper function to extract first name
    const getFirstName = (fullName) => {
        if (!fullName || typeof fullName !== 'string') return '';
        return fullName.trim().split(' ')[0].toLowerCase();
    };

    // Handle suggestion click - toggle selection
    const handleSuggestionClick = (suggestion) => {
        if (selectedTags.includes(suggestion)) {
            // Remove if already selected
            setSelectedTags(selectedTags.filter(tag => tag !== suggestion));
        } else {
            // Add if not selected
            setSelectedTags([...selectedTags, suggestion]);
        }
        // Don't search immediately - wait for Enter
    };

    // Handle Enter key to search
    const handleSearchSubmit = () => {
        // Combine temp search with selected tags
        const searchTerms = [];
        if (tempSearch.trim()) {
            searchTerms.push(tempSearch.trim());
        }
        if (selectedTags.length > 0) {
            searchTerms.push(...selectedTags);
        }

        // Set the search and close the section
        setSearch(searchTerms.join(' '));
        setShowSearchSection(false);
        setTempSearch('');
        setSelectedTags([]);
    };

    // Handle search section open/close
    const handleSearchBarClick = () => {
        setShowSearchSection(true);
        setTempSearch(search); // Load current search into temp
    };

    // Prevent background scroll when modal is open
    useEffect(() => {
        if (showSearchSection) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        // Cleanup on unmount
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [showSearchSection]);

    // Handle adding new suggestion
    const handleAddSuggestion = () => {
        if (newSuggestion.trim() && !searchSuggestions.includes(newSuggestion.trim())) {
            const updatedSuggestions = [...searchSuggestions, newSuggestion.trim()].sort();
            setSearchSuggestions(updatedSuggestions);
            setNewSuggestion('');
            setShowAddSuggestion(false);
        }
    };

    // Handle removing suggestion - show confirmation first
    const handleRemoveSuggestion = (suggestionToRemove) => {
        setSuggestionToDelete(suggestionToRemove);
        setShowDeleteConfirmation(true);
    };

    // Confirm deletion
    const confirmDeleteSuggestion = () => {
        const updatedSuggestions = searchSuggestions.filter(s => s !== suggestionToDelete);
        setSearchSuggestions(updatedSuggestions);
        setShowDeleteConfirmation(false);
        setSuggestionToDelete('');
    };

    // Cancel deletion
    const cancelDeleteSuggestion = () => {
        setShowDeleteConfirmation(false);
        setSuggestionToDelete('');
    };


    // Fetch completed order counts per vendor
    useEffect(() => {
        if (!vendors || vendors.length === 0) return;
        // Fetch all completed and in-progress orders and count per vendor
        const fetchCounts = async () => {
            const ordersSnap = await getDocs(query(collection(db, 'orders'), where('status', 'in', ['Completed', 'In Progress'])));
            const counts = {};
            for (const orderDoc of ordersSnap.docs) {
                const orderId = orderDoc.id;
                // Only count best quotes
                const quotesSnap = await getDocs(query(collection(db, `orders/${orderId}/quotes`), where('isBestQuote', '==', true)));
                for (const quoteDoc of quotesSnap.docs) {
                    const quoteData = quoteDoc.data();
                    const supplierFirstName = getFirstName(quoteData.supplier);
                    // Find matching vendor by first name
                    const matchingVendor = vendors.find(v => getFirstName(v.name) === supplierFirstName);
                    if (matchingVendor) {
                        counts[matchingVendor.name] = (counts[matchingVendor.name] || 0) + 1;
                    }
                }
            }
            setCompletedCounts(counts);
        };
        fetchCounts();
    }, [vendors]);

    // Filter and sort vendors
    let filteredVendors = vendors || [];

    // Apply text search filter
    if (search) {
        const s = search.toLowerCase();
        filteredVendors = filteredVendors.filter(v => {
            // Basic vendor information
            const basicMatch = (
                (v.name && v.name.toLowerCase().includes(s)) ||
                (v.city && v.city.toLowerCase().includes(s)) ||
                (v.email && v.email.toLowerCase().includes(s)) ||
                (v.phone && v.phone.toLowerCase().includes(s))
            );

            // Vendor details from the specialization section
            const detailsMatch = (
                (v.preferredCommunication && v.preferredCommunication.toLowerCase().includes(s)) ||
                (v.products && v.products.toLowerCase().includes(s)) ||
                (v.industries && v.industries.toLowerCase().includes(s)) ||
                (v.brands && v.brands.toLowerCase().includes(s))
            );

            return basicMatch || detailsMatch;
        });
    }

    // Apply selected tags filter
    if (selectedTags.length > 0) {
        filteredVendors = filteredVendors.filter(v => {
            return selectedTags.some(tag => {
                const tagLower = tag.toLowerCase();
                return (
                    (v.name && v.name.toLowerCase().includes(tagLower)) ||
                    (v.city && v.city.toLowerCase().includes(tagLower)) ||
                    (v.email && v.email.toLowerCase().includes(tagLower)) ||
                    (v.phone && v.phone.toLowerCase().includes(tagLower)) ||
                    (v.preferredCommunication && v.preferredCommunication.toLowerCase().includes(tagLower)) ||
                    (v.products && v.products.toLowerCase().includes(tagLower)) ||
                    (v.industries && v.industries.toLowerCase().includes(tagLower)) ||
                    (v.brands && v.brands.toLowerCase().includes(tagLower))
                );
            });
        });
    }
    if (sortBy === 'alphabetical') {
        filteredVendors = [...filteredVendors].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    } else if (sortBy === 'completedOrders') {
        filteredVendors = [...filteredVendors].sort((a, b) => (completedCounts[b.name] || 0) - (completedCounts[a.name] || 0));
    }

    const handleSaveVendor = async (id, data) => {
        const vendorRef = doc(db, 'vendors', id);

        // Get the current vendor data to check if name is being changed
        const currentVendorDoc = await getDoc(vendorRef);
        const currentVendorData = currentVendorDoc.data();

        // If the vendor name is being changed, update all quotes with the old name
        if (currentVendorData && currentVendorData.name !== data.name) {
            const oldName = currentVendorData.name;
            const newName = data.name;

            // Use the utility function to update quotes
            try {
                const updatedCount = await updateQuotesForVendorNameChange(oldName, newName);
                if (updatedCount > 0) {

                }
            } catch (error) {
                console.error('Error updating quotes for vendor name change:', error);
            }
        }

        await updateDoc(vendorRef, data);
        setVendorToEdit(null);
    }

    // Restore scroll position on mount
    useEffect(() => {
        if (typeof scrollPosition === 'number' && scrollPosition > 0) {
            window.scrollTo(0, scrollPosition);
        }
    }, [scrollPosition]);

    return (
        <div>
            <button onClick={onBack} className="text-sm font-medium mb-4">&larr; Back to List</button>
            <h1 className="text-2xl font-bold mb-6">Vendors</h1>
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-4 justify-between">
                <div className="flex gap-2 items-center flex-1">
                    <div className="max-w-xs w-full">
                        <div className="relative">
                            <input
                                type="text"
                                placeholder="Search vendors..."
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                className="pl-10 pr-10 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black w-full"
                            />
                            {search && (
                                <button
                                    onClick={() => setSearch('')}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-black"
                                    tabIndex={0}
                                    aria-label="Clear search"
                                >
                                    <X size={16} />
                                </button>
                            )}
                            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        </div>
                    </div>

                    {/* Search Options Button */}
                    <button
                        onClick={handleSearchBarClick}
                        className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-black transition-colors"
                        title="Open search options and suggestions"
                    >
                        <Filter size={16} />
                        Search Options
                    </button>
                </div>
                <div className="flex gap-2 items-center">
                    <SortVendorsDropdown sortBy={sortBy} setSortBy={setSortBy} />
                    {/* Add Vendor Button */}
                    <button
                        onClick={() => setShowAddVendor(true)}
                        className="flex items-center gap-2 bg-black text-white px-4 py-2 rounded-md hover:bg-gray-800"
                    >
                        <Plus size={18} /> Add Vendor
                    </button>
                </div>
            </div>

            {/* Search Section Modal */}
            {showSearchSection && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-hidden">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-auto max-h-[90vh] flex flex-col">
                        {/* Header */}
                        <div className="flex items-center justify-between p-6 border-b border-gray-200">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-black rounded-lg flex items-center justify-center">
                                    <Search size={20} className="text-white" />
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-black">Search Options</h3>
                                    <p className="text-sm text-gray-600">Select popular searches or type your own</p>
                                </div>
                            </div>
                            <button
                                onClick={() => {
                                    setShowSearchSection(false);
                                    setTempSearch('');
                                    setSelectedTags([]);
                                }}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <X size={20} className="text-gray-500" />
                            </button>
                        </div>

                        {/* Search Input - Fixed */}
                        <div className="px-6 pt-6 pb-4 border-b border-gray-200">
                            <label className="block text-sm font-semibold text-black mb-2">Search Query</label>
                            <div className="relative">
                                <input
                                    type="text"
                                    placeholder="Type to search vendors..."
                                    value={tempSearch}
                                    onChange={e => setTempSearch(e.target.value)}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter') {
                                            handleSearchSubmit();
                                        } else if (e.key === 'Escape') {
                                            setShowSearchSection(false);
                                            setTempSearch('');
                                            setSelectedTags([]);
                                        }
                                    }}
                                    className="w-full pl-12 pr-4 py-3 text-sm border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent transition-all"
                                    autoFocus
                                />
                                <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                            </div>
                        </div>

                        {/* Content - Scrollable */}
                        <div className="p-6 flex-1 overflow-y-auto">

                            {/* Popular Searches Section */}
                            <div className="mb-6">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center gap-2">
                                        <div className="w-6 h-6 bg-gray-100 rounded-lg flex items-center justify-center">
                                            <Filter size={14} className="text-black" />
                                        </div>
                                        <h4 className="text-lg font-semibold text-black">Popular Searches</h4>
                                    </div>
                                    <button
                                        onClick={() => setShowAddSuggestion(!showAddSuggestion)}
                                        className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                                    >
                                        <Plus size={16} />
                                        Add New
                                    </button>
                                </div>

                                {/* Add New Suggestion Input */}
                                {showAddSuggestion && (
                                    <div className="mb-4 p-4 border-2 border-gray-200 rounded-lg bg-gray-50">
                                        <div className="flex gap-3">
                                            <input
                                                type="text"
                                                placeholder="Enter new suggestion..."
                                                value={newSuggestion}
                                                onChange={e => setNewSuggestion(e.target.value)}
                                                onKeyDown={e => {
                                                    if (e.key === 'Enter') {
                                                        handleAddSuggestion();
                                                    } else if (e.key === 'Escape') {
                                                        setShowAddSuggestion(false);
                                                        setNewSuggestion('');
                                                    }
                                                }}
                                                className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                                                autoFocus
                                            />
                                            <button
                                                onClick={handleAddSuggestion}
                                                className="px-4 py-2 text-sm bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
                                            >
                                                Add
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setShowAddSuggestion(false);
                                                    setNewSuggestion('');
                                                }}
                                                className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* Suggestions Grid - Scrollable */}
                                <div className="h-80 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        {searchSuggestions.map((suggestion, index) => (
                                            <div key={index} className="group">
                                                <div className="flex items-center gap-3 p-4 border-2 border-gray-200 rounded-lg hover:border-black hover:shadow-md transition-all cursor-pointer bg-white">
                                                    <button
                                                        onClick={() => handleSuggestionClick(suggestion)}
                                                        className="flex-1 flex items-center gap-3 text-left"
                                                    >
                                                        <div className="w-5 h-5 flex items-center justify-center">
                                                            {selectedTags.includes(suggestion) ? (
                                                                <div className="w-4 h-4 bg-black border-2 border-black rounded flex items-center justify-center">
                                                                    <div className="w-2 h-2 bg-white rounded-sm"></div>
                                                                </div>
                                                            ) : (
                                                                <div className="w-4 h-4 border-2 border-gray-300 rounded hover:border-black transition-colors"></div>
                                                            )}
                                                        </div>
                                                        <span className="text-sm font-medium text-black group-hover:text-black">{suggestion}</span>
                                                    </button>
                                                    <button
                                                        onClick={() => handleRemoveSuggestion(suggestion)}
                                                        className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                                                        title="Remove suggestion"
                                                    >
                                                        <X size={16} />
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Footer Actions */}
                            <div className="flex items-center justify-between pt-6 border-t border-gray-200">
                                <div className="text-sm text-gray-600">
                                    {selectedTags.length > 0 && (
                                        <span className="font-medium">{selectedTags.length} selected</span>
                                    )}
                                </div>
                                <div className="flex gap-3">
                                    <button
                                        onClick={() => {
                                            setShowSearchSection(false);
                                            setTempSearch('');
                                            setSelectedTags([]);
                                        }}
                                        className="px-6 py-3 text-sm font-medium text-gray-600 border-2 border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleSearchSubmit}
                                        className="px-6 py-3 text-sm font-medium bg-black text-white rounded-lg hover:bg-gray-800 transition-all shadow-lg hover:shadow-xl"
                                    >
                                        Search
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteConfirmation && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]">
                    <div className="bg-white rounded-lg p-6 w-full max-w-sm mx-4">
                        <div className="text-center">
                            <div className="mb-4">
                                <div className="w-12 h-12 mx-auto bg-red-100 rounded-full flex items-center justify-center mb-3">
                                    <X size={24} className="text-red-600" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Suggestion</h3>
                                <p className="text-sm text-gray-600">
                                    Are you sure you want to delete <span className="font-semibold">"{suggestionToDelete}"</span>?
                                </p>
                                <p className="text-xs text-gray-500 mt-1">This action cannot be undone.</p>
                            </div>

                            <div className="flex gap-3 justify-center">
                                <button
                                    onClick={cancelDeleteSuggestion}
                                    className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={confirmDeleteSuggestion}
                                    className="px-4 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700"
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <div className="bg-white border rounded-lg">
                <ul className="divide-y divide-gray-200">
                    {vendors === null ? (
                        <li className="p-4 text-center text-gray-500">
                            <LoadingScreen message="Loading vendors..." />
                        </li>
                    ) : filteredVendors && filteredVendors.length > 0 ? filteredVendors.map((vendor, idx) => (
                        <RevealOnScroll key={vendor.id} delay={idx * 0.05}>
                            <li onClick={() => onSelectVendor(vendor)} className="p-4 flex items-center justify-between gap-4 hover:bg-gray-50 group hover:shadow-md hover:border-black transition-all duration-200 cursor-pointer">
                                <div className="flex-grow">
                                    <span className="font-medium">{vendor.name}</span>
                                    {vendor.city && <span className="text-sm text-gray-500 ml-2">({vendor.city})</span>}
                                    {vendor.email && <span className="block text-xs text-gray-500 mt-1">{vendor.email}</span>}
                                </div>
                                <div className="flex items-center gap-2">
                                    {sortBy === 'completedOrders' && (
                                        <span className="text-xs bg-black text-white px-2 py-1 rounded-full">Supplied: {completedCounts[vendor.name] || 0}</span>
                                    )}
                                    <button onClick={(e) => { e.stopPropagation(); setVendorToEdit(vendor); }} className="p-2 rounded-full hover:bg-gray-200 opacity-0 group-hover:opacity-100 transition-opacity"><Edit size={16} /></button>
                                    <ChevronRight size={20} className="text-gray-400 group-hover:text-black transition-colors" />
                                </div>
                            </li>
                        </RevealOnScroll>
                    )) : (
                        <li className="p-4 text-center text-gray-500">No vendors have been saved yet.</li>
                    )}
                </ul>
            </div>
            {vendorToEdit && <EditVendorModal vendor={vendorToEdit} onClose={() => setVendorToEdit(null)} onSave={handleSaveVendor} />}
            {showAddVendor && <AddVendorModal onClose={() => setShowAddVendor(false)} />}
        </div>
    );
};

const EditVendorModal = ({ vendor, onClose, onSave }) => {
    const [name, setName] = useState(vendor.name);
    const [city, setCity] = useState(vendor.city || '');
    const [email, setEmail] = useState(vendor.email || '');
    const [deleting, setDeleting] = useState(false);

    const handleSave = useCallback(() => {
        if (!name.trim()) return;
        onSave(vendor.id, { name, city, email });
    }, [name, city, email, vendor.id, onSave]);

    const handleDelete = async () => {
        if (!window.confirm('Are you sure you want to delete this vendor? This action cannot be undone.')) return;
        setDeleting(true);
        try {
            await deleteDoc(doc(db, 'vendors', vendor.id));
            setDeleting(false);
            onClose();
        } catch (err) {
            setDeleting(false);
        }
    };

    React.useEffect(() => {
        const handleEscEnter = (e) => {
            if (e.key === 'Escape') onClose();
            if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
                if (name.trim()) handleSave();
            }
        };
        window.addEventListener('keydown', handleEscEnter);
        return () => window.removeEventListener('keydown', handleEscEnter);
    }, [name, city, email, onClose, handleSave]);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-lg m-4">
                <h2 className="text-xl font-bold mb-4">Edit Vendor</h2>
                <div className="space-y-4">
                    <InputField label="Vendor Name" value={name} onChange={e => setName(e.target.value)} />
                    <InputField label="City" value={city} onChange={e => setCity(e.target.value)} />
                    <InputField label="Email" type="email" value={email} onChange={e => setEmail(e.target.value)} />
                </div>
                <div className="mt-6 flex justify-end gap-4">
                    <button onClick={onClose} className="bg-gray-200 px-4 py-2 rounded-md hover:bg-gray-300">Cancel</button>
                    <button onClick={handleSave} className="bg-black text-white px-4 py-2 rounded-md hover:bg-gray-800">Save Changes</button>
                    <button onClick={handleDelete} disabled={deleting} className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700">{deleting ? 'Deleting...' : 'Delete Vendor'}</button>
                </div>
            </div>
        </div>
    );
};

// AddVendorModal component
const AddVendorModal = ({ onClose }) => {
    const [name, setName] = useState('');
    const [city, setCity] = useState('');
    const [email, setEmail] = useState('');
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);

    const handleSave = useCallback(async () => {
        if (!name.trim()) {
            setError('Vendor name is required');
            return;
        }
        setSaving(true);
        try {
            await addDoc(collection(db, 'vendors'), { name: name.trim(), city: city.trim(), email: email.trim() });
            setSaving(false);
            onClose();
        } catch (err) {
            setError('Failed to add vendor');
            setSaving(false);
        }
    }, [name, city, email, onClose]);

    React.useEffect(() => {
        const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [onClose]);

    const requiredFilled = name.trim() && city.trim() && email.trim();
    React.useEffect(() => {
        const handleEscEnter = (e) => {
            if (e.key === 'Escape') onClose();
            if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
                if (requiredFilled) handleSave();
            }
        };
        window.addEventListener('keydown', handleEscEnter);
        return () => window.removeEventListener('keydown', handleEscEnter);
    }, [name, city, email, onClose, requiredFilled, handleSave]);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-lg m-4">
                <h2 className="text-xl font-bold mb-4">Add Vendor</h2>
                <div className="space-y-4">
                    <InputField label="Vendor Name" value={name} onChange={e => setName(e.target.value)} required />
                    <InputField label="City" value={city} onChange={e => setCity(e.target.value)} />
                    <InputField label="Email" type="email" value={email} onChange={e => setEmail(e.target.value)} />
                </div>
                {error && <div className="text-red-600 mt-2">{error}</div>}
                <div className="mt-6 flex justify-end gap-4">
                    <button onClick={onClose} className="bg-gray-200 px-4 py-2 rounded-md hover:bg-gray-300">Cancel</button>
                    <button onClick={handleSave} className="bg-black text-white px-4 py-2 rounded-md hover:bg-gray-800" disabled={saving}>{saving ? 'Saving...' : 'Add Vendor'}</button>
                </div>
            </div>
        </div>
    );
};


const VendorDetailsPage = ({ vendor, onBack, onSelectOrder, allOrders = [] }) => {
    const [allQuotes, setAllQuotes] = useState([]);
    const [isLoading, setIsLoading] = useState(false); // Start as false since we'll use existing data
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');
    const [quoteStatusFilter, setQuoteStatusFilter] = useState('All');
    const [sortBy, setSortBy] = useState('date');
    const [sortOrder, setSortOrder] = useState('desc');
    const [showReportModal, setShowReportModal] = useState(false);
    const [editingSection, setEditingSection] = useState(null);
    const [vendorData, setVendorData] = useState(vendor);

    // Date filtering state
    const [dateFilter, setDateFilter] = useState('all');
    const [customStartDate, setCustomStartDate] = useState('');
    const [customEndDate, setCustomEndDate] = useState('');
    const [showDateDropdown, setShowDateDropdown] = useState(false);
    const dateDropdownRef = useRef(null);

    // Save vendor data function
    const saveVendorData = async (updates) => {
        try {
            const vendorRef = doc(db, 'vendors', vendor.id);
            await updateDoc(vendorRef, updates);
            setVendorData({ ...vendorData, ...updates });
            setEditingSection(null);
        } catch (error) {
            console.error('Error updating vendor:', error);
        }
    };

    // Dropdown state management
    const [isOrderStatusOpen, setIsOrderStatusOpen] = useState(false);
    const [isQuoteStatusOpen, setIsQuoteStatusOpen] = useState(false);
    const [isSortOpen, setIsSortOpen] = useState(false);

    // Refs for dropdowns
    const orderStatusRef = useRef(null);
    const quoteStatusRef = useRef(null);
    const sortRef = useRef(null);

    // Close dropdowns when clicking outside
    useClickOutside(orderStatusRef, () => setIsOrderStatusOpen(false));
    useClickOutside(quoteStatusRef, () => setIsQuoteStatusOpen(false));
    useClickOutside(sortRef, () => setIsSortOpen(false));
    useClickOutside(dateDropdownRef, () => setShowDateDropdown(false));

    // Helper function to extract first name
    const getFirstName = (fullName) => {
        if (!fullName || typeof fullName !== 'string') return '';
        return fullName.trim().split(' ')[0].toLowerCase();
    };

    // Function to fetch fresh quotes data from database
    const fetchFreshQuotesData = async () => {
        if (!vendor || !allOrders || allOrders.length === 0) return [];

        const vendorFirstName = getFirstName(vendor.name);

        try {
            const allQuotesData = [];

            // Get all orders from the database to ensure we have the latest data
            const ordersQuery = query(collection(db, 'orders'));
            const ordersSnapshot = await getDocs(ordersQuery);
            const latestOrders = ordersSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            // Process orders in parallel for maximum speed
            const quotePromises = latestOrders.map(async (order) => {
                try {
                    const quotesQuery = query(collection(db, `orders/${order.id}/quotes`));
                    const quotesSnapshot = await getDocs(quotesQuery);

                    const orderQuotes = [];
                    for (const quoteDoc of quotesSnapshot.docs) {
                        const quoteData = quoteDoc.data();
                        const supplierFirstName = getFirstName(quoteData.supplier);

                        if (supplierFirstName === vendorFirstName) {
                            orderQuotes.push({
                                id: quoteDoc.id,
                                orderId: order.id,
                                order: order,
                                quote: quoteData
                            });
                        }
                    }
                    return orderQuotes;
                } catch (error) {
                    console.error(`Error fetching quotes for order ${order.id}:`, error);
                    return [];
                }
            });

            const allResults = await Promise.all(quotePromises);
            allResults.forEach(quotes => {
                allQuotesData.push(...quotes);
            });

            return allQuotesData;
        } catch (error) {
            console.error('Error fetching fresh quotes data:', error);
            return [];
        }
    };

    // Function to refresh quotes data manually
    const refreshQuotesData = async () => {
        setIsRefreshing(true);
        try {
            const freshQuotes = await fetchFreshQuotesData();
            setAllQuotes(freshQuotes);
        } catch (error) {
            console.error('Error refreshing quotes:', error);
        } finally {
            setIsRefreshing(false);
        }
    };

    // INSTANT vendor quote loading using existing data
    useEffect(() => {
        if (!vendor || !allOrders || allOrders.length === 0) return;

        const vendorFirstName = getFirstName(vendor.name);

        // Use existing orders data to find vendor quotes instantly
        const loadVendorQuotesFromExistingData = async () => {
            setIsLoading(true);
            try {
                const allQuotesData = [];

                // Process orders in parallel for maximum speed
                const quotePromises = allOrders.slice(0, 100).map(async (order) => { // Limit to first 100 orders for speed
                    try {
                        const quotesQuery = query(collection(db, `orders/${order.id}/quotes`));
                        const quotesSnapshot = await getDocs(quotesQuery);

                        const orderQuotes = [];
                        for (const quoteDoc of quotesSnapshot.docs) {
                            const quoteData = quoteDoc.data();
                            const supplierFirstName = getFirstName(quoteData.supplier);

                            if (supplierFirstName === vendorFirstName) {
                                orderQuotes.push({
                                    id: quoteDoc.id,
                                    orderId: order.id,
                                    order: order,
                                    quote: quoteData
                                });
                            }
                        }
                        return orderQuotes;
                    } catch (error) {
                        console.error(`Error fetching quotes for order ${order.id}:`, error);
                        return [];
                    }
                });

                const allResults = await Promise.all(quotePromises);
                allResults.forEach(quotes => {
                    allQuotesData.push(...quotes);
                });

                // Sort by date (newest first)
                allQuotesData.sort((a, b) => {
                    const dateA = a.quote.createdAt?.toDate?.() || a.order.createdAt?.toDate?.() || 0;
                    const dateB = b.quote.createdAt?.toDate?.() || b.order.createdAt?.toDate?.() || 0;
                    return dateB - dateA;
                });

                setAllQuotes(allQuotesData);
            } catch (error) {
                console.error('Error loading vendor quotes:', error);
            } finally {
                setIsLoading(false);
            }
        };

        loadVendorQuotesFromExistingData();

        // Also fetch fresh data after initial load to ensure we have the latest quotes
        setTimeout(() => {
            refreshQuotesData();
        }, 1000); // Small delay to let initial load complete first
    }, [vendor, allOrders]);

    // Filter and sort quotes based on search and filters
    const filteredQuotes = useMemo(() => {
        let filtered = [...allQuotes];

        // Apply search filter
        if (searchQuery) {
            const lowercasedQuery = searchQuery.toLowerCase();
            filtered = filtered.filter(item =>
                (item.order.orderTitle && item.order.orderTitle.toLowerCase().includes(lowercasedQuery)) ||
                (item.order.description && item.order.description.toLowerCase().includes(lowercasedQuery)) ||
                (item.order.serialNumber && item.order.serialNumber.toLowerCase().includes(lowercasedQuery)) ||
                (item.quote.notes && item.quote.notes.toLowerCase().includes(lowercasedQuery))
            );
        }

        // Apply order status filter
        if (statusFilter !== 'All') {
            filtered = filtered.filter(item => item.order.status === statusFilter);
        }

        // Apply quote status filter
        if (quoteStatusFilter !== 'All') {
            filtered = filtered.filter(item => item.quote.status === quoteStatusFilter);
        }

        // Apply date filter
        if (dateFilter !== 'all') {
            const now = new Date();
            let startDate;

            switch (dateFilter) {
                case '3m':
                    startDate = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
                    break;
                case '6m':
                    startDate = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
                    break;
                case '1y':
                    startDate = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
                    break;
                case 'custom':
                    if (customStartDate && customEndDate) {
                        startDate = new Date(customStartDate);
                        const endDate = new Date(customEndDate);
                        filtered = filtered.filter(item => {
                            const quoteDate = item.quote.createdAt?.toDate?.() || item.order.createdAt?.toDate?.() || new Date(0);
                            return quoteDate >= startDate && quoteDate <= endDate;
                        });
                        break;
                    }
                    return filtered;
                default:
                    return filtered;
            }

            if (dateFilter !== 'custom') {
                filtered = filtered.filter(item => {
                    const quoteDate = item.quote.createdAt?.toDate?.() || item.order.createdAt?.toDate?.() || new Date(0);
                    return quoteDate >= startDate;
                });
            }
        }

        // Apply sorting
        filtered.sort((a, b) => {
            let valA, valB;

            switch (sortBy) {
                case 'date':
                    valA = a.quote.createdAt?.toDate?.() || a.order.createdAt?.toDate?.() || 0;
                    valB = b.quote.createdAt?.toDate?.() || b.order.createdAt?.toDate?.() || 0;
                    break;
                case 'price':
                    valA = a.quote.price || 0;
                    valB = b.quote.price || 0;
                    break;
                case 'quantity':
                    valA = a.quote.quantity || 0;
                    valB = b.quote.quantity || 0;
                    break;
                case 'orderTitle':
                    valA = a.order.orderTitle?.toLowerCase() || '';
                    valB = b.order.orderTitle?.toLowerCase() || '';
                    break;
                default:
                    valA = 0;
                    valB = 0;
            }

            if (typeof valA === 'string' && typeof valB === 'string') {
                return sortOrder === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
            } else {
                return sortOrder === 'asc' ? valA - valB : valB - valA;
            }
        });

        return filtered;
    }, [allQuotes, searchQuery, statusFilter, quoteStatusFilter, sortBy, sortOrder, dateFilter, customStartDate, customEndDate]);

    // Get unique statuses for filter dropdown
    const availableStatuses = useMemo(() => {
        const statuses = new Set(allQuotes.map(item => item.order.status));
        return ['All', ...Array.from(statuses).sort()];
    }, [allQuotes]);

    // Get unique quote statuses for filter dropdown
    const availableQuoteStatuses = useMemo(() => {
        const statuses = new Set(allQuotes.map(item => item.quote.status).filter(Boolean));
        return ['All', ...Array.from(statuses).sort()];
    }, [allQuotes]);

    if (!vendor) {
        return <LoadingScreen message="Loading Vendor Details..." />;
    }

    return (
        <div>
            <div className="flex items-center justify-between mb-4">
                <button onClick={onBack} className="text-sm font-medium">&larr; Back to Vendors</button>
                <div className="flex items-center gap-3">
                    <button
                        onClick={refreshQuotesData}
                        disabled={isRefreshing}
                        className="flex items-center gap-2 border border-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
                        title="Refresh quotes data"
                    >
                        <RefreshCw size={18} className={isRefreshing ? 'animate-spin' : ''} />
                        {isRefreshing ? 'Refreshing...' : 'Refresh'}
                    </button>
                    <button
                        onClick={() => setShowReportModal(true)}
                        className="flex items-center gap-2 bg-black text-white px-4 py-2 rounded-md hover:bg-gray-800 transition-colors"
                        title="Generate Vendor Report"
                    >
                        <FileText size={18} />
                        Generate Report
                    </button>
                </div>
            </div>
            <div className="flex items-center justify-between mb-2">
                <h1 className="text-2xl font-bold">Quote History for {vendor.name}</h1>
            </div>
            <p className="text-gray-500 mb-6">Showing all quotes submitted by this vendor across all orders.</p>

            {/* Summary Statistics */}
            {!isLoading && allQuotes.length > 0 && (
                <div className="mb-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                        <div className="text-2xl font-bold text-blue-800">{allQuotes.length}</div>
                        <div className="text-sm text-blue-600">Total Quotes</div>
                    </div>
                    <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                        <div className="text-2xl font-bold text-orange-800">
                            ₹{formatINR(allQuotes.reduce((sum, item) => sum + (item.quote.price || 0), 0))}
                        </div>
                        <div className="text-sm text-orange-600">Total Value</div>
                    </div>
                </div>
            )}

            {/* Vendor Details Section - Always Visible */}
            <div className="mb-6 bg-white border rounded-lg p-6">
                <h2 className="text-lg font-semibold mb-4 text-gray-800">Vendor Information</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Vendor Information Box */}
                    <div className="bg-white border rounded-lg p-4 hover:shadow-lg transition-shadow duration-200">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="font-medium text-gray-800 flex items-center gap-2">
                                <Building size={18} className="text-black" />
                                Vendor Information
                            </h3>
                            <button
                                onClick={() => setEditingSection(editingSection === 'vendor' ? null : 'vendor')}
                                className="p-1 rounded hover:bg-gray-100 transition-colors"
                                title="Edit vendor information"
                            >
                                <Edit size={16} className="text-gray-500 hover:text-gray-700" />
                            </button>
                        </div>
                        {editingSection === 'vendor' ? (
                            <VendorEditForm
                                vendor={vendorData}
                                onSave={saveVendorData}
                                onCancel={() => setEditingSection(null)}
                            />
                        ) : (
                            <div className="space-y-2 text-sm">
                                <p><span className="font-medium">Name:</span> {vendorData.name || 'Not provided'}</p>
                                <p><span className="font-medium">Phone:</span> {vendorData.phone || '+91 98765 43210'}</p>
                                <p><span className="font-medium">Email:</span> {vendorData.email || 'vendor@example.com'}</p>
                                <p><span className="font-medium">Preferred Communication:</span> {vendorData.preferredCommunication || 'Email'}</p>
                            </div>
                        )}
                    </div>

                    {/* Specialization Box */}
                    <div className="bg-white border rounded-lg p-4 hover:shadow-lg transition-shadow duration-200">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="font-medium text-gray-800 flex items-center gap-2">
                                <Package size={18} className="text-black" />
                                Specialization
                            </h3>
                            <button
                                onClick={() => setEditingSection(editingSection === 'specialization' ? null : 'specialization')}
                                className="p-1 rounded hover:bg-gray-100 transition-colors"
                                title="Edit specialization"
                            >
                                <Edit size={16} className="text-gray-500 hover:text-gray-700" />
                            </button>
                        </div>
                        {editingSection === 'specialization' ? (
                            <SpecializationEditForm
                                vendor={vendorData}
                                onSave={saveVendorData}
                                onCancel={() => setEditingSection(null)}
                            />
                        ) : (
                            <div className="space-y-2 text-sm">
                                <p><span className="font-medium">Products:</span> {vendorData.products || 'Valves, Pumps, Pipes'}</p>
                                <p><span className="font-medium">Industries:</span> {vendorData.industries || 'Manufacturing, Oil & Gas'}</p>
                                <p><span className="font-medium">Brands:</span> {vendorData.brands || 'Bosch, Siemens, ABB, Schneider Electric'}</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Search and Filter Controls */}
            <div className="mb-6 space-y-4">
                <div className="flex flex-col sm:flex-row gap-4">
                    {/* Search Input */}
                    <div className="flex-1">
                        <div className="relative">
                            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search orders, descriptions, or quote notes..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                            />
                        </div>
                    </div>

                    {/* Date Range Filter */}
                    <div className="relative" ref={dateDropdownRef}>
                        <Tooltip text="Filter by Date Range">
                            <button
                                onClick={() => setShowDateDropdown(!showDateDropdown)}
                                className="flex items-center gap-2 px-3 py-2 text-sm font-medium bg-white border border-gray-300 rounded-md hover:bg-gray-50 interactive-button"
                            >
                                <Calendar size={16} />
                                <span>
                                    {dateFilter === 'all' ? 'All Time' :
                                        dateFilter === '3m' ? 'Last 3M' :
                                            dateFilter === '6m' ? 'Last 6M' :
                                                dateFilter === '1y' ? 'Last 1Y' :
                                                    dateFilter === 'custom' ? 'Custom' : 'All Time'}
                                </span>
                            </button>
                        </Tooltip>
                        {showDateDropdown && (
                            <div className="absolute left-0 mt-2 w-48 bg-white border rounded-md shadow-lg z-20">
                                <button
                                    onClick={() => {
                                        setDateFilter('all');
                                        setShowDateDropdown(false);
                                    }}
                                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${dateFilter === 'all' ? 'bg-gray-50 font-semibold' : ''}`}
                                >
                                    All Time
                                </button>
                                <button
                                    onClick={() => {
                                        setDateFilter('3m');
                                        setShowDateDropdown(false);
                                    }}
                                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${dateFilter === '3m' ? 'bg-gray-50 font-semibold' : ''}`}
                                >
                                    Last 3 Months
                                </button>
                                <button
                                    onClick={() => {
                                        setDateFilter('6m');
                                        setShowDateDropdown(false);
                                    }}
                                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${dateFilter === '6m' ? 'bg-gray-50 font-semibold' : ''}`}
                                >
                                    Last 6 Months
                                </button>
                                <button
                                    onClick={() => {
                                        setDateFilter('1y');
                                        setShowDateDropdown(false);
                                    }}
                                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${dateFilter === '1y' ? 'bg-gray-50 font-semibold' : ''}`}
                                >
                                    Last 1 Year
                                </button>
                                <button
                                    onClick={() => {
                                        setDateFilter('custom');
                                        setShowDateDropdown(false);
                                    }}
                                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${dateFilter === 'custom' ? 'bg-gray-50 font-semibold' : ''}`}
                                >
                                    Custom Range
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Order Status Filter */}
                    <div className="relative" ref={orderStatusRef}>
                        <Tooltip text="Filter by Order Status">
                            <button
                                onClick={() => setIsOrderStatusOpen(!isOrderStatusOpen)}
                                className="flex items-center gap-2 px-3 py-2 text-sm font-medium bg-white border border-gray-300 rounded-md hover:bg-gray-50 interactive-button"
                            >
                                <span>Order: {statusFilter}</span>
                                <span className="text-xs text-gray-500">
                                    ({filteredQuotes.filter(item => statusFilter === 'All' || item.order.status === statusFilter).length})
                                </span>
                            </button>
                        </Tooltip>
                        {isOrderStatusOpen && (
                            <div className="absolute left-0 mt-2 w-48 bg-white border rounded-md shadow-lg z-20">
                                {availableStatuses.map(status => (
                                    <button
                                        key={status}
                                        onClick={() => {
                                            setStatusFilter(status);
                                            setIsOrderStatusOpen(false);
                                        }}
                                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                    >
                                        {status} ({filteredQuotes.filter(item => status === 'All' || item.order.status === status).length})
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Quote Status Filter */}
                    <div className="relative" ref={quoteStatusRef}>
                        <Tooltip text="Filter by Quote Status">
                            <button
                                onClick={() => setIsQuoteStatusOpen(!isQuoteStatusOpen)}
                                className="flex items-center gap-2 px-3 py-2 text-sm font-medium bg-white border border-gray-300 rounded-md hover:bg-gray-50 interactive-button"
                            >
                                <span>Quote: {quoteStatusFilter === 'All' ? 'All' : quoteStatusDisplayNames[quoteStatusFilter] || quoteStatusFilter}</span>
                                <span className="text-xs text-gray-500">
                                    ({filteredQuotes.filter(item => quoteStatusFilter === 'All' || item.quote.status === quoteStatusFilter).length})
                                </span>
                            </button>
                        </Tooltip>
                        {isQuoteStatusOpen && (
                            <div className="absolute left-0 mt-2 w-48 bg-white border rounded-md shadow-lg z-20">
                                {availableQuoteStatuses.map(status => (
                                    <button
                                        key={status}
                                        onClick={() => {
                                            setQuoteStatusFilter(status);
                                            setIsQuoteStatusOpen(false);
                                        }}
                                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                    >
                                        {status === 'All' ? 'All' : quoteStatusDisplayNames[status] || status} ({filteredQuotes.filter(item => status === 'All' || item.quote.status === status).length})
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Sort Controls */}
                    <div className="flex gap-2">
                        <div className="relative" ref={sortRef}>
                            <Tooltip text="Sort quotes by field">
                                <button
                                    onClick={() => setIsSortOpen(!isSortOpen)}
                                    className="flex items-center gap-2 px-3 py-2 text-sm font-medium bg-white border border-gray-300 rounded-md hover:bg-gray-50 interactive-button"
                                >
                                    <span>Sort by: {sortBy === 'date' ? 'Date' : sortBy === 'price' ? 'Price' : sortBy === 'quantity' ? 'Quantity' : 'Order Title'}</span>
                                </button>
                            </Tooltip>
                            {isSortOpen && (
                                <div className="absolute right-0 mt-2 w-48 bg-white border rounded-md shadow-lg z-20">
                                    <button
                                        onClick={() => {
                                            setSortBy('date');
                                            setIsSortOpen(false);
                                        }}
                                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                    >
                                        Date
                                    </button>
                                    <button
                                        onClick={() => {
                                            setSortBy('price');
                                            setIsSortOpen(false);
                                        }}
                                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                    >
                                        Price
                                    </button>
                                    <button
                                        onClick={() => {
                                            setSortBy('quantity');
                                            setIsSortOpen(false);
                                        }}
                                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                    >
                                        Quantity
                                    </button>
                                    <button
                                        onClick={() => {
                                            setSortBy('orderTitle');
                                            setIsSortOpen(false);
                                        }}
                                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                    >
                                        Order Title
                                    </button>
                                </div>
                            )}
                        </div>
                        <Tooltip text={`Sort ${sortOrder === 'asc' ? 'Ascending' : 'Descending'}`}>
                            <button
                                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                                className="p-2 bg-gray-100 rounded-md hover:bg-gray-200 interactive-button"
                            >
                                <ArrowDownUp size={16} />
                            </button>
                        </Tooltip>
                    </div>
                </div>

                {/* Custom Date Range Picker */}
                {dateFilter === 'custom' && (
                    <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg border">
                        <div className="flex items-center gap-2">
                            <Calendar size={16} className="text-gray-500" />
                            <span className="text-sm font-medium text-gray-700">Custom Date Range:</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <input
                                type="date"
                                value={customStartDate}
                                onChange={(e) => setCustomStartDate(e.target.value)}
                                className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                                placeholder="Start Date"
                            />
                            <span className="text-gray-500">to</span>
                            <input
                                type="date"
                                value={customEndDate}
                                onChange={(e) => setCustomEndDate(e.target.value)}
                                className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                                placeholder="End Date"
                            />
                        </div>
                    </div>
                )}

                {/* Results Summary */}
                <div className="text-sm text-gray-600">
                    Showing {filteredQuotes.length} of {allQuotes.length} quotes
                    {searchQuery && ` for "${searchQuery}"`}
                    {statusFilter !== 'All' && ` with order status "${statusFilter}"`}
                    {quoteStatusFilter !== 'All' && ` with quote status "${quoteStatusDisplayNames[quoteStatusFilter] || quoteStatusFilter}"`}
                    {dateFilter !== 'all' && (
                        dateFilter === 'custom' ?
                            (customStartDate && customEndDate ? ` from ${customStartDate} to ${customEndDate}` : '') :
                            ` from last ${dateFilter === '3m' ? '3 months' : dateFilter === '6m' ? '6 months' : '1 year'}`
                    )}
                </div>
            </div>

            {/* Quotes List */}
            <div className="space-y-4">
                {isLoading ? (
                    <div className="text-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black mx-auto mb-4"></div>
                        <p className="text-gray-600">Loading vendor quotes...</p>
                        <p className="text-sm text-gray-500 mt-2">This will be much faster now!</p>
                    </div>
                ) : filteredQuotes.length > 0 ? (
                    filteredQuotes.map(item => (
                        <div key={`${item.orderId}-${item.id}`} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 hover:border-black transition-all cursor-pointer" onClick={() => onSelectOrder(item.order)}>
                            <div className="flex flex-col lg:flex-row gap-4">
                                {/* Order Information */}
                                <div className="flex-1">
                                    <div className="flex items-start justify-between mb-2">
                                        <div className="flex-1">
                                            <h3 className="font-bold text-lg truncate flex items-center">
                                                {truncateOrderTitle(item.order.orderTitle)}
                                                {item.order.orderTitle !== truncateOrderTitle(item.order.orderTitle) && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            // Toggle between truncated and full title
                                                            const titleElement = e.target.closest('h3');
                                                            if (titleElement) {
                                                                const isCurrentlyTruncated = titleElement.textContent.includes('...');
                                                                if (isCurrentlyTruncated) {
                                                                    titleElement.innerHTML = `${item.order.orderTitle} <span class="font-normal text-gray-600 ml-2">(Qty: ${item.order.quantity !== null && item.order.quantity !== undefined && item.order.quantity !== '' ? item.order.quantity : 'N/A'})</span>`;
                                                                } else {
                                                                    titleElement.innerHTML = `${truncateOrderTitle(item.order.orderTitle)} <span class="font-normal text-gray-600 ml-2">(Qty: ${item.order.quantity !== null && item.order.quantity !== undefined && item.order.quantity !== '' ? item.order.quantity : 'N/A'})</span>`;
                                                                }
                                                            }
                                                        }}
                                                        className="text-gray-400 hover:text-gray-600 ml-1 flex-shrink-0 transition-colors p-1 -m-1 rounded hover:bg-gray-100"
                                                        title="Toggle full title"
                                                    >
                                                        <ChevronDownIcon
                                                            size={16}
                                                            className="transition-transform duration-200"
                                                        />
                                                    </button>
                                                )}
                                                <span className="font-normal text-gray-600 ml-2">(Qty: {item.order.quantity !== null && item.order.quantity !== undefined && item.order.quantity !== '' ? item.order.quantity : 'N/A'})</span>
                                            </h3>
                                            <p className="text-sm text-gray-500">{item.order.serialNumber}</p>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Tooltip text={`Order Status: ${item.order.status}`}>
                                                <span className={`px-3 py-1 text-xs font-semibold rounded-full ${statusColors[item.order.status] || 'bg-gray-100 text-gray-800'}`}>
                                                    {item.order.status}
                                                </span>
                                            </Tooltip>
                                            {item.quote.status && (
                                                <Tooltip text={`Quote Status: ${quoteStatusDisplayNames[item.quote.status] || item.quote.status}`}>
                                                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${quoteStatusColors[item.quote.status] || 'bg-gray-100 text-gray-800'}`}>
                                                        {quoteStatusDisplayNames[item.quote.status] || item.quote.status}
                                                    </span>
                                                </Tooltip>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2 mb-2">
                                        <p className="text-sm text-gray-600 flex-grow">{item.order.description}</p>
                                        <Tooltip text="Copy description">
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    navigator.clipboard.writeText(item.order.description || '');
                                                }}
                                                className="p-1 rounded-md hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600 flex-shrink-0"
                                            >
                                                <Copy size={12} />
                                            </button>
                                        </Tooltip>
                                    </div>

                                    {/* Quote Details */}
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                                        <div>
                                            <span className="font-medium text-gray-700">Price:</span>
                                            <p className="text-gray-900">₹{item.quote.price ? item.quote.price.toLocaleString() : 'N/A'}</p>
                                        </div>
                                        <div>
                                            <span className="font-medium text-gray-700">Quantity:</span>
                                            <p className="text-gray-900">{item.quote.quantity || 'N/A'}</p>
                                        </div>
                                        <div>
                                            <span className="font-medium text-gray-700">Timeline:</span>
                                            <p className="text-gray-900">{item.quote.deliveryTimeline ? `${item.quote.deliveryTimeline} days` : 'N/A'}</p>
                                        </div>
                                        <div>
                                            <span className="font-medium text-gray-700">Advance:</span>
                                            <p className="text-gray-900">{item.quote.advancePayment ? `${item.quote.advancePayment}%` : 'N/A'}</p>
                                        </div>
                                    </div>

                                    {item.quote.notes && (
                                        <div className="mt-2 p-2 bg-gray-50 rounded text-sm text-gray-700">
                                            <span className="font-medium">Notes:</span> {item.quote.notes}
                                        </div>
                                    )}

                                    <div className="text-xs text-gray-500 mt-2">
                                        Quote submitted: {item.quote.createdAt ? new Date(item.quote.createdAt.toDate()).toLocaleDateString('en-GB') : 'Unknown date'}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="text-center text-gray-500 mt-8 py-8 border-2 border-dashed rounded-lg">
                        {searchQuery || statusFilter !== 'All' || quoteStatusFilter !== 'All' ? (
                            <div>
                                <p className="mb-2">No quotes found</p>
                                <p className="text-sm">Try adjusting your search or filters.</p>
                            </div>
                        ) : (
                            <p>This vendor has not submitted any quotes yet.</p>
                        )}
                    </div>
                )}
            </div>

            {/* Report Modal */}
            {showReportModal && (
                <ReportModal
                    vendor={vendor}
                    allQuotes={allQuotes}
                    fetchFreshQuotesData={fetchFreshQuotesData}
                    onClose={() => setShowReportModal(false)}
                />
            )}
        </div>
    );
};

// Report Modal Component
const ReportModal = ({ vendor, allQuotes, fetchFreshQuotesData, onClose }) => {
    const [reportFormat, setReportFormat] = useState('excel');
    const [dateRange, setDateRange] = useState('all');
    const [customStartDate, setCustomStartDate] = useState('');
    const [customEndDate, setCustomEndDate] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);

    // Filter quotes based on selected date range
    const filterQuotesByDateRange = (quotes) => {
        if (dateRange === 'all') return quotes;

        const now = new Date();
        let startDate;

        switch (dateRange) {
            case '3m':
                startDate = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
                break;
            case '6m':
                startDate = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
                break;
            case '1y':
                startDate = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
                break;
            case 'custom':
                if (customStartDate && customEndDate) {
                    startDate = new Date(customStartDate);
                    const endDate = new Date(customEndDate);
                    return quotes.filter(item => {
                        const quoteDate = item.quote.createdAt?.toDate?.() || item.order.createdAt?.toDate?.() || new Date(0);
                        return quoteDate >= startDate && quoteDate <= endDate;
                    });
                }
                return quotes;
            default:
                return quotes;
        }

        if (dateRange !== 'custom') {
            return quotes.filter(item => {
                const quoteDate = item.quote.createdAt?.toDate?.() || item.order.createdAt?.toDate?.() || new Date(0);
                return quoteDate >= startDate;
            });
        }

        return quotes;
    };

    const generateReport = async () => {
        setIsGenerating(true);
        try {
            // Always fetch fresh data before generating report
            console.log('Fetching fresh quotes data for report...');
            const freshQuotes = await fetchFreshQuotesData();
            console.log(`Found ${freshQuotes.length} quotes for report`);

            // Filter quotes based on selected date range
            const filteredQuotes = filterQuotesByDateRange(freshQuotes);
            console.log(`Filtered to ${filteredQuotes.length} quotes for date range: ${dateRange}`);

            if (reportFormat === 'excel') {
                await generateExcelReport(vendor, filteredQuotes);
            } else if (reportFormat === 'pdf') {
                await generatePDFReport(vendor, filteredQuotes);
            } else {
                await generateWordReport(vendor, filteredQuotes);
            }
            onClose();
        } catch (error) {
            console.error('Error generating report:', error);
            alert('Failed to generate report. Please try again.');
        } finally {
            setIsGenerating(false);
        }
    };

    // Helper function to generate PI filename
    const getPIFilename = (proformaInvoiceUrl, orderTitle, quoteDate) => {
        if (!proformaInvoiceUrl) return 'N/A';

        // Clean order title for filename (remove special characters)
        const cleanTitle = (orderTitle || 'Unknown').replace(/[^a-zA-Z0-9\s-]/g, '').replace(/\s+/g, '-');

        // Format date as YYYY-MM-DD
        const dateStr = quoteDate ? new Date(quoteDate.toDate ? quoteDate.toDate() : quoteDate).toISOString().split('T')[0] : 'Unknown';

        return `PI-${cleanTitle}-${dateStr}`;
    };

    const generateExcelReport = async (vendor, quotes) => {
        // Sort quotes chronologically by quote creation date (oldest first)
        const sortedQuotes = [...quotes].sort((a, b) => {
            const dateA = a.quote.createdAt?.toDate?.() || a.order.createdAt?.toDate?.() || new Date(0);
            const dateB = b.quote.createdAt?.toDate?.() || b.order.createdAt?.toDate?.() || new Date(0);
            return dateA - dateB; // Ascending order (oldest first)
        });

        try {
            // Import xlsx dynamically to avoid SSR issues
            const XLSX = await import('xlsx');

            // Create adaptive workbook that works on both PC and mobile
            const workbook = XLSX.utils.book_new();

            // Calculate summary statistics using sorted quotes
            const totalValue = sortedQuotes.reduce((sum, item) => sum + (item.quote.price || 0), 0);
            const avgValue = sortedQuotes.length > 0 ? totalValue / sortedQuotes.length : 0;

            // Status breakdown using sorted quotes
            const statusBreakdown = sortedQuotes.reduce((acc, item) => {
                const status = item.order.status || 'Unknown';
                acc[status] = (acc[status] || 0) + 1;
                return acc;
            }, {});

            // Create adaptive data structure - single sheet that works on both PC and mobile
            const adaptiveData = [];

            // Header section
            adaptiveData.push(['VENDOR QUOTE HISTORY REPORT']);
            adaptiveData.push(['']);
            adaptiveData.push(['Vendor Name:', vendor.name || 'N/A']);
            adaptiveData.push(['City:', vendor.city || 'N/A']);
            adaptiveData.push(['Email:', vendor.email || 'N/A']);
            adaptiveData.push(['Phone:', vendor.phone || 'N/A']);
            adaptiveData.push(['Report Generated:', new Date().toLocaleDateString('en-GB')]);
            adaptiveData.push(['']);

            // Summary Statistics
            adaptiveData.push(['SUMMARY STATISTICS']);
            adaptiveData.push(['']);
            adaptiveData.push(['Total Quotes:', sortedQuotes.length]);
            adaptiveData.push(['Total Value (₹):', totalValue.toLocaleString()]);
            adaptiveData.push(['Average Value (₹):', avgValue.toLocaleString()]);
            adaptiveData.push(['']);

            // Status Breakdown
            adaptiveData.push(['STATUS BREAKDOWN']);
            adaptiveData.push(['']);
            Object.entries(statusBreakdown).forEach(([status, count]) => {
                adaptiveData.push([status, count]);
            });
            adaptiveData.push(['']);

            // Quote Details Header
            adaptiveData.push(['QUOTE DETAILS']);
            adaptiveData.push(['']);
            adaptiveData.push([
                'Order Title',
                'Serial #',
                'Status',
                'Price (₹)',
                'Qty',
                'Timeline',
                'Advance %',
                'Quote Date',
                'RFQ Sent',
                'PI',
                'Delivery Remarks',
                'Notes'
            ]);

            // Quote Details Data - using sorted quotes
            sortedQuotes.forEach(item => {
                const piFilename = getPIFilename(
                    item.quote.proformaInvoiceUrl,
                    item.order.orderTitle,
                    item.quote.createdAt
                );

                adaptiveData.push([
                    item.order.orderTitle || 'N/A',
                    item.order.serialNumber || 'N/A',
                    statusDisplayNames[item.order.status] || item.order.status || 'N/A',
                    item.quote.price || 'N/A',
                    item.quote.quantity || 'N/A',
                    item.quote.deliveryTimeline ? `${item.quote.deliveryTimeline} days` : 'N/A',
                    item.quote.advancePayment ? `${item.quote.advancePayment}%` : 'N/A',
                    item.quote.createdAt ? new Date(item.quote.createdAt.toDate()).toLocaleDateString('en-GB') : 'N/A',
                    item.order.createdAt ? new Date(item.order.createdAt.toDate()).toLocaleDateString('en-GB') : 'N/A',
                    piFilename,
                    item.order.deliveryRemarks || 'N/A',
                    item.quote.notes || 'N/A'
                ]);
            });

            // Create worksheet
            const worksheet = XLSX.utils.aoa_to_sheet(adaptiveData);

            // Adaptive column widths - optimized for both PC and mobile
            worksheet['!cols'] = [
                { width: 30 }, // Order Title
                { width: 12 }, // Serial Number
                { width: 12 }, // Status
                { width: 12 }, // Price
                { width: 8 },  // Quantity
                { width: 10 }, // Timeline
                { width: 10 }, // Advance Payment
                { width: 12 }, // Quote Date
                { width: 12 }, // RFQ Sent
                { width: 20 }, // PI
                { width: 25 }, // Delivery Remarks
                { width: 25 }  // Notes
            ];

            // Add some basic formatting for better readability
            const range = XLSX.utils.decode_range(worksheet['!ref']);

            // Style header rows
            for (let row = 0; row <= range.e.r; row++) {
                const cellAddress = XLSX.utils.encode_cell({ r: row, c: 0 });
                if (!worksheet[cellAddress]) continue;

                const cell = worksheet[cellAddress];

                // Style main headers
                if (cell.v && typeof cell.v === 'string') {
                    if (cell.v.includes('VENDOR QUOTE HISTORY REPORT')) {
                        cell.s = {
                            font: { bold: true, size: 16 },
                            alignment: { horizontal: 'center' }
                        };
                    } else if (cell.v.includes('SUMMARY STATISTICS') ||
                        cell.v.includes('STATUS BREAKDOWN') ||
                        cell.v.includes('QUOTE DETAILS')) {
                        cell.s = {
                            font: { bold: true, size: 14 },
                            fill: { fgColor: { rgb: 'E3F2FD' } }
                        };
                    } else if (cell.v === 'Order Title' || cell.v === 'Serial #' ||
                        cell.v === 'Status' || cell.v === 'Price (₹)' ||
                        cell.v === 'Qty' || cell.v === 'Timeline' ||
                        cell.v === 'Advance %' || cell.v === 'Quote Date' ||
                        cell.v === 'RFQ Sent' || cell.v === 'PI' || cell.v === 'Notes') {
                        cell.s = {
                            font: { bold: true },
                            fill: { fgColor: { rgb: 'F5F5F5' } }
                        };
                    }
                }
            }

            // Add worksheet to workbook
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Vendor Report');

            // Export the workbook
            XLSX.writeFile(workbook, `${vendor.name}_Report.xlsx`);

        } catch (error) {
            console.error('Error generating Excel report:', error);
            // Fallback to CSV if Excel generation fails
            const csvContent = createCSVContent(vendor, sortedQuotes);
            downloadFile(csvContent, `${vendor.name}_Report.csv`, 'text/csv');
        }
    };



    const generatePDFReport = async (vendor, quotes) => {
        try {
            // Import jsPDF dynamically to avoid SSR issues
            const jsPDF = (await import('jspdf')).default;
            const autoTable = (await import('jspdf-autotable')).default;

            // Sort quotes chronologically by quote creation date (oldest first)
            const sortedQuotes = [...quotes].sort((a, b) => {
                const dateA = a.quote.createdAt?.toDate?.() || a.order.createdAt?.toDate?.() || new Date(0);
                const dateB = b.quote.createdAt?.toDate?.() || b.order.createdAt?.toDate?.() || new Date(0);
                return dateA - dateB; // Ascending order (oldest first)
            });

            // Create PDF document with proper page size
            const doc = new jsPDF('p', 'mm', 'a4');

            // Set document properties
            doc.setProperties({
                title: `${vendor.name} - Vendor Report`,
                subject: 'Vendor Quote History Report',
                author: 'UrbanTech Order Management',
                creator: 'UrbanTech System'
            });

            // Helper function to truncate text
            const truncateText = (text, maxLength = 25) => {
                if (!text) return 'N/A';
                return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
            };

            // Calculate summary statistics using sorted quotes
            const totalValue = sortedQuotes.reduce((sum, item) => sum + (item.quote.price || 0), 0);
            const avgValue = sortedQuotes.length > 0 ? totalValue / sortedQuotes.length : 0;

            // Status breakdown using sorted quotes
            const statusBreakdown = sortedQuotes.reduce((acc, item) => {
                const status = item.order.status || 'Unknown';
                acc[status] = (acc[status] || 0) + 1;
                return acc;
            }, {});

            // Add title with better styling
            doc.setFontSize(18);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(25, 25, 112); // Dark blue color
            doc.text('VENDOR QUOTE HISTORY REPORT', 105, 20, { align: 'center' });

            // Add vendor name
            doc.setFontSize(14);
            doc.setTextColor(0, 0, 0); // Black color
            doc.text(vendor.name || 'N/A', 105, 30, { align: 'center' });

            // Add report date
            doc.setFontSize(10);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(100, 100, 100); // Gray color
            doc.text(`Report Generated: ${new Date().toLocaleDateString('en-GB')}`, 105, 38, { align: 'center' });

            let yPosition = 50;

            // Vendor Information with better styling
            doc.setFontSize(12);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(0, 0, 0);
            doc.text('VENDOR INFORMATION', 20, yPosition);
            yPosition += 8;

            // Vendor info table
            const vendorData = [
                ['Name', vendor.name || 'N/A'],
                ['City', vendor.city || 'N/A'],
                ['Email', vendor.email || 'N/A'],
                ['Phone', vendor.phone || 'N/A']
            ];

            autoTable(doc, {
                body: vendorData,
                startY: yPosition,
                theme: 'grid',
                styles: {
                    fontSize: 9,
                    cellPadding: 3,
                    lineColor: [200, 200, 200],
                    lineWidth: 0.5,
                },
                headStyles: {
                    fillColor: [240, 240, 240],
                    textColor: [0, 0, 0],
                    fontStyle: 'bold',
                },
                columnStyles: {
                    0: { cellWidth: 25, halign: 'left' },
                    1: { cellWidth: 60, halign: 'left' }
                },
                margin: { left: 20, right: 20 }
            });

            yPosition = doc.lastAutoTable.finalY + 10;

            // Summary Statistics with better styling
            doc.setFontSize(12);
            doc.setFont('helvetica', 'bold');
            doc.text('SUMMARY STATISTICS', 20, yPosition);
            yPosition += 8;

            const summaryData = [
                ['Total Quotes', sortedQuotes.length.toString()],
                ['Total Value', `₹${totalValue.toLocaleString()}`],
                ['Average Value', `₹${avgValue.toLocaleString()}`]
            ];

            autoTable(doc, {
                body: summaryData,
                startY: yPosition,
                theme: 'grid',
                styles: {
                    fontSize: 9,
                    cellPadding: 3,
                    lineColor: [200, 200, 200],
                    lineWidth: 0.5,
                },
                headStyles: {
                    fillColor: [240, 240, 240],
                    textColor: [0, 0, 0],
                    fontStyle: 'bold',
                },
                columnStyles: {
                    0: { cellWidth: 40, halign: 'left' },
                    1: { cellWidth: 45, halign: 'right' }
                },
                margin: { left: 20, right: 20 }
            });

            yPosition = doc.lastAutoTable.finalY + 10;

            // Status Breakdown with better styling
            doc.setFontSize(12);
            doc.setFont('helvetica', 'bold');
            doc.text('STATUS BREAKDOWN', 20, yPosition);
            yPosition += 8;

            const statusData = Object.entries(statusBreakdown).map(([status, count]) => [status, count.toString()]);

            autoTable(doc, {
                body: statusData,
                startY: yPosition,
                theme: 'grid',
                styles: {
                    fontSize: 9,
                    cellPadding: 3,
                    lineColor: [200, 200, 200],
                    lineWidth: 0.5,
                },
                headStyles: {
                    fillColor: [240, 240, 240],
                    textColor: [0, 0, 0],
                    fontStyle: 'bold',
                },
                columnStyles: {
                    0: { cellWidth: 60, halign: 'left' },
                    1: { cellWidth: 25, halign: 'center' }
                },
                margin: { left: 20, right: 20 }
            });

            yPosition = doc.lastAutoTable.finalY + 15;

            // Quote Details Table with improved styling
            doc.setFontSize(12);
            doc.setFont('helvetica', 'bold');
            doc.text('QUOTE DETAILS', 20, yPosition);
            yPosition += 10;

            // Prepare table data with truncated text - using sorted quotes
            const tableData = sortedQuotes.map(item => {
                const piFilename = getPIFilename(
                    item.quote.proformaInvoiceUrl,
                    item.order.orderTitle,
                    item.quote.createdAt
                );

                return [
                    truncateText(item.order.orderTitle, 20),
                    item.order.serialNumber || 'N/A',
                    statusDisplayNames[item.order.status] || item.order.status || 'N/A',
                    item.quote.price ? `₹${item.quote.price.toLocaleString()}` : 'N/A',
                    item.quote.quantity || 'N/A',
                    item.quote.deliveryTimeline ? `${item.quote.deliveryTimeline}d` : 'N/A',
                    item.quote.advancePayment ? `${item.quote.advancePayment}%` : 'N/A',
                    item.quote.createdAt ? new Date(item.quote.createdAt.toDate()).toLocaleDateString('en-GB') : 'N/A',
                    item.order.createdAt ? new Date(item.order.createdAt.toDate()).toLocaleDateString('en-GB') : 'N/A',
                    truncateText(piFilename, 15),
                    truncateText(item.order.deliveryRemarks, 15),
                    truncateText(item.quote.notes, 15)
                ];
            });

            // Add table with improved styling
            autoTable(doc, {
                head: [['Order Title', 'Serial #', 'Status', 'Price (₹)', 'Qty', 'Timeline', 'Advance %', 'Quote Date', 'RFQ Sent', 'PI', 'Delivery Remarks', 'Notes']],
                body: tableData,
                startY: yPosition,
                theme: 'grid',
                styles: {
                    fontSize: 7,
                    cellPadding: 2,
                    lineColor: [200, 200, 200],
                    lineWidth: 0.3,
                    overflow: 'linebreak',
                    halign: 'left',
                },
                headStyles: {
                    fillColor: [25, 25, 112], // Dark blue
                    textColor: 255,
                    fontStyle: 'bold',
                    fontSize: 8,
                },
                alternateRowStyles: {
                    fillColor: [248, 248, 248], // Light gray
                },
                columnStyles: {
                    0: { cellWidth: 25, halign: 'left' },   // Order Title
                    1: { cellWidth: 12, halign: 'center' }, // Serial #
                    2: { cellWidth: 12, halign: 'center' }, // Status
                    3: { cellWidth: 18, halign: 'right' },  // Price
                    4: { cellWidth: 8, halign: 'center' },  // Qty
                    5: { cellWidth: 12, halign: 'center' }, // Timeline
                    6: { cellWidth: 12, halign: 'center' }, // Advance %
                    7: { cellWidth: 18, halign: 'center' }, // Quote Date
                    8: { cellWidth: 18, halign: 'center' }, // RFQ Sent
                    9: { cellWidth: 15, halign: 'left' },   // PI
                    10: { cellWidth: 20, halign: 'left' }   // Notes
                },
                margin: { left: 10, right: 10 },
                tableWidth: 'wrap',
                showHead: 'everyPage',
                didDrawPage: function (data) {
                    // Add page numbers
                    const pageCount = doc.internal.getNumberOfPages();
                    const currentPage = doc.internal.getCurrentPageInfo().pageNumber;
                    doc.setFontSize(8);
                    doc.setTextColor(100, 100, 100);
                    doc.text(`Page ${currentPage} of ${pageCount}`, 105, doc.internal.pageSize.height - 10, { align: 'center' });
                }
            });

            // Save the PDF
            doc.save(`${vendor.name}_Report.pdf`);

        } catch (error) {
            console.error('Error generating PDF report:', error);
            alert('Failed to generate PDF report. Please try again.');
        }
    };

    const generateWordReport = async (vendor, quotes) => {
        try {
            console.log('Starting Word document generation...');

            // Sort quotes chronologically by quote creation date (oldest first)
            const sortedQuotes = [...quotes].sort((a, b) => {
                const dateA = a.quote.createdAt?.toDate?.() || a.order.createdAt?.toDate?.() || new Date(0);
                const dateB = b.quote.createdAt?.toDate?.() || b.order.createdAt?.toDate?.() || new Date(0);
                return dateA - dateB; // Ascending order (oldest first)
            });

            // Import docx dynamically
            const docx = await import('docx');
            console.log('docx library imported successfully');

            const { Document, Packer, Paragraph, TextRun, AlignmentType } = docx;

            // Calculate summary statistics using sorted quotes
            const totalValue = sortedQuotes.reduce((sum, item) => sum + (item.quote.price || 0), 0);
            const avgValue = sortedQuotes.length > 0 ? totalValue / sortedQuotes.length : 0;

            // Status breakdown using sorted quotes
            const statusBreakdown = sortedQuotes.reduce((acc, item) => {
                const status = item.order.status || 'Unknown';
                acc[status] = (acc[status] || 0) + 1;
                return acc;
            }, {});

            console.log('Creating document structure...');

            // Create a very simple Word document first
            const doc = new Document({
                sections: [{
                    properties: {},
                    children: [
                        // Title
                        new Paragraph({
                            children: [
                                new TextRun({
                                    text: "VENDOR QUOTE HISTORY REPORT",
                                    bold: true,
                                }),
                            ],
                            alignment: AlignmentType.CENTER,
                        }),

                        new Paragraph({ text: "" }),

                        // Vendor name
                        new Paragraph({
                            children: [
                                new TextRun({
                                    text: vendor.name || 'N/A',
                                    bold: true,
                                }),
                            ],
                            alignment: AlignmentType.CENTER,
                        }),

                        new Paragraph({ text: "" }),

                        // Report date
                        new Paragraph({
                            children: [
                                new TextRun(`Report Generated: ${new Date().toLocaleDateString('en-GB')}`),
                            ],
                            alignment: AlignmentType.CENTER,
                        }),

                        new Paragraph({ text: "" }),
                        new Paragraph({ text: "" }),

                        // Vendor Information Section
                        new Paragraph({
                            children: [
                                new TextRun({
                                    text: "VENDOR INFORMATION",
                                    bold: true,
                                }),
                            ],
                        }),

                        new Paragraph({
                            children: [
                                new TextRun(`Name: ${vendor.name || 'N/A'}`),
                            ],
                        }),

                        new Paragraph({
                            children: [
                                new TextRun(`City: ${vendor.city || 'N/A'}`),
                            ],
                        }),

                        new Paragraph({
                            children: [
                                new TextRun(`Email: ${vendor.email || 'N/A'}`),
                            ],
                        }),

                        new Paragraph({
                            children: [
                                new TextRun(`Phone: ${vendor.phone || 'N/A'}`),
                            ],
                        }),

                        new Paragraph({ text: "" }),

                        // Summary Statistics Section
                        new Paragraph({
                            children: [
                                new TextRun({
                                    text: "SUMMARY STATISTICS",
                                    bold: true,
                                }),
                            ],
                        }),

                        new Paragraph({
                            children: [
                                new TextRun(`Total Quotes: ${sortedQuotes.length}`),
                            ],
                        }),

                        new Paragraph({
                            children: [
                                new TextRun(`Total Value: ₹${totalValue.toLocaleString()}`),
                            ],
                        }),

                        new Paragraph({
                            children: [
                                new TextRun(`Average Value: ₹${avgValue.toLocaleString()}`),
                            ],
                        }),

                        new Paragraph({ text: "" }),

                        // Status Breakdown Section
                        new Paragraph({
                            children: [
                                new TextRun({
                                    text: "STATUS BREAKDOWN",
                                    bold: true,
                                }),
                            ],
                        }),

                        ...Object.entries(statusBreakdown).map(([status, count]) =>
                            new Paragraph({
                                children: [
                                    new TextRun(`${status}: ${count}`),
                                ],
                            })
                        ),

                        new Paragraph({ text: "" }),

                        // Quote Details Section
                        new Paragraph({
                            children: [
                                new TextRun({
                                    text: "QUOTE DETAILS",
                                    bold: true,
                                }),
                            ],
                        }),

                        // Quote details as simple paragraphs - using sorted quotes
                        ...sortedQuotes.map((item, index) => [
                            new Paragraph({
                                children: [
                                    new TextRun({
                                        text: `Quote ${index + 1}:`,
                                        bold: true,
                                    }),
                                ],
                            }),
                            new Paragraph({
                                children: [
                                    new TextRun(`Order Title: ${item.order.orderTitle || 'N/A'}`),
                                ],
                            }),
                            new Paragraph({
                                children: [
                                    new TextRun(`Serial #: ${item.order.serialNumber || 'N/A'}`),
                                ],
                            }),
                            new Paragraph({
                                children: [
                                    new TextRun(`Status: ${statusDisplayNames[item.order.status] || item.order.status || 'N/A'}`),
                                ],
                            }),
                            new Paragraph({
                                children: [
                                    new TextRun(`Price: ₹${item.quote.price ? item.quote.price.toLocaleString() : 'N/A'}`),
                                ],
                            }),
                            new Paragraph({
                                children: [
                                    new TextRun(`Quantity: ${item.quote.quantity || 'N/A'}`),
                                ],
                            }),
                            new Paragraph({
                                children: [
                                    new TextRun(`Timeline: ${item.quote.deliveryTimeline ? item.quote.deliveryTimeline + ' days' : 'N/A'}`),
                                ],
                            }),
                            new Paragraph({
                                children: [
                                    new TextRun(`Advance Payment: ${item.quote.advancePayment ? item.quote.advancePayment + '%' : 'N/A'}`),
                                ],
                            }),
                            new Paragraph({
                                children: [
                                    new TextRun(`Quote Date: ${item.quote.createdAt ? new Date(item.quote.createdAt.toDate()).toLocaleDateString('en-GB') : 'N/A'}`),
                                ],
                            }),
                            new Paragraph({
                                children: [
                                    new TextRun(`RFQ Sent: ${item.order.createdAt ? new Date(item.order.createdAt.toDate()).toLocaleDateString('en-GB') : 'N/A'}`),
                                ],
                            }),
                            new Paragraph({
                                children: [
                                    new TextRun(`PI: ${getPIFilename(item.quote.proformaInvoiceUrl, item.order.orderTitle, item.quote.createdAt)}`),
                                ],
                            }),
                            new Paragraph({
                                children: [
                                    new TextRun(`Delivery Remarks: ${item.order.deliveryRemarks || 'N/A'}`),
                                ],
                            }),
                            new Paragraph({
                                children: [
                                    new TextRun(`Notes: ${item.quote.notes || 'N/A'}`),
                                ],
                            }),
                            new Paragraph({ text: "" }),
                        ]).flat(),
                    ],
                }],
            });

            console.log('Document created, generating blob...');

            // Generate the Word document as a blob (browser-compatible)
            const blob = await Packer.toBlob(doc);
            console.log('Blob generated, downloading...');

            // Use native browser download method
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${vendor.name}_Report.docx`;
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            console.log('Word document generated successfully!');

        } catch (error) {
            console.error('Error generating Word report:', error);
            console.error('Error details:', error.message);
            console.error('Error stack:', error.stack);
            alert(`Failed to generate Word document: ${error.message}`);
        }
    };

    const createCSVContent = (vendor, quotes) => {
        const lines = [];

        // Header
        lines.push('Vendor Report');
        lines.push('');

        // Vendor Profile Section
        lines.push('VENDOR PROFILE');
        lines.push('Name', vendor.name || 'N/A');
        lines.push('City', vendor.city || 'N/A');
        lines.push('Email', vendor.email || 'N/A');
        lines.push('');

        // Summary Statistics
        lines.push('SUMMARY STATISTICS');
        lines.push('Total Quotes', quotes.length);
        lines.push('Total Value', `₹${quotes.reduce((sum, item) => sum + (item.quote.price || 0), 0).toLocaleString()}`);
        lines.push('Average Quote Value', `₹${quotes.length > 0 ? (quotes.reduce((sum, item) => sum + (item.quote.price || 0), 0) / quotes.length).toLocaleString() : 0}`);
        lines.push('');

        // Quote Details Table Header
        lines.push('QUOTE DETAILS');
        lines.push('Order Title,Serial Number,Order Status,Quote Status,Price,Quantity,Timeline (days),Advance Payment (%),Quote Date,RFQ Sent,PI,Delivery Remarks,Notes');

        // Quote Details Rows
        quotes.forEach(item => {
            const piFilename = getPIFilename(
                item.quote.proformaInvoiceUrl,
                item.order.orderTitle,
                item.quote.createdAt
            );

            const row = [
                item.order.orderTitle || 'N/A',
                item.order.serialNumber || 'N/A',
                statusDisplayNames[item.order.status] || item.order.status || 'N/A',
                item.quote.status || 'N/A',
                item.quote.price ? `₹${item.quote.price.toLocaleString()}` : 'N/A',
                item.quote.quantity || 'N/A',
                item.quote.deliveryTimeline || 'N/A',
                item.quote.advancePayment ? `${item.quote.advancePayment}%` : 'N/A',
                item.quote.createdAt ? new Date(item.quote.createdAt.toDate()).toLocaleDateString('en-GB') : 'N/A',
                item.order.createdAt ? new Date(item.order.createdAt.toDate()).toLocaleDateString('en-GB') : 'N/A',
                piFilename,
                item.order.deliveryRemarks || 'N/A',
                item.quote.notes || 'N/A'
            ];
            lines.push(row.join(','));
        });

        return lines.join('\n');
    };

    const createTextContent = (vendor, quotes) => {
        const lines = [];

        // Header
        lines.push('VENDOR REPORT');
        lines.push('='.repeat(50));
        lines.push('');

        // Vendor Profile Section
        lines.push('VENDOR PROFILE');
        lines.push('-'.repeat(20));
        lines.push(`Name: ${vendor.name || 'N/A'}`);
        lines.push(`City: ${vendor.city || 'N/A'}`);
        lines.push(`Email: ${vendor.email || 'N/A'}`);
        lines.push('');

        // Summary Statistics
        lines.push('SUMMARY STATISTICS');
        lines.push('-'.repeat(20));
        lines.push(`Total Quotes: ${quotes.length}`);
        lines.push(`Total Value: ₹${quotes.reduce((sum, item) => sum + (item.quote.price || 0), 0).toLocaleString()}`);
        lines.push(`Average Quote Value: ₹${quotes.length > 0 ? (quotes.reduce((sum, item) => sum + (item.quote.price || 0), 0) / quotes.length).toLocaleString() : 0}`);
        lines.push('');

        // Quote Details Table
        lines.push('QUOTE DETAILS');
        lines.push('-'.repeat(20));
        lines.push('');

        // Table Header
        const header = [
            'Order Title'.padEnd(30),
            'Serial #'.padEnd(15),
            'Status'.padEnd(15),
            'Price'.padEnd(15),
            'Quantity'.padEnd(10),
            'Timeline'.padEnd(10),
            'RFQ Sent'.padEnd(15),
            'Notes'
        ].join(' | ');
        lines.push(header);
        lines.push('-'.repeat(header.length));

        // Table Rows
        quotes.forEach(item => {
            const row = [
                (item.order.orderTitle || 'N/A').substring(0, 28).padEnd(30),
                (item.order.serialNumber || 'N/A').substring(0, 13).padEnd(15),
                (statusDisplayNames[item.order.status] || item.order.status || 'N/A').substring(0, 13).padEnd(15),
                (item.quote.price ? `₹${item.quote.price.toLocaleString()}` : 'N/A').substring(0, 13).padEnd(15),
                (item.quote.quantity || 'N/A').toString().padEnd(10),
                (item.quote.deliveryTimeline ? `${item.quote.deliveryTimeline}d` : 'N/A').padEnd(10),
                (item.order.createdAt ? new Date(item.order.createdAt.toDate()).toLocaleDateString('en-GB') : 'N/A').padEnd(15),
                item.quote.notes || 'N/A'
            ].join(' | ');
            lines.push(row);
        });

        return lines.join('\n');
    };

    const downloadFile = (content, filename, mimeType) => {
        const blob = new Blob([content], { type: mimeType });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold text-gray-900">Generate Vendor Report</h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600"
                    >
                        <X size={24} />
                    </button>
                </div>

                <div className="space-y-6">
                    {/* Step 1: Date Range Selection */}
                    <div>
                        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                            <Calendar size={20} className="text-blue-600" />
                            Step 1: Select Date Range
                        </h3>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            <button
                                onClick={() => setDateRange('all')}
                                className={`p-3 rounded-lg border-2 transition-all ${dateRange === 'all'
                                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                                    : 'border-gray-200 hover:border-gray-300'
                                    }`}
                            >
                                <div className="text-sm font-medium">All Time</div>
                            </button>
                            <button
                                onClick={() => setDateRange('3m')}
                                className={`p-3 rounded-lg border-2 transition-all ${dateRange === '3m'
                                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                                    : 'border-gray-200 hover:border-gray-300'
                                    }`}
                            >
                                <div className="text-sm font-medium">Last 3 Months</div>
                            </button>
                            <button
                                onClick={() => setDateRange('6m')}
                                className={`p-3 rounded-lg border-2 transition-all ${dateRange === '6m'
                                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                                    : 'border-gray-200 hover:border-gray-300'
                                    }`}
                            >
                                <div className="text-sm font-medium">Last 6 Months</div>
                            </button>
                            <button
                                onClick={() => setDateRange('1y')}
                                className={`p-3 rounded-lg border-2 transition-all ${dateRange === '1y'
                                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                                    : 'border-gray-200 hover:border-gray-300'
                                    }`}
                            >
                                <div className="text-sm font-medium">Last 1 Year</div>
                            </button>
                        </div>

                        {/* Custom Date Range */}
                        <div className="mt-4">
                            <button
                                onClick={() => setDateRange('custom')}
                                className={`p-3 rounded-lg border-2 transition-all w-full ${dateRange === 'custom'
                                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                                    : 'border-gray-200 hover:border-gray-300'
                                    }`}
                            >
                                <div className="text-sm font-medium">Custom Date Range</div>
                            </button>

                            {dateRange === 'custom' && (
                                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Start Date
                                            </label>
                                            <input
                                                type="date"
                                                value={customStartDate}
                                                onChange={(e) => setCustomStartDate(e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                End Date
                                            </label>
                                            <input
                                                type="date"
                                                value={customEndDate}
                                                onChange={(e) => setCustomEndDate(e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Step 2: Document Format Selection */}
                    <div>
                        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                            <FileText size={20} className="text-green-600" />
                            Step 2: Select Document Format
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            <label className="flex items-center gap-3 cursor-pointer p-4 border-2 rounded-lg transition-all hover:border-gray-300">
                                <input
                                    type="radio"
                                    name="format"
                                    value="excel"
                                    checked={reportFormat === 'excel'}
                                    onChange={(e) => setReportFormat(e.target.value)}
                                    className="text-blue-600"
                                />
                                <div className="flex items-center gap-3">
                                    <FileSpreadsheet size={24} className="text-green-600" />
                                    <div>
                                        <div className="font-medium">Excel (.xlsx)</div>
                                        <div className="text-xs text-gray-500">Spreadsheet format</div>
                                    </div>
                                </div>
                            </label>

                            <label className="flex items-center gap-3 cursor-pointer p-4 border-2 rounded-lg transition-all hover:border-gray-300">
                                <input
                                    type="radio"
                                    name="format"
                                    value="pdf"
                                    checked={reportFormat === 'pdf'}
                                    onChange={(e) => setReportFormat(e.target.value)}
                                    className="text-blue-600"
                                />
                                <div className="flex items-center gap-3">
                                    <FileText size={24} className="text-red-600" />
                                    <div>
                                        <div className="font-medium">PDF (.pdf)</div>
                                        <div className="text-xs text-gray-500">Professional layout</div>
                                    </div>
                                </div>
                            </label>

                            <label className="flex items-center gap-3 cursor-pointer p-4 border-2 rounded-lg transition-all hover:border-gray-300">
                                <input
                                    type="radio"
                                    name="format"
                                    value="word"
                                    checked={reportFormat === 'word'}
                                    onChange={(e) => setReportFormat(e.target.value)}
                                    className="text-blue-600"
                                />
                                <div className="flex items-center gap-3">
                                    <FileTextIcon size={24} className="text-blue-600" />
                                    <div>
                                        <div className="font-medium">Word (.docx)</div>
                                        <div className="text-xs text-gray-500">Editable document</div>
                                    </div>
                                </div>
                            </label>
                        </div>
                    </div>

                    {/* Step 3: Generate Button */}
                    <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                        <button
                            onClick={onClose}
                            className="px-6 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={generateReport}
                            disabled={isGenerating || (dateRange === 'custom' && (!customStartDate || !customEndDate))}
                            className="flex items-center gap-2 bg-black text-white px-6 py-2 rounded-md hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            {isGenerating ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                    Generating Report...
                                </>
                            ) : (
                                <>
                                    <Download size={16} />
                                    Generate Report
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

async function processWithGemini(description) {
    const OPENAI_API_KEY = process.env.REACT_APP_OPENAI_API_KEY;

    const prompt = `Analyze the following technical description and extract key information. Return a JSON object with these fields:

1. summary: A clean, bulleted list of the technical details (CRITICAL: Do NOT include quantity, qty, pcs, units, or any number count in the summary - ONLY technical specifications)
2. orderTitle: Extract company/manufacturer name and part number in format "COMPANY NAME - PART NUMBER". If company name is not found, omit it and just use "PART NUMBER". If part number is not found, use "No Part Number".
3. quantity: The number of items needed (extract from text like "Qty: 4", "4 units", "4 pcs", "4 pieces", "4 nos", etc.)

CRITICAL EXTRACTION RULES:

Part Number Extraction:
- Look for part numbers with these labels: "P/N", "P/N#", "P/N:", "Part No.", "Part Number", "Part#", "PN", "Model", "Model No.", "Article No.", "Item No."
- Part numbers can be alphanumeric (e.g., "ABC-123", "12345-XYZ", "SSV-2-150")
- If multiple part numbers are found, use the FIRST one mentioned
- Remove any prefixes like "P/N:", "Part No.:", etc. from the extracted part number

Company/Manufacturer Name Extraction:
- Look for company names, brand names, or manufacturer names
- Common patterns: "from [Company]", "by [Company]", "[Company] brand", manufacturer's name at the beginning
- Brand names like: Bosch, Siemens, ABB, Schneider, etc.
- If the description starts with a company name, use it
- If no company/brand is found, do NOT add "Unknown Company" - just use the part number alone

Parent Part Information:
- If a "parent part" or "parent part number" is mentioned, include it in the summary as "* Parent Part: [number]"
- Common labels: "Parent P/N", "Parent Part", "Main Assembly", "Parent Part Number"

Summary Rules:
- Start each line with an asterisk and space (*)
- Focus ONLY on technical specifications: material, size, dimensions, pressure rating, voltage, power, color, type, etc.
- **NEVER include quantity, qty, count, pieces, units, or any numbers referring to how many items** in the summary
- If parent part is mentioned, include it in summary
- Keep it concise and technical
- Do NOT repeat the part number in the summary if it's already in orderTitle

Quantity Extraction:
- Extract ONLY the numeric value
- Look for patterns: "Qty: 4", "Quantity: 4", "4 units", "4 pcs", "4 pieces", "4 nos", "4x", "4 sets", "Qty 4", "QTY:4"
- Return as a number (e.g., 4, not "4 units")
- If no quantity found, set to null

Format Rules:
- Return ONLY valid JSON, no markdown formatting, no code blocks
- orderTitle format: "COMPANY NAME - PART NUMBER" OR just "PART NUMBER" (if no company)
- If no company found, DO NOT use "Unknown Company"

Example 1 - With company and P/N#:
Input: "Bosch Pressure Sensor P/N# 0281002937, Qty: 5, Material: Stainless Steel, Voltage: 12V"
Output:
{
  "summary": "* Product: Pressure Sensor\\n* Material: Stainless Steel\\n* Voltage: 12V",
  "orderTitle": "Bosch - 0281002937",
  "quantity": 5
}

Example 2 - With parent part:
Input: "Valve Assembly Part No. VA-2023, parent part P/N 1234-MAIN, 10 units, Material: Brass, Size: 2 inch"
Output:
{
  "summary": "* Product: Valve Assembly\\n* Parent Part: 1234-MAIN\\n* Material: Brass\\n* Size: 2 inch",
  "orderTitle": "VA-2023",
  "quantity": 10
}

Example 3 - With manufacturer name:
Input: "Schneider Electric Circuit Breaker, Model: EZC250N3200, 3 pcs, Rating: 200A, Poles: 3P"
Output:
{
  "summary": "* Product: Circuit Breaker\\n* Rating: 200A\\n* Poles: 3P",
  "orderTitle": "Schneider Electric - EZC250N3200",
  "quantity": 3
}

Example 4 - No company name:
Input: "Hydraulic Pump P/N HP-5000, 2 units, Max Pressure: 3000 PSI, Flow Rate: 20 GPM"
Output:
{
  "summary": "* Product: Hydraulic Pump\\n* Max Pressure: 3000 PSI\\n* Flow Rate: 20 GPM",
  "orderTitle": "HP-5000",
  "quantity": 2
}`;

    const apiUrl = 'https://api.openai.com/v1/chat/completions';
    const payload = {
        model: 'gpt-4o-mini',
        messages: [
            {
                role: 'system',
                content: 'You are a technical assistant that extracts structured information from product descriptions. Always respond with valid JSON only.'
            },
            {
                role: 'user',
                content: prompt + '\n\nDescription: "' + description + '"'
            }
        ],
        temperature: 0.3,
        response_format: { type: 'json_object' }
    };

    try {
        return await retryWithBackoff(async () => {
            return await rateLimitedApiCall(async () => {
                const response = await fetch(apiUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${OPENAI_API_KEY}`
                    },
                    body: JSON.stringify(payload)
                });

                if (!response.ok) {
                    const errorBody = await response.text();
                    throw new Error(`API call failed with status: ${response.status} - ${errorBody}`);
                }

                const result = await response.json();
                if (result.choices && result.choices[0].message) {
                    const textResponse = result.choices[0].message.content;

                    try {
                        // Clean the response text - remove markdown code blocks if present
                        let cleanText = textResponse.trim();

                        // Remove markdown code blocks if present
                        if (cleanText.startsWith('```json')) {
                            cleanText = cleanText.replace(/^```json\s*/, '');
                        }
                        if (cleanText.startsWith('```')) {
                            cleanText = cleanText.replace(/^```\s*/, '');
                        }
                        if (cleanText.endsWith('```')) {
                            cleanText = cleanText.replace(/\s*```$/, '');
                        }

                        // Try to parse the cleaned JSON response
                        const parsedResponse = JSON.parse(cleanText);

                        // Validate the response structure
                        if (typeof parsedResponse !== 'object' || parsedResponse === null) {
                            throw new Error('Response is not a valid object');
                        }

                        return {
                            summary: parsedResponse.summary || textResponse,
                            orderTitle: parsedResponse.orderTitle || null,
                            quantity: parsedResponse.quantity || null
                        };
                    } catch (parseError) {
                        console.warn("Failed to parse AI response as JSON, falling back to text:", parseError);
                        console.warn("Raw response:", textResponse);

                        // Try to extract information from the text response as fallback
                        let extractedTitle = null;
                        let extractedQuantity = null;

                        // Simple regex patterns to extract information
                        const titleMatch = textResponse.match(/orderTitle["\s]*:["\s]*"([^"]+)"/i);
                        if (titleMatch) extractedTitle = titleMatch[1];

                        const quantityMatch = textResponse.match(/quantity["\s]*:["\s]*(\d+)/i);
                        if (quantityMatch) extractedQuantity = parseInt(quantityMatch[1]);

                        return {
                            summary: textResponse,
                            orderTitle: extractedTitle,
                            quantity: extractedQuantity
                        };
                    }
                } else {
                    console.warn("OpenAI did not return summary content.", { result });
                    return {
                        summary: "AI could not generate a summary for this description.",
                        orderTitle: null,
                        quantity: null
                    };
                }
            }, geminiRateLimiter, 'OpenAI');
        }, 3, 2000); // Retry up to 3 times with 2 second base delay

    } catch (error) {
        console.error('Error processing with OpenAI:', error);

        // Check if it's a 503 service unavailable error
        if (error.message && error.message.includes('503')) {
            return {
                summary: "AI service is temporarily overloaded. Please try again in a few minutes.",
                orderTitle: null,
                quantity: null
            };
        }

        // Check if it's a rate limit error
        if (error.message && error.message.includes('rate limit')) {
            return {
                summary: "AI service is being rate limited. Please wait a moment and try again.",
                orderTitle: null,
                quantity: null
            };
        }

        return {
            summary: "AI could not generate a summary due to an error. Please try again later.",
            orderTitle: null,
            quantity: null
        };
    }
};

// Add a reusable Tooltip component
const Tooltip = ({ text, children }) => {
    const [show, setShow] = useState(false);
    return (
        <span className="relative" onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>
            {children}
            {show && (
                <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1 rounded bg-black text-white text-xs whitespace-nowrap z-50 shadow-lg">
                    {text}
                </span>
            )}
        </span>
    );
};

// Add a TruncatedText component for showing limited lines with expand/collapse
const TruncatedText = ({ text, maxLines = 4, className = "" }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [shouldShowButton, setShouldShowButton] = useState(false);
    const [showCopiedMessage, setShowCopiedMessage] = useState(false);
    const textRef = useRef(null);

    useEffect(() => {
        if (textRef.current) {
            const lineHeight = parseInt(getComputedStyle(textRef.current).lineHeight);
            const maxHeight = lineHeight * maxLines;
            setShouldShowButton(textRef.current.scrollHeight > maxHeight);
        }
    }, [text, maxLines]);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(text);
            setShowCopiedMessage(true);
            setTimeout(() => setShowCopiedMessage(false), 2000);
        } catch (err) {
            // Fallback for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = text;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            setShowCopiedMessage(true);
            setTimeout(() => setShowCopiedMessage(false), 2000);
        }
    };

    if (!text) return null;

    return (
        <div className={className}>
            <div className="flex items-start justify-between mb-2">
                <div className="flex-grow">
                    <div
                        ref={textRef}
                        className={`whitespace-pre-wrap transition-all duration-300 ${!isExpanded ? `line-clamp-${maxLines}` : ''
                            }`}
                        style={{
                            display: '-webkit-box',
                            WebkitLineClamp: isExpanded ? 'none' : maxLines,
                            WebkitBoxOrient: 'vertical',
                            overflow: isExpanded ? 'visible' : 'hidden'
                        }}
                    >
                        {text}
                    </div>
                </div>
                <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                    <Tooltip text="Copy description">
                        <button
                            onClick={handleCopy}
                            className="p-1.5 rounded-md hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-700"
                        >
                            {showCopiedMessage ? (
                                <CheckCircle size={14} className="text-green-600" />
                            ) : (
                                <Copy size={14} />
                            )}
                        </button>
                    </Tooltip>
                </div>
            </div>
            {shouldShowButton && (
                <button
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="text-black hover:text-gray-700 text-sm font-medium mt-2 transition-colors"
                >
                    {isExpanded ? 'View Less' : 'View More'}
                </button>
            )}
        </div>
    );
};

// Vendor Edit Forms
const VendorEditForm = ({ vendor, onSave, onCancel }) => {
    const [name, setName] = useState(vendor.name || '');
    const [phone, setPhone] = useState(vendor.phone || '');
    const [email, setEmail] = useState(vendor.email || '');
    const [preferredCommunication, setPreferredCommunication] = useState(vendor.preferredCommunication || 'Email');

    const handleSave = () => {
        onSave({
            name: name.trim(),
            phone: phone.trim(),
            email: email.trim(),
            preferredCommunication: preferredCommunication.trim()
        });
    };

    return (
        <div className="mt-4 p-4 bg-white border-2 border-black rounded-lg">
            <h4 className="text-black font-medium mb-3 text-sm">Edit Vendor Information</h4>
            <div className="space-y-3">
                <div>
                    <label className="block text-black text-xs mb-1">Vendor Name</label>
                    <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="w-full p-3 bg-white border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-black"
                        placeholder="Vendor Name"
                    />
                </div>
                <div>
                    <label className="block text-black text-xs mb-1">Phone Number</label>
                    <input
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        className="w-full p-3 bg-white border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-black"
                        placeholder="Phone Number"
                    />
                </div>
                <div>
                    <label className="block text-black text-xs mb-1">Email ID</label>
                    <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full p-3 bg-white border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-black"
                        placeholder="Email ID"
                    />
                </div>
                <div>
                    <label className="block text-black text-xs mb-1">Preferred Communication Mode</label>
                    <input
                        type="text"
                        value={preferredCommunication}
                        onChange={(e) => setPreferredCommunication(e.target.value)}
                        className="w-full p-3 bg-white border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-black"
                        placeholder="e.g., Email, Phone, WhatsApp"
                    />
                </div>
                <div className="flex gap-2 pt-2">
                    <button
                        onClick={handleSave}
                        className="px-4 py-2 bg-black text-white text-sm font-medium rounded hover:bg-gray-800 transition-colors"
                    >
                        Save Changes
                    </button>
                    <button
                        onClick={onCancel}
                        className="px-4 py-2 bg-gray-300 text-black text-sm font-medium rounded hover:bg-gray-400 transition-colors"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
};

const SpecializationEditForm = ({ vendor, onSave, onCancel }) => {
    const [products, setProducts] = useState(vendor.products || 'Valves, Pumps, Pipes');
    const [industries, setIndustries] = useState(vendor.industries || 'Manufacturing, Oil & Gas');
    const [brands, setBrands] = useState(vendor.brands || 'Bosch, Siemens, ABB, Schneider Electric');

    const handleSave = () => {
        onSave({
            products: products.trim(),
            industries: industries.trim(),
            brands: brands.trim()
        });
    };

    return (
        <div className="mt-4 p-4 bg-white border-2 border-black rounded-lg">
            <h4 className="text-black font-medium mb-3 text-sm">Edit Specialization</h4>
            <div className="space-y-3">
                <div>
                    <label className="block text-black text-xs mb-1">Products/Services</label>
                    <input
                        type="text"
                        value={products}
                        onChange={(e) => setProducts(e.target.value)}
                        className="w-full p-3 bg-white border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-black"
                        placeholder="e.g., Valves, Pumps, Pipes"
                    />
                </div>
                <div>
                    <label className="block text-black text-xs mb-1">Industries</label>
                    <input
                        type="text"
                        value={industries}
                        onChange={(e) => setIndustries(e.target.value)}
                        className="w-full p-3 bg-white border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-black"
                        placeholder="e.g., Manufacturing, Oil & Gas"
                    />
                </div>
                <div>
                    <label className="block text-black text-xs mb-1">Brands</label>
                    <input
                        type="text"
                        value={brands}
                        onChange={(e) => setBrands(e.target.value)}
                        className="w-full p-3 bg-white border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-black"
                        placeholder="e.g., Bosch, Siemens, ABB, Schneider Electric"
                    />
                </div>
                <div className="flex gap-2 pt-2">
                    <button
                        onClick={handleSave}
                        className="px-4 py-2 bg-black text-white text-sm font-medium rounded hover:bg-gray-800 transition-colors"
                    >
                        Save Changes
                    </button>
                    <button
                        onClick={onCancel}
                        className="px-4 py-2 bg-gray-300 text-black text-sm font-medium rounded hover:bg-gray-400 transition-colors"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
};

// Add a TruncatedTitle component for order titles in cards
const TruncatedTitle = ({ title, quantity, className = "" }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [shouldShowButton, setShouldShowButton] = useState(false);
    const titleRef = useRef(null);

    useEffect(() => {
        if (titleRef.current) {
            // Check if the title is longer than one line
            const lineHeight = parseInt(getComputedStyle(titleRef.current).lineHeight);
            const maxHeight = lineHeight * 1; // Only one line
            setShouldShowButton(titleRef.current.scrollHeight > maxHeight);
        }
    }, [title]);

    if (!title) return null;

    // Use truncated title for display, but show full title when expanded
    const displayTitle = isExpanded ? title : truncateOrderTitle(title);
    const isTruncated = !isExpanded && title !== truncateOrderTitle(title);

    return (
        <div className={className}>
            <div className="flex items-center gap-2">
                <div
                    ref={titleRef}
                    className={`font-bold text-lg flex items-center transition-all duration-300 ${!isExpanded ? 'line-clamp-1' : ''
                        }`}
                    style={{
                        display: isExpanded ? 'block' : '-webkit-box',
                        WebkitLineClamp: isExpanded ? 'none' : 1,
                        WebkitBoxOrient: 'vertical',
                        overflow: isExpanded ? 'visible' : 'hidden'
                    }}
                >
                    {displayTitle}
                    {isTruncated && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation(); // Prevent card click when expanding title
                                setIsExpanded(!isExpanded);
                            }}
                            className="text-gray-400 hover:text-gray-600 ml-1 flex-shrink-0 transition-colors p-1 -m-1 rounded hover:bg-gray-100"
                            title={isExpanded ? 'Show less' : 'Show more'}
                        >
                            <ChevronDownIcon
                                size={16}
                                className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                            />
                        </button>
                    )}
                </div>
                <span className="font-normal text-gray-600 text-lg flex-shrink-0">Qty: {quantity !== null && quantity !== undefined && quantity !== '' ? quantity : 'N/A'}</span>
            </div>
        </div>
    );
};

// Utility for INR formatting with Indian numbering system (commas after every 2 digits)
const formatINR = (value) => {
    if (value === undefined || value === null || isNaN(value)) return 'N/A';

    const num = Number(value);
    const fixed = num.toFixed(2);
    const parts = fixed.split('.');
    const integerPart = parts[0];
    const decimalPart = parts[1];

    // Indian numbering system: commas after every 2 digits except for the last 3
    let formatted = '';
    const len = integerPart.length;

    for (let i = 0; i < len; i++) {
        if (i > 0 && (len - i) % 2 === 1 && i !== len - 1) {
            formatted += ',';
        }
        formatted += integerPart[i];
    }

    return formatted + '.' + decimalPart;
};

// Utility to truncate order title: Brand name + first 7 chars of part number + "..."
const truncateOrderTitle = (title) => {
    if (!title || typeof title !== 'string') return title;

    // Find the first "-" in the title
    const dashIndex = title.indexOf(' - ');
    if (dashIndex === -1) {
        // If no dash found, return original title
        return title;
    }

    // Get brand name (before the dash)
    const brandName = title.substring(0, dashIndex + 1); // Include the dash

    // Get part number (after the dash)
    const partNumber = title.substring(dashIndex + 3); // Skip " - "

    // Take first 7 characters of part number
    const truncatedPartNumber = partNumber.substring(0, 7);

    // Return brand + truncated part number + "..."
    return `${brandName} ${truncatedPartNumber}...`;
};

// Utility function to update existing orders with quotes to Negotiation status






// Utility: Fetch new emails from Microsoft Graph API
async function fetchSupplierEmails(accessToken, supplierEmails, lastSync) {
    if (!accessToken || !supplierEmails.length) return [];
    const lowerSupplierEmails = supplierEmails.map(email => email.toLowerCase());

    console.log(`Email sync: Looking for emails from suppliers:`, lowerSupplierEmails);

    // Fetch only the 5 latest emails without any date filter
    const url = `https://graph.microsoft.com/v1.0/me/mailFolders/inbox/messages?$top=5&$orderby=receivedDateTime desc`;

    const res = await fetch(url, { headers: { Authorization: `Bearer ${accessToken}` } });
    if (!res.ok) return [];
    const data = await res.json();

    console.log(`Email sync: Found ${data.value?.length || 0} total emails in inbox`);
    console.log(`Email sync: Latest emails:`, data.value?.map(msg => ({
        subject: msg.subject,
        from: msg.from?.emailAddress?.address,
        receivedDateTime: msg.receivedDateTime
    })));

    const filtered = (data.value || []).filter(msg =>
        msg.from && lowerSupplierEmails.includes(msg.from.emailAddress.address.toLowerCase())
    );

    console.log(`Email sync: Filtered to ${filtered.length} supplier emails`);

    // Sort by receivedDateTime ascending (oldest first for processing)
    filtered.sort((a, b) => new Date(a.receivedDateTime) - new Date(b.receivedDateTime));
    return filtered;
}

// Gemini: Extract order/quote info from email
async function processEmailWithGemini(emailBody, emailSubject = '') {
    const prompt = `Extract the following fields from this email if it contains a quote for an order. Return a JSON object with these fields: \n- orderTitle\n- partNumber (IMPORTANT: Look for any alphanumeric codes, model numbers, or identifiers that appear next to product names. These are often part numbers even if not explicitly labeled as "part number". Common patterns include: codes like "ABC123", "XYZ-456", model numbers like "Model X1", "Type A", or any alphanumeric identifier that seems to identify a specific product. If you see a product name followed by a code/number, that's likely the part number. Extract this as a string. ALSO check the email subject line for part numbers - they are often mentioned there)\n- supplier\n- price (as it appears in the email, do not convert)\n- currency (as it appears in the email, do not convert)\n- quantity (only extract this if it is mentioned in the email else dont add this field)\n- timeline (in days)\n- timelineUnit\n- notes\n- advancePayment\n- priceType (set to "perQty" if the email mentions "per qty", "per quantity", "per unit", "per piece", "per item", or similar per-unit pricing. Set to "total" if it mentions total price, total cost, total amount, or if no per-unit indicator is found)\n\nExtract the price and currency exactly as they appear in the email, without any conversion. My code will handle currency conversion. The part number is the most important field for matching orders in our database. Be intelligent about identifying part numbers - they don't always have to be explicitly labeled as "part number".`;
    const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash-exp:generateContent?key=${GEMINI_API_KEY}`;
    const payload = { contents: [{ parts: [{ text: prompt + '\n\nEmail Subject: ' + emailSubject + '\n\nEmail Body:\n"""' + emailBody + '"""' }] }] };

    try {
        return await retryWithBackoff(async () => {
            return await rateLimitedApiCall(async () => {
                const response = await fetch(apiUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });

                if (!response.ok) {
                    const errorBody = await response.text();
                    throw new Error(`API call failed with status: ${response.status} - ${errorBody}`);
                }

                const result = await response.json();
                if (result.candidates && result.candidates[0].content) {
                    const textResponse = result.candidates[0].content.parts[0].text;
                    const cleanJsonString = textResponse.replace(/```json/g, '').replace(/```/g, '').trim();

                    try {
                        const parsed = JSON.parse(cleanJsonString);
                        return parsed;
                    } catch (parseError) {
                        console.error('Error parsing Gemini response:', parseError);
                        return null;
                    }
                } else {
                    return null;
                }
            }, geminiRateLimiter, 'Gemini AI');
        }, 2, 1000); // Retry up to 2 times with 1 second base delay

    } catch (error) {
        console.error('Error processing email with Gemini:', error);

        // Check if it's a 503 service unavailable error
        if (error.message && error.message.includes('503')) {
            console.warn('Gemini service is temporarily overloaded');
            return null;
        }

        // Check if it's a rate limit error
        if (error.message && error.message.includes('rate limit')) {
            console.warn('Gemini service is being rate limited');
            return null;
        }

        return null;
    }
};



// Fetch live USD to INR rate
async function fetchUsdToInrRate() {
    try {
        const response = await fetch('https://api.exchangerate.host/latest?base=USD&symbols=INR');
        const data = await response.json();
        return data.rates && data.rates.INR ? data.rates.INR : 85.65; // fallback to 85.65 if API fails
    } catch (err) {
        console.error('Error fetching USD to INR rate:', err);
        return 85.65; // fallback rate
    }
};

// Move this above VendorsPage so it's defined before use
const SortVendorsDropdown = ({ sortBy, setSortBy }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    const sortOptions = [
        { value: 'alphabetical', label: 'Alphabetical' },
        { value: 'completedOrders', label: 'Most Orders Completed' }
    ];

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen]);

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium bg-white border border-gray-300 rounded-md hover:bg-gray-50 interactive-button"
            >
                <span>Sort: {sortOptions.find(opt => opt.value === sortBy)?.label}</span>
                <ChevronRight size={14} className={`transform transition-transform ${isOpen ? 'rotate-90' : ''}`} />
            </button>
            {isOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white border rounded-md shadow-lg z-20">
                    {sortOptions.map(opt => (
                        <button
                            key={opt.value}
                            onClick={() => { setSortBy(opt.value); setIsOpen(false); }}
                            className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${sortBy === opt.value ? 'bg-gray-50 font-semibold' : ''}`}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

// --- Utility: Backfill Serial Numbers for Existing Orders ---
/**
 * Assigns serial numbers to all existing orders in creation order (oldest first).
 * Each order gets serialNumber: RN00001, RN00002, ...
 * Usage: Call backfillOrderSerialNumbers() from a dev/admin console.
 */
// Utility function to update quotes when vendor names change
export async function updateQuotesForVendorNameChange(oldName, newName) {
    try {
        // Get all orders
        const ordersQuery = query(collection(db, 'orders'));
        const ordersSnapshot = await getDocs(ordersQuery);

        const batch = writeBatch(db);
        let updatedQuotesCount = 0;

        for (const orderDoc of ordersSnapshot.docs) {
            const quotesQuery = query(collection(db, `orders/${orderDoc.id}/quotes`));
            const quotesSnapshot = await getDocs(quotesQuery);

            for (const quoteDoc of quotesSnapshot.docs) {
                const quoteData = quoteDoc.data();
                if (quoteData.supplier === oldName) {
                    const quoteRef = doc(db, `orders/${orderDoc.id}/quotes`, quoteDoc.id);
                    batch.update(quoteRef, { supplier: newName });
                    updatedQuotesCount++;
                }
            }
        }

        if (updatedQuotesCount > 0) {
            await batch.commit();

            return updatedQuotesCount;
        }

        return 0;
    } catch (error) {
        console.error('Error updating quotes for vendor name change:', error);
        throw error;
    }
}

// One-time fix for the Binda to Binda Jain issue
export async function fixBindaJainQuotes() {
    return await updateQuotesForVendorNameChange('Binda', 'Binda Jain');
}

// Make it available globally for console access
window.fixBindaJainQuotes = fixBindaJainQuotes;

// Utility function to update quotes without status to "Under Negotiation"
export async function updateQuotesWithoutStatus() {
    try {
        // Get all orders
        const ordersQuery = query(collection(db, 'orders'));
        const ordersSnapshot = await getDocs(ordersQuery);

        const batch = writeBatch(db);
        let updatedQuotesCount = 0;

        for (const orderDoc of ordersSnapshot.docs) {
            const quotesQuery = query(collection(db, `orders/${orderDoc.id}/quotes`));
            const quotesSnapshot = await getDocs(quotesQuery);

            for (const quoteDoc of quotesSnapshot.docs) {
                const quoteData = quoteDoc.data();
                // Check if quote doesn't have a status or has an empty status
                if (!quoteData.status || quoteData.status === '') {
                    const quoteRef = doc(db, `orders/${orderDoc.id}/quotes`, quoteDoc.id);
                    batch.update(quoteRef, { status: 'under_negotiation' });
                    updatedQuotesCount++;
                }
            }
        }

        if (updatedQuotesCount > 0) {
            await batch.commit();
            return updatedQuotesCount;
        }

        return 0;
    } catch (error) {
        console.error('Error updating quotes without status:', error);
        throw error;
    }
}

// Make it available globally for console access
window.updateQuotesWithoutStatus = updateQuotesWithoutStatus;



export async function backfillOrderSerialNumbers() {
    const ordersCol = collection(db, 'orders');
    const q = query(ordersCol, orderBy('createdAt', 'asc'));
    const snapshot = await getDocs(q);
    let serial = 1;
    for (const docSnap of snapshot.docs) {
        const data = docSnap.data();
        const orderRef = doc(db, 'orders', docSnap.id);
        if (data.categoryId || data.isCategoryOrder) {
            // Remove RN number from category orders
            await updateDoc(orderRef, { serialNumber: deleteField() });
            continue;
        }
        const serialNumber = `RN${String(serial).padStart(5, '0')}`;
        await updateDoc(orderRef, { serialNumber });
        serial++;
    }
    // Optionally update the counter document
    const counterRef = doc(db, 'counters', 'orders');
    await setDoc(counterRef, { lastSerial: serial - 1 }, { merge: true });
}

/**
 * Corrects RN numbers for all orders based on their creation date.
 * This function ensures that RN numbers are assigned sequentially according to when orders were created,
 * fixing any glitches or gaps in the numbering system.
 * 
 * Features:
 * - Sorts orders by creation date (oldest first)
 * - Reassigns RN numbers sequentially: RN00001, RN00002, etc.
 * - Skips category orders (removes their RN numbers)
 * - Updates the counter document to reflect the correct last serial number
 * - Handles orders without creation dates by using document ID as fallback
 * - Provides detailed logging and progress tracking
 * 
 * @returns {Promise<Object>} Object containing results of the correction process
 */
export async function correctOrderRNNumbers(categories = []) {
    try {
        console.log('Starting RN number correction process...');

        // Get all orders sorted by creation date
        const ordersCol = collection(db, 'orders');
        const q = query(ordersCol, orderBy('createdAt', 'asc'));
        const snapshot = await getDocs(q);

        if (snapshot.empty) {
            console.log('No orders found to correct.');
            return { success: true, message: 'No orders found to correct.', correctedCount: 0 };
        }

        console.log(`Found ${snapshot.docs.length} orders to process.`);

        let correctedCount = 0;
        let skippedCount = 0;
        let errorCount = 0;
        let serial = 1;

        // Process orders in batches to avoid overwhelming Firestore
        const batchSize = 50;
        const batches = [];

        for (let i = 0; i < snapshot.docs.length; i += batchSize) {
            batches.push(snapshot.docs.slice(i, i + batchSize));
        }

        console.log(`Processing ${batches.length} batches...`);

        for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
            const batch = batches[batchIndex];
            const batchWrite = writeBatch(db);

            console.log(`Processing batch ${batchIndex + 1}/${batches.length} (${batch.length} orders)...`);

            for (const docSnap of batch) {
                const data = docSnap.data();
                const orderRef = doc(db, 'orders', docSnap.id);

                try {
                    // Handle category orders - preserve category RN numbers for orders created within categories
                    if (data.categoryId || data.isCategoryOrder) {
                        // Check if this order was created within a category (has category's RN) or added later
                        const category = categories.find(cat => cat.id === data.categoryId);
                        if (category && data.serialNumber === category.serialNumber) {
                            // Order was created within category, keep its RN number
                            console.log(`Preserving category RN for order ${docSnap.id}: ${data.serialNumber}`);
                            skippedCount++;
                            continue;
                        } else {
                            // Order was added to category later, remove RN number
                            batchWrite.update(orderRef, { serialNumber: deleteField() });
                            skippedCount++;
                            console.log(`Removed RN from order added to category: ${docSnap.id}`);
                            continue;
                        }
                    }

                    // Generate new RN number
                    const serialNumber = `RN${String(serial).padStart(5, '0')}`;

                    // Check if the RN number actually needs to change
                    if (data.serialNumber === serialNumber) {
                        console.log(`Order ${docSnap.id} already has correct RN: ${serialNumber}`);
                        serial++;
                        continue;
                    }

                    // Update the order with new RN number
                    batchWrite.update(orderRef, { serialNumber });
                    correctedCount++;

                    console.log(`Corrected order ${docSnap.id}: ${data.serialNumber || 'No RN'} → ${serialNumber}`);

                    serial++;

                } catch (error) {
                    console.error(`Error processing order ${docSnap.id}:`, error);
                    errorCount++;
                }
            }

            // Commit the batch
            try {
                await batchWrite.commit();
                console.log(`Batch ${batchIndex + 1} committed successfully.`);
            } catch (error) {
                console.error(`Error committing batch ${batchIndex + 1}:`, error);
                errorCount += batch.length;
            }
        }

        // Update the counter document to reflect the correct last serial number
        const counterRef = doc(db, 'counters', 'orders');
        const lastSerial = serial - 1;
        await setDoc(counterRef, { lastSerial }, { merge: true });

        console.log(`RN number correction completed successfully!`);
        console.log(`Results: ${correctedCount} corrected, ${skippedCount} skipped, ${errorCount} errors`);
        console.log(`Counter updated to: ${lastSerial}`);

        return {
            success: true,
            message: 'RN numbers corrected successfully',
            correctedCount,
            skippedCount,
            errorCount,
            lastSerial,
            totalOrders: snapshot.docs.length
        };

    } catch (error) {
        console.error('Error correcting RN numbers:', error);
        return {
            success: false,
            message: 'Failed to correct RN numbers',
            error: error.message
        };
    }
}

/**
 * Validates RN number consistency across all orders.
 * This function checks if RN numbers are sequential and match the expected pattern.
 * 
 * @returns {Promise<Object>} Object containing validation results
 */
export async function validateOrderRNNumbers(categories = []) {
    try {
        console.log('Starting RN number validation...');

        const ordersCol = collection(db, 'orders');
        const q = query(ordersCol, orderBy('createdAt', 'asc'));
        const snapshot = await getDocs(q);

        if (snapshot.empty) {
            return { success: true, message: 'No orders found to validate.', issues: [] };
        }

        const issues = [];
        let expectedSerial = 1;
        let validCount = 0;

        for (const docSnap of snapshot.docs) {
            const data = docSnap.data();

            // Handle category orders
            if (data.categoryId || data.isCategoryOrder) {
                const category = categories.find(cat => cat.id === data.categoryId);
                if (category && data.serialNumber === category.serialNumber) {
                    // Order was created within category, this is correct
                    validCount++;
                } else if (data.serialNumber && data.serialNumber !== category?.serialNumber) {
                    // Order has wrong RN number
                    issues.push({
                        orderId: docSnap.id,
                        issue: 'Category order has incorrect RN number',
                        currentRN: data.serialNumber,
                        expectedRN: category?.serialNumber || 'Category RN'
                    });
                } else if (!data.serialNumber && category?.serialNumber) {
                    // Order was added to category later, should not have RN
                    validCount++;
                }
                continue;
            }

            const expectedRN = `RN${String(expectedSerial).padStart(5, '0')}`;

            if (!data.serialNumber) {
                issues.push({
                    orderId: docSnap.id,
                    issue: 'Missing RN number',
                    currentRN: 'None',
                    expectedRN
                });
            } else if (data.serialNumber !== expectedRN) {
                issues.push({
                    orderId: docSnap.id,
                    issue: 'RN number mismatch',
                    currentRN: data.serialNumber,
                    expectedRN
                });
            } else {
                validCount++;
            }

            expectedSerial++;
        }

        const result = {
            success: true,
            message: 'RN number validation completed',
            totalOrders: snapshot.docs.length,
            validCount,
            issueCount: issues.length,
            issues
        };

        console.log('Validation results:', result);
        return result;

    } catch (error) {
        console.error('Error validating RN numbers:', error);
        return {
            success: false,
            message: 'Failed to validate RN numbers',
            error: error.message
        };
    }
}

// These functions are now made available globally from within the main component

// Quote status constants
const QUOTE_STATUSES = {
    AWAITING_REQUIREMENTS: 'awaiting_requirements',
    UNDER_NEGOTIATION: 'under_negotiation',
    AWAITING_INFORMATION: 'awaiting_information',
    PROFORMA_RECEIVED: 'proforma_received',
    ADVANCE_PAID: 'advance_paid',
    IN_PROGRESS: 'in_progress',
    COMPLETED: 'completed',
    CATEGORY: 'category'
};

// Helper function to determine quote status based on filled fields
const determineQuoteStatus = (quoteData) => {
    const { supplier, price, quantity, advancePayment, deliveryTimeline, notes, supplierCity } = quoteData;

    // Check if only supplier name is provided (and possibly supplierCity which is required)
    const hasOnlySupplierInfo = supplier && supplier.trim() &&
        (!price || price === '' || price === 0) &&
        (!quantity || quantity === '' || quantity === 0) &&
        (!advancePayment || advancePayment === '' || advancePayment === 0) &&
        (!deliveryTimeline || deliveryTimeline === '' || deliveryTimeline === 0) &&
        (!notes || notes.trim() === '');

    // If only supplier info is provided, it's awaiting quotes
    if (hasOnlySupplierInfo) {
        return QUOTE_STATUSES.AWAITING_REQUIREMENTS;
    }

    // If any additional details are provided, it's under negotiation
    return QUOTE_STATUSES.UNDER_NEGOTIATION;
};

// Quote status display names
const quoteStatusDisplayNames = {
    [QUOTE_STATUSES.AWAITING_REQUIREMENTS]: 'Awaiting Quotes',
    [QUOTE_STATUSES.UNDER_NEGOTIATION]: 'Under Negotiation',
    [QUOTE_STATUSES.AWAITING_INFORMATION]: 'Awaiting Information',
    [QUOTE_STATUSES.PROFORMA_RECEIVED]: 'Proforma Received',
    [QUOTE_STATUSES.ADVANCE_PAID]: 'Advance Paid',
    [QUOTE_STATUSES.IN_PROGRESS]: 'Awaiting Delivery',
    [QUOTE_STATUSES.COMPLETED]: 'Completed',
    [QUOTE_STATUSES.CATEGORY]: 'Category'
};

// Shorter status names for tooltips
const quoteStatusShortNames = {
    [QUOTE_STATUSES.AWAITING_REQUIREMENTS]: 'Awaiting Quotes',
    [QUOTE_STATUSES.UNDER_NEGOTIATION]: 'Negotiating',
    [QUOTE_STATUSES.AWAITING_INFORMATION]: 'Awaiting Info',
    [QUOTE_STATUSES.PROFORMA_RECEIVED]: 'Proforma',
    [QUOTE_STATUSES.ADVANCE_PAID]: 'Advance Paid',
    [QUOTE_STATUSES.IN_PROGRESS]: 'In Progress',
    [QUOTE_STATUSES.COMPLETED]: 'Completed',
    [QUOTE_STATUSES.CATEGORY]: 'Category'
};

// Map quote status to order status
const quoteStatusToOrderStatus = {
    [QUOTE_STATUSES.AWAITING_REQUIREMENTS]: 'Awaiting Requirements',
    [QUOTE_STATUSES.UNDER_NEGOTIATION]: 'Negotiation',
    [QUOTE_STATUSES.AWAITING_INFORMATION]: 'Information',
    [QUOTE_STATUSES.PROFORMA_RECEIVED]: 'Proforma',
    [QUOTE_STATUSES.ADVANCE_PAID]: 'Advance',
    [QUOTE_STATUSES.IN_PROGRESS]: 'In Progress',
    [QUOTE_STATUSES.COMPLETED]: 'Completed',
    [QUOTE_STATUSES.CATEGORY]: 'Category'
};

// Utility: Map internal status values to user-facing labels
const statusDisplayNames = {
    'Awaiting Requirements': 'Awaiting Quotes',
    'Negotiation': 'Under Negotiation',
    'Information': 'Awaiting Information',
    'Proforma': 'Proforma Received',
    'Advance': 'Advance Paid',
    'In Progress': 'In Transit',
    'Completed': 'Completed',
    'Category': 'Category',
};

// Quote status colors - matching order status colors
const quoteStatusColors = {
    [QUOTE_STATUSES.AWAITING_REQUIREMENTS]: 'bg-amber-100 text-amber-800 border border-amber-200',
    [QUOTE_STATUSES.UNDER_NEGOTIATION]: 'bg-orange-100 text-orange-800 border border-orange-200',
    [QUOTE_STATUSES.AWAITING_INFORMATION]: 'bg-sky-100 text-sky-800 border border-sky-200',
    [QUOTE_STATUSES.PROFORMA_RECEIVED]: 'bg-emerald-100 text-emerald-800 border border-emerald-200',
    [QUOTE_STATUSES.ADVANCE_PAID]: 'bg-violet-100 text-violet-800 border border-violet-200',
    [QUOTE_STATUSES.IN_PROGRESS]: 'bg-indigo-100 text-indigo-800 border border-indigo-200',
    [QUOTE_STATUSES.COMPLETED]: 'bg-green-100 text-green-800 border border-green-200',
    [QUOTE_STATUSES.CATEGORY]: 'bg-gray-100 text-gray-800 border border-gray-200'
};

// Helper function to send discussion notifications
const sendDiscussionNotification = async (orderId, order, currentUser, allUsers, messageType = 'text') => {
    try {
        // Get all users except the message sender
        const otherUsers = allUsers.filter(user => user.uid !== currentUser.uid);

        // Create notification message based on message type
        let notificationMessage;
        switch (messageType) {
            case 'voice':
                notificationMessage = `${currentUser.displayName} sent a voice message in order "${order.orderTitle || order.serialNumber}"`;
                break;
            case 'image':
                notificationMessage = `${currentUser.displayName} sent an image in order "${order.orderTitle || order.serialNumber}"`;
                break;
            case 'text':
            default:
                notificationMessage = `${currentUser.displayName} sent a message in order "${order.orderTitle || order.serialNumber}"`;
                break;
        }

        // Send notification to each user
        const notificationPromises = otherUsers.map(user =>
            addDoc(collection(db, 'notifications'), {
                recipientUid: user.uid,
                orderId: orderId,
                orderTitle: order.orderTitle,
                orderStatus: order.status,
                message: notificationMessage,
                type: 'discussion_message',
                isRead: false,
                createdAt: serverTimestamp(),
                senderName: currentUser.displayName,
                senderUid: currentUser.uid,
                messageType: messageType
            })
        );

        await Promise.all(notificationPromises);
        console.log(`Discussion notifications sent to ${otherUsers.length} users`);
    } catch (error) {
        console.error('Error sending discussion notifications:', error);
    }
};

// --- Add DiscussionBox component ---
const DiscussionBox = ({ orderId, order, currentUser, allUsers }) => {
    const [messages, setMessages] = useState([]);
    const [newMsg, setNewMsg] = useState("");
    const [sending, setSending] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [recordingTime, setRecordingTime] = useState(0);
    const [recordingStartTime, setRecordingStartTime] = useState(0);
    const [mediaRecorder, setMediaRecorder] = useState(null);
    const [audioChunks, setAudioChunks] = useState([]);
    const [playingAudio, setPlayingAudio] = useState(null);
    const [audioProgress, setAudioProgress] = useState({});
    const [audioDuration, setAudioDuration] = useState({});
    const [audioElements, setAudioElements] = useState({});
    const [audioWaveforms, setAudioWaveforms] = useState({});
    const [audioLoading, setAudioLoading] = useState({});
    const [uploadingImage, setUploadingImage] = useState(false);

    const [audioLevel, setAudioLevel] = useState(0);
    const [micWarning, setMicWarning] = useState('');
    const [microphones, setMicrophones] = useState([]);
    const [selectedMicId, setSelectedMicId] = useState('');
    const audioContextRef = useRef(null);
    const analyserRef = useRef(null);
    const levelIntervalRef = useRef(null);
    const fileInputRef = useRef(null);
    const messagesEndRef = useRef(null);
    const recordingIntervalRef = useRef(null);


    // Auto-scroll to bottom when new messages arrive
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    useEffect(() => {
        if (!orderId) return;
        const q = query(collection(db, `orders/${orderId}/discussion`), orderBy('createdAt', 'asc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setMessages(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });
        return unsubscribe;
    }, [orderId]);

    // Load available microphones
    useEffect(() => {
        if (!navigator.mediaDevices?.enumerateDevices) return;
        navigator.mediaDevices.enumerateDevices()
            .then(devices => {
                const mics = devices.filter(d => d.kind === 'audioinput');
                setMicrophones(mics);
            })
            .catch(() => { });
    }, []);

    const refreshMicrophones = async () => {
        try {
            const temp = await navigator.mediaDevices.getUserMedia({ audio: true });
            temp.getTracks().forEach(t => t.stop());
            const devices = await navigator.mediaDevices.enumerateDevices();
            const mics = devices.filter(d => d.kind === 'audioinput');
            setMicrophones(mics);
        } catch (_) { }
    };

    // Cleanup recording interval
    useEffect(() => {
        return () => {
            if (recordingIntervalRef.current) {
                clearInterval(recordingIntervalRef.current);
            }
        };
    }, []);



    const handleSend = async (e) => {
        e.preventDefault();
        if (!newMsg.trim()) return;
        setSending(true);
        try {
            await addDoc(collection(db, `orders/${orderId}/discussion`), {
                type: 'text',
                text: newMsg.trim(),
                author: { uid: currentUser.uid, displayName: currentUser.displayName, role: currentUser.role },
                createdAt: serverTimestamp(),
            });

            // Send notification to other users
            await sendDiscussionNotification(orderId, order, currentUser, allUsers, 'text');

            setNewMsg("");
        } catch (error) {
            console.error('Error sending message:', error);
        } finally {
            setSending(false);
        }
    };

    // Cleanup effect for recording state
    useEffect(() => {
        return () => {
            // Cleanup recording state when component unmounts
            resetRecordingState();
        };
    }, []);

    // Reset recording state when order changes
    useEffect(() => {
        // Reset recording state when order changes
        resetRecordingState();
    }, [orderId]);

    // Reset recording state manually
    const resetRecordingState = () => {
        setIsRecording(false);
        setRecordingTime(0);
        setRecordingStartTime(0);
        if (recordingIntervalRef.current) {
            clearInterval(recordingIntervalRef.current);
        }
        if (levelIntervalRef.current) {
            clearInterval(levelIntervalRef.current);
        }
        if (audioContextRef.current) {
            try { audioContextRef.current.close(); } catch (_) { }
            audioContextRef.current = null;
        }
        if (mediaRecorder) {
            if (mediaRecorder.stream) {
                mediaRecorder.stream.getTracks().forEach(track => track.stop());
            }
            setMediaRecorder(null);
        }
        setAudioChunks([]);
    };

    // Voice recording functions
    const startRecording = async () => {
        // Always reset any previous recording state before starting new recording
        resetRecordingState();

        try {
            const audioConstraints = {
                channelCount: 1,
                noiseSuppression: true,
                echoCancellation: true,
                autoGainControl: true,
                sampleRate: 48000
            };
            if (selectedMicId) {
                audioConstraints.deviceId = { exact: selectedMicId };
            }
            const stream = await navigator.mediaDevices.getUserMedia({ audio: audioConstraints });

            // Choose the best supported audio mime type (with codecs where possible)
            // Prioritize more widely supported formats
            const candidateMimeTypes = [
                'audio/mp4;codecs=mp4a.40.2',
                'audio/mp4',
                'audio/wav',
                'audio/webm;codecs=opus',
                'audio/webm',
                'audio/ogg;codecs=opus',
                'audio/ogg'
            ];
            let selectedMimeType = '';
            if (window.MediaRecorder && typeof MediaRecorder.isTypeSupported === 'function') {
                for (const type of candidateMimeTypes) {
                    try {
                        if (MediaRecorder.isTypeSupported(type)) {
                            selectedMimeType = type;
                            console.log('Selected audio format:', type);
                            break;
                        }
                    } catch (_) {
                        // Some browsers may throw on unknown strings; ignore and continue
                    }
                }
            }

            // Fallback to default if no specific format is supported
            if (!selectedMimeType) {
                console.log('No specific audio format supported, using default');
            }

            const recorder = selectedMimeType
                ? new MediaRecorder(stream, { mimeType: selectedMimeType })
                : new MediaRecorder(stream);
            const chunks = [];

            // Create analyser to drive the level meter
            try {
                audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
                const source = audioContextRef.current.createMediaStreamSource(stream);
                analyserRef.current = audioContextRef.current.createAnalyser();
                analyserRef.current.fftSize = 2048;
                source.connect(analyserRef.current);
                const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
                levelIntervalRef.current = setInterval(() => {
                    if (!analyserRef.current) return;
                    analyserRef.current.getByteTimeDomainData(dataArray);
                    let sum = 0;
                    for (let i = 0; i < dataArray.length; i++) {
                        const v = (dataArray[i] - 128) / 128; // -1..1
                        sum += v * v;
                    }
                    const rms = Math.sqrt(sum / dataArray.length); // 0..1
                    setAudioLevel(rms);
                }, 100);
            } catch (_) {
                // Ignore level meter setup errors
            }

            recorder.ondataavailable = (e) => {
                if (e.data.size > 0) {
                    chunks.push(e.data);
                }
            };

            recorder.onstop = async () => {
                const audioBlob = new Blob(chunks, { type: recorder.mimeType });
                if (audioBlob.size > 0) {
                    await uploadVoiceMessage(audioBlob);
                }
                stream.getTracks().forEach(track => track.stop());
                if (levelIntervalRef.current) clearInterval(levelIntervalRef.current);
                if (audioContextRef.current) {
                    try { await audioContextRef.current.close(); } catch (_) { }
                    audioContextRef.current = null;
                }
            };

            recorder.onerror = (event) => {
                console.error('Recording error:', event.error);
                setMicWarning('Recording error. Try another microphone from the selector.');
                alert('Recording failed. Please try again.');
                stopRecording();
            };

            // Start recording with timeslice to ensure data is captured properly
            recorder.start(100); // Capture data every 100ms
            setMediaRecorder(recorder);
            setAudioChunks(chunks);
            setIsRecording(true);
            setRecordingTime(0);
            const startTime = Date.now();
            setRecordingStartTime(startTime);
            console.log('Recording started at:', startTime, 'recordingStartTime set to:', startTime);

            // Start precise timer (update every 100ms for smooth display)
            recordingIntervalRef.current = setInterval(() => {
                const elapsed = Math.floor((Date.now() - startTime) / 1000);
                setRecordingTime(elapsed);

                // Auto-stop after 5 minutes
                if (elapsed >= 300) {
                    stopRecording();
                }
            }, 100);
        } catch (error) {
            console.error('Error starting recording:', error);
            // Reset recording state on error
            resetRecordingState();
            if (error.name === 'NotAllowedError') {
                alert('Microphone access denied. Please allow microphone permissions and try again.');
            } else {
                alert('Could not access microphone. Please check permissions and try again.');
            }
        }
    };

    const stopRecording = () => {
        if (mediaRecorder && isRecording) {
            // Calculate final recording duration precisely
            const currentTime = Date.now();
            const finalDuration = Math.floor((currentTime - recordingStartTime) / 1000);
            console.log('Recording stopped - currentTime:', currentTime, 'recordingStartTime:', recordingStartTime, 'calculated duration:', finalDuration);

            // Don't save recordings shorter than 1 second
            if (finalDuration < 1) {
                mediaRecorder.stop();
                resetRecordingState();
                alert('Recording too short. Please record for at least 1 second.');
                return;
            }

            // Update recording time with final precise duration
            setRecordingTime(finalDuration);

            // Stop the recorder and clean up
            mediaRecorder.stop();
            setIsRecording(false);
            if (recordingIntervalRef.current) {
                clearInterval(recordingIntervalRef.current);
            }

            // Clean up stream and audio context
            if (mediaRecorder.stream) {
                mediaRecorder.stream.getTracks().forEach(track => track.stop());
            }
            if (levelIntervalRef.current) clearInterval(levelIntervalRef.current);
            if (audioContextRef.current) {
                try { audioContextRef.current.close(); } catch (_) { }
                audioContextRef.current = null;
            }
        } else {
            // If not recording but stopRecording is called (e.g., on mouse leave), reset everything
            resetRecordingState();
        }
    };

    const uploadVoiceMessage = async (audioBlob) => {
        try {
            // Use the final precise recording duration
            const currentTime = Date.now();
            const finalDuration = Math.floor((currentTime - recordingStartTime) / 1000);
            console.log('Uploading voice message - currentTime:', currentTime, 'recordingStartTime:', recordingStartTime, 'calculated duration:', finalDuration);

            console.log('Uploading voice message:', {
                blobSize: audioBlob.size,
                blobType: audioBlob.type,
                duration: finalDuration
            });

            // Derive extension from blob type for correct playback on all browsers
            const blobType = audioBlob.type || 'audio/webm';
            let extension = 'webm';
            if (blobType.includes('ogg')) extension = 'ogg';
            else if (blobType.includes('mp4')) extension = 'm4a';
            else if (blobType.includes('mpeg')) extension = 'mp3';
            else if (blobType.includes('wav')) extension = 'wav';

            const fileName = `voice_${Date.now()}.${extension}`;
            const storageReference = storageRef(storage, `orders/${orderId}/voice/${fileName}`);

            console.log('Uploading to storage path:', `orders/${orderId}/voice/${fileName}`);
            console.log('Audio blob type:', blobType, 'Size:', audioBlob.size);

            // Preserve content type metadata so the browser decodes correctly
            await uploadBytes(storageReference, audioBlob, { contentType: blobType });

            const downloadURL = await getDownloadURL(storageReference);
            console.log('Download URL obtained:', downloadURL);

            const messageData = {
                type: 'voice',
                audioUrl: downloadURL,
                duration: finalDuration,
                author: { uid: currentUser.uid, displayName: currentUser.displayName, role: currentUser.role },
                createdAt: serverTimestamp(),
            };

            console.log('Saving message to Firestore:', messageData);
            const docRef = await addDoc(collection(db, `orders/${orderId}/discussion`), messageData);
            console.log('Voice message saved with ID:', docRef.id);

            // Send notification to other users
            await sendDiscussionNotification(orderId, order, currentUser, allUsers, 'voice');

        } catch (error) {
            console.error('Error uploading voice message:', error);
            alert(`Failed to upload voice message: ${error.message}`);
        }
    };

    // Image upload functions
    const handleImageUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            alert('Please select an image file');
            return;
        }

        if (file.size > 5 * 1024 * 1024) { // 5MB limit
            alert('Image size should be less than 5MB');
            return;
        }

        setUploadingImage(true);
        try {
            const fileName = `image_${Date.now()}_${file.name}`;
            const storageReference = storageRef(storage, `orders/${orderId}/images/${fileName}`);
            await uploadBytes(storageReference, file);
            const downloadURL = await getDownloadURL(storageReference);

            await addDoc(collection(db, `orders/${orderId}/discussion`), {
                type: 'image',
                imageUrl: downloadURL,
                imageName: file.name,
                author: { uid: currentUser.uid, displayName: currentUser.displayName, role: currentUser.role },
                createdAt: serverTimestamp(),
            });

            // Send notification to other users
            await sendDiscussionNotification(orderId, order, currentUser, allUsers, 'image');

        } catch (error) {
            console.error('Error uploading image:', error);
            alert('Failed to upload image');
        } finally {
            setUploadingImage(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    // Audio playback functions
    const playAudio = async (audioUrl, messageId, storedDuration = 0) => {
        // Check if this is a pause/resume action
        if (playingAudio === messageId) {
            const audio = audioElements[messageId];
            if (audio) {
                if (audio.paused) {
                    // Resume playback
                    audio.play().catch(error => {
                        console.error('Error resuming audio:', error);
                        setPlayingAudio(null);
                    });
                } else {
                    // Pause playback
                    audio.pause();
                    setPlayingAudio(null);
                }
            }
            return;
        }

        // Check if browser supports audio
        if (!window.AudioContext && !window.webkitAudioContext) {
            alert('Your browser does not support audio playback. Please try a different browser.');
            return;
        }


        console.log('Attempting to play audio:', { audioUrl, messageId });

        if (!audioUrl) {
            console.error('No audio URL provided');
            alert('Audio URL is missing');
            return;
        }

        if (playingAudio === messageId) {
            console.log('Stopping current audio');
            setPlayingAudio(null);
            return;
        }

        try {
            // Stop any currently playing audio
            if (playingAudio && audioElements[playingAudio]) {
                audioElements[playingAudio].pause();
                audioElements[playingAudio].currentTime = 0;
            }

            // Set loading state
            setAudioLoading(prev => ({ ...prev, [messageId]: true }));

            const audio = new Audio();

            // Add event listeners for better debugging and progress tracking
            audio.onloadstart = () => console.log('Audio loading started');
            audio.oncanplay = () => console.log('Audio can play');
            audio.oncanplaythrough = () => console.log('Audio can play through');
            audio.onloadedmetadata = () => {
                console.log('Audio metadata loaded, duration:', audio.duration, 'stored duration:', storedDuration);

                // Validate audio duration to prevent Infinity issues
                let validDuration = 0;

                // First priority: use audio.duration if it's valid
                if (isFinite(audio.duration) && !isNaN(audio.duration) && audio.duration > 0) {
                    validDuration = audio.duration;
                    console.log('Using audio.duration:', validDuration);
                }
                // Second priority: use stored duration if it's valid
                else if (storedDuration && isFinite(storedDuration) && !isNaN(storedDuration) && storedDuration > 0) {
                    validDuration = storedDuration;
                    console.log('Using stored duration:', validDuration);
                }
                // Fallback: use a reasonable default
                else {
                    validDuration = 60; // Default to 1 minute if no duration info
                    console.log('Using fallback duration:', validDuration);
                }

                // Store the validated duration
                setAudioDuration(prev => ({ ...prev, [messageId]: validDuration }));
                console.log('Final duration set for message', messageId, ':', validDuration);
            };
            audio.ontimeupdate = () => {
                // Get the most reliable duration from state
                const duration = audioDuration[messageId];
                if (duration && isFinite(duration) && !isNaN(duration) && duration > 0) {
                    const progress = (audio.currentTime / duration) * 100;
                    setAudioProgress(prev => ({ ...prev, [messageId]: progress }));
                } else {
                    console.warn('Invalid duration in ontimeupdate for message', messageId, 'duration:', duration);
                }
            };
            audio.onended = () => {
                console.log('Audio playback ended');
                setPlayingAudio(null);
                setAudioProgress(prev => ({ ...prev, [messageId]: 0 }));
                // Clean up audio element
                setAudioElements(prev => {
                    const newElements = { ...prev };
                    delete newElements[messageId];
                    return newElements;
                });
            };

            // Add seeking functionality
            audio.onseeked = () => {
                console.log('Audio seeked to:', audio.currentTime);
            };

            audio.onpause = () => {
                console.log('Audio paused');
                setPlayingAudio(null);
            };
            audio.onerror = (e) => {
                clearTimeout(loadTimeout); // Clear the loading timeout
                console.error('Audio error:', e);
                console.error('Audio error details:', audio.error);
                console.error('Audio src that failed:', audio.src);
                console.error('Audio duration when error occurred:', audio.duration);
                console.error('Audio readyState:', audio.readyState);
                console.error('Audio networkState:', audio.networkState);

                // Check if this is a CORS or format issue
                const isCorsError = audio.error?.code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED;
                const isFormatError = audio.error?.code === MediaError.MEDIA_ERR_DECODE;

                // Provide more specific error messages
                let errorMessage = 'Unknown error';
                if (audio.error) {
                    switch (audio.error.code) {
                        case MediaError.MEDIA_ERR_ABORTED:
                            errorMessage = 'Audio playback was aborted';
                            break;
                        case MediaError.MEDIA_ERR_NETWORK:
                            errorMessage = 'Network error while loading audio - check your internet connection';
                            break;
                        case MediaError.MEDIA_ERR_DECODE:
                            errorMessage = 'Audio format not supported or file is corrupted. Try recording in a different format.';
                            break;
                        case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
                            errorMessage = 'Audio source not supported. This might be a CORS issue with Firebase Storage.';
                            break;
                        default:
                            errorMessage = audio.error.message || 'Audio playback failed';
                    }
                }

                // Provide more helpful error message for CORS/format issues
                if (errorMessage.includes('CORS') || errorMessage.includes('format')) {
                    alert(`Audio playback failed: ${errorMessage}\n\nTry recording a new voice message - the system will automatically use the best supported format for your browser.`);
                } else {
                    alert(`Failed to play audio message: ${errorMessage}`);
                }

                setPlayingAudio(null);
                setAudioLoading(prev => ({ ...prev, [messageId]: false }));
                setAudioProgress(prev => ({ ...prev, [messageId]: 0 }));
                // Clean up audio element
                setAudioElements(prev => {
                    const newElements = { ...prev };
                    delete newElements[messageId];
                    return newElements;
                });
            };
            audio.onabort = () => {
                clearTimeout(loadTimeout); // Clear the loading timeout
                console.log('Audio playback aborted');
                setPlayingAudio(null);
                setAudioLoading(prev => ({ ...prev, [messageId]: false }));
                setAudioProgress(prev => ({ ...prev, [messageId]: 0 }));
                // Clean up audio element
                setAudioElements(prev => {
                    const newElements = { ...prev };
                    delete newElements[messageId];
                    return newElements;
                });
            };

            // Set the source directly to the Firebase Storage URL
            audio.preload = 'auto';
            audio.volume = 1.0;
            audio.muted = false;

            // Try to determine the best audio format for the browser
            const url = new URL(audioUrl);
            const fileExtension = url.pathname.split('.').pop()?.toLowerCase();
            console.log('Audio file extension:', fileExtension);

            // Set audio source with error handling
            audio.src = audioUrl;

            // Add additional error handling for CORS issues
            audio.onloadstart = () => {
                console.log('Audio loading started - attempting to load from Firebase Storage');
            };

            // Store the audio element for later control
            setAudioElements(prev => ({ ...prev, [messageId]: audio }));

            // Set a timeout to handle cases where audio never loads properly
            const loadTimeout = setTimeout(() => {
                if (audioLoading[messageId] && !playingAudio) {
                    console.warn('Audio loading timeout - duration:', audio.duration);
                    setAudioLoading(prev => ({ ...prev, [messageId]: false }));

                    // Use stored duration if available and valid, otherwise set a reasonable default
                    let fallbackDuration = 0;
                    if (storedDuration && isFinite(storedDuration) && !isNaN(storedDuration) && storedDuration > 0) {
                        fallbackDuration = storedDuration;
                        console.log('Using stored duration as fallback:', fallbackDuration);
                    } else {
                        fallbackDuration = 60; // Default to 1 minute
                        console.log('Using default fallback duration:', fallbackDuration);
                    }

                    console.error('Audio failed to load properly, using fallback duration:', fallbackDuration);
                    setAudioDuration(prev => ({ ...prev, [messageId]: fallbackDuration }));

                    // Clean up the failed audio element
                    setAudioElements(prev => {
                        const newElements = { ...prev };
                        delete newElements[messageId];
                        return newElements;
                    });

                    // Show retry option to user with format conversion suggestion
                    if (window.confirm('Audio failed to load. This might be due to format compatibility or CORS issues with Firebase Storage. Would you like to try again?')) {
                        // Retry loading the audio
                        setTimeout(() => {
                            playAudio(audioUrl, messageId, storedDuration);
                        }, 1000);
                    }
                }
            }, 15000); // 15 second timeout for slower connections

            // Attempt to play
            audio.play().then(() => {
                clearTimeout(loadTimeout); // Clear the loading timeout
                console.log('Audio playback started successfully');
                console.log('Audio volume:', audio.volume);
                console.log('Audio muted:', audio.muted);
                console.log('Audio duration:', audio.duration, 'stored duration:', storedDuration);
                setPlayingAudio(messageId);
                setAudioLoading(prev => ({ ...prev, [messageId]: false }));

                // Check if audio is actually playing
                setTimeout(() => {
                    if (audio.paused) {
                        console.warn('Audio appears to be paused after play attempt');
                        // Try to resume if it got paused
                        audio.play().catch(e => console.error('Failed to resume audio:', e));
                    } else {
                        console.log('Audio is playing successfully');
                    }
                }, 100);

            }).catch(error => {
                clearTimeout(loadTimeout); // Clear the loading timeout
                console.error('Error playing audio:', error);

                // Handle specific play() errors
                let errorMessage = 'Unknown error';
                if (error.name === 'NotAllowedError') {
                    errorMessage = 'Audio playback was blocked by the browser. Please allow audio permissions and try again.';
                } else if (error.name === 'NotSupportedError') {
                    errorMessage = 'Audio format not supported by your browser.';
                } else if (error.name === 'AbortError') {
                    errorMessage = 'Audio playback was interrupted. Please try again.';
                } else if (error.name === 'NetworkError') {
                    errorMessage = 'Network error while loading audio. Please check your connection and try again.';
                } else {
                    errorMessage = error.message || 'Failed to start audio playback';
                }

                console.error('Audio playback error details:', error);

                // Clean up the failed audio element
                setAudioElements(prev => {
                    const newElements = { ...prev };
                    delete newElements[messageId];
                    return newElements;
                });

                // Provide more helpful error message for CORS/format issues
                if (errorMessage.includes('CORS') || errorMessage.includes('format')) {
                    alert(`Audio playback failed: ${errorMessage}\n\nTry recording a new voice message - the system will automatically use the best supported format for your browser.`);
                } else {
                    alert(`Failed to play audio message: ${errorMessage}`);
                }

                setPlayingAudio(null);
                setAudioLoading(prev => ({ ...prev, [messageId]: false }));
            });

        } catch (error) {
            console.error('Error creating audio element:', error);
            alert(`Failed to create audio element: ${error.message}`);
            setPlayingAudio(null);
        }
    };

    // Generate waveform data for voice messages
    const generateWaveform = (messageId, duration = 10) => {
        // Create a stylized waveform with random heights that simulate audio levels
        const bars = Math.min(Math.max(duration * 2, 20), 40); // 2 bars per second, min 20, max 40
        const waveform = Array.from({ length: bars }, () => Math.random() * 0.8 + 0.2); // Random values between 0.2 and 1.0

        setAudioWaveforms(prev => ({ ...prev, [messageId]: waveform }));
        return waveform;
    };

    // Seek through audio
    const seekAudio = (messageId, progressPercent) => {
        const audioElement = audioElements[messageId];
        if (audioElement && playingAudio === messageId) {
            // Get the most reliable duration from multiple sources
            let duration = 0;

            // First try audioElement.duration if valid
            if (audioElement.duration && isFinite(audioElement.duration) && !isNaN(audioElement.duration) && audioElement.duration > 0) {
                duration = audioElement.duration;
            }
            // Then try stored duration from state
            else if (audioDuration[messageId] && isFinite(audioDuration[messageId]) && !isNaN(audioDuration[messageId]) && audioDuration[messageId] > 0) {
                duration = audioDuration[messageId];
            }
            // Finally try message duration if available
            else if (messages.find(m => m.id === messageId)?.duration &&
                isFinite(messages.find(m => m.id === messageId).duration) &&
                !isNaN(messages.find(m => m.id === messageId).duration) &&
                messages.find(m => m.id === messageId).duration > 0) {
                duration = messages.find(m => m.id === messageId).duration;
            }

            if (duration > 0) {
                const seekTime = (progressPercent / 100) * duration;
                audioElement.currentTime = seekTime;
                console.log('Seeked to:', { messageId, progressPercent, seekTime, duration });
            } else {
                console.warn('Cannot seek: invalid duration for message', messageId);
            }
        }
    };

    // Delete handler
    const handleDelete = async (msgId, msgType, msgData) => {
        if (!window.confirm('Delete this message?')) return;

        try {
            // Handle backward compatibility - if no type is specified, treat as text
            const messageType = msgType || 'text';

            // Delete from storage if it's a media message
            if (messageType === 'voice' && msgData.audioUrl) {
                const audioRef = storageRef(storage, msgData.audioUrl);
                await deleteObject(audioRef);
            } else if (messageType === 'image' && msgData.imageUrl) {
                const imageRef = storageRef(storage, msgData.imageUrl);
                await deleteObject(imageRef);
            }

            // Delete from Firestore
            await deleteDoc(doc(db, `orders/${orderId}/discussion`, msgId));
        } catch (error) {
            console.error('Error deleting message:', error);
            alert('Failed to delete message');
        }
    };

    // Helper to check if user can delete
    const canDelete = (msg) => {
        return msg.author?.uid === currentUser.uid || ['master', 'auto-master'].includes(currentUser.role);
    };

    // Helper function to get the most reliable duration for a message
    const getReliableDuration = (messageId, fallbackDuration = 0) => {
        // First try audioElements duration if valid
        if (audioElements[messageId]?.duration &&
            isFinite(audioElements[messageId].duration) &&
            !isNaN(audioElements[messageId].duration) &&
            audioElements[messageId].duration > 0) {
            return audioElements[messageId].duration;
        }

        // Then try stored duration from state
        if (audioDuration[messageId] &&
            isFinite(audioDuration[messageId]) &&
            !isNaN(audioDuration[messageId]) &&
            audioDuration[messageId] > 0) {
            return audioDuration[messageId];
        }

        // Finally try message duration if available
        const message = messages.find(m => m.id === messageId);
        if (message?.duration &&
            isFinite(message.duration) &&
            !isNaN(message.duration) &&
            message.duration > 0) {
            return message.duration;
        }

        // Return fallback if nothing else works
        return fallbackDuration;
    };

    // Format recording time
    const formatRecordingTime = (seconds) => {
        // Handle invalid duration values
        if (!isFinite(seconds) || isNaN(seconds) || seconds <= 0) {
            return '0s';
        }

        const flooredSeconds = Math.floor(seconds);

        if (flooredSeconds < 60) {
            // For durations less than 60 seconds, show as "1s", "2s", etc.
            return `${flooredSeconds}s`;
        } else {
            // For longer durations, use MM:SS format
            const mins = Math.floor(flooredSeconds / 60);
            const secs = flooredSeconds % 60;
            return `${mins}:${secs.toString().padStart(2, '0')}`;
        }
    };



    // Test audio permissions and provide guidance
    const testAudioPermissions = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            stream.getTracks().forEach(track => track.stop());
            alert('✅ Audio permissions are working! If you still can\'t hear voice messages, check:\n\n1. Your system volume\n2. Browser tab volume (click the speaker icon in the tab)\n3. Any browser extensions that might block audio');
        } catch (error) {
            alert('❌ Audio permissions are blocked. Please:\n\n1. Click the microphone icon in your browser\'s address bar\n2. Allow microphone access\n3. Refresh the page and try again');
        }
    };

    const renderMessage = (msg) => {
        const isOwnMessage = msg.author?.uid === currentUser.uid;
        const messageClass = isOwnMessage
            ? "bg-black text-white ml-auto"
            : "bg-gray-100 text-gray-800";

        // Handle backward compatibility - if no type is specified, treat as text
        const messageType = msg.type || 'text';

        // Get user initials
        const getUserInitials = (name) => {
            if (!name) return 'U';
            return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
        };

        return (
            <div key={msg.id} className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'} mb-3 items-end gap-2`}>
                {!isOwnMessage && (
                    <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center text-xs font-semibold text-gray-600 flex-shrink-0">
                        {getUserInitials(msg.author?.displayName)}
                    </div>
                )}
                <div className={`max-w-xs lg:max-w-md rounded-lg p-3 relative group ${messageClass}`}>
                    {(messageType === 'text' || !messageType) && (
                        <div className="text-sm">{msg.text}</div>
                    )}

                    {messageType === 'image' && (
                        <div>
                            <img
                                src={msg.imageUrl}
                                alt={msg.imageName || 'Image'}
                                className="max-w-full h-auto rounded cursor-pointer hover:opacity-90 transition-opacity"
                                onClick={() => window.open(msg.imageUrl, '_blank')}
                            />
                            {msg.imageName && (
                                <div className="text-xs mt-1 opacity-75">{msg.imageName}</div>
                            )}
                        </div>
                    )}

                    {messageType === 'voice' && (
                        <div className="flex items-center space-x-2">
                            <button
                                onClick={() => {
                                    console.log('Voice message clicked:', {
                                        messageId: msg.id,
                                        audioUrl: msg.audioUrl,
                                        duration: msg.duration,
                                        type: msg.type
                                    });
                                    playAudio(msg.audioUrl, msg.id, msg.duration);
                                }}
                                className={`flex items-center justify-center w-10 h-10 rounded-full transition-all duration-200 transform hover:scale-105 ${playingAudio === msg.id
                                    ? 'bg-white bg-opacity-40 shadow-lg animate-pulse'
                                    : audioLoading[msg.id]
                                        ? 'bg-white bg-opacity-35 shadow-md'
                                        : 'bg-white bg-opacity-25 hover:bg-opacity-35 shadow-md'
                                    }`}
                                title={playingAudio === msg.id
                                    ? `Pause voice message (${formatRecordingTime(audioElements[msg.id]?.currentTime || 0)} / ${formatRecordingTime(getReliableDuration(msg.id, 0))})`
                                    : `Play voice message (${formatRecordingTime(getReliableDuration(msg.id, 0))})`
                                }
                            >
                                {playingAudio === msg.id ? (
                                    <div className="flex items-center space-x-0.5">
                                        <div className="w-1 h-3 bg-black rounded-sm animate-pulse"></div>
                                        <div className="w-1 h-2 bg-black rounded-sm animate-pulse" style={{ animationDelay: '0.1s' }}></div>
                                        <div className="w-1 h-4 bg-black rounded-sm animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                                        <div className="w-1 h-2 bg-black rounded-sm animate-pulse" style={{ animationDelay: '0.1s' }}></div>
                                        <div className="w-1 h-3 bg-black rounded-sm animate-pulse"></div>
                                    </div>
                                ) : audioLoading[msg.id] ? (
                                    <div className="flex items-center space-x-0.5">
                                        <div className="w-1 h-2 bg-black rounded-sm animate-bounce"></div>
                                        <div className="w-1 h-3 bg-black rounded-sm animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                                        <div className="w-1 h-2 bg-black rounded-sm animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                                    </div>
                                ) : (
                                    <div className="flex items-center">
                                        <div className="w-0 h-0 border-l-4 border-l-black border-t-2 border-t-transparent border-b-2 border-b-transparent ml-0.5"></div>
                                    </div>
                                )}
                            </button>
                            <div className="flex-1">
                                <div
                                    className="w-full bg-white bg-opacity-10 rounded-lg h-6 cursor-pointer relative overflow-hidden flex items-end space-x-0.5 p-1"
                                    onClick={(e) => {
                                        if (playingAudio === msg.id) {
                                            const rect = e.currentTarget.getBoundingClientRect();
                                            const progressPercent = ((e.clientX - rect.left) / rect.width) * 100;
                                            seekAudio(msg.id, progressPercent);
                                        }
                                    }}
                                >
                                    {(() => {
                                        // Generate waveform if not already generated
                                        const waveform = audioWaveforms[msg.id] || generateWaveform(msg.id,
                                            (audioElements[msg.id]?.duration && isFinite(audioElements[msg.id].duration) && !isNaN(audioElements[msg.id].duration) && audioElements[msg.id].duration > 0)
                                                ? audioElements[msg.id].duration
                                                : (msg.duration && isFinite(msg.duration) && !isNaN(msg.duration) && msg.duration > 0)
                                                    ? msg.duration
                                                    : 10
                                        );
                                        return waveform.map((height, index) => {
                                            const isPlayed = playingAudio === msg.id && (index / waveform.length) * 100 <= (audioProgress[msg.id] || 0);
                                            return (
                                                <div
                                                    key={index}
                                                    className={`flex-1 min-w-[2px] rounded-sm transition-all duration-100 ${isPlayed
                                                        ? 'bg-white'
                                                        : playingAudio === msg.id
                                                            ? 'bg-white bg-opacity-30'
                                                            : 'bg-white bg-opacity-20'
                                                        }`}
                                                    style={{
                                                        height: `${Math.max(height * 20, 3)}px`, // Scale height, minimum 3px
                                                    }}
                                                />
                                            );
                                        });
                                    })()}
                                </div>
                            </div>
                            <span className="text-xs">{formatRecordingTime(getReliableDuration(msg.id, 0))}</span>
                            {!msg.audioUrl && (
                                <span className="text-xs text-red-300">No audio URL</span>
                            )}
                            {playingAudio === msg.id && (
                                <div className="flex flex-col items-center space-y-1">
                                    <div className="text-xs text-green-300 animate-pulse flex items-center space-x-1">
                                        <div className="w-2 h-2 bg-green-300 rounded-full animate-ping"></div>
                                        <span>Playing</span>
                                    </div>
                                    <div className="text-xs text-white text-opacity-70">
                                        {audioElements[msg.id] ?
                                            `${formatRecordingTime(audioElements[msg.id].currentTime || 0)} / ${formatRecordingTime(getReliableDuration(msg.id, 0))}`
                                            :
                                            formatRecordingTime(getReliableDuration(msg.id, 0))
                                        }
                                    </div>
                                </div>
                            )}

                        </div>
                    )}

                    <div className={`text-xs mt-1 flex justify-between items-center ${isOwnMessage ? 'text-white text-opacity-75' : 'text-gray-500'}`}>
                        <span>{msg.author?.displayName || "User"}</span>
                        <span>{msg.createdAt?.toDate ? msg.createdAt.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ""}</span>
                        {canDelete(msg) && (
                            <button
                                onClick={() => handleDelete(msg.id, msg.type || 'text', msg)}
                                className="ml-2 p-1 rounded-full hover:bg-black hover:bg-opacity-20 opacity-0 group-hover:opacity-100 transition-opacity"
                                title="Delete message"
                            >
                                <Trash2 size={12} />
                            </button>
                        )}
                    </div>
                </div>
                {isOwnMessage && (
                    <div className="w-8 h-8 bg-black rounded-full flex items-center justify-center text-xs font-semibold text-white flex-shrink-0">
                        {getUserInitials(currentUser.displayName)}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="flex flex-col h-full border rounded-lg bg-white">
            {/* Header */}
            <div className="bg-black text-white p-4 rounded-t-lg">
                <div className="flex justify-between items-start">
                    <div>
                        <h3 className="font-semibold text-lg">Discussion</h3>
                        <p className="text-sm opacity-90">{order?.serialNumber || `Order #${orderId}`}</p>
                        {/* Microphone selector */}
                        {microphones.length > 0 && (
                            <div className="mt-2 flex items-center gap-2">
                                <label className="text-xs opacity-80">Mic:</label>
                                <select
                                    value={selectedMicId}
                                    onChange={(e) => setSelectedMicId(e.target.value)}
                                    className="text-black text-xs px-2 py-1 rounded"
                                    title="Select microphone"
                                >
                                    <option value="">Default</option>
                                    {microphones.map((m) => (
                                        <option key={m.deviceId} value={m.deviceId}>
                                            {m.label || `Microphone (${m.deviceId.slice(0, 6)}...)`}
                                        </option>
                                    ))}
                                </select>
                                <button
                                    type="button"
                                    onClick={refreshMicrophones}
                                    className="text-xs bg-white bg-opacity-20 hover:bg-opacity-30 px-2 py-1 rounded"
                                    title="Refresh microphones"
                                >
                                    ↻
                                </button>
                            </div>
                        )}
                    </div>
                    <button
                        onClick={testAudioPermissions}
                        className="text-xs bg-white bg-opacity-20 hover:bg-opacity-30 px-2 py-1 rounded transition-colors"
                        title="Test audio permissions"
                    >
                        Test Audio
                    </button>
                </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-gray-50" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23f0f0f0" fill-opacity="0.1"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }}>
                {messages.length === 0 && (
                    <div className="text-center text-gray-400 text-sm py-8">
                        <p>No messages yet. Start a conversation!</p>
                    </div>
                )}
                {messages.map(renderMessage)}
                <div ref={messagesEndRef} />
            </div>

            {/* Recording indicator */}
            {isRecording && (
                <div className="bg-red-500 text-white p-3 text-center">
                    <div className="flex items-center justify-center space-x-3">
                        <div className="w-3 h-3 bg-white rounded-full animate-pulse"></div>
                        <span>Recording... {formatRecordingTime(recordingTime)}</span>
                        <div className="flex items-center space-x-1">
                            <span className="text-xs opacity-80">Level</span>
                            <div className="w-24 h-2 bg-white bg-opacity-20 rounded">
                                <div
                                    className="h-2 bg-white rounded"
                                    style={{ width: `${Math.min(100, Math.max(5, Math.round(audioLevel * 200)))}%` }}
                                />
                            </div>
                        </div>
                        {micWarning && (
                            <span className="text-xs text-yellow-200">{micWarning}</span>
                        )}
                        <button
                            onClick={stopRecording}
                            className="ml-2 px-3 py-1 bg-white bg-opacity-20 rounded hover:bg-opacity-30"
                        >
                            Stop
                        </button>
                    </div>
                </div>
            )}

            {/* Input area */}
            <div className="p-4 border-t bg-white">
                <form onSubmit={handleSend} className="flex items-center gap-2">

                    {/* Image upload button */}
                    <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadingImage}
                        className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50"
                        title="Upload image"
                    >
                        <Image size={20} />
                    </button>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                    />

                    {/* Text input */}
                    <input
                        type="text"
                        value={newMsg}
                        onChange={e => setNewMsg(e.target.value)}
                        onKeyPress={e => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                if (newMsg.trim() && !sending && !uploadingImage) {
                                    handleSend(e);
                                }
                            }
                        }}
                        placeholder="Type a message..."
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                        disabled={sending || uploadingImage}
                    />

                    {/* Voice recording button */}
                    <button
                        type="button"
                        onMouseDown={startRecording}
                        onMouseUp={stopRecording}
                        onMouseLeave={stopRecording}
                        onTouchStart={startRecording}
                        onTouchEnd={stopRecording}
                        disabled={sending || uploadingImage}
                        className={`p-2 rounded-full transition-colors disabled:opacity-50 ${isRecording
                            ? 'text-red-500 bg-red-50 animate-pulse'
                            : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                            }`}
                        title="Hold to record voice message"
                    >
                        {isRecording ? <MicOff size={20} /> : <Mic size={20} />}
                    </button>

                    {/* Send button */}
                    <button
                        type="submit"
                        className="bg-black text-white p-2 rounded-full hover:bg-gray-800 disabled:bg-gray-400 transition-colors"
                        disabled={sending || uploadingImage || !newMsg.trim()}
                    >
                        {sending ? (
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                            <Send size={20} />
                        )}
                    </button>
                </form>

                {/* Upload indicator */}
                {uploadingImage && (
                    <div className="text-center text-sm text-gray-500 mt-2">
                        Uploading image...
                    </div>
                )}
            </div>
        </div>
    );
};

// Add CategoryOrderDetails component
const CategoryOrderDetails = ({ order, categoryId, setCurrentView, allVendors, currentUser, isMaster, categories = [], decreaseRNCounter, handleRemoveOrderFromCategory, allUsers, setSelectedOrder }) => {
    const [quotes, setQuotes] = useState([]);
    const [quotesLoading, setQuotesLoading] = useState(true);
    const [quotesError, setQuotesError] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [activeTab, setActiveTab] = useState('received');
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [isEditingDesc, setIsEditingDesc] = useState(false);
    const [isEditingSummary, setIsEditingSummary] = useState(false);
    const [description, setDescription] = useState(order.description);
    const [aiSummary, setAiSummary] = useState(order.aiSummary);
    const [quoteToEdit, setQuoteToEdit] = useState(null);
    const [isEditingFinalPrice, setIsEditingFinalPrice] = useState(false);
    const [finalPrice, setFinalPrice] = useState(order.finalPrice || '');
    const [draggedIndex, setDraggedIndex] = useState(null);
    const [showDescriptionDropdown, setShowDescriptionDropdown] = useState(false);
    const descriptionDropdownRef = useRef(null);
    const [expandedQuoteId, setExpandedQuoteId] = useState(null);
    const [comments, setComments] = useState([]);
    const [newComment, setNewComment] = useState("");
    const [deliveryRemarks, setDeliveryRemarks] = useState(order.deliveryRemarks || '');
    const [isEditingDeliveryRemarks, setIsEditingDeliveryRemarks] = useState(false);

    // Click outside handler for description dropdown
    useClickOutside(descriptionDropdownRef, () => {
        setShowDescriptionDropdown(false);
    });

    // Extract key points from description for dropdown
    const getDescriptionKeyPoints = () => {
        if (!description) return [];
        const lines = description.split('\n').filter(line => line.trim());
        return lines.slice(0, 5); // Show first 5 lines as key points
    };

    useEffect(() => {
        if (!order || !order.id || !categoryId) {
            setQuotes([]);
            setQuotesLoading(false);
            return;
        }

        setQuotesLoading(true);
        setQuotesError(null);

        const quotesQuery = query(collection(db, `orderCategories/${categoryId}/orders/${order.id}/quotes`));
        const unsubscribe = onSnapshot(quotesQuery, (snapshot) => {
            try {
                const fetchedQuotes = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                // Sort quotes by lastUpdated timestamp (most recently updated first)
                fetchedQuotes.sort((a, b) => {
                    const aTime = a.lastUpdated?.toDate ? a.lastUpdated.toDate() : a.lastUpdated || a.createdAt?.toDate ? a.createdAt.toDate() : a.createdAt || 0;
                    const bTime = b.lastUpdated?.toDate ? b.lastUpdated.toDate() : b.lastUpdated || b.createdAt?.toDate ? b.createdAt.toDate() : b.createdAt || 0;
                    return bTime - aTime; // Descending order (newest first)
                });
                setQuotes(fetchedQuotes);
                setQuotesLoading(false);
            } catch (error) {
                console.error('Error fetching category quotes:', error);
                setQuotes([]);
                setQuotesError(error.message);
                setQuotesLoading(false);
            }
        }, (error) => {
            console.error('Firestore error fetching category quotes:', error);
            setQuotes([]);
            setQuotesError(error.message);
            setQuotesLoading(false);
        });

        setFinalPrice(order.finalPrice || '');
        return unsubscribe;
    }, [order, categoryId]);

    const handleDragStart = (index) => {
        setDraggedIndex(index);
    };

    const handleDragOver = (index) => {
        if (draggedIndex === null || draggedIndex === index) return;

        // Reorder quotes array
        const reorderedQuotes = [...quotes];
        const [draggedQuote] = reorderedQuotes.splice(draggedIndex, 1);
        reorderedQuotes.splice(index, 0, draggedQuote);

        // Update the dragged index to the new position
        setDraggedIndex(index);

        // Persist the new order to Firestore immediately
        const batch = writeBatch(db);
        reorderedQuotes.forEach((quote, idx) => {
            const quoteRef = doc(db, `orderCategories/${categoryId}/orders/${order.id}/quotes`, quote.id);
            batch.update(quoteRef, { orderIndex: idx });
        });
        batch.commit().catch(error => console.error('Failed to update quote order:', error));
    };

    const handleDrop = async () => {
        setDraggedIndex(null);
        // Remove dragging class from all quote cards
        document.querySelectorAll('.quote-card-dragging').forEach(el => {
            el.classList.remove('quote-card-dragging');
        });

        // Update order status based on the top quote's status
        if (quotes.length > 0) {
            try {
                const topQuote = quotes[0];
                if (topQuote && topQuote.status && quoteStatusToOrderStatus[topQuote.status]) {
                    const orderRef = doc(db, `orderCategories/${categoryId}/orders`, order.id);
                    const newOrderStatus = quoteStatusToOrderStatus[topQuote.status];
                    await updateDoc(orderRef, { status: newOrderStatus });
                }
            } catch (error) {
                console.error('Failed to update order status:', error);
            }
        }
    };

    // Handlers (adapted for subcollection path)
    const handleUpdateOrder = async (updates) => {
        const orderRef = doc(db, `orderCategories/${categoryId}/orders`, order.id);
        try {
            // If description is being updated, regenerate AI summary
            if (updates.description && updates.description !== order.description) {
                try {
                    const result = await processWithGemini(updates.description);
                    updates.aiSummary = result.summary;
                } catch (error) {
                    console.error('Failed to regenerate AI summary:', error);
                    // If AI summary generation fails, keep the old one
                    delete updates.aiSummary;
                }
            }

            await updateDoc(orderRef, updates);
            setShowEditModal(false);
            setIsEditingFinalPrice(false);
        } catch (error) {
            console.error('Error updating order:', error);
        }
    };

    const handleSaveDeliveryRemarks = async () => {
        try {
            await handleUpdateOrder({ deliveryRemarks: deliveryRemarks.trim() });
            setIsEditingDeliveryRemarks(false);
        } catch (error) {
            console.error('Error saving delivery remarks:', error);
        }
    };
    const handleDeleteOrder = async () => {
        try {
            await deleteDoc(doc(db, `orderCategories/${categoryId}/orders`, order.id));
            // Decrease RN counter when order is deleted
            await decreaseRNCounter();
            setCurrentView('list');
        } catch (error) {
            console.error('Error deleting category order:', error);
        }
    };
    const handleUpdateQuote = async (quoteId, updates) => {
        const quoteRef = doc(db, `orderCategories/${categoryId}/orders/${order.id}/quotes`, quoteId);
        try {
            // Get current quote to check if status needs to be updated
            const quoteDoc = await getDoc(quoteRef);
            const currentQuote = quoteDoc.data();

            // Determine new status based on updated fields
            const updatedQuoteData = { ...currentQuote, ...updates };
            const newStatus = determineQuoteStatus(updatedQuoteData);

            // Update quote with new status if it changed
            const updateData = {
                ...updates,
                status: newStatus,
                lastUpdated: serverTimestamp(),
                orderIndex: 0 // Move edited quote to top
            };

            await updateDoc(quoteRef, updateData);

            // Update orderIndex for all other quotes to shift them down
            const allQuotesQuery = query(collection(db, `orderCategories/${categoryId}/orders/${order.id}/quotes`));
            const snapshot = await getDocs(allQuotesQuery);
            const batch = writeBatch(db);

            snapshot.docs.forEach(doc => {
                if (doc.id !== quoteId) {
                    const currentOrderIndex = doc.data().orderIndex || 999;
                    if (currentOrderIndex < 999) {
                        batch.update(doc.ref, { orderIndex: currentOrderIndex + 1 });
                    }
                }
            });

            await batch.commit();

            // Update order status if quote status changed
            if (currentQuote.status !== newStatus) {
                const newOrderStatus = quoteStatusToOrderStatus[newStatus];
                if (newOrderStatus) {
                    const orderRef = doc(db, `orderCategories/${categoryId}/orders`, order.id);
                    await updateDoc(orderRef, { status: newOrderStatus });
                }
            }

            setQuoteToEdit(null);
        } catch (error) {
            console.error('Error updating quote:', error);
        }
    };

    // Handle quote status change and sync with order status
    const handleQuoteStatusChange = async (quoteId, newStatus) => {
        const quoteRef = doc(db, `orderCategories/${categoryId}/orders/${order.id}/quotes`, quoteId);
        try {
            // Get the current quote to check its previous status
            const quoteDoc = await getDoc(quoteRef);
            const currentQuote = quoteDoc.data();

            // Update the quote status
            await updateDoc(quoteRef, {
                status: newStatus,
                lastUpdated: serverTimestamp()
            });

            // Update order status based on quote status change
            const orderRef = doc(db, `orderCategories/${categoryId}/orders`, order.id);
            const newOrderStatus = quoteStatusToOrderStatus[newStatus];
            if (newOrderStatus && newOrderStatus !== order.status) {
                await updateDoc(orderRef, { status: newOrderStatus });
                // Note: Local state update not needed here as parent component handles it
            }
        } catch (error) {
            console.error('Failed to update quote status:', error);
        }
    };

    // Handle selecting best quote for categorized orders
    const handleSelectBestQuote = async (quoteId) => {
        const orderRef = doc(db, `orderCategories/${categoryId}/orders`, order.id);
        const batch = writeBatch(db);
        const winningQuote = quotes.find(q => q.id === quoteId);
        if (!winningQuote) return;

        let deliveryDate = null;
        if (winningQuote.deliveryTimeline && winningQuote.deliveryTimeline > 0) {
            deliveryDate = new Date();
            deliveryDate.setDate(deliveryDate.getDate() + winningQuote.deliveryTimeline);
        }

        quotes.forEach(q => {
            const quoteRef = doc(db, `orderCategories/${categoryId}/orders/${order.id}/quotes`, q.id);
            batch.update(quoteRef, {
                isBestQuote: (q.id === quoteId),
                finalizedAt: q.id === quoteId ? serverTimestamp() : null
            });
        });

        batch.update(orderRef, {
            status: 'In Progress',
            deliveryDate: deliveryDate,
            deliveryTimeline: winningQuote.deliveryTimeline,
            quantity: winningQuote.quantity,
            supplierCity: winningQuote.supplierCity,
            price: winningQuote.price,
            advancePayment: winningQuote.advancePayment,
            paymentTerms: winningQuote.paymentTerms,
            finalPrice: winningQuote.price, // Set initial final price from winning quote
        });

        await batch.commit();

        if (winningQuote) {
            const notifRef = doc(collection(db, 'notifications'));
            try {
                await setDoc(notifRef, {
                    recipientUid: winningQuote.submittedBy.uid,
                    orderId: order.id,
                    orderTitle: order.orderTitle,
                    orderStatus: 'In Progress',
                    message: `Your quote for '${order.orderTitle}' was selected!`,
                    createdAt: serverTimestamp(),
                    isRead: false,
                });
            } catch (error) {
                console.error('Failed to create notification:', error);
            }
        }
    };
    const handleCreateQuote = async (updates) => {
        const quotesCol = collection(db, `orderCategories/${categoryId}/orders/${order.id}/quotes`);
        try {
            // Validate required fields
            if (!order || !order.id) {
                throw new Error('Order ID is required');
            }
            if (!categoryId) {
                throw new Error('Category ID is required');
            }
            if (!currentUser || !currentUser.uid) {
                throw new Error('User authentication required');
            }
            if (!updates || Object.keys(updates).length === 0) {
                throw new Error('Quote data is required');
            }

            // Determine status based on filled fields
            const quoteStatus = determineQuoteStatus(updates);

            const docRef = await addDoc(quotesCol, {
                ...updates,
                status: quoteStatus, // Set status based on filled fields
                submittedBy: { uid: currentUser.uid, displayName: currentUser.displayName },
                createdAt: serverTimestamp(),
                lastUpdated: serverTimestamp(), // Add timestamp for sorting
                isBestQuote: false,
            });
            setQuoteToEdit(null);
        } catch (error) {
            console.error('Error creating quote:', error);
            console.error('Error details:', {
                code: error.code,
                message: error.message,
                stack: error.stack
            });
            alert('Failed to create quote. Please try again.');
        }
    };
    const handleDescriptionSave = async () => {
        const orderRef = doc(db, `orderCategories/${categoryId}/orders`, order.id);
        try {
            // Regenerate AI summary when description changes
            let updates = { description };
            try {
                const result = await processWithGemini(description);
                updates.aiSummary = result.summary;
            } catch (error) {
                console.error('Failed to regenerate AI summary:', error);
                // If AI summary generation fails, keep the old one
            }

            await updateDoc(orderRef, updates);
            setIsEditingDesc(false);

            // Note: Local state update not needed in category context
        } catch (error) {
            console.error('Error saving description:', error);
            alert('Failed to save description. Please try again.');
        }
    };
    const handleSummarySave = async () => {
        const orderRef = doc(db, `orderCategories/${categoryId}/orders`, order.id);
        try {
            await updateDoc(orderRef, { aiSummary });
            setIsEditingSummary(false);

            // Note: Local state update not needed in category context
        } catch (error) {
            console.error('Error saving summary:', error);
            alert('Failed to save summary. Please try again.');
        }
    };
    // Comments
    useEffect(() => {
        if (!order || !order.id || !categoryId) return;
        const commentsQuery = query(collection(db, `orderCategories/${categoryId}/orders/${order.id}/comments`));
        const unsubscribe = onSnapshot(commentsQuery, (snapshot) => {
            const fetchedComments = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            fetchedComments.sort((a, b) => (a.createdAt?.toDate() || 0) - (b.createdAt?.toDate() || 0));
            setComments(fetchedComments);
        });
        return unsubscribe;
    }, [order, categoryId]);
    const handleAddComment = async (e) => {
        e.preventDefault();
        if (!newComment.trim()) return;
        const commentsCol = collection(db, `orderCategories/${categoryId}/orders/${order.id}/comments`);
        await addDoc(commentsCol, {
            text: newComment.trim(),
            author: { uid: currentUser.uid, displayName: currentUser.displayName, role: currentUser.role },
            createdAt: serverTimestamp(),
        });

        // Send notification to other users about the comment
        await sendDiscussionNotification(order.id, order, currentUser, allUsers, 'text');

        setNewComment('');
    };

    // UI (copied/adapted from OrderDetails)
    const finalizedQuote = useMemo(() => quotes.find(q => q.id === order.finalizedQuoteId), [quotes, order.finalizedQuoteId]);
    const otherQuotes = useMemo(() => quotes.filter(q => q.id !== order.finalizedQuoteId), [quotes, order.finalizedQuoteId]);

    // Check if any quote has "Advance Paid" status
    const hasAdvancePaidQuote = useMemo(() => {
        return quotes.some(quote => quote.status === QUOTE_STATUSES.ADVANCE_PAID);
    }, [quotes]);
    const tabClass = (tabName) => `py-2 px-4 text-sm font-medium cursor-pointer ${activeTab === tabName ? 'border-b-2 border-black text-black' : 'text-gray-500 hover:text-black'}`;
    // Add this inside CategoryOrderDetails, before the return statement:
    const handleDeleteQuote = async (quoteId) => {
        if (!window.confirm('Are you sure you want to delete this quotation? This action cannot be undone.')) return;
        const quoteRef = doc(db, `orderCategories/${categoryId}/orders/${order.id}/quotes`, quoteId);
        await deleteDoc(quoteRef);
    };



    return (
        <div>
            <div className="flex flex-col lg:flex-row gap-8">
                <div className="lg:w-2/3 space-y-8">
                    <div>
                        <div className="flex justify-between items-start mb-4 gap-4">
                            <div className="flex-grow min-w-0">
                                <h2 className="text-xl font-bold break-words flex items-center">
                                    {order.orderTitle}
                                    <span className="font-normal text-gray-600 ml-2">(Qty: {order.quantity !== null && order.quantity !== undefined && order.quantity !== '' ? order.quantity : 'N/A'})</span>
                                    {/* Delivery Remarks in Title - Only for In Progress orders */}
                                    {order.status === 'In Progress' && order.deliveryRemarks && (
                                        <span className="ml-3 px-2 py-1 text-xs font-medium bg-indigo-100 text-indigo-800 border border-indigo-200 rounded-full">
                                            📦 {order.deliveryRemarks.length > 30 ? `${order.deliveryRemarks.substring(0, 30)}...` : order.deliveryRemarks}
                                        </span>
                                    )}
                                </h2>
                            </div>
                            <div className="flex-shrink-0 flex items-center gap-2">
                                {isMaster && (
                                    <StatusChangeDropdown
                                        currentStatus={order.status}
                                        onStatusChange={(newStatus) => handleUpdateOrder({ status: newStatus })}
                                    />
                                )}
                                <Tooltip text="Edit Order">
                                    <button onClick={() => setShowEditModal(true)} className="p-2 rounded-full hover:bg-gray-200">
                                        <Edit size={16} />
                                    </button>
                                </Tooltip>
                                <Tooltip text="Email Supplier">
                                    <button onClick={() => setShowEmailModal(true)} className="p-2 rounded-full hover:bg-gray-200">
                                        <Mail size={16} />
                                    </button>
                                </Tooltip>
                            </div>
                        </div>


                        <div className="border-b border-gray-200 mb-4">
                            <nav className="-mb-px flex space-x-4">
                                <button className={tabClass('received')} onClick={() => setActiveTab('received')}>
                                    Received Requirement
                                </button>
                                <button className={tabClass('generated')} onClick={() => setActiveTab('generated')}>
                                    Requirement Summary
                                </button>
                                <button className={tabClass('emails')} onClick={() => setActiveTab('emails')}>
                                    Mails Sent
                                </button>
                            </nav>
                        </div>
                        <div className="relative min-h-[120px]" style={{ overflow: 'visible' }}>
                            <div className={`transition-all duration-300 ${activeTab === 'received' ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8 pointer-events-none h-0 overflow-hidden'}`}
                                aria-hidden={activeTab !== 'received'}>
                                <div className="p-4 bg-gray-50 border rounded-lg">
                                    {isEditingDesc ? (
                                        <div className="space-y-2">
                                            <textarea value={description} onChange={e => setDescription(e.target.value)} rows={8} className="w-full p-2 border-gray-300 rounded-md" />
                                            <div className="flex gap-2">
                                                <button onClick={handleDescriptionSave} className="bg-black text-white px-3 py-1 rounded-md text-sm">Save</button>
                                                <button onClick={() => setIsEditingDesc(false)} className="bg-gray-200 px-3 py-1 rounded-md text-sm">Cancel</button>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="flex justify-end mb-2">
                                                <Tooltip text="Edit Received Requirement">
                                                    <button onClick={() => setIsEditingDesc(true)} className="p-2 rounded-full hover:bg-gray-200">
                                                        <Edit size={16} />
                                                    </button>
                                                </Tooltip>
                                            </div>
                                            <TruncatedText text={description} className="text-gray-700" maxLines={4} />
                                        </>
                                    )}
                                </div>
                            </div>
                            <div className={`transition-all duration-300 ${activeTab === 'generated' ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8 pointer-events-none h-0 overflow-hidden'}`}
                                aria-hidden={activeTab !== 'generated'}>
                                <div className="p-4 bg-gray-50 border rounded-lg">
                                    {isEditingSummary ? (
                                        <div className="space-y-2">
                                            <textarea value={aiSummary} onChange={e => setAiSummary(e.target.value)} rows={8} className="w-full p-2 border border-gray-300 rounded-md" />
                                            <div className="flex gap-2">
                                                <button onClick={handleSummarySave} className="bg-black text-white px-3 py-1 rounded-md text-sm">Save</button>
                                                <button onClick={() => setIsEditingSummary(false)} className="bg-gray-200 px-3 py-1 rounded-md text-sm">Cancel</button>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="flex justify-end mb-2">
                                                <Tooltip text="Edit Summarized Requirement">
                                                    <button onClick={() => setIsEditingSummary(true)} className="p-2 rounded-full hover:bg-gray-200">
                                                        <Edit size={16} />
                                                    </button>
                                                </Tooltip>
                                            </div>
                                            {order.aiSummary ? <AISummary data={aiSummary} /> : <div className="p-4 text-center text-gray-500 border-2 border-dashed rounded-lg flex items-center justify-center gap-2"><Cpu size={16} /><p>Awaiting requirement generation...</p></div>}
                                        </>
                                    )}
                                </div>
                            </div>
                            <div className={`transition-all duration-300 ${activeTab === 'emails' ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8 pointer-events-none h-0 overflow-hidden'}`}
                                aria-hidden={activeTab !== 'emails'}>
                                {/* You can implement a SentEmailsTimeline for subcollection if needed */}
                                <div className="p-4 bg-gray-50 border rounded-lg text-center text-gray-400">Email timeline not implemented for grouped orders.</div>
                            </div>
                        </div>
                    </div>
                    <div>
                        {finalizedQuote && (order.status === 'In Progress' || order.status === 'Completed') && (
                            <div className="mb-8">
                                <h2 className="font-semibold mb-4">Finalized Requirement</h2>
                                {isEditingFinalPrice ? (
                                    <div className="p-4 border border-blue-500 rounded-lg bg-blue-50">
                                        <InputField label="Final Price (INR)" name="finalPrice" type="number" value={finalPrice} onChange={(e) => setFinalPrice(e.target.value)} />
                                        <div className="flex gap-2 mt-2">
                                            <button onClick={() => handleUpdateOrder({ finalPrice: parseFloat(finalPrice) })} className="bg-black text-white px-3 py-1 rounded-md text-sm">Save</button>
                                            <button onClick={() => { setIsEditingFinalPrice(false); setFinalPrice(order.finalPrice || finalizedQuote.price); }} className="bg-gray-200 px-3 py-1 rounded-md text-sm">Cancel</button>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div className="flex justify-between items-center mb-2">
                                            <p className="text-lg font-bold">Final Price: ₹{order.finalPrice ? formatINR(order.finalPrice) : (finalizedQuote?.price.toFixed(2) || 'N/A')}</p>
                                            {isMaster && (
                                                <button onClick={() => setIsEditingFinalPrice(true)} className="p-2 rounded-full hover:bg-gray-200" title="Edit Final Price">
                                                    <Edit size={16} />
                                                </button>
                                            )}
                                        </div>
                                        <QuoteCard
                                            key={finalizedQuote.id}
                                            quote={finalizedQuote}
                                            orderId={order.id}
                                            currentUser={currentUser}
                                            isMaster={isMaster}
                                            onSelectBest={() => { }}
                                            onEdit={() => setQuoteToEdit(finalizedQuote)}
                                            disabled={order.status === 'Completed'}
                                            expanded={expandedQuoteId === finalizedQuote.id}
                                            onToggle={() => setExpandedQuoteId(expandedQuoteId === finalizedQuote.id ? null : finalizedQuote.id)}
                                            onDelete={isMaster ? () => handleDeleteQuote(finalizedQuote.id) : undefined}
                                            onQuoteStatusChange={handleQuoteStatusChange}
                                            orderStatus={order.status}
                                            order={order}
                                            allUsers={allUsers}
                                            setSelectedOrder={setSelectedOrder}
                                        />

                                        {/* Revert Finalization Button */}
                                        {isMaster && order.status === 'In Progress' && (
                                            <div className="mt-3 flex justify-center">
                                                <button
                                                    onClick={async () => {
                                                        if (!window.confirm('Are you sure you want to revert this quote finalization? This will move the quote back to RFQ and set the order status to the previous status.')) {
                                                            return;
                                                        }

                                                        try {
                                                            // Remove finalizedAt timestamp from the quote
                                                            const quoteRef = doc(db, `orderCategories/${order.categoryId}/orders/${order.id}/quotes`, finalizedQuote.id);
                                                            await updateDoc(quoteRef, {
                                                                finalizedAt: deleteField()
                                                            });

                                                            // Revert order status to previous status (based on quote status)
                                                            const previousStatus = finalizedQuote.status === QUOTE_STATUSES.PROFORMA_RECEIVED ? 'Proforma' :
                                                                finalizedQuote.status === QUOTE_STATUSES.UNDER_NEGOTIATION ? 'Negotiation' :
                                                                    'Awaiting Requirements';

                                                            const orderRef = doc(db, `orderCategories/${order.categoryId}/orders`, order.id);
                                                            await updateDoc(orderRef, {
                                                                status: previousStatus,
                                                                finalizedQuoteId: deleteField(),
                                                                deliveryDate: deleteField(),
                                                                deliveryTimeline: deleteField(),
                                                                quantity: deleteField(),
                                                                supplierCity: deleteField(),
                                                                price: deleteField(),
                                                                advancePayment: deleteField(),
                                                                paymentTerms: deleteField(),
                                                                finalPrice: deleteField()
                                                            });

                                                            // Clear quotes cache to ensure fresh data
                                                            const { clearQuotesCache } = await import('./utils/quotesService');
                                                            clearQuotesCache();

                                                            // Update local state immediately for instant UI update
                                                            if (setSelectedOrder) {
                                                                setSelectedOrder(prev => ({
                                                                    ...prev,
                                                                    status: previousStatus,
                                                                    finalizedQuoteId: undefined,
                                                                    deliveryDate: undefined,
                                                                    deliveryTimeline: undefined,
                                                                    quantity: undefined,
                                                                    supplierCity: undefined,
                                                                    price: undefined,
                                                                    advancePayment: undefined,
                                                                    paymentTerms: undefined,
                                                                    finalPrice: undefined
                                                                }));
                                                            }

                                                            // Force a small delay to ensure state updates are processed
                                                            setTimeout(() => {
                                                                alert(`Quote finalization reverted! Order status set back to ${previousStatus}.`);
                                                            }, 100);
                                                        } catch (error) {
                                                            console.error('Error reverting quote finalization:', error);
                                                            alert('Failed to revert quote finalization. Please try again.');
                                                        }
                                                    }}
                                                    className="bg-orange-600 text-white px-4 py-2 rounded-md hover:bg-orange-700 transition-colors text-sm font-medium"
                                                    title="Revert quote finalization and move back to RFQ"
                                                >
                                                    Revert Finalization
                                                </button>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        )}

                        {/* Info note when quotes are finalized */}
                        {finalizedQuote && order.status === 'In Progress' && (
                            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                <p className="text-sm text-blue-800">
                                    Other quotes are currently disabled since a quote has been finalized.
                                    You can revert the finalization to make them interactive again.
                                </p>
                            </div>
                        )}

                        <h2 className="font-semibold mb-4">Request for Quotation ({otherQuotes.length})</h2>
                        <div className="space-y-4">
                            {otherQuotes.map((quote, index) => (
                                <QuoteCard
                                    key={quote.id}
                                    quote={quote}
                                    orderId={order.id}
                                    currentUser={currentUser}
                                    isMaster={isMaster}
                                    onSelectBest={() => handleSelectBestQuote(quote.id)}
                                    onEdit={() => setQuoteToEdit(quote)}
                                    disabled={order.status === 'Completed' || (finalizedQuote && order.status === 'In Progress')}
                                    expanded={expandedQuoteId === quote.id}
                                    onToggle={() => setExpandedQuoteId(expandedQuoteId === quote.id ? null : quote.id)}
                                    onDelete={isMaster ? () => handleDeleteQuote(quote.id) : undefined}
                                    draggable={!finalizedQuote || order.status !== 'In Progress'}
                                    isDragging={draggedIndex === index}
                                    onDragStart={() => handleDragStart(index)}
                                    onDragOver={() => handleDragOver(index)}
                                    onDrop={handleDrop}
                                    onQuoteStatusChange={handleQuoteStatusChange}
                                    categoryId={categoryId}
                                    orderStatus={order.status}
                                    order={order}
                                    allUsers={allUsers}
                                    setSelectedOrder={setSelectedOrder}
                                />
                            ))}
                            {/* Show the empty + card at the end, only if order is not completed and no quote is finalized */}
                            {(order.status !== 'Completed') && (!finalizedQuote || order.status !== 'In Progress') && (
                                <QuoteCard
                                    key="empty-new-quote"
                                    quote={{}}
                                    orderId={order.id}
                                    currentUser={currentUser}
                                    isMaster={isMaster}
                                    onSelectBest={() => { }}
                                    onEdit={() => setQuoteToEdit({ isNew: true })}
                                    disabled={false}
                                    isEmptyCard={true}
                                    orderStatus={order.status}
                                    order={order}
                                    allUsers={allUsers}
                                    setSelectedOrder={setSelectedOrder}
                                />
                            )}
                            {quotes.length === 0 && !finalizedQuote && <p className="text-gray-500">No requirements submitted yet.</p>}
                        </div>
                    </div>
                </div>
                <div className="lg:w-1/3">
                    <div className="lg:sticky top-24 space-y-4 max-h-[calc(100vh-7rem)] overflow-y-auto pr-2">
                        {/* Replace QuoteForm with DiscussionBox */}
                        <div className="bg-gray-50 p-4 border-t border-gray-200">
                            <h4 className="text-sm font-semibold mb-2">Discussion</h4>
                            <div className="space-y-3 mb-4 max-h-40 overflow-y-auto">
                                {comments.map(comment => (
                                    <div key={comment.id} className="text-sm flex items-center justify-between group">
                                        <span>
                                            <span className={`font-semibold ${comment.author.role === 'master' ? 'text-black' : 'text-gray-700'}`}>{comment.author.displayName}:</span> {comment.text}
                                            <span className="text-xs text-gray-400 ml-2">{comment.createdAt?.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                        </span>
                                        {(currentUser.uid === comment.author.uid || isMaster) && (
                                            <button
                                                onClick={async () => {
                                                    await deleteDoc(doc(db, `orderCategories/${categoryId}/orders/${order.id}/comments`, comment.id));
                                                }}
                                                className="ml-2 text-gray-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                                                title="Delete Comment"
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        )}
                                    </div>
                                ))}
                                {comments.length === 0 && <p className="text-xs text-gray-500">No comments yet.</p>}
                            </div>
                            <form onSubmit={handleAddComment} className="flex gap-2">
                                <input type="text" value={newComment} onChange={(e) => setNewComment(e.target.value)} placeholder="Add a comment..." className="flex-grow w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-black" />
                                <button type="submit" className="bg-black text-white px-4 py-1.5 rounded-md"><Send size={16} /></button>
                            </form>
                        </div>

                        {/* Delivery Remarks Section - Only for In Progress orders */}
                        {order.status === 'In Progress' && (
                            <div className="mt-8 p-6 border border-gray-200 bg-gray-50 rounded-lg">
                                <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                                    <span className="px-3 py-1 text-xs font-semibold rounded-full bg-indigo-100 text-indigo-800 border border-indigo-200">
                                        Awaiting Delivery
                                    </span>
                                    Delivery Remarks
                                </h3>
                                {isEditingDeliveryRemarks ? (
                                    <div className="space-y-3">
                                        <textarea
                                            value={deliveryRemarks}
                                            onChange={(e) => setDeliveryRemarks(e.target.value)}
                                            placeholder="Add delivery remarks (e.g., 'Delivery in transit', 'Expected delivery tomorrow')..."
                                            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
                                            rows={3}
                                        />
                                        <div className="flex gap-2">
                                            <button
                                                onClick={handleSaveDeliveryRemarks}
                                                className="px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 transition-colors"
                                            >
                                                Save Remarks
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setIsEditingDeliveryRemarks(false);
                                                    setDeliveryRemarks(order.deliveryRemarks || '');
                                                }}
                                                className="px-4 py-2 bg-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-300 transition-colors"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {deliveryRemarks ? (
                                            <div className="p-3 bg-white border border-gray-200 rounded-lg">
                                                <p className="text-gray-700 text-sm">{deliveryRemarks}</p>
                                            </div>
                                        ) : (
                                            <p className="text-gray-500 text-sm italic">No delivery remarks added yet</p>
                                        )}
                                        <button
                                            onClick={() => setIsEditingDeliveryRemarks(true)}
                                            className="w-full flex items-center justify-center gap-2 text-sm bg-indigo-600 text-white px-3 py-2 rounded-md hover:bg-indigo-700 transition-colors"
                                        >
                                            <Edit size={16} /> {deliveryRemarks ? 'Edit Remarks' : 'Add Remarks'}
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}

                        {isMaster && (
                            <div className="mt-8 p-6 border border-gray-200 bg-gray-50 rounded-lg">
                                <h3 className="font-semibold text-lg mb-4">Master Actions</h3>
                                <div className="space-y-4">
                                    {order.status === 'In Progress' && (
                                        <button
                                            onClick={() => handleUpdateOrder({ status: 'Completed' })}
                                            className="w-full flex items-center justify-center gap-2 text-sm bg-green-600 text-white px-3 py-2 rounded-md hover:bg-green-700 transition-colors"
                                        >
                                            <CheckCircle size={16} /> Mark as Completed
                                        </button>
                                    )}
                                    <button
                                        onClick={() => setShowDeleteConfirm(true)}
                                        className="w-full flex items-center justify-center gap-2 text-sm bg-red-600 text-white px-3 py-2 rounded-md hover:bg-red-700 transition-colors"
                                    >
                                        <Trash2 size={16} /> Delete This Order
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {showDeleteConfirm && <ConfirmDeleteModal onCancel={() => setShowDeleteConfirm(false)} onConfirm={handleDeleteOrder} />}
            {showEmailModal && <EmailModal order={order} onClose={() => setShowEmailModal(false)} onEmailSent={() => { }} allVendors={allVendors} />}
            {showEditModal && <EditOrderModal order={order} onClose={() => setShowEditModal(false)} onSave={handleUpdateOrder} />}
            {quoteToEdit && (
                quoteToEdit.isNew ? (
                    <EditQuoteModal orderId={order.id} quote={{}} onClose={() => setQuoteToEdit(null)} onCreate={handleCreateQuote} allVendors={allVendors} isNew={true} />
                ) : (
                    <EditQuoteModal orderId={order.id} quote={quoteToEdit} onClose={() => setQuoteToEdit(null)} onSave={handleUpdateQuote} allVendors={allVendors} />
                )
            )}
        </div>
    );
};

// Add MoveOrderDropdown component in MainApp
const MoveOrderDropdown = ({ categories, onMove }) => {
    const [open, setOpen] = useState(false);
    return (
        <div className="relative">
            <button
                onClick={e => { e.stopPropagation(); setOpen(o => !o); }}
                className="p-2 rounded-full bg-blue-100 text-blue-600 hover:bg-blue-200"
                title="Move to another category"
            >
                <ChevronRight size={16} />
            </button>
            {open && (
                <div className="absolute right-0 mt-2 w-48 bg-white border rounded-md shadow-lg z-20">
                    <div className="p-2 text-xs text-gray-500">Move to:</div>
                    {categories.length === 0 ? (
                        <div className="p-2 text-gray-400">No other categories</div>
                    ) : (
                        categories.map(cat => (
                            <button
                                key={cat.id}
                                onClick={e => { e.stopPropagation(); onMove(cat.id); setOpen(false); }}
                                className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                            >
                                {cat.title}
                            </button>
                        ))
                    )}
                </div>
            )}
        </div>
    );
};

// 4. Add the CategoryEmailModal component at the bottom of the file (near EmailModal)
const CategoryEmailModal = ({ category, orders, onClose, allVendors }) => {
    const [selectedVendors, setSelectedVendors] = useState([]);
    const [cc, setCc] = useState('');
    const [bcc, setBcc] = useState('');
    const [subject, setSubject] = useState(`Requirement for ${category.title}`);
    const [body, setBody] = useState(() =>
        orders.map((order, idx) =>
            `${idx + 1}. ${order.orderTitle}\n${order.description}\n\n`
        ).join('')
    );
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState('');
    const [successCount, setSuccessCount] = useState(0);
    const [failCount, setFailCount] = useState(0);
    const [vendorSearch, setVendorSearch] = useState('');
    const [vendorSuggestions, setVendorSuggestions] = useState([]);
    const [showVendorSuggestions, setShowVendorSuggestions] = useState(false);
    const vendorSearchRef = useRef(null);

    // Vendor search logic (reuse from EmailModal)
    useEffect(() => {
        if (vendorSearch.trim()) {
            const filtered = allVendors.filter(vendor =>
                vendor.name?.toLowerCase().includes(vendorSearch.toLowerCase()) ||
                vendor.email?.toLowerCase().includes(vendorSearch.toLowerCase())
            );
            setVendorSuggestions(filtered);
            setShowVendorSuggestions(filtered.length > 0);
        } else {
            setVendorSuggestions([]);
            setShowVendorSuggestions(false);
        }
    }, [vendorSearch, allVendors]);

    // Close vendor suggestions when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (vendorSearchRef.current && !vendorSearchRef.current.contains(event.target)) {
                setShowVendorSuggestions(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleVendorToggle = (vendor) => {
        setSelectedVendors(prev => {
            if (prev.some(v => v.id === vendor.id)) {
                return prev.filter(v => v.id !== vendor.id);
            } else {
                return [...prev, vendor];
            }
        });
        setShowVendorSuggestions(false); // Close dropdown
        setVendorSearch(''); // Clear search bar
    };
    const handleRemoveVendor = (vendorId) => {
        setSelectedVendors(prev => prev.filter(v => v.id !== vendorId));
    };

    // Send email logic (copy from EmailModal, but for all selected vendors)
    const handleSendEmail = useCallback(async () => {
        setSending(true);
        setSent(false);
        setError('');
        setSuccessCount(0);
        setFailCount(0);
        const provider = new OAuthProvider('microsoft.com');
        provider.addScope('mail.send');
        try {
            const result = await signInWithPopup(auth, provider);
            const credential = OAuthProvider.credentialFromResult(result);
            const token = credential.accessToken;
            let successes = 0;
            let fails = 0;
            for (const vendor of selectedVendors) {
                const toEmail = vendor.email;
                if (!toEmail) continue;
                // Parse CC/BCC (comma-separated)
                const ccList = cc.split(',').map(e => e.trim()).filter(Boolean);
                const bccList = bcc.split(',').map(e => e.trim()).filter(Boolean);
                const message = {
                    message: {
                        subject: subject,
                        body: {
                            contentType: "Text",
                            content: body,
                        },
                        toRecipients: [
                            {
                                emailAddress: {
                                    address: toEmail,
                                },
                            },
                        ],
                        ccRecipients: ccList.map(addr => ({ emailAddress: { address: addr } })),
                        bccRecipients: bccList.map(addr => ({ emailAddress: { address: addr } })),
                    },
                    saveToSentItems: "true",
                };
                const graphResponse = await fetch("https://graph.microsoft.com/v1.0/me/sendMail", {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(message),
                });
                if (graphResponse.ok) {
                    // Save email details to Firestore
                    try {
                        const emailData = {
                            categoryId: category.id,
                            recipient: toEmail,
                            cc,
                            bcc,
                            subject,
                            body,
                            sentBy: {
                                uid: auth.currentUser?.uid,
                                displayName: auth.currentUser?.displayName || 'Unknown User',
                                email: auth.currentUser?.email
                            },
                            sentAt: serverTimestamp(),
                            status: 'sent',
                            isCategoryEmail: true
                        };
                        await addDoc(collection(db, 'sentEmails'), emailData);
                    } catch (firestoreError) {
                        // Don't fail the email send if Firestore save fails
                    }
                    successes++;
                } else {
                    fails++;
                }
            }
            setSuccessCount(successes);
            setFailCount(fails);
            setSent(true);
            setSending(false);
        } catch (error) {
            setError(error.message || 'Failed to send email.');
            setSending(false);
        }
    }, [subject, body, cc, bcc, selectedVendors, category.id]);

    const requiredFilled = selectedVendors.length > 0 && subject.trim() && body.trim();

    // Keyboard shortcuts (Escape/Enter)
    React.useEffect(() => {
        const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [onClose]);
    React.useEffect(() => {
        const handleEscEnter = (e) => {
            if (e.key === 'Escape') onClose();
            if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
                if (requiredFilled && !sending && !sent) handleSendEmail();
            }
        };
        window.addEventListener('keydown', handleEscEnter);
        return () => window.removeEventListener('keydown', handleEscEnter);
    }, [selectedVendors, subject, body, sending, sent, onClose, requiredFilled, handleSendEmail]);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl m-4 max-h-[80vh] overflow-y-auto">
                <h2 className="text-xl font-bold mb-4">Email All Suppliers for {category.title}</h2>
                <div className="mb-4" ref={vendorSearchRef}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">To (search and select vendors)</label>
                    <input
                        type="text"
                        value={vendorSearch}
                        onChange={e => setVendorSearch(e.target.value)}
                        placeholder="Search vendors by name or email..."
                        className="w-full p-2 border border-gray-300 rounded-md mb-2"
                    />
                    {showVendorSuggestions && (
                        <div className="border rounded-md bg-white shadow-md max-h-40 overflow-y-auto absolute z-30">
                            {vendorSuggestions.map(vendor => (
                                <div
                                    key={vendor.id}
                                    className="px-3 py-2 cursor-pointer hover:bg-gray-100 flex justify-between items-center"
                                    onClick={() => handleVendorToggle(vendor)}
                                >
                                    <span>{vendor.name} <span className="text-xs text-gray-500">{vendor.email}</span></span>
                                    {selectedVendors.some(v => v.id === vendor.id) && <span className="text-green-600 font-bold">✓</span>}
                                </div>
                            ))}
                        </div>
                    )}
                    {/* Show selected vendors as tags */}
                    <div className="flex flex-wrap gap-2 mt-2">
                        {selectedVendors.map(vendor => (
                            <span key={vendor.id} className="bg-gray-200 px-2 py-1 rounded-full text-xs flex items-center">
                                {vendor.name}
                                <button type="button" className="ml-1 text-gray-500 hover:text-red-600" onClick={() => handleRemoveVendor(vendor.id)}>&times;</button>
                            </span>
                        ))}
                    </div>
                </div>
                <InputField label="CC" name="cc" value={cc} onChange={e => setCc(e.target.value)} placeholder="Comma-separated emails" />
                <InputField label="BCC" name="bcc" value={bcc} onChange={e => setBcc(e.target.value)} placeholder="Comma-separated emails" />
                <InputField label="Subject" name="subject" value={subject} onChange={e => setSubject(e.target.value)} />
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Body</label>
                    <textarea value={body} onChange={e => setBody(e.target.value)} rows="10" className="w-full p-2 border border-gray-300 rounded-md"></textarea>
                </div>
                <div className="mt-6 flex justify-end gap-4">
                    <button onClick={onClose} className="bg-gray-200 px-4 py-2 rounded-md hover:bg-gray-300">Cancel</button>
                    <button onClick={handleSendEmail} className="bg-black text-white px-4 py-2 rounded-md hover:bg-gray-800" disabled={sending || sent || !requiredFilled}>Send</button>
                </div>
                {/* Status message */}
                <div className="mt-4 min-h-[24px]">
                    {sending && <span className="text-blue-600 font-medium">Sending email...</span>}
                    {sent && !sending && (
                        <span className="text-green-600 font-medium">
                            {successCount > 0 && `Sent to ${successCount} vendor${successCount > 1 ? 's' : ''}. `}
                            {failCount > 0 && `${failCount} failed.`}
                        </span>
                    )}
                    {error && <span className="text-red-600 font-medium">{error}</span>}
                </div>
            </div>
        </div>
    );
};

// AddCategoryModal for creating a new category
const AddCategoryModal = ({ onClose, onCreate, loading }) => {
    const [title, setTitle] = useState('');
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md m-4">
                <h2 className="text-xl font-bold mb-4">Add Category</h2>
                <input
                    type="text"
                    value={title}
                    onChange={e => setTitle(e.target.value)}
                    placeholder="Category Title"
                    className="w-full border px-3 py-2 rounded mb-4"
                />
                <div className="flex justify-end gap-2">
                    <button onClick={onClose} className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300">Cancel</button>
                    <button
                        onClick={() => { if (title.trim()) onCreate(title); }}
                        className="px-4 py-2 bg-black text-white rounded hover:bg-gray-800"
                        disabled={loading || !title.trim()}
                    >
                        {loading ? 'Creating...' : 'Create'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// AddExistingOrderToCategoryModal for adding existing orders to categories
const AddExistingOrderToCategoryModal = ({ onClose, onAdd, categories, order, loading }) => {
    const [selectedCategoryId, setSelectedCategoryId] = useState('');
    const [searchQuery, setSearchQuery] = useState('');

    const filteredCategories = categories.filter(category =>
        category.title.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const handleSubmit = (e) => {
        e.preventDefault();
        if (selectedCategoryId) {
            onAdd(selectedCategoryId);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md m-4 max-h-[90vh] overflow-y-auto">
                <form onSubmit={handleSubmit} className="space-y-4">
                    <h2 className="text-xl font-bold mb-4">Add Order to Category</h2>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Order Details
                        </label>
                        <div className="bg-gray-50 p-3 rounded-md">
                            <p className="font-semibold">{order?.orderTitle}</p>
                            <p className="text-sm text-gray-600">{order?.description}</p>
                            <p className="text-sm text-gray-500">RN: {order?.serialNumber || 'No RN'}</p>
                        </div>
                    </div>

                    <div>
                        <label htmlFor="categorySearch" className="block text-sm font-medium text-gray-700 mb-2">
                            Search Categories
                        </label>
                        <input
                            type="text"
                            id="categorySearch"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                            placeholder="Type to search categories..."
                        />
                    </div>

                    <div>
                        <label htmlFor="categorySelect" className="block text-sm font-medium text-gray-700 mb-2">
                            Select Category
                        </label>
                        <select
                            id="categorySelect"
                            value={selectedCategoryId}
                            onChange={(e) => setSelectedCategoryId(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                            required
                        >
                            <option value="">Choose a category...</option>
                            {filteredCategories.map(category => (
                                <option key={category.id} value={category.id}>
                                    {category.title} {category.serialNumber && `(${category.serialNumber})`}
                                </option>
                            ))}
                        </select>
                    </div>

                    {filteredCategories.length === 0 && searchQuery && (
                        <div className="text-sm text-gray-500 text-center py-2">
                            No categories found matching "{searchQuery}"
                        </div>
                    )}

                    <div className="flex justify-end gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading || !selectedCategoryId}
                            className="px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            {loading ? 'Adding...' : 'Add to Category'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// AddOrdersToCategoryModal for adding multiple existing orders to a specific category
const AddOrdersToCategoryModal = ({ onClose, onAdd, category, allOrders, loading }) => {
    const [selectedOrderIds, setSelectedOrderIds] = useState(new Set());
    const [searchQuery, setSearchQuery] = useState('');

    // Filter out orders that are already in categories
    const availableOrders = allOrders.filter(order =>
        !order.isCategoryOrder && !order.categoryId
    );

    const filteredOrders = availableOrders.filter(order =>
        order.orderTitle?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        order.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        order.serialNumber?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const handleToggleOrder = (orderId) => {
        setSelectedOrderIds(prev => {
            const newSet = new Set(prev);
            if (newSet.has(orderId)) {
                newSet.delete(orderId);
            } else {
                newSet.add(orderId);
            }
            return newSet;
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (selectedOrderIds.size > 0) {
            onAdd(Array.from(selectedOrderIds));
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl m-4 max-h-[90vh] overflow-y-auto">
                <form onSubmit={handleSubmit} className="space-y-4">
                    <h2 className="text-xl font-bold mb-4">Add Orders to Category</h2>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Category Details
                        </label>
                        <div className="bg-gray-50 p-3 rounded-md">
                            <p className="font-semibold">{category?.title}</p>
                            <p className="text-sm text-gray-500">RN: {category?.serialNumber || 'No RN'}</p>
                        </div>
                    </div>

                    <div>
                        <label htmlFor="orderSearch" className="block text-sm font-medium text-gray-700 mb-2">
                            Search Orders
                        </label>
                        <input
                            type="text"
                            id="orderSearch"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                            placeholder="Type to search orders by title, description, or RN number..."
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Available Orders ({filteredOrders.length})
                        </label>
                        <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-md">
                            {filteredOrders.length === 0 ? (
                                <div className="p-4 text-center text-gray-500">
                                    {searchQuery ? 'No orders found matching your search.' : 'No standalone orders available.'}
                                </div>
                            ) : (
                                <div className="space-y-2 p-2">
                                    {filteredOrders.map(order => (
                                        <label key={order.id} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={selectedOrderIds.has(order.id)}
                                                onChange={() => handleToggleOrder(order.id)}
                                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                            />
                                            <div className="flex-1">
                                                <TruncatedTitle
                                                    title={order.orderTitle}
                                                    quantity={order.quantity}
                                                    className="text-sm"
                                                />
                                                <div className="flex items-center gap-2">
                                                    <p className="text-sm text-gray-600 flex-grow">{order.description}</p>
                                                    <Tooltip text="Copy description">
                                                        <button
                                                            onClick={() => navigator.clipboard.writeText(order.description || '')}
                                                            className="p-1 rounded-md hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600 flex-shrink-0"
                                                        >
                                                            <Copy size={12} />
                                                        </button>
                                                    </Tooltip>
                                                </div>
                                                <p className="text-xs text-gray-500">RN: {order.serialNumber || 'No RN'}</p>
                                            </div>
                                        </label>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {selectedOrderIds.size > 0 && (
                        <div className="text-sm text-blue-600">
                            {selectedOrderIds.size} order{selectedOrderIds.size !== 1 ? 's' : ''} selected
                        </div>
                    )}

                    <div className="flex justify-end gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading || selectedOrderIds.size === 0}
                            className="px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            {loading ? 'Adding...' : `Add ${selectedOrderIds.size} Order${selectedOrderIds.size !== 1 ? 's' : ''} to Category`}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// Helper to get vendor name by id
function getVendorNameById(vendorId, allVendors, fallbackSupplier) {
    if (!vendorId || !Array.isArray(allVendors)) return fallbackSupplier || '';
    const vendor = allVendors.find(v => v.id === vendorId);
    return vendor ? vendor.name : (fallbackSupplier || '');
}

// Add this function at the top of the file, after the imports
const useQuotesWithLoading = (orderId) => {
    const [quotes, setQuotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!orderId) {
            setQuotes([]);
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);

        const quotesQuery = query(collection(db, `orders/${orderId}/quotes`));
        const unsubscribe = onSnapshot(quotesQuery, (snapshot) => {
            try {
                const fetchedQuotes = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                // Sort quotes by orderIndex first, then by lastUpdated timestamp
                fetchedQuotes.sort((a, b) => {
                    // First, sort by orderIndex if both have it
                    if (a.orderIndex !== undefined && b.orderIndex !== undefined) {
                        return a.orderIndex - b.orderIndex; // Ascending order (0 is first)
                    }

                    // If only one has orderIndex, it comes first
                    if (a.orderIndex !== undefined) return -1;
                    if (b.orderIndex !== undefined) return 1;

                    // Otherwise, sort by lastUpdated timestamp (most recently updated first)
                    const aTime = a.lastUpdated?.toDate ? a.lastUpdated.toDate() : a.lastUpdated || a.createdAt?.toDate ? a.createdAt.toDate() : a.createdAt || 0;
                    const bTime = b.lastUpdated?.toDate ? b.lastUpdated.toDate() : b.lastUpdated || b.createdAt?.toDate ? b.createdAt.toDate() : b.createdAt || 0;
                    return bTime - aTime; // Descending order (newest first)
                });
                setQuotes(fetchedQuotes);
                setLoading(false);
            } catch (error) {
                console.error('Error fetching quotes:', error);
                setQuotes([]);
                setError(error.message);
                setLoading(false);
            }
        }, (error) => {
            console.error('Firestore error fetching quotes:', error);
            setQuotes([]);
            setError(error.message);
            setLoading(false);
        });

        return unsubscribe;
    }, [orderId]);

    return { quotes, loading, error };
};

export default App;